import type { Translations } from "./en";

export const cs: Translations = {
  // ── Common ──────────────────────────────────────────────
  common: {
    optivo: "Optivo",
    save: "Uložit",
    saving: "Ukládání...",
    cancel: "Zrušit",
    test: "Test",
    active: "Aktivní",
    loading: "Načítání...",
    dismiss: "Zavřít",
    // Viz en.ts — název křížku, který si dialog a boční panel kreslí samy.
    close: "Zavřít",
    delete: "Smazat",
    confirm: "Potvrdit",
    error: "Něco se pokazilo",
  },

  // ── Stránkování ─────────────────────────────────────────
  // Viz en.ts — slova, která si stránkování (`ui/pagination`) kreslí samo.
  pagination: {
    label: "Stránkování",
    previous: "Předchozí",
    next: "Další",
    previousPage: "Předchozí stránka",
    nextPage: "Další stránka",
    morePages: "Další stránky",
    page: "Stránka {page}",
  },

  // ── Kalendář ────────────────────────────────────────────
  // Viz en.ts — názvy, které si kalendář (`ui/calendar`) dává sám.
  calendar: {
    navigation: "Přepínání měsíců",
    previousMonth: "Předchozí měsíc",
    nextMonth: "Další měsíc",
    today: "Dnes, {date}",
    selected: "{date}, vybráno",
    monthDropdown: "Vyberte měsíc",
    yearDropdown: "Vyberte rok",
    weekNumber: "Týden {week}",
    weekNumberHeader: "Číslo týdne",
  },

  // ── Strength presets ────────────────────────────────────
  strengthPreset: {
    off: "Vypnout",
    mild: "Mírný",
    medium: "Střední",
    high: "Vysoký",
    custom: "Vlastní",
  },

  // ── Applying a property-wide setting to every room type ─────────────────
  // Viz en.ts — jedna sada slov pro každou sekci, která vlastní nastavení,
  // jež si typ pokoje může převzít.
  applyToRoomKinds: {
    action: "Použít na všechny typy pokojů",
    title: "Přepsat, co si tyto typy pokojů nastavily?",
    body: "Tyto typy pokojů si toto nastavily samy, takže se k nim změna zde nedostane. Použitím se jejich nastavení zruší a budou znovu sledovat nastavení celého objektu — teď i při každé další změně.",
    confirm: "Použít na všechny typy pokojů",
  },

  accessScope: {
    legend: "Které objekty",
    legendRemove: "Odebrat z objektů",
    onlyThis: "Jen {name}",
    allFull: "Všechny objekty účtu ({count}), i ty, které účet přidá později",
    allReadOnly: "Všechny objekty, které účet má dnes ({count})",
    allRemove: "Všechny objekty účtu, ke kterým má přístup ({count})",
    some: "Vybrané objekty",
    chooseOne: "Vyberte alespoň jeden objekt.",
    alwaysIncluded: "Objekt {name} je zahrnut vždy.",
    propertyById: "Objekt č. {id}",
    reachWhole: "Všechny objekty účtu",
    reachOnlyThis: "Jen tento objekt",
    reachSome: "{count} z {total} objektů: {names}",
    reachFull: "Plný přístup: {names}",
    reachFullWhole: "Plný přístup: všechny objekty účtu",
    reachReadOnly: "Jen pro čtení: {names}",
    keeps: "Ponechá si přístup k objektům: {names}.",
    keepsOne: "Ponechá si přístup k objektu {names}.",
    keepsFull: "Ponechá si plný přístup k objektům: {names}.",
    keepsFullOne: "Ponechá si plný přístup k objektu {names}.",
    keepsReadOnly: "Ponechá si přístup jen pro čtení k objektům: {names}.",
    keepsReadOnlyOne: "Ponechá si přístup jen pro čtení k objektu {names}.",
    addedLater: "Objekty, které účet přidá později, už zahrnuty nebudou.",
    leaves: "Nebude mít přístup k žádnému objektu tohoto účtu.",
    noOther: "K žádnému jinému objektu tohoto účtu přístup nemá.",
    legacyLoses: "Ztratí přístup ke všem ostatním objektům tohoto účtu.",
    notHonoured: "Plný přístup byl udělen ke všem objektům účtu, nikoli jen k vybraným. Pokud to tak být nemá, přístup odeberte nebo ho změňte na jeho řádku.",
    alreadyWholeAccount: "Tento člověk už má plný přístup ke všem objektům účtu, nic se tedy nezměnilo.",
    alreadyFullAccess: "Tento člověk už má plný přístup k objektům: {names}.",
    alreadyFullAccessOne: "Tento člověk už má plný přístup k objektu {names}.",
    alreadyReadOnlyElsewhere: "Tento člověk už má přístup jen pro čtení ({kind}) k objektům: {names}. Změňte ho na jeho řádku v přístupech daného objektu.",
    alreadyReadOnlyElsewhereOne: "Tento člověk už má přístup jen pro čtení ({kind}) k objektu {names}. Změňte ho na jeho řádku v přístupech daného objektu.",
    removeTitle: "Z kterých objektů odebrat uživatele {user}?",
    removeDescription: "Uživatel {user} má přístup k {count} objektům tohoto účtu. Zvolte, ze kterých ho odebrat.",
    removeAction: "Odebrat přístup",
    fullAccessTitle: "Dát uživateli {user} plný přístup?",
    fullAccessDescription: "Bude moci měnit ceny, strategie a nastavení objektů, které zvolíte. Přístup jen pro čtení k těmto objektům se tím nahradí.",
    fullAccessAction: "Dát plný přístup",
    promoteTitle: "Povýšit uživatele {user} na vlastníka?",
    promoteDescription: "Vlastník má přístup ke všem objektům účtu, i k těm, které účet přidá později. Dnes má uživatel {user} přístup k objektům: {names}.",
    promoteDescriptionOne: "Vlastník má přístup ke všem objektům účtu, i k těm, které účet přidá později. Dnes má uživatel {user} přístup k objektu {names}.",
    promoteAction: "Povýšit na vlastníka",
    cancel: "Zrušit",
  },

  // ── Odkud úroveň poptávky platí ─────────────────────────
  // Viz en.ts — úroveň poptávky navázaného plánu je celková poptávka po noci
  // oproti běžné noci typu pokoje, kdežto kalendář cen barví stejnými slovy
  // jen tempo rezervací. `{pct}` přichází se znaménkem („−15“, „+10“).
  demandLevelBand: {
    below: "pod {pct}\u00a0%",
    from: "od {pct}\u00a0%",
  },

  // ── Analysis-derived values ─────────────────────────────
  followsAnalysis: {
    bookingWindow:
      "Typ pokoje, který jste nezměnili, se řídí měřením z vaší historie rezervací, přepočítávaným denně: kolik dní před příjezdem už bývá zarezervována polovina jeho obsazených nocí (počítá se každá noc pobytu). To říká, kdy hosté rezervují, ne jak dlouho držet cenu, takže typ pokoje, který se rezervuje dlouho dopředu, drží cenu dlouho. Jakmile číslo vyplníte, platí, dokud pole nevymažete.",
    basePrice:
      "Měřeno z vaší historie rezervací a přepočítáváno denně. Prázdné pole se řídí tímto měřením; vyplněná cena zůstane pevná, dokud ji nevymažete.",
    caution:
      "Hodnotu přepisujte jen tehdy, když pro to máte důvod: přestane sledovat vaše skutečné obchodování a je to základ, vůči kterému se počítá zbytek cen.",
  },

  // ── Ceny z ceníku v PMS ─────────────────────────────────
  priceListSource: {
    effective: "Optivo použije {value} — cenu všedního dne z ceníku ve vašem PMS.",
    reference: "Z ceníku v PMS",
    basePrice:
      "Tento typ pokoje zatím nemá historii rezervací, a proto Optivo vychází z ceny všedního dne, kterou pro něj uvádí ceník ve vašem PMS. Jakmile bude mít dost vlastních rezervací k měření, bude se cena řídit jimi. Chcete-li vlastní cenu, zadejte ji.",
  },

  // ── Okno rezervací pro víkendy a svátky ─────────────────
  peakNightBookingWindow: {
    label: "Víkendové a sváteční noci",
    hint: "Páteční a sobotní noci, noci před svátkem nebo zvláštním dnem s příplatkem a přemosťovací noci mezi nimi nemohou klesnout pod svou upravenou základní cenu dřív než tolik dní před příjezdem. Hodí se, když se tyto noci prodávají za plnou cenu i krátce před příjezdem. Platí pro všechny typy pokojů a sezóny místo oken v tabulce.",
    follows: "Prázdné pole: tyto noci se řídí oknem svého typu pokoje v tabulce.",
    stated: "Vymažte pole a tyto noci se znovu budou řídit tabulkou.",
  },

  // ── Dny na okraji špičky ────────────────────────────────
  // Viz en.ts — slova patří ovládacímu prvku, který vykreslují obě plochy.
  shoulderDay: {
    enable: "Upravit cenu dnů na okraji špičky",
    hint: "Den na okraji špičky je běžná noc těsně vedle svátečního volna: noc před jeho začátkem a noc jeho posledního dne. Poptávka se soustředí na samotné volno a po obou jeho stranách obvykle slábne. Běžný víkend je nemá — svátek k němu musí přidat noc, nebo připadnout doprostřed týdne.",
    adjustment: "Úprava ceny dne na okraji špičky",
    adjustmentHint: "Počítá se ze základní ceny a sčítá se s úpravou pro daný den v týdnu. Obvykle sleva zhruba do 15 % — zadejte plus, pokud si hosté volno o tyto noci naopak prodlužují.",
  },

  // ── Minimální cena podle sezónnosti ─────────────────────
  // Viz en.ts — jedna volba pro obě minima a každá věta, která říká, co dělá.
  minimumSeasonality: {
    label: "Minimální cena během roku",
    lastMinuteLabel: "Last-minute minimum během roku",
    // Obě volby stojí pod „Minimální cena“ (ž. r.) i pod „Last-minute
    // minimum“ (stř. r.), proto žádná nesmí mít rod.
    fixed: "Celý rok stejně",
    follows: "Podle sezónnosti",
    fixedHint: "Minimální cena je ve všech měsících stejná.",
    followsHint: "Minimum se posouvá o stejné procento jako měsíc noci v Sezónnosti: minimum 120 je v měsíci s −20 % 96 a v měsíci s +25 % 150.",
    lastMinuteFixedHint: "Last-minute minimum je stejné v každém měsíci.",
    lastMinuteFollowsHint: "Last-minute minimum se posouvá podle měsíce noci v Sezónnosti, nikdy podle příplatku za víkend nebo svátek.",
    followsUsual: "Dokud tu nic nezvolíte, řídí se volbou „{label}“ v sekci {setIn} — teď {choice}.",
    departsFromUsual: "Zvoleno tady: „{label}“ v sekci {setIn} je {choice}. Zvolte totéž a bude se zase řídit tou volbou.",
    lastMinuteFollowsUsual: "Stejně jako minimální cena",
    range: "Podle sezónnosti: od {low} ({lowMonth}) do {high} ({highMonth}).",
    ordinaryMonth: "běžný měsíc",
    bandSummary: "U typu {roomKind} počítá Optivo ceny od {base}: noční cena zůstane pod {max} a nad minimem, které se mění se sezónností, {minLow}–{minHigh}.",
    fixedLowMonth: "Minimum je pevné celý rok, takže slabší měsíc v Sezónnosti cenu pod {min} nesníží.",
    fixedLowMonthLastMinuteFollows: "Minimum je pevné celý rok, takže slabší měsíc v Sezónnosti cenu pod {min} nesníží, s výjimkou last-minute okna: tam se last-minute minimum posouvá podle Sezónnosti.",
    // Měsíc v závorce, kde stojí v 1. pádě (Intl jiný pád nedá).
    aboveMaximum: "Minimum se posune na {value} ({month}), nad maximum {max} — tam platí maximum.",
    lastMinuteColumn: "Každá částka v tomto sloupci se posouvá podle měsíce noci v Sezónnosti, od {low} do {high}.",
    seasonStatus: "Minimální ceny: {label} — {choice} ({setIn}); {lastMinuteLabel} — {lastMinuteChoice} ({lastMinuteSetIn}).",
  },

  // ── Hranice obsazenosti nevratné ceny v pokojích ────────
  // Viz en.ts. Sloveso i podstatné jméno se shodují s číslem, které větu řídí:
  // 1 pokoj je prodán, 2–4 pokoje jsou prodány, 5 a více pokojů je prodáno.
  nonRefundableCeilingRooms: {
    offeredUpTo: {
      one: "Nabízí se, dokud je prodán nejvýše {count} z {rooms} pokojů tohoto typu.",
      few: "Nabízí se, dokud jsou prodány nejvýše {count} z {rooms} pokojů tohoto typu.",
      other: "Nabízí se, dokud je prodáno nejvýše {count} z {rooms} pokojů tohoto typu.",
    },
    offeredWhileNoneSold: "Nabízí se, jen dokud není prodán žádný z {rooms} pokojů tohoto typu.",
    neverWithheld: {
      one: "Typ má {count} pokoj, který je buď volný, nebo prodaný, takže tato hranice nevratnou cenu nikdy nevypne.",
      few: "Typ má {count} pokoje a tato hranice nevratnou cenu nevypne, dokud je aspoň jeden z nich volný.",
      other: "Typ má {count} pokojů a tato hranice nevratnou cenu nevypne, dokud je aspoň jeden z nich volný.",
    },
  },

  // ── Minimální pobyt podle typu pokoje ──────────────────
  // Viz en.ts — slova patří tabulce, kterou vykreslují obě plochy.
  losRoomKinds: {
    title: "Podle typu pokoje",
    hint: "Kterékoli z těchto nastavení můžete určit jen pro jeden typ pokoje — třeba pro apartmán, který se nevyplatí uklízet po jedné noci. Prázdné pole nebo volba „Podle objektu“ se řídí nastavením výše, i každou jeho pozdější změnou.",
    roomKindColumn: "Typ pokoje",
    minLosColumn: "Minimální pobyt",
    weekendColumn: "Víkendy a svátky",
    releaseColumn: "Uvolnit do",
    weakWeekColumn: "Slabá poptávka",
    nights: "noci",
    days: "dní",
    follows: "Podle objektu",
    on: "Zapnuto",
    off: "Vypnuto",
    release: "Uvolnit",
    hold: "Držet",
    reset: "Obnovit nastavení objektu",
    noRoomKinds: "Zatím nejsou k dispozici žádné typy pokojů.",
    floorNote: "Minimální pobyt typu pokoje je jeho spodní hranice: blok volna o víkendu nebo o svátcích může vyžadovat více nocí a každé uvolnění se vrací právě k této hranici, nikdy níž.",
    releaseNote: "Oba sloupce uvolnění platí jen tam, kde je minimální pobyt o víkendech a svátcích zapnutý.",
    releaseForRoomKinds: "Pro objekt je vypnuto. Nastavení uvolnění níže platí pro typy pokojů, které si minimum v tabulce zapnou samy.",
  },

  timingPreset: {
    early: "Dříve",
    regular: "Standardně",
    late: "Později",
  },

  pacePreset: {
    faster: "Rychlejší",
    linear: "Lineární",
    slower: "Pomalejší",
  },

  syncCadence: {
    30: "každých 30 minut",
    60: "každou hodinu",
    120: "každé 2 hodiny",
    360: "každých 6 hodin",
    720: "každých 12 hodin",
    1440: "každých 24 hodin",
  },

  strategySectionGroups: {
    foundation: "Základ",
    prices: "Ceny",
    sellingRules: "Pravidla prodeje",
    calendar: "Kalendář",
    distribution: "Distribuce",
  },

  // ── Pomoc se strategií ──────────────────────────────────
  // Viz komentář v `en.ts`. Analýza se vždy jmenuje "analýza strategie revenue
  // managementu", nikdy "studie tržeb".
  strategyHelp: {
    askButton: "Zeptat se Optiva",
    askHint: "Pošlete nám dotaz, nebo si vyžádejte analýzu strategie revenue managementu s nastavením, které vašemu ubytování doporučujeme.",
    askHintChat: "Získejte odpověď hned od asistenta Optiva (AI), pošlete nám dotaz, nebo si vyžádejte analýzu strategie revenue managementu s nastavením, které vašemu ubytování doporučujeme.",
    dialogTitle: "Zeptat se Optiva",
    dialogDescription: "Zpráva dorazí podpoře Optiva i s tím, o které ubytování a kterou strategii jde.",
    dialogDescriptionChat: "Píšete asistentovi s umělou inteligencí. Čte nastavení a ceny vašeho ubytování.",
    // Přepínač způsobů pod záhlavím okna: každý způsob jedním slovem
    // (`kind…Short`), celý název a popis v čele jeho formuláře. Viz `en.ts`.
    kindLegend: "S čím potřebujete pomoct?",
    kindChat: "Chat s asistentem Optiva (AI)",
    kindChatShort: "Chat (AI)",
    kindChatHint: "Odpoví hned. Čte nastavení a ceny vašeho ubytování. Je to asistent s umělou inteligencí: může se mýlit a nic nemění.",
    kindQuestion: "Mám dotaz k nastavení nebo cenám",
    kindQuestionShort: "Dotaz",
    kindQuestionHint: "Odpovíme e-mailem do {hours} hodin.",
    kindStudy: "Analýza strategie s doporučeným nastavením",
    kindStudyShort: "Analýza",
    kindStudyHint: "Projdeme vaše rezervace a ceny a pošleme vám e-mailem analýzu strategie revenue managementu s tlačítkem, které doporučené nastavení aktivuje. Dokud ho neaktivujete, nic se nezmění. Jedna analýza za čtvrtletí je v ceně předplatného, další po individuální dohodě. Do {hours} hodin se ozveme, kdy analýzu čekat.",
    messageQuestion: "Váš dotaz",
    messageStudy: "Máme o něčem vědět? (nepovinné)",
    placeholderQuestion: "Například: proč je sobota levnější než pátek?",
    placeholderStudy: "Například: víkendy vyprodáme měsíce dopředu, ale všední dny zůstávají prázdné.",
    replyTo: "Odpovíme na {email}.",
    replyToAccount: "Odpovíme na e-mail vašeho účtu.",
    // {count} je MAX_HELP_COPIES (5) – „adres“ a „kolegů“ jsou 2. pád
    // množného čísla; počet 2–4 by chtěl „adresy“ a „kolegy“.
    copyAdd: "Přidat kolegu do kopie",
    copyLabel: "Kopie",
    copyPlaceholder: "E-mail kolegy",
    copyHint: "Naši odpověď dostanou také. Nejvýše {count} adres, každou potvrďte Enterem.",
    copySuggestions: "Vaši kolegové v Optivu:",
    copySuggestionAdd: "Přidat {email} do kopie",
    copyRemove: "Odebrat {email} z kopie",
    errorCopyInvalid: "„{email}“ není e-mailová adresa.",
    errorCopyOwn: "To je vaše vlastní adresa – odpověď vám přijde tak jako tak.",
    errorCopyTooMany: "Do kopie lze přidat nejvýše {count} kolegů.",
    sentCopies: "V kopii: {emails}.",
    send: "Odeslat",
    sending: "Odesílám…",
    sentTitle: "Odesláno podpoře Optiva",
    sentQuestion: "Odpovíme na {email} do {hours} hodin.",
    sentStudy: "Analýzu pošleme na {email}.",
    failedTitle: "Žádost se neodeslala",
    errorLimit: "Z tohoto ubytování už dnes odešlo dost žádostí. Pokud spěcháte, napište na {email}.",
    errorNoEmail: "Váš účet nemá e-mailovou adresu, na kterou bychom mohli odpovědět. Napište nám prosím na {email}.",
    errorMessageRequired: "Nejdřív napište svůj dotaz.",
    errorGeneric: "Zkuste to prosím za chvíli znovu, nebo napište na {email}.",
    openStudy: "Analýza strategie vyžádána {date} – Optivo ji připravuje.",
    openQuestion: "Dotaz odeslán {date} – Optivo odpoví e-mailem.",
    proposalTitle: "Optivo navrhlo nové nastavení vaší strategie",
    reviewProposal: "Prohlédnout návrh",
    // {count} je MAX_HELP_ATTACHMENTS (5) – „souborů“ je jeho 2. pád množného
    // čísla; počet 2–4 by chtěl „soubory“.
    attachLabel: "Snímky obrazovky a soubory",
    attachHintMac: "Snímek obrazovky ukáže podpoře přesně to, co vidíte vy: odsuňte okno stranou, stiskněte ⌘⇧4 a náhled z rohu obrazovky přetáhněte sem.",
    attachHintOther: "Snímek obrazovky ukáže podpoře přesně to, co vidíte vy: odsuňte okno stranou, stiskněte Win+Shift+S a snímek sem vložte klávesami Ctrl+V.",
    attachHintTouch: "Snímek obrazovky ukáže podpoře přesně to, co vidíte vy: okno minimalizujte, pořiďte snímek a přiložte ho níže.",
    attachLimits: "Přiložit můžete i PDF, video, tabulku nebo text – nejvýše {count} souborů, celkem {size}.",
    attachButton: "Vybrat soubor",
    removeAttachment: "Odebrat {name}",
    errorAttachTooMany: "K jedné žádosti lze přiložit nejvýše {count} souborů.",
    errorAttachTooLarge: "Soubory jsou dohromady větší než {size}. Některý vynechte, nebo přiložte menší.",
    errorAttachType: "Soubor {name} poslat nelze – přiložte obrázek, PDF, video, tabulku nebo text.",
    errorAttachEmpty: "Soubor {name} je prázdný.",
    errorAttachUnreadable: "Soubor {name} se nepodařilo načíst. Přiložte ho prosím znovu.",
    sentFilesUndelivered: "Přiložené soubory k nám ale nedorazily – pošlete je prosím na {email}.",
    // Okno plave nad stránkou a nezakrývá ji (Hotel Zámek Svijany, říjen
    // 2026): posouvá se za záhlaví, mění velikost za okraje a minimalizuje
    // do „Zeptat se Optiva“ v liště, aby plachta zůstala po ruce, zatímco se
    // píše dotaz a fotí obrazovka.
    windowMove: "Přesunout okno",
    windowMoveHint: "Okno přetáhněte za záhlaví, nebo ho posuňte šipkami.",
    windowMinimize: "Minimalizovat",
    windowMinimizeHint: "Skrýt okno a rozepsaný dotaz si nechat – vrátí ho tlačítko „Zeptat se Optiva“.",
    windowResize: "Změnit velikost okna",
    windowResizeHint: "Táhněte za kterýkoli okraj nebo roh okna, nebo velikost měňte šipkami.",
    windowClose: "Zavřít",
    windowMinimized: "Okno s dotazem je minimalizované.",
    windowRestoreHint: "Kliknutím ho vrátíte na obrazovku.",
    windowMinimizedElsewhere: "Okno s dotazem je minimalizované. Najdete ho v nabídce pod „Zeptat se Optiva“.",
    // Viz komentář v `en.ts`: okno s dotazem nejde vykreslit.
    windowFailed: "Okno s dotazem se nepodařilo zobrazit",
    windowFailedHint: "Načtěte stránku znovu a zkuste to ještě jednou, nebo napište na {email}.",
  },

  // ── Zeptat se Optiva: živý asistent ─────────────────────
  // Viz komentář v `en.ts`. Každá odpověď asistenta je označená jako napsaná
  // umělou inteligencí (AI Act, čl. 50). Limity ({limit}) se uvádějí
  // v závorce, aby věta nezávisela na pádu podle počtu.
  assistantChat: {
    assistantLabel: "Asistent Optiva (AI)",
    youLabel: "Vy",
    transcriptLabel: "Konverzace s asistentem Optiva (AI)",
    intro: "Dobrý den, jsem asistent Optiva – umělá inteligence. Čtu nastavení, ceny a rezervace vašeho ubytování (jen v souhrnech, nikdy jednotlivé hosty) a hned vám je vysvětlím. Mohu se mýlit a nic neměním: kde je potřeba něco změnit, řeknu vám, kde to uděláte.",
    aiNotice: "Píšete asistentovi s umělou inteligencí. Může se mýlit a nic nemění.",
    aiNoticeShort: "Asistent s AI se může mýlit.",
    composerLabel: "Váš dotaz pro asistenta",
    composerPlaceholder: "Například: proč je sobota levnější než pátek?",
    composerHint: "Nepište jména hostů ani jejich kontakty. Konverzaci zpracovává společnost Anthropic.",
    privacyLink: "Ochrana osobních údajů",
    retention: "Konverzace uchováváme {days} dní.",
    questionsLeft: "Dnes zbývá dotazů: {count}.",
    send: "Odeslat",
    stop: "Zastavit",
    newConversation: "Nová konverzace",
    startNewConversation: "Začít novou konverzaci",
    handOver: "Předat podpoře",
    askSupportInstead: "Poslat dotaz podpoře",
    suggestionsTitle: "Můžete se zeptat třeba:",
    loading: "Načítám konverzaci…",
    loadFailed: "Konverzaci se nepodařilo načíst.",
    retry: "Zkusit znovu",
    jumpToLatest: "Na konec konverzace",
    filesNotRead: "Asistent soubory nečte. Snímek obrazovky pošlete s dotazem podpoře (volba výše), nebo tuto konverzaci předejte podpoře.",
    statusThinking: "Připravuji odpověď…",
    toolPropertyOverview: "Čtu údaje o ubytování…",
    toolStrategySettings: "Čtu vaši strategii…",
    toolStrategyChanges: "Porovnávám verze strategie…",
    toolPrices: "Čtu ceny…",
    toolPricesFrom: "Čtu ceny od {date}…",
    toolExplainNight: "Rozebírám cenu noci {date}…",
    toolCompareNights: "Porovnávám dvě noci…",
    toolNightHistory: "Čtu vývoj ceny noci {date}…",
    toolOccupancy: "Čtu obsazenost a tempo rezervací…",
    toolOccupancyFrom: "Čtu obsazenost a tempo rezervací od {date}…",
    toolOutlook: "Čtu výhled…",
    toolSync: "Ověřuji synchronizaci a odesílání cen do PMS…",
    toolAnalysis: "Čtu analýzu ubytování…",
    toolMarket: "Čtu situaci na trhu…",
    toolMarketFrom: "Čtu situaci na trhu od {date}…",
    toolBookings: "Čtu poslední rezervace…",
    toolDistribution: "Čtu propojené cenové plány…",
    toolLinkedPlansNight: "Čtu propojené cenové plány na noc {date}…",
    toolWhatIf: "Zkouším, co by změna udělala…",
    toolDocs: "Hledám v dokumentaci Optiva…",
    toolFindSetting: "Hledám, kde se to nastavuje…",
    stopped: "Zastaveno.",
    outcomeRefusal: "S tímto dotazem vám asistent nepomůže. Konverzaci můžete předat podpoře.",
    outcomeMaxTokens: "Odpověď se nevešla celá. Zeptejte se na tu část, kterou potřebujete.",
    outcomeIterationLimit: "Asistent nestihl odpověď dokončit. Zeptejte se úžeji, nebo konverzaci předejte podpoře.",
    outcomeTimeout: "Odpověď trvala příliš dlouho, a proto není celá.",
    outcomeInterrupted: "Odpověď se přerušila.",
    outcomeFailed: "Odpověď se nepodařilo napsat. Zeptejte se znovu, nebo konverzaci předejte podpoře.",
    outcomeStillWriting: "Tato odpověď se ještě píše…",
    errorFailed: "Asistent odpověď nedokončil. Zeptejte se znovu, nebo konverzaci předejte podpoře.",
    errorOverloaded: "Asistent je teď vytížený. Zkuste to prosím za minutu.",
    errorRateLimited: "Právě se odpovídá na příliš mnoho dotazů. Zkuste to prosím za minutu.",
    errorTimeout: "Odpověď trvala příliš dlouho. Zeptejte se znovu, třeba úžeji.",
    errorRestarting: "Optivo se právě restartuje. Zeptejte se prosím za chvíli znovu.",
    errorUnavailable: "Asistent teď není k dispozici. Pošlete prosím dotaz podpoře.",
    errorInvalid: "Dotaz se nepodařilo odeslat. Může mít nejvýše {max} znaků.",
    errorPersonalData: "Vynechte prosím e-mailové adresy, telefonní čísla a čísla karet či účtů: asistent osobní údaje hostů nepřijímá.",
    errorConversationGone: "Tato konverzace už není k dispozici. Začněte novou.",
    errorConversationClosed: "Tato konverzace skončila. Pro další dotazy začněte novou.",
    errorConversationFull: "V této konverzaci už další dotaz položit nelze. Pro další dotazy začněte novou.",
    errorBusy: "Odpověď se ještě píše. Zeptejte se, až bude hotová.",
    errorLimit: "Dnes jste vyčerpali denní limit dotazů na asistenta ({limit}). Zeptejte se zítra, nebo pošlete dotaz podpoře.",
    errorGeneric: "Dotaz se nepodařilo odeslat. Zkuste to prosím za chvíli znovu, nebo napište na {email}.",
    feedbackUp: "Užitečná odpověď",
    feedbackDown: "Neužitečná odpověď",
    feedbackFailed: "Hodnocení se nepodařilo uložit.",
    handOverTitle: "Předat konverzaci podpoře Optiva?",
    handOverBody: "Podpora dostane celou konverzaci včetně odpovědí asistenta a odpoví e-mailem na {email} do {hours} hodin.",
    handOverBodyAccount: "Podpora dostane celou konverzaci včetně odpovědí asistenta a odpoví na e-mail vašeho účtu do {hours} hodin.",
    handOverNoteLabel: "Chcete něco doplnit? (nepovinné)",
    handOverConfirm: "Předat",
    handingOver: "Předávám…",
    handOverCancel: "Zrušit",
    handOverFailedTitle: "Konverzaci se nepodařilo předat",
    handedOver: "Předáno podpoře jako {case}. Odpovíme e-mailem na {email} do {hours} hodin.",
    handedOverAccount: "Předáno podpoře jako {case}. Odpovíme na e-mail vašeho účtu do {hours} hodin.",
    handedOverEarlier: "Tato konverzace je u podpory Optiva jako {case}. Odpovíme e-mailem.",
    errorHandOverEmpty: "Zatím tu není žádný zodpovězený dotaz, který by šlo předat.",
    errorHandOverAlready: "Tato konverzace už je u podpory.",
    errorHandOverBusy: "Počkejte, až se odpověď dopíše, a pak konverzaci předejte.",
    answered: "Asistent Optiva (AI) odpověděl",
    answeredHint: "Kliknutím si odpověď přečtete.",
    suggestPrices1: "Proč má příští sobota takovou cenu?",
    suggestPrices2: "Které ceny ještě nebyly odeslány do PMS?",
    suggestPrices3: "Proč se cena od včerejška změnila?",
    suggestStrategy1: "Co tato sekce mění na mých cenách?",
    suggestStrategy2: "Co udělala poslední změna strategie?",
    suggestStrategy3: "Která nastavení jsou moje a která pocházejí z analýzy?",
    suggestDashboard1: "Jak si tento měsíc vedeme ve srovnání s loňskem?",
    suggestDashboard2: "Proč má dnešní noc takovou cenu?",
    suggestDashboard3: "Funguje synchronizace s PMS?",
    suggestAnalytics1: "Co tato čísla znamenají pro moje ceny?",
    suggestAnalytics2: "Jak přibývají rezervace na příští měsíc?",
    suggestAnalytics3: "Jak trh ovlivňuje moje ceny?",
    suggestConnections1: "Které ceny posílá automatická synchronizace a jak často?",
    suggestConnections2: "Kdy byly ceny naposledy odeslány do PMS?",
    suggestConnections3: "Proč bylo nahrání cen odmítnuto?",
    suggestGeneral1: "Jak Optivo nastavuje moje ceny?",
    suggestGeneral2: "Proč má dnešní noc takovou cenu?",
    suggestGeneral3: "Která nastavení mají na moje ceny největší vliv?",
  },

  // ── Návrh strategie ─────────────────────────────────────
  // Viz komentář v `en.ts`: stránka, na kterou vede e-mail s návrhem.
  strategyProposal: {
    title: "Navržené nastavení strategie",
    subtitle: "Od podpory Optiva, {date}",
    back: "Zpět na strategie",
    loading: "Načítám návrh…",
    notFound: "Na této adrese žádný návrh není. Možná patří účtu, jehož nejste členem.",
    loadFailed: "Návrh se nepodařilo načíst.",
    retry: "Zkusit znovu",
    otherProperty: "Tento návrh je pro {property}, ne pro ubytování, které máte otevřené.",
    switchProperty: "Přepnout na {property}",
    summaryTitle: "Ve zkratce",
    downloadStudy: "Celá analýza s podrobnostmi (PDF)",
    changesTitle: "Co se změní",
    changesAgainst: "Ve srovnání se strategií {strategy} (verze {version}), která vaše ubytování právě naceňuje.",
    changeCount: { one: "Změní se {count} nastavení", few: "Změní se {count} nastavení", other: "Změní se {count} nastavení" },
    columnSetting: "Nastavení",
    columnNow: "Nyní",
    columnProposed: "Navrženo",
    otherSettings: "Další nastavení",
    valueOn: "Zapnuto",
    valueOff: "Vypnuto",
    valueUnset: "nenastaveno",
    valueFollowsProperty: "Podle celého ubytování",
    // Typ pokoje, který se řídí celým ubytováním, když stránka zná jeho hodnotu.
    valueFollowsPropertyAs: "Jako celé ubytování ({value})",
    // Speciální den, který má jen jedna strana návrhu.
    specialDateNotListed: "není v seznamu",
    specialDateRemoved: "odebráno ze seznamu",
    // Název nastavení tam, kde je popisek editoru strategie zkrácený pro
    // hustou mřížku nebo žádný není; ostatní řádky čtou popisek editoru
    // (`proposal-changes.ts`), aby ho provozovatel v editoru našel.
    fields: {
      occupancyScaleMax: "Maximální přirážka dle obsazenosti",
      paceLower: "Sleva při nízkém tempu",
      paceUpper: "Příplatek při vysokém tempu",
      occupancyDamping: "Jak klidně cena reaguje",
      propertyOccupancyWeight: "Váha obsazenosti celého ubytování",
      minPricePct: "Výchozí minimální cena",
      maxPricePct: "Výchozí maximální cena",
      bookingWindowTypical: "Typické rezervační okno",
      bookingWindowAverage: "Průměrné rezervační okno",
      bookingWindowHigh: "Rezervační okno v hlavní sezóně",
      bookingWindowLow: "Rezervační okno v nízké sezóně",
      peakNightBookingWindow: "Rezervační okno víkendových a svátečních nocí",
      leadTimeCurveHoldDays: "Držení ceny kolem vrcholu poptávky",
      bridgeDayAdjustment: "Úprava ceny přemosťovacího dne",
      seasonOfMonth: "Sezóna – {month}",
      specialDate: "Speciální den {n}",
      maxGuests: "Maximální počet hostů",
      lastMinuteStep: "Sleva v kroku {n}",
    },
    days: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
    nights: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
    rooms: { one: "{count} pokoj", few: "{count} pokoje", other: "{count} pokojů" },
    guests: { one: "{count} host", few: "{count} hosté", other: "{count} hostů" },
    truncated: {
      one: "Ještě {count} změna se do seznamu nevešla.",
      few: "Další {count} změny se do seznamu nevešly.",
      other: "Dalších {count} změn se do seznamu nevešlo.",
    },
    noChanges: "Vaše strategie už všechna tato nastavení má, takže není co aktivovat.",
    noActiveStrategy: "Vaše ubytování nemá aktivní strategii, na kterou by se nastavení dalo použít. Napište nám na {email}.",
    activate: "Aktivovat toto nastavení",
    activating: "Aktivuji…",
    decline: "Odmítnout",
    declining: "Odmítám…",
    activateTitle: "Aktivovat navržené nastavení?",
    activateBody: "Uloží se jako nová verze strategie {strategy} a ceny se podle něj přepočítají. Verze {version} zůstane uložená, takže se k ní ze seznamu strategií můžete kdykoli vrátit.",
    declineTitle: "Odmítnout tento návrh?",
    declineBody: "Vaše strategie zůstane beze změny. Podpora Optiva uvidí, že jste návrh odmítli.",
    activatedToast: "Navržené nastavení je aktivní",
    activatedToastDesc: "Vaše ubytování teď naceňuje verze {version}. Verze {previous} zůstává uložená.",
    declinedToast: "Návrh odmítnut",
    statusAccepted: "Tento návrh jste aktivovali {date}.",
    statusDeclined: "Tento návrh byl odmítnut {date}.",
    statusSuperseded: "Tento návrh nahradil novější.",
    openStrategy: "Otevřít strategii",
    errorDecided: "O návrhu už mezitím bylo rozhodnuto.",
    errorOtherProperty: "Nejdřív přepněte na ubytování, pro které je návrh.",
    errorSpecialDate: "Jedno ze zvláštních dat v návrhu nejde přečíst. Napište nám na {email}.",
    errorGeneric: "Návrh se nepodařilo aktivovat. Zkuste to znovu, nebo napište na {email}.",
    // Kroky návrhu: proč se každý dělá a co jím sledujeme, s nastavením, které
    // mění, nebo s místem, kde ho provozovatel udělá sám.
    stepsTitle: "Doporučené kroky",
    stepWhy: "Proč",
    stepAim: "Co tím sledujeme",
    stepByHand: "Uděláte sami",
    stepWhere: "Kde to najdete",
    stepSection: "V nastavení: {section}",
    stepAlreadySet: "Vaše strategie už toto nastavení má.",
    otherChangesTitle: "Další změny",
    activateStepsTitle: "Co aktivujete a proč",
    activateStepByHand: "Po aktivaci uděláte sami: {where}",
    remainingTitle: "Teď zbývá udělat sami:",
    // Navazující analýza nabízená s návrhem, a kde je.
    followUpTitle: "Navazující analýza",
    followUpOffered: "Zhruba {days} po aktivaci se k nastavení vrátíme v navazující analýze a ověříme, jestli dělá, co má. Při aktivaci ji jen potvrdíte.",
    followUpScheduled: "Navazující analýzu připravíme kolem {date}.",
    followUpOpened: "Navazující analýzu právě připravujeme.",
    followUpDelivered: "Navazující analýzu jsme vám poslali.",
    followUpCancelled: "Navazující analýza se už připravovat nebude.",
    followUpCancelledByNewer: "Navazující analýzu zrušila aktivace novějšího návrhu.",
    followUpDeclined: "Navazující analýzu jste při aktivaci nechtěli.",
    followUpChoice: "Ano, chci navazující analýzu zhruba za {days} (kolem {date}).",
    followUpChoiceHint: "V ní ověříme, jestli nastavení dělá, co má.",
  },

  // ── Archiv analýz ───────────────────────────────────────
  // Viz komentář v `en.ts`: co Optivo poslalo otevřenému ubytování – analýzy
  // strategie, doporučená nastavení a odpovědi – v analytické části. Dopis sám
  // se zobrazuje v jazyce, ve kterém byl napsán.
  studyArchive: {
    title: "Archiv analýz",
    subtitle: "Analýzy, doporučená nastavení a odpovědi od týmu Optiva",
    refresh: "Obnovit",
    loading: "Načítám archiv…",
    loadFailed: "Archiv se nepodařilo načíst.",
    retry: "Zkusit znovu",
    empty: "Optivo vám zatím neposlalo žádnou analýzu ani odpověď.",
    emptyHint: "O analýzu strategie požádáte nebo se na cokoli zeptáte přes „Zeptat se Optiva“ v levém menu. Odpověď se pak uloží sem.",
    waitingQuestion: "Odpověď na váš dotaz {case} z {date} připravujeme.",
    waitingStudy: "Analýzu strategie, o kterou jste požádali {date} ({case}), připravujeme.",
    waitingFollowUp: "Navazující analýzu, kterou jste potvrdili při aktivaci {date} ({case}), připravujeme.",
    kind: {
      study: "Analýza strategie",
      settings: "Doporučené nastavení",
      answer: "Odpověď",
    },
    status: {
      pending: "Čeká na vaše rozhodnutí",
      accepted: "Aktivováno",
      declined: "Odmítnuto",
      superseded: "Nahrazeno novějším návrhem",
    },
    untitled: {
      study: "Analýza strategie",
      settings: "Doporučené nastavení",
      answer: "Odpověď z Optiva",
    },
    asked: "Váš dotaz: {text}",
    studyFile: "PDF",
    back: "Zpět do archivu",
    entryLoading: "Načítám…",
    entryNotFound: "Na této adrese žádná analýza ani odpověď není. Možná patří účtu, jehož nejste členem.",
    entryLoadFailed: "Obsah se nepodařilo načíst.",
    otherProperty: "Patří k ubytování {property}, ne k tomu, které máte otevřené.",
    sentOn: "Odesláno {date}",
    preparedOn: "Připraveno {date}",
    recipient: "Komu: {email}",
    recipientWithCopies: "Komu: {email}, v kopii {copies}",
    summaryTitle: "Ve zkratce",
    letterTitle: "Dopis",
    figure: "Obrázek {n}",
    figureUnnumbered: "Obrázek",
    downloadStudy: "Stáhnout analýzu (PDF)",
    pendingTitle: "Doporučené nastavení je připravené v Optivu",
    pendingBody: "Na jedné stránce uvidíte každé nastavení, které se změní, a jedním klepnutím ho aktivujete. Vaše současná verze strategie zůstane uložená, takže se k ní můžete kdykoli vrátit.",
    review: "Prohlédnout a aktivovat nastavení",
    accepted: "Aktivováno {date}.",
    acceptedBy: "Aktivováno {date} ({email}).",
    declined: "Odmítnuto {date}.",
    declinedBy: "Odmítnuto {date} ({email}).",
    superseded: "Tento návrh nahradil novější.",
    viewProposal: "Otevřít návrh",
  },

  // ── Dotaz, na který Optivo odpovídá ─────────────────────
  // Viz komentář v `en.ts`: co provozovatel položil, zopakované u odpovědi na
  // stránce návrhu a v archivu analýz. Je to táž otázka, kterou e-mail s
  // odpovědí cituje pod podpisem. O analýzu nebo o nastavení lze požádat i beze
  // slova, a pak větou říkáme, o co šlo. Stejně tak lze bez poznámky předat
  // konverzaci s asistentem: `handedOver` to říká slovy e-mailu, nikdy větou,
  // kterou si případ ukládá místo poznámky a která je naše, ne provozovatele.
  askedQuestion: {
    title: {
      question: "Váš dotaz",
      study: "Vaše žádost",
      setup: "Vaše žádost",
    },
    study: "Požádali jste o analýzu strategie.",
    setup: "Požádali jste Optivo o nastavení.",
    handedOver: "Předali jste podpoře konverzaci s asistentem Optiva (AI).",
    followUp: "Při aktivaci doporučení jste potvrdili navazující analýzu.",
    followUpCase: "Při aktivaci doporučení ({case}) jste potvrdili navazující analýzu.",
    by: "{email}, {date}",
  },

  // ── Měna ceníku ─────────────────────────────────────────
  // Viz komentář v `en.ts`: v jaké měně vede PMS ceník nemovitosti, řečeno
  // tam, kde se volí měna nemovitosti. Název systému stojí za slovem
  // "systém", protože Previo ani Better Hotel se neskloňují.
  priceListCurrency: {
    reading: "Zjišťujeme, v jaké měně vede systém {pms} ceník této nemovitosti.",
    keptIn: "Systém {pms} vede ceník této nemovitosti v {listCurrency}.",
    keptInSeveral: "Systém {pms} vede ceník této nemovitosti ve více měnách: {groups}.",
    group: "{roomTypes} v {listCurrency}",
    groupSeparator: "; ",
    differs:
      "Ceny spočítané v {pricedIn} do ceníku vedeného v {listCurrency} zapsat nelze, a proto by Optivo každé odeslání zastavilo, dokud se obě měny neshodnou.",
    differsSeveral:
      "Ať zde zvolíte kteroukoli měnu, Optivo každé odeslání zastaví, dokud systém {pms} nebude vést ceník všech typů pokojů v měně nemovitosti.",
    unsupported:
      "Optivo neumí počítat ceny v {listCurrency}. Aby šlo ceny odesílat, změňte měnu ceníku v systému {pms} na CZK, EUR nebo USD.",
    unsaid: "Systém {pms} neuvedl, v jaké měně vede ceník této nemovitosti.",
    unreadable:
      "Optivo nemohlo zjistit, v jaké měně vede systém {pms} ceník této nemovitosti. Uložení to neovlivní a každé odeslání měnu zkontroluje znovu.",
    use: "Použít {listCurrency}",
    dialogTitle: "Systém {pms} vede tento ceník v {listCurrency}",
    dialogTitleSeveral: "Systém {pms} vede tento ceník ve více měnách",
    dialogDescription:
      "Nemovitost se chystáte uložit v {pricedIn}. Ceny spočítané v {pricedIn} do ceníku vedeného v {listCurrency} zapsat nelze, a proto by Optivo každé odeslání zastavilo, dokud se obě měny neshodnou.",
    dialogUse: "Uložit v {listCurrency}",
    dialogKeep: "Uložit v {pricedIn}",
    dialogCancel: "Zrušit",
    adopted: "Měna nemovitosti je {listCurrency}, stejná jako měna ceníku v systému {pms}.",
    confirmDiffers:
      "Systém {pms} vede ceník této nemovitosti v {listCurrency}, a proto by Optivo každé odeslání zastavilo, dokud bude nemovitost naceněná v {to}.",
    confirmSeveral:
      "Systém {pms} vede ceník této nemovitosti ve více měnách, a proto Optivo každé odeslání zastaví, ať zvolíte kteroukoli měnu.",
  },

  // ── Tabulka konkurence ──────────────────────────────────
  compsetTable: {
    inSet: "V sadě",
    property: "Ubytování",
    trend: "Vývoj cen",
    rating: "Hodnocení",
    reason: "Proč",
    rate: "Cena",
    change: "Od minulého zachycení",
    typicalRate: "Obvyklá cena",
    reviews: "Recenze",
    groupIncluded: "V sadě ({count})",
    groupExcluded: "Vyřazeno ({count})",
    checkboxLabel: "Počítat do sady: {name}",
    lighthouseSet: "Tuto sadu vedete ve svém účtu Lighthouse.",
    soldOut: "Vyprodáno",
    trendRising: "Zdražuje",
    trendFalling: "Zlevňuje",
    trendMixed: "Kolísá",
    trendStable: "Stabilní",
    trendUnknown: "Málo dat",
    trendNights: "{moved} z {paired} nocí",
    trendHint:
      "Vývoj porovnává nejnovější zachycení každé noci se zachycením alespoň o 3 dny starším. Konkurent zdražuje nebo zlevňuje, když se alespoň čtvrtina jeho nocí pohnula o 2 % a víc; menší pohyby jsou zaokrouhlení, ne cenové rozhodnutí.",
    reasonOwn: "Vaše ubytování",
    reasonManual: "Vyřazeno ručně",
    reasonPinOff: "Připnutý, nepočítá se",
    reasonType: "Typ ubytování",
    reasonBand: "Mimo cenové pásmo",
    reasonRating: "Nízké hodnocení",
    reasonReviews: "Málo recenzí",
    keptIncluded: "Přidáno ručně",
    keptPin: "Připnutý konkurent",
    counter: "V sadě {selected} z {total} ubytování, cenu uvádí {priced}",
    coverage:
      "Sada má alespoň {floor} cen v {covered} z {captured}. Vliv trhu i odvozování ceny od trhu působí jen v těchto nocích.",
    coverageNone:
      "Sada nemá alespoň {floor} cen v žádné z {captured}, takže konkurence vaše ceny neovlivní. Zaškrtněte další ubytování nebo rozšiřte pravidla.",
    capturedNights: { one: "{count} zachycené noci", few: "{count} zachycených nocí", other: "{count} zachycených nocí" },
    coverageNoCaptures: "Zatím není zachycena žádná noc, takže konkurence vaše ceny neovlivní.",
    coverageRecalculating: "Přepočítávám…",
    reset: "Použít celý trh",
    saving: "Ukládám…",
    saved: "Uloženo — použije se při příštím výpočtu cen.",
    saveFailed: "Uložení se nezdařilo — poslední změna byla vrácena.",
    retry: "Zkusit znovu",
    propertyTypes: "Typy ubytování",
    band: "Pásmo obvyklé ceny",
    bandFrom: "Od",
    bandTo: "Do",
    bandHint:
      "Porovnává obvyklou cenu ubytování za noc — medián jeho zachycených nocí. Ubytování v pásmu se počítá ve všech nocích, i v té, kdy je jeho cena mimo pásmo.",
    spread:
      "Ubytování splňující ostatní pravidla obvykle prodávají za {p25}–{p75} {currency} za noc pro dva dospělé, medián {median} {currency}.",
    spreadTooFew: "Zatím je málo ubytování s cenou, aby šlo obvyklou cenu popsat.",
    minRating: "Minimální hodnocení",
    minReviews: "Minimální počet recenzí",
    pinned: "Připnutý",
    unpin: "Odebrat připnutí: {name}",
    unpinHint: "Odebrat připnutí",
    pinNotCaptured: "Zatím nezachyceno.",
    pinNotFound:
      "Ve výsledcích vlastního hledání se neobjevuje — vyhledávání se za něj utrácejí, ale nic nevracejí. Odeberte jej a připněte znovu s přesnějším názvem.",
    pinNoUpcoming: "Žádná nadcházející noc není zachycena (naposledy {date}).",
    pinNoPrice: "Tento hotel je nabízen bez ceny, do mediánu tedy nic nepřidá.",
    unpriced: "Cena neuvedena",
    noPrevious: "První zachycení",
    ownRow: "Vaše ubytování — do trhu se nikdy nepočítá, je zde pro srovnání.",
    ownUnmark: "Není moje",
    ownSuggestion: "„{name}“ vypadá jako vaše vlastní ubytování. Vaše vlastní nabídka by se do trhu počítat neměla.",
    ownMark: "Označit jako moje",
    markOwn: "Označit jako vaše ubytování: {name}",
    markOwnHint: "Toto je moje ubytování",
    addOpen: "Přidat konkurenta mimo trh",
    addTitle: "Konkurenti mimo váš trh",
    // Co připnutý konkurent spotřebuje, viz komentář v `en.ts`.
    addAllowanceNote:
      "Konkurent, kterého připnete, se načítá zvlášť: jedno vyhledání pokryje jednu noc jen tohoto hotelu, kdežto vyhledání vašeho trhu vrátí všechna okolní ubytování. Každý takový konkurent spotřebuje přibližně {monthly} vyhledání měsíčně ze stejného měsíčního limitu. Připnutých konkurentů v ceně předplatného: {limit}. Další po individuální dohodě.",
    addSearchPlaceholder: "Název hotelu…",
    addSearch: "Hledat",
    addSearching: "Hledám…",
    addSearchCost: "Každé hledání spotřebuje jedno z vašich měsíčních vyhledání, proto se spustí až po stisknutí tlačítka — ne během psaní.",
    addNoResults: "Tomuto názvu nic neodpovídá.",
    addPin: "Připnout",
    addLimitReached: "Dosáhli jste počtu připnutých konkurentů, který je v ceně předplatného. Chcete-li přidat dalšího, jednoho odeberte. Větší počet sjednáváme individuálně: napište nám přes „Zeptat se Optiva“.",
    addClose: "Zavřít",
    addPinnedHint:
      "Připnutý hotel se v tabulce objeví vypnutý: jeho vývoj cen uvidíte hned a do mediánu vstoupí, až ho zaškrtnete.",
    empty: "Zatím nic zachyceno.",
    pageInfo: "Zobrazeno {from}–{to} z {total}",
    sortAscending: "Seřadit vzestupně",
    sortDescending: "Seřadit sestupně",
    pagination: "Stránky konkurentů",
    previousPage: "Předchozí stránka",
    nextPage: "Další stránka",
    showGroup: "Zobrazit",
    hideGroup: "Skrýt",
  },

  // ── Welcome / Landing Page ──────────────────────────────
  welcome: {
    logIn: "Přihlásit se",
    signUpFree: "Registrace zdarma",
    signUp: "Registrace",
    badge: "Navrženo ubytovateli pro ubytovatele",
    heroTitle1: "Řídíte hotel, nebo",
    heroTitle2: "přepisujete ceník?",
    heroTagline: "Optivo. Chytřejší ceny pro vaše ubytování.",
    heroDescription:
      "Známe to. Sami provozujeme hotel, apartmány i kemp a ceny jsme roky upravovali ručně. Optivo to dělá za vás: sleduje poptávku, konkurenci i počasí a každou cenu vám vysvětlí.",
    ctaCreate: "Vytvořit účet zdarma",
    ctaLogIn: "Přihlásit se do účtu",
    resetPassword: "Obnovit heslo",
    ctaSubtext: "Není potřeba platební karta. Hotelový systém připojíte po registraci.",
    valuePropTitle: "Příliš levně, nebo příliš draze? Už nemusíte hádat.",
    valuePropDescription:
      "Optivo vám pomáhá nastavit ceny podle dat a aktuální poptávky. Pro hotely, penziony, apartmány i kempy, které chtějí mít v cenách jasno.",
    featureDynamicTitle: "Dynamická úprava sazeb",
    featureDynamicDesc:
      "Sazby upravujeme automaticky podle poptávky, tempa rezervací, historické výkonnosti a tlaku trhu tak, abychom maximalizovali vaše výnosy.",
    featurePmsTitle: "Integrace s vaším hotelovým systémem",
    featurePmsDesc:
      "Stávající hotelový systém (PMS) připojíte v pár krocích. Kategorie pokojů, historická data i cenové plány si stáhneme sami.",
    featureMonitorTitle: "Úprava sazeb až každých 30 minut",
    featureMonitorDesc:
      "Sledujeme nové rezervace, vaši poptávku i změny na trhu a ceny jim přizpůsobíme až 24× denně.",
    featureCompetitorsTitle: "Vidíme, jak se chová vaše konkurence",
    featureCompetitorsDesc:
      "Sledujeme ceny ubytování ve vašem okolí noc po noci. Když se trh pohne, vaše ceny zareagují v mezích, které určíte.",
    featureWeatherTitle: "Reagujeme na počasí",
    featureWeatherDesc:
      "Slunečný víkend plní pokoje jinak než propršený. Předpověď čteme za vás a promítáme ji do cen.",
    featureHolidaysTitle: "Svátky a významné dny sledujeme sami",
    featureHolidaysDesc:
      "Kdy mají svátek v Německu? Státní svátky u vás i tam, odkud jezdí vaši hosté, najdeme a do cenového kalendáře zapíšeme sami.",
    featureRatePlansTitle: "Dynamika tam, kde ji nečekáte",
    featureRatePlansDesc:
      "Poptávkou řídíme i navázané cenové plány, každý zvlášť: přirážku pro prodejní kanál i slevu nevratné ceny. Všude, kde váš hotelový systém cenové plány má.",
    featureMinStayTitle: "Minimální pobyt pro každou kategorii zvlášť",
    featureMinStayDesc:
      "Minimální délku pobytu řídíme stejně dynamicky jako cenu, pro každou kategorii a každou noc. Když se navazující noc vyprodá, minimum samo povolí.",
    featureMoreLabel: "A k tomu",
    featureHorizonTitle: "Ceny až na 18 měsíců dopředu",
    featureHorizonDesc:
      "Ceny počítáme až na {nights} nocí dopředu. Do hotelového systému posíláme jen ty, které se změnily, takže ho nezahltíme.",
    featureHistoryTitle: "Historie cen a strategií",
    featureHistoryDesc:
      "U každé noci vidíte, jak se její cena vyvíjela. K předchozí verzi strategie i k cenám před odesláním se můžete vrátit.",
    featureControlTitle: "Plná kontrola",
    featureControlDesc:
      "Každá cena se sama vysvětlí. Nejnižší a nejvyšší cenu určujete vy a kteroukoli noc si můžete zamknout na své ceně.",
    featureSupportTitle: "Komunikujeme česky",
    featureSupportDesc:
      "Optivo i lidé za ním mluví česky a anglicky. Zeptejte se přímo z aplikace, odpovíme do {hours} hodin.",
    featureSecurityTitle: "Vaše data jsou v bezpečí",
    featureSecurityDesc:
      "Přihlašovací údaje k hotelovému systému ukládáme šifrovaně. Jména ani kontakty vašich hostů neukládáme vůbec.",
    featureSmallTitle: "Navrženo i pro malá ubytování",
    featureSmallDesc:
      "Ať už spravujete butikový hotel, apartmán nebo celé portfolio, Optivo se přizpůsobí vašemu měřítku. Navrženo ubytovateli pro ubytovatele.",
    howItWorksTitle: "Dost odhadů, čas na optivalizaci.",
    step1Title: "Zaregistrujte se",
    step1Desc:
      "Účet si vytvoříte e-mailem. Nové účty mohou získat 30 dní zdarma a platební kartu nepotřebujete.",
    step2Title: "Připojte svůj hotelový systém",
    step2Desc:
      "Zadáte přihlašovací údaje k hotelovému systému (PMS) a Optivo si načte vaše pokoje, ceny a rezervace.",
    step3Title: "Optivalizujte ceny",
    step3Desc:
      "Zkontrolujete ceny v cenovém kalendáři, zapnete automatické odesílání a Optivo vám pomůže vydělat víc na každé rezervaci.",
    aboutTitle: "„Asi tak za dva tisíce“ není cenová strategie.",
    aboutStory: [
      "Provozujeme hotel, apartmány a kemp. Dynamické ceny používáme víc než deset let a víme, že jsou konkurenční výhodou. Správná cena ve správný čas správnému klientovi: strategie, která má výsledky a zajišťuje dlouhodobý růst.",
      "Ceny jsme stanovovali kombinací odhadu, zkušeností, dat z minulosti a reakce trhu. Rutina, kterou zná asi každý majitel nebo revenue manažer. Nervózní kontrola hotelového systému v mobilu: prodal se už poslední pokoj, nebo je potřeba ho zlevnit?",
      "Optivo vzniklo z jednoduché otázky našeho syna: proč ceny upravuju já jako majitel a nedělá to počítač? Žádnému softwaru jsem nevěřil tolik jako své intuici, zkušenosti a zodpovědnosti majitele. Proto jsme vyvinuli Optivo. Mělo sloužit výlučně naší skupině, nanejvýš pár známým. Později jsme ale pochopili, že s takovými nároky na cenotvorbu nejsme sami, a z Optiva se stal opravdový systém pro revenue management.",
      "Proto víme, jak moc záleží na správné cenotvorbě a dlouhodobé strategii. Odhadovat ceny není strategie. Nastavit chamtivou cenu a pak dramaticky zlevňovat není strategie. Držet roky stejné ceny a bát se je zvýšit, když všechny náklady šly nahoru, to také není strategie.",
      "My jsme to ve svých provozech prožívali taky. Nejste na to sami.",
    ],
    aboutClosing: "Vyvinuto hoteliéry pro ubytovatele.",
    aboutRole: "spoluzakladatel Optiva a hoteliér",
    // Jediné, co stránka říká o tom, kdo Optivo používá: počet z databáze
    // vedle tohoto popisku, viz komentář v `en.ts`.
    pricedRoomsEyebrow: "Optivo v provozu",
    pricedRoomsTitle: "Pokoje, kterým Optivo právě teď nastavuje ceny",
    pricedRoomsDescription:
      "Živé číslo z našeho systému: pokoje a apartmány hotelů, penzionů a pronájmů, kterým Optivo každý den počítá ceny.",
    // Kolik to stojí: vlastní věty sekce. Ceny a to, co je v nich, píše ceník
    // (`legal/priceListCopy.ts`), viz komentář v `en.ts`.
    pricingTitle: "Jedna cena pro hotel, druhá pro penzion a pronájem. Všechny funkce jsou v obou.",
    pricingDescription:
      "Platíte za objekt a k tomu malou částku za každý pokoj. Žádný tarif si nevybíráte: cenu určuje to, o jaký objekt jde.",
    pricingTrial:
      "Nové účty mohou získat 30 dní zdarma, bez platební karty. Po skončení zkušebního období se nic samo nestrhne: předplatné začíná, až ho zaplatíte.",
    pricingFullList: "Celý ceník a podmínky předplatného",
    closingTitle: "Kolikrát dnes ještě otevřete ceník?",
    closingDescription: "Nové účty mohou Optivo vyzkoušet 30 dní zdarma. Platební kartu nepotřebujete.",
    footerCopyright: "© {year} Optivo",
    footerPricing: "Ceník",
    footerPayment: "Platba a dodání",
    footerTerms: "Obchodní podmínky",
    footerContact: "Kontakt",
    footerPrivacy: "Ochrana údajů",
    footerComplaints: "Reklamační řád",
    footerCompanyId: "IČO",
    footerPaymentsLabel: "Přijímáme",
    navLabel: "Hlavní navigace",
    navMenu: "Menu",
    navAbout: "Kdo jsme?",
    navFeatures: "Co umíme?",
    navHowItWorks: "Jak to funguje?",
    navPricing: "Kolik to stojí?",
    previewTitle: "Ceny na týden dopředu",
    previewExample: "Ukázka",
    previewGridLabel: "Ukázkové ceny podle typu pokoje a noci, v {currency}",
    previewRoomColumn: "Typ pokoje",
    previewRooms: {
      double: "Dvoulůžkový",
      family: "Rodinný",
      suite: "Apartmá",
    },
    previewHint: "Vyberte noc a uvidíte, proč stojí tolik, kolik stojí.",
    previewWhy: "Proč {price}?",
    previewBase: "Základní cena",
    previewDemand: "Poptávka: {level}",
    previewWeather: {
      sunny: "Počasí: slunečno",
      neutral: "Počasí",
      rainy: "Počasí: déšť",
    },
    previewCompetitors: {
      cheaper: "Konkurence: levnější",
      level: "Konkurence",
      pricier: "Konkurence: dražší",
    },
    previewFinal: "Optivo cena",
    priceChartCaption: "Cena jedné noci, jak se prodávala",
    priceChartPrice: "Cena",
    priceChartBooking: "Rezervace",
    priceChartStart: "{days} dní před příjezdem",
    priceChartArrival: "Příjezd",
    marketSketchMedian: "Medián trhu",
    marketSketchYours: "Vaše cena",
    weatherSketchLabel: "Ukázková předpověď na týden dopředu",
    holidaySketchLabel: "Nejbližší státní svátky",
    ratePlanSketchLabel: "Ukázkové ceny navázaných cenových plánů",
    ratePlanBase: "Základní plán",
    ratePlanChannel: "Prodejní kanál",
    ratePlanNonRefundable: "Nevratná cena",
    minStaySketchLabel: "Minimální pobyt, počet nocí",
  },

  passwordReset: {
    requestTitle: "Obnovit heslo",
    requestDescription: "Zadejte e-mailovou adresu, kterou používáte v Optivu, a pošleme vám odkaz pro obnovu hesla.",
    emailLabel: "E-mailová adresa",
    emailPlaceholder: "jmeno@example.com",
    sendLink: "Poslat odkaz pro obnovu",
    requestSuccessTitle: "Zkontrolujte svůj e-mail",
    requestSuccessMessage: "Pokud účet existuje, odkaz pro obnovu hesla je na cestě.",
    newPasswordTitle: "Zvolte nové heslo",
    newPasswordDescription: "Nastavte nové heslo pro svůj účet Optivo.",
    newPasswordLabel: "Nové heslo",
    confirmPasswordLabel: "Potvrzení nového hesla",
    passwordPlaceholder: "Zadejte nové heslo",
    confirmPasswordPlaceholder: "Zopakujte nové heslo",
    savePassword: "Uložit nové heslo",
    successTitle: "Heslo bylo změněno",
    successMessage: "Vaše heslo bylo změněno. Nyní se můžete přihlásit novým heslem.",
    signInCta: "Zpět na přihlášení",
    mismatch: "Hesla se neshodují.",
    invalidToken: "Tento odkaz pro obnovu hesla je neplatný nebo vypršel.",
    genericError: "Heslo se teď nepodařilo obnovit.",
  },

  // ── Sidebar ─────────────────────────────────────────────
  sidebar: {
    title: "Optivo",
    titlePrevioLine1: "Previo",
    titlePrevioLine2: "RMS by Optivo",
    titleBetterHotelLine1: "Better Hotel",
    titleBetterHotelLine2: "RMS by Optivo",
    dashboard: "Přehled",
    performance: "Výkonnost",
    strategies: "Strategie",
    drafts: "Ceny",
    settings: "Nastavení Property Manageru",
    administration: "Administrace",
    userManagement: "Uživatelé",
    priceDraftsAdmin: "Cenové plány",
    wirePaymentsAdmin: "Bankovní převody",
    connectionsAdmin: "PMS připojení",
    activityAdmin: "Záznam aktivit",
    strategyHelpAdmin: "Pomoc se strategií",
    supportRemindersAdmin: "Připomínky",
    featuresAdmin: "Funkce",
    resetPassword: "Obnovit heslo",
    signOut: "Odhlásit se",
    analysis: "Analýza",
    recentBookings: "Poslední rezervace",
    forecast: "Prognóza tržeb",
    demandForecast: "Prognóza poptávky",
    segmentChannel: "Kanály a segmenty",
    marketConditions: "Situace na trhu",
    studyArchive: "Archiv analýz",
    setup: "Průvodce nastavením",
    reviewSetup: "Zkontrolovat nastavení",
    sectionAnalytics: "Analytika",
    sectionPricing: "Tvorba cen",
    expand: "Rozbalit menu",
    collapse: "Sbalit menu",
    // Viz komentář v `en.ts`: křížek, který na telefonu menu zavírá.
    closeMenu: "Zavřít menu",
    // Viz komentář v `en.ts`: tlačítko v horní liště, které menu otevírá.
    openMenu: "Otevřít menu",
    // Viz komentář v `en.ts`: jak se menu jmenuje čtečce obrazovky.
    menu: "Menu",
    // Viz komentář v `en.ts`: nápověda čtečce u sbalené ikony Ceny.
    pricesFlyoutHint: "Šipkou doprava otevřete jeho záložky a zobrazení kalendáře.",
    // The folded rail's one button for the footer's controls.
    moreControls: "Další volby",
    // Viz komentář v `en.ts`: co stojí na místě menu, když ho nejde vykreslit.
    unavailableTitle: "Menu se nepodařilo zobrazit",
    unavailableHint: "Načtěte stránku znovu a vrátí se.",
  },

  // ── Rychlý přechod ──────────────────────────────────────
  // Viz komentář v `en.ts`: rychlý přechod na stránku, záložku Cen nebo zobrazení kalendáře.
  commandPalette: {
    title: "Přejít na",
    description: "Najděte stránku, záložku nebo zobrazení kalendáře a otevřete je.",
    placeholder: "Hledat stránky, záložky a zobrazení…",
    empty: "Nic neodpovídá.",
    open: "Přejít na…",
    groupPages: "Stránky",
    // The name of the list of matches, for a screen reader.
    results: "Výsledky",
  },

  // ── Dashboard ───────────────────────────────────────────
  // ── Theme (light / dark) ────────────────────────────────
  theme: {
    switchToDark: "Přepnout na tmavý režim",
    switchToSystem: "Řídit se zařízením",
    pinLight: "Vždy používat světlý režim",
    matchingSystem: "Řídí se vaším zařízením — aktuálně {mode}.",
    light: "Světlý",
    dark: "Tmavý",
  },

  dashboard: {
    title: "Přehled",
    subtitle: "Přejděte přímo do oblasti, kterou potřebujete.",
    sectionAnalytics: "Analytika",
    sectionPricing: "Ceny",
    sectionSetup: "Nastavení",
    performanceTitle: "Výkonnost",
    performanceDesc: "Sledujte trendy výnosů, obsazenosti a průměrné ceny za uplynulé pobyty.",
    forecastTitle: "Prognóza",
    forecastDesc: "Zobrazte tempo rezervací a očekávanou obsazenost na nadcházející termíny.",
    demandForecastTitle: "Prognóza poptávky",
    demandForecastDesc: "Pochopte budoucí tlak poptávky napříč kalendářem.",
    segmentChannelTitle: "Kanály a segmenty",
    segmentChannelDesc: "Porovnejte rezervace podle distribučního kanálu a segmentu hostů.",
    recentBookingsTitle: "Poslední rezervace",
    recentBookingsDesc: "Prohlédněte si nejnovější rezervace přicházející z vašeho PMS.",
    marketConditionsTitle: "Situace na trhu",
    marketConditionsDesc:
      "Podívejte se, jak jsou nastavené okolní ubytování a co žene poptávku pro každou noc.",
    studyArchiveTitle: "Archiv analýz",
    studyArchiveDesc: "Přečtěte si analýzy strategie, doporučená nastavení a odpovědi, které vám poslal tým Optiva.",
    pricingCalendarTitle: "Rezervace",
    pricingCalendarDesc: "Prohlédněte si aktuální a navržené ceny podle pokoje a data.",
    strategiesTitle: "Strategie",
    strategiesDesc: "Upravte pravidla a postoj, podle kterých Optivo tvoří ceny.",
    draftsTitle: "Ceny",
    draftsDesc: "Zkontrolujte čekající návrhy cen před odesláním do vašeho PMS.",
    connectionsTitle: "Spravovat připojení",
    connectionsDesc: "Přidejte, přepněte nebo odeberte PMS připojení napájející Optivo.",
    open: "Otevřít",
    snapshotSection: "V číslech",
    performanceSnapshotTitle: "Výkon na 30 dní",
    performanceSnapshotHint: "Obsazenost na knihách vs průměrná cena za noc na další čtyři týdny.",
    performanceSnapshotEmpty: "Ve zvoleném období zatím nejsou žádné rezervace ani ceny.",
    performanceSnapshotLegendOccupancy: "Obsazenost (OTB)",
    performanceSnapshotLegendAdr: "Průměrná cena za noc",
    performanceSnapshotWeekend: "Víkend",
    performanceSnapshotToday: "Dnes",
    stlySnapshotTitle: "Stejné období loni",
    stlySnapshotHint: "Jak je na tom tento týden ve srovnání se stejným týdnem před rokem.",
    stlySnapshotEmpty: "Pro srovnání není dost loňských dat.",
    stlyMetricAdr: "Prům. cena",
    stlyMetricOcc: "Obsazenost",
    stlyMetricPickup: "Nové rezervace (tento týden)",
    stlyMetricRevenue: "Tržby (tento týden)",
    stlyVsLastYear: "vs loni",
    stlyLastYearLabel: "Loni",
    // Výhled: tři noci a dva měsíce řečené náladou místo čísel; jediný počet
    // jsou pokoje, které má noc ještě volné. Každé slovo odpovídá jedné hodnotě
    // ze serveru (`services/outlookApi.ts`), takže nová nálada potřebuje své
    // slovo v obou souborech.
    outlook: {
      statusTitle: "Stav",
      nightsTitle: "Výhled na tři dny",
      nightsHint: "Kolik zbývá prodat, jak je na tom trh a co Optivo za posledních 24 hodin udělalo s cenami volných pokojů.",
      monthsTitle: "Tento a příští měsíc",
      monthsHint: "Noci a tržby, které už máte v knize, proti stejnému dni loni.",
      loadError: "Výhled se nepodařilo načíst.",
      manualSending: "Ceny se do vašeho PMS neodesílají automaticky. Výhled popisuje plán Optiva; k hostům se dostane, až ho odešlete.",
      staleCalculation: "Tyto ceny se déle než den nepřepočítaly.",
      openPrices: "Otevřít ceny",
      openForecast: "Otevřít prognózu",
      columns: {
        night: "Noc",
        sales: "Prodej",
        market: "Trh",
        prices: "Optivo",
        lastMinute: "Last-minute",
      },
      nightNames: {
        today: "Dnes",
        tomorrow: "Zítra",
        dayAfter: "Pozítří",
      },
      occupancy: {
        soldOut: "Vyprodáno",
        // `lastRooms`, když je volný právě jeden pokoj: jednotné číslo doplňuje rozhraní.
        lastRoom: "Zbývá poslední pokoj",
        lastRooms: "Zbývají poslední pokoje",
        busy: "Dobře obsazeno",
        partlyFree: "Zčásti volno",
        mostlyFree: "Většina pokojů volná",
        unknown: "Obsazenost zatím neznáme",
      },
      // Volné pokoje vedle obsazenosti: volné ze všech.
      rooms: {
        figure: "{free}/{total}",
        // U nálady, která mluví o obsazených pokojích, řekne údaj, co počítá.
        figureFree: "volno {free}/{total}",
        // Tentýž počet slovy, pro čtenáře, který údaj nevidí.
        label: {
          one: "{count} volný pokoj z {total}",
          few: "{count} volné pokoje z {total}",
          other: "{count} volných pokojů z {total}",
        },
      },
      // {level} je slovo pro poptávku z kalendáře, malým písmenem.
      demand: "Poptávka: {level}",
      pricing: {
        discounting: "Zlevňuje",
        holding: "Drží ceny",
        raising: "Zdražuje",
        adjusting: "Ladí ceny oběma směry",
        locked: "Ceny držíte zámkem",
        nothingToSell: "Není co prodávat",
        unknown: "Zatím bez srovnání",
      },
      pricingStatus: {
        discounting: "Optivo dnes zlevňuje",
        holding: "Optivo dnes drží ceny",
        raising: "Optivo dnes zdražuje",
        adjusting: "Optivo dnes ladí ceny",
        locked: "Dnešní ceny držíte zámkem",
        nothingToSell: "Dnešní noc je vyprodaná",
        unknown: "Dnešní ceny zatím nelze srovnat",
      },
      lastMinute: {
        off: "Vypnuto",
        notOpen: "Okno se zatím neotevřelo",
        waiting: "Okno je otevřené, sleva zatím nenastoupila",
        discounting: "Zlevňuje volné pokoje",
        holdingDiscount: "Drží slevu, které dosáhlo",
        atFloor: "Cena je na last-minute minimu",
      },
      market: {
        strong: "Silný",
        favourable: "Příznivý",
        calm: "Klidný trh",
        soft: "Slabší",
        weak: "Slabý",
        unknown: "Bez dat",
      },
      marketStatus: {
        strong: "Trh je silný",
        favourable: "Trh je příznivý",
        calm: "Trh je klidný",
        soft: "Trh je slabší",
        weak: "Trh je slabý",
        unknown: "O trhu nemáme data",
      },
      drivers: {
        schoolHoliday: "Školní prázdniny",
        specialDate: "Speciální termín",
        weatherUp: "Přeje počasí",
        weatherDown: "Kazí to počasí",
        weatherExtreme: "Extrémní počasí",
        competitorRatesUp: "Ceny v okolí jsou vysoko",
        competitorRatesDown: "Ceny v okolí jsou nízko",
      },
      yearOverYear: {
        wellAhead: "výrazně lépe než loni",
        ahead: "lépe než loni",
        level: "podobně jako loni",
        behind: "slaběji než loni",
        wellBehind: "výrazně slaběji než loni",
      },
      monthColumns: {
        month: "Měsíc",
        overall: "Vývoj",
        revenue: "Tržby",
        occupancy: "Obsazenost",
      },
      // Verdikt měsíce proti loňsku. Žádné společné první slovo: dva měsíce
      // se rozliší hned prvním slovem. Tvary se shodují se slovem „vývoj“ ze
      // záhlaví sloupce, takže sedí u každého měsíce, i u září.
      monthHeadline: {
        wellAhead: "Výrazně lepší než loni",
        ahead: "Lepší než loni",
        level: "Podobný jako loni",
        behind: "Slabší než loni",
        wellBehind: "Výrazně slabší než loni",
      },
      // Tržby a obsazenost proti loňsku, každé ve vlastním sloupci. Jedna sada
      // slov slouží oběma sloupcům, proto tvary, které se u „tržby“ (množné
      // číslo) ani u „obsazenost“ nemění. „Než loni“ se neopakuje: v řádku to
      // jednou říká verdikt vedle.
      monthPart: {
        wellAhead: "Výrazně vyšší",
        ahead: "Vyšší",
        level: "Na úrovni loňska",
        behind: "Nižší",
        wellBehind: "Výrazně nižší",
      },
      monthPartUnknown: "Nelze srovnat",
      monthPace: {
        faster: "Plní se rychleji než obvykle",
        usual: "Plní se jako obvykle",
        slower: "Plní se pomaleji než obvykle",
      },
      monthNoComparison: "Zatím není s čím srovnat",
      // Měsíc ve stavovém pruhu: „{month}: {mood}“, nálada malým písmenem.
      monthStatus: "{month}: {mood}",
      monthDetailNoLastYear: "S loňskem zatím nelze srovnat: loňské rezervace chybí, nebo jich je málo.",
    },
  },

  // ── Header ──────────────────────────────────────────────
  header: {
    monthly: "Měsíční",
    weekly: "Týdenní",
    weekAbbr: "Týd",
    selectWeek: "Vybrat týden",
    selectDatePrompt: "Vyberte časové období",
    selectDateHint: "Otevřete kalendář a vyberte měsíc nebo týden pro načtení cenových dat.",
    currentYearPrices: "Aktuální ceny",
    loadingCurrentPrices: "Načítání aktuálních cen...",
    clearCurrentPricesCache: "Vymazat cache",
    fromDate: "Od data",
    toDate: "Do data",
    quickRanges: "Rychlý výběr",
    today: "Dnes",
    thisWeek: "Tento týden",
    nextWeek: "Příští týden",
    thisMonth: "Tento měsíc",
    next14Days: "Příštích 14 dní",
    next30Days: "Příštích 30 dní",
    changeRange: "Změnit období",
    previousRange: "Předchozí období",
    nextRange: "Následující období",
  },

  // ── Pricing Calendar ────────────────────────────────────
  pricingCalendar: {
    weeklyView: "7denní",
    monthlyView: "Měsíční",
    aiPricing: "AI oceňování",
    stopAiPricing: "Zastavit AI oceňování ({seconds}s)",
    room: "Pokoj",
    loadingData: "Načítání cenových dat...",
    fetchError: "Nepodařilo se načíst cenová data",
    noData: "Žádné pokoje ani cenová data nejsou k dispozici",
    noDataHint: "Ujistěte se, že váš Property Management je připojen a má nakonfigurované pokoje pro toto období.",
    filters: "Filtry",
    filterActive: "aktivní",
    filterTitle: "Filtrovat pokoje a dny",
    filterDesc: "Vyberte konkrétní pokoje a/nebo dny pro zúžení zobrazení kalendáře. Hromadné operace se budou vztahovat pouze na viditelnou podmnožinu.",
    filterSummary: "{rooms}/{totalRooms} pokojů, {days}/{totalDays} dnů",
    filterActiveLabel: "Zobrazeno {rooms} pokojů, {days} dnů",
    filterClearAll: "Zrušit všechny filtry",
    filterRoomsLabel: "Pokoje",
    filterDaysLabel: "Dny v týdnu",
    filterSelectAll: "Vše",
    filterClear: "Nic",
    filterDaysShowing: "Zobrazeno {count} z {total} dnů",
    roomId: "ID pokoje",
    roomKind: "Typ pokoje",
    allRoomTypes: "Všechny typy pokojů",
    clearFilters: "Vyčistit filtry",
    noFilterResults: "Žádné ceny neodpovídají vybraným filtrům.",
    occupancy: "Obsazenost",
    pricingTier: "Cenová úroveň",
    draftOverlay: "Na kalendáři je zobrazeno {count} návrhů cen",
    bookingHighlight: {
      title: "Rezervace · {roomKind}",
      cellsAffected: "dotčených buněk",
      nightsAffected: "dotčených nocí",
      nightsHighlighted: "zvýrazněných nocí",
      priceBefore: "Před",
      priceAfter: "Po",
      price: "Cena",
      occupancy: "Obsazenost",
      explanationLoading: "Zjišťujeme, co tato rezervace změnila…",
      demandShift: "Poptávka se změnila z {demandBefore} na {demandAfter}.",
      demandSteady: "Poptávka zůstala {demandAfter}.",
      explanationHighlight: "Níže je zvýrazněno {nights} nocí tohoto pobytu — u každé buňky je aktuální doporučená cena pro danou noc.",
      explanationUnchanged: "Obsazenost těchto nocí zůstala na {occ}%, takže se doporučená cena nemění.",
      explanationUp: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}% a cenový model zvýšil doporučenou noční sazbu.",
      explanationDown: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}% a cenový model snížil doporučenou noční sazbu.",
      explanationFlat: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}%, ale doporučená noční sazba vyšla stejně.",
    },
    showPastDays: "{count} minulých dnů",
    hidePastDays: "Skrýt minulé",
    todayLabel: "Dnes",
    tierBudget: "Budget",
    tierEconomy: "Economy",
    tierStandard: "Standard",
    tierHigh: "High",
    tierLuxury: "Luxury",
    demand: {
      legendLabel: "Poptávka",
      minimal: "Minimální",
      low: "Nízká",
      standard: "Standardní",
      elevated: "Zvýšená",
      high: "Vysoká",
      minimalTitle: "Minimální poptávka — tento typ pokoje má teď na tento termín výrazně méně rezervací, než v této době před příjezdem obvykle mívá.",
      lowTitle: "Nízká poptávka — tento typ pokoje má teď na tento termín méně rezervací, než v této době před příjezdem obvykle mívá.",
      standardTitle: "Standardní poptávka — tento typ pokoje má teď na tento termín zhruba tolik rezervací, kolik v této době před příjezdem obvykle mívá.",
      elevatedTitle: "Zvýšená poptávka — tento typ pokoje má teď na tento termín víc rezervací, než v této době před příjezdem obvykle mívá.",
      highTitle: "Vysoká poptávka — tento typ pokoje má teď na tento termín výrazně víc rezervací, než v této době před příjezdem obvykle mívá.",
    },
  },

  // ── Price Cell ──────────────────────────────────────────
  priceCell: {
    unitsAvailable: "Volných {available} z {total} jednotek",
    optivoSuggestion: "Optivo návrh",
    reserved: "Rezervováno",
    blocked: "Blokováno",
    option: "Opce",
    closed: "Zavřeno",
    notPricedYet: "Zatím neoceněno",
    noPriceCalculated: "Cena nevypočítána",
    noPriceReasons: {
      missingBasePrice: "Pro tento typ pokoje zatím není nastavena základní cena, takže Optivo nemůže cenu vypočítat. Nastavte ji v Strategie → Úpravy podle typu pokoje.",
    },
    aiPriceSuggestion: "Detail cenového návrhu",
    reservationDetails: "Detaily rezervace",
    price: "Cena:",
    guests: "Hosté:",
    created: "Vytvořeno:",
    partner: "Partner:",
    rejectAi: "Zamítnout",
    regenerateAi: "Přegenerovat",
    generateAi: "Generovat AI cenu",
    sendToPms: "Odeslat do PMS",
    pushSuccess: "Cena odeslána do PMS pro {date}",
    pushError: "Nepodařilo se odeslat cenu do PMS",
    sale: {
      heading: "Za kolik se noc prodala",
      kinds: {
        accommodation: "Ubytování",
        accommodation_services: "Ubytování + služby",
        package: "Balíček",
        package_unseparated: "Balíček (pokoj bez samostatné ceny)",
        no_room_line: "Bez položky ubytování",
      },
      perNight: "{price} za noc",
      stayAverage: "Průměr za {nights}: PMS vede ubytování za celý pobyt jako jednu položku",
      unseparated: "Balíček je jedna položka, údaj je celý balíček za noc",
      zeroAmount: "Za pokoj nebyla účtována žádná částka (voucher nebo pobyt zdarma)",
      groupSpread: "Skupinová rezervace rozpočítaná na {rooms}",
      pendingValue: "Ještě nenačteno",
      pending: "Rozpis této rezervace se z PMS ještě nenačetl",
      unavailableValue: "Bez rozpisu",
      unavailable: "PMS k této rezervaci rozpis nevede",
      accommodation: "Ubytování {amount}",
      services: "Služby {amount}",
      packageAmount: "Balíček {amount}",
      other: "Útrata během pobytu {amount}",
      fees: "Poplatky {amount}",
      stayTotal: "Celý pobyt {total} · {nights}",
      notTwoGuests: "Ceny Optiva v mřížce jsou pro dva hosty",
      ratePlan: "Cenový plán: {name}",
      packageName: "Balíček: {name}",
      bookedOn: "Rezervováno {date}",
      cellLabel: "Prodáno za {price} za noc · {kind}",
      nights: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
      rooms: { one: "{count} pokoj", few: "{count} pokoje", other: "{count} pokojů" },
      guests: { one: "{count} host", few: "{count} hosté", other: "{count} hostů" },
      legendLabel: "Prodané noci",
      legendPrice: "Cena pokoje za noc",
    },
  },

  // ── Suggestion Detail Panel ──────────────────────────────
  suggestionDetail: {
    title: "Detail cenového návrhu",
    basePrice: "základní cena (2 hosté)",
    occupancyPrices: "Ceny dle obsazenosti",
    occupancy: "Obsazenost",
    price: "Cena",
    guest: "host",
    guests: "hosté",
    // `{nights}` je tvar z `bulkEdit.nightCount`.
    losRelaxed: "Zkráceno z {from} na {nights}, protože poptávka po tomto datu je slabá.",
    losRelaxedWeekendFill: "Zkráceno z {from} na {nights}: o víkendech v tomto období bylo loni obsazeno jen {fill} % pokojů.",
    losRelaxedSoldOut: "Zkráceno z {from} na {to}: tento typ pokoje je v noci {night} vyprodaný, takže delší pobyt dnes začít nemůže. Jakmile se v tu noc uvolní pokoj, minimum se vrátí.",
    losRelaxedSoldOutBefore: "Zkráceno z {from} na {to}: tento typ pokoje je vyprodaný v předchozí noci {night}, takže dřívější příjezd do dnešní noci nedosáhne a minimum žádá jen zbytek víkendu či svátků. Jakmile se v tu noc uvolní pokoj, minimum se vrátí.",
    minLosHint: "1 noc znamená bez minimálního pobytu. Uložený minimální pobyt se zamkne samostatně: Optivo ho ponechá a cenu noci dál počítá. Odešle se pouze při zapnutí volby „Odeslat minimální délku pobytu“. Platí jen pro tento typ pokoje — ostatní typy si na danou noc drží vlastní minimum. Navázané cenové plány dostanou stejné minimum v noci, kdy se jim nastavuje cena.",
    losTitle: "Délka pobytu",
    minLos: "Min délka",
    nights: "nocí",
    justificationTitle: "Vysvětlení ceny",
    justificationStaleWarning: "Toto vysvětlení patří ke staršímu výpočtu a nemusí odpovídat ceně výše. Optivo ho nahradí, až tuto noc příště přepočítá.",
    priceSourceCalculated: "Vypočítáno cenovým strojem",
    priceSourceManual: "Nastaveno ručně",
    priceSourceUnknown: "Neznámý zdroj",
    savePrices: "Uložit a zamknout ceny",
    bookingImpactTitle: "Dopad rezervace",
    bookingImpactDesc: "Cena se změnila po rezervaci pro {roomKind}.",
    priceBefore: "Před",
    priceAfter: "Po",
    occupancyBefore: "Obsazenost před",
    occupancyAfter: "Obsazenost po",
    // ── Navázané cenové plány, pod minimální délkou pobytu ────────────────
    // Pokoje stojí za „z“ ve 2. pádě (1 pokoje, 2 a více pokojů), dny za „za“
    // a „alespoň“ ve 4. pádě (1 den, 2–4 dny, 5 a více dní).
    linkedPlansTitle: "Navázané cenové plány",
    linkedPlansDemand: "Poptávka po této noci: {demand} oproti běžné noci — úroveň poptávky: {level}.",
    linkedPlansDemandOrdinary: "Poptávka po této noci: stejná jako v běžné noci — úroveň poptávky: {level}.",
    linkedPlansDemandNotRecorded: "Tato noc byla naposledy spočítána dřív, než Optivo zaznamenalo její poptávku, takže úroveň se řídí obsazeností typu pokoje: {level}.",
    linkedPlansOccupancyNow: "Nyní prodáno {booked} z {rooms} ({percent}).",
    linkedPlansOccupancyUnknown: "Poptávka po této noci zatím není zaznamenaná a obsazenost tohoto typu pokoje se nepodařilo načíst z PMS, proto jsou kanálové plány uvedené na nejnižší úrovni poptávky — tak, jak by je ocenilo příští odeslání.",
    linkedPlansRooms: { one: "{count} pokoje", few: "{count} pokojů", other: "{count} pokojů" },
    linkedPlansDays: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
    linkedPlansGuests: { one: "{count} host", few: "{count} hosté", other: "{count} hostů" },
    linkedPlansPlanColumn: "Cenový plán",
    linkedPlansAdjustmentColumn: "Úprava",
    linkedPlansPriceColumn: "{occupancy} ({currency})",
    linkedPlansNonRefundable: "Nevratná cena",
    linkedPlansPercent: "{value}\u00a0%",
    linkedPlansAmount: "{value}\u00a0{currency}",
    linkedPlansNotOffered: "Nenabízí se",
    linkedPlansWithheldOccupancyUnknown: "Nevratná cena se pro tuto noc nenabízí: obsazenost tohoto typu pokoje se nepodařilo načíst a odeslání ji bez ní nenabídne.",
    linkedPlansWithheldAboveCeiling: "Nevratná cena se pro tuto noc nenabízí: prodáno {booked} z {rooms}, což je nad jejím stropem {ceiling}.",
    linkedPlansWithheldInsideLeadTime: "Nevratná cena se pro tuto noc nenabízí: příjezd je za {days} a nabízí se jen alespoň {minDays} předem.",
    linkedPlansWithheldArrivalToday: "Nevratná cena se pro tuto noc nenabízí: příjezd je dnes a nabízí se jen alespoň {minDays} předem.",
    linkedPlansWithheldExcludedNight: "Nevratná cena se pro tuto noc nenabízí: nikdy se nenabízí na {nightTypes}.",
    linkedPlansWithheldNightTypesUnknown: "Nevratná cena se pro tuto noc nenabízí: nepodařilo se určit noci, na které se nikdy nenabízí, a odeslání ji proto zadrží.",
    linkedPlansNightTypeWeekend: "páteční a sobotní noci",
    linkedPlansNightTypeHoliday: "svátky a významné dny",
    linkedPlansNightTypeBridgeDay: "spojovací dny",
    linkedPlansHint: "Spočítáno z uložené ceny, z poptávky, kterou Optivo zjistilo při posledním výpočtu této noci, a ze současné obsazenosti tohoto typu pokoje — stejně jako při příštím odeslání.",
    linkedPlansEditedNote: "Vychází z uložené ceny, ne z právě upravované.",
    linkedPlansLoadError: "Navázané cenové plány se nepodařilo načíst.",
  },

  // ── Ručně zadaná cena, která vypadá jako překlep ────────────────────────
  // Otázka před uložením v detailu ceny a v „Upravit více nocí“. Počet cen
  // se skloňuje (1 cena, 2–4 ceny, 5 a více cen); hosté stojí za „pro“ ve
  // 4. pádě (1 hosta, 2–4 hosty, 5 a více hostů).
  implausiblePrices: {
    title: { one: "Je tato cena správně?", few: "Jsou tyto ceny správně?", other: "Jsou tyto ceny správně?" },
    leadNight: {
      one: "Jedna z cen, které se chystáte uložit, vypadá neobvykle.",
      few: "{count} ceny, které se chystáte uložit, vypadají neobvykle.",
      other: "{count} cen, které se chystáte uložit, vypadá neobvykle.",
    },
    leadBulk: {
      one: "Tato úprava by zapsala jednu cenu, která vypadá neobvykle.",
      few: "Tato úprava by zapsala {count} ceny, které vypadají neobvykle.",
      other: "Tato úprava by zapsala {count} cen, které vypadají neobvykle.",
    },
    belowReplaced: "Méně než polovina ceny, kterou nahrazuje ({price}).",
    aboveReplaced: "Více než trojnásobek ceny, kterou nahrazuje ({price}).",
    belowNextGuestCount: "Méně než polovina ceny {guests} ({price}).",
    aboveNextGuestCount: "Více než cena {guests} ({price}).",
    otherReason: "Vedle {price} vypadá neobvykle.",
    forGuests: { one: "pro {count} hosta", few: "pro {count} hosty", other: "pro {count} hostů" },
    more: { one: "a {count} další", few: "a {count} další", other: "a {count} dalších" },
    consequenceNight: "Uložením se tyto ceny zamknou tak, jak jsou zadané: Optivo je přestane přepočítávat a při příštím odeslání odejdou do vašeho Property Management systému.",
    consequenceBulk: "Provedením úpravy se tyto ceny zamknou tak, jak jsou zde uvedené: Optivo je přestane přepočítávat a při příštím odeslání odejdou do vašeho Property Management systému.",
    back: "Zpět k cenám",
    saveAnyway: "Přesto uložit",
    applyAnyway: "Přesto provést",
    previewNote: {
      one: "Jedna z těchto cen vypadá neobvykle. Optivo se před provedením úpravy zeptá.",
      few: "{count} z těchto cen vypadají neobvykle. Optivo se před provedením úpravy zeptá.",
      other: "{count} z těchto cen vypadá neobvykle. Optivo se před provedením úpravy zeptá.",
    },
  },

  // ── Pricing Explanation (structured decision table) ─────
  // Zamčená cena vedle ceny, kterou by nastavilo Optivo (`drafts/lockedPrice.ts`).
  lockedPrice: {
    lockedLabel: "Vaše zamčená cena",
    viewerLockedLabel: "Zamčená cena",
    optivoLabel: "Cena Optiva teď",
    optivoShort: "Optivo",
    gapHigher: "o {difference} ({percent}) víc než vaše zamčená cena",
    gapLower: "o {difference} ({percent}) méně než vaše zamčená cena",
    gapSame: "Stejně jako vaše zamčená cena",
    calculatedAt: "spočteno {time}",
    explainer: "Do PMS odchází vaše zamčená cena. Optivo pro tuto noc vedle ní dál počítá svou cenu a po odemčení ji použije.",
    notCalculatedYet: "Optivo tuto noc od zamčení ještě nepřepočítalo. Svou cenu tu ukáže po dalším přepočtu.",
    explanationTitle: "Jak Optivo počítá svou cenu",
    explanationFinalPrice: "Cena Optiva",
    explanationNote: "Vysvětluje cenu, kterou by pro noc nastavilo Optivo, ne vaši zamčenou cenu.",
    compactMovesTitle: "Co hýbe cenou Optiva",
    optivoColumn: "Optivo",
    unlockWillSet: "Odemčením začne platit cena Optiva: {price} podle posledního výpočtu ({time}).",
    markHigher: "Zamčeno. Optivo by nacenilo víc: {price}",
    markLower: "Zamčeno. Optivo by nacenilo méně: {price}",
    markSame: "Zamčeno. Optivo by nacenilo stejně",
    legendLabel: "Zamčené ceny",
    legendHigher: "Optivo by nacenilo víc",
    legendLower: "Optivo by nacenilo méně",
    reasonQuestion: "Proč cenu zamykáte?",
    reasonQuestionMany: "Proč ceny zamykáte?",
    reasonQuestionTyped: "Proč měníte cenu Optiva?",
    reasonOptional: "Nepovinné. Pomůže nám poznat, kde ceny Optiva nesedí.",
    reasonLabel: "Proč je zamčeno",
    reasonNone: "Důvod neuveden",
    reasonAdd: "Doplnit důvod",
    reasonChange: "Změnit důvod",
    reasonSave: "Uložit důvod",
    reasonSavedToast: "Důvod uložen",
    reasons: {
      tooHigh: "Cena Optiva je příliš vysoká",
      tooLow: "Cena Optiva je příliš nízká",
      event: "Akce nebo poptávka, o které Optivo neví",
      agreedPrice: "Cena dohodnutá s hostem, skupinou nebo partnerem",
      competitors: "Kvůli cenám konkurence",
      keepStable: "Nechci, aby se cena měnila",
      other: "Jiný důvod",
    },
    lockSelectionTitle: "Zamknout ceny?",
    lockSelectionWithMinStayTitle: "Zamknout ceny i minimální pobyty?",
    lockSelectionDesc: "{selection}. Zamčené ceny Optivo nepřepíše; svou cenu pro každou noc dál počítá a ukáže ji v detailu noci.",
    lockAction: "Zamknout",
  },

  pricingExplanation: {
    title: "Vysvětlení ceny",
    summary: {
      finalPrice: "Výsledná cena",
      strategyGoal: "Cíl strategie",
      goalRevenue: "Maximalizovat tržby",
      goalOccupancy: "Maximalizovat obsazenost",
      goalHybrid: "Doporučeno Optivem",
      daysFromNow: "za {count} den",
      daysFromNowPlural: "za {count} dní",
      today: "dnes",
    },
    market: {
      title: "Tržní kontext",
      occupancy: "Obsazenost",
      basePrice: "Základní cena",
      minAcceptable: "Minimální cena",
      minAcceptableLastMinute: "Minimální cena (last minute)",
      // Minimum, které se řídí Sezónností: číslo pod ním je nastavené
      // minimum posunuté měsícem noci.
      minAcceptableSeason: "Minimální cena (Sezónnost {seasonSigned} %)",
      minAcceptableLastMinuteSeason: "Minimální cena (last minute, Sezónnost {seasonSigned} %)",
      pace: "Tempo rezervací",
      paceStrong: "silné",
      paceNormal: "normální",
      paceWeak: "slabé",
      // Dokud řádek tempa cení noc podle toho, jak se obvykle prodává její typ,
      // je vlastní tempo noci samo tou půlkou, která mate: prázdná sobota
      // měsíce dopředu měla pod přirážkou za tempo „0.00 · slabé“. Obě čtení
      // proto stojí vedle sebe ve stejné jednotce – kolik má v tomto předstihu
      // průměrná noc – a slovo se vynechává.
      paceWithType: "dnes {pace} · {nightType} obvykle {typical}",
      paceWithHoliday: "dnes {pace} · loni před tímto svátkem {typical}",
      // Totéž tam, kde řádek tempa cenil noc podle celého objektu: typ pokoje
      // o pár pokojích čte tempo objektu, a „0.00 · slabé“ nad řádkem na 157 %
      // bylo jedno jméno nad dvěma čísly. Slovo se vynechává i tady.
      paceWithProperty: "tento typ {pace} · celý objekt {property}",
      paceWithPropertyBlend: "tento typ {pace} · celý objekt {property} · dohromady {blend}",
      paceWithPropertyOnly: "tento typ zatím bez očekávání · celý objekt {property}",
      currentVsTarget: "{current}% aktuálně",
      roomKindVsProperty: "{roomKind} % tento typ pokoje · {property} % objekt",
    },
    table: {
      title: "Jak vznikla tato cena",
      colDecision: "Rozhodnutí",
      colWhy: "Proč",
      colHow: "Jak",
      colImpact: "Dopad ({currency})",
      colRunning: "Průběžná cena ({currency})",
      noEffect: "bez dopadu",
      ofWhich: "z toho {amount}",
    },
    // ── Kompaktní karta, kterou ukazují buňky kalendáře ──────
    compact: {
      biggestMoves: "Největší vlivy",
      moreMoves: "+{count} dalších",
      viewDetail: "Zobrazení: {view}",
      occupancyPrices: "Ceny podle obsazenosti",
      noExplanation: "Optivo pro tuto noc nezaznamenalo vysvětlení.",
      openDetail: "Kliknutím na buňku zobrazíte celý rozpad",
      selectThenOpenDetail: "Kliknutím buňku označíte, dalším kliknutím zobrazíte celý rozpad",
    },
    notApplied: {
      toggleShow: "Zobrazit faktory, které se neuplatnily",
      toggleHide: "Skrýt faktory, které se neuplatnily",
      colFactor: "Faktor",
      colReason: "Proč ne",
      reasons: {
        disabled: "Vypnuto ve vaší cenové strategii.",
        neutral: "Podmínky byly v běžném rozmezí, úprava nebyla potřeba.",
        notApplicable: "Toto datum nesplňovalo podmínky pro tuto úpravu.",
        outsideWindow: "Datum je stále mimo last-minute okno.",
        noData: "Zatím není dost dat pro uplatnění tohoto faktoru.",
        lockedByLastMinute: "Konečnou cenu určilo last-minute, tento krok byl přeskočen.",
        overriddenBySpecialDay: "Příplatek speciálního dne je u této noci alespoň tak vysoký jako její běžná úprava — za den v týdnu, a kde jste ho nastavili, i za měsíc — a proto ji nahrazuje; nikdy se nesčítají.",
        outrankedByDayAdjustment: "Vlastní příplatek za den v týdnu je u této noci vyšší než příplatek speciálního dne, a proto platí on — nikdy se nesčítají.",
        nearTerm: "Tento termín je již natolik blízko, že cenu určují živé signály — obsazenost, tempo rezervací, ceny konkurence. Sezónnost převzatá z analýzy ovlivňuje pouze vzdálenější termíny; měsíc, který nastavíte sami, platí pro každou noc.",
        atLastMinuteFloor: "Cena už klesla na vaše last-minute minimum, takže nebylo co dál zlevňovat — ale právě tímto minimem byla tato noc držena.",
        belowLastMinuteCurve: "Last-minute pro tuto noc už běží, ale ostatní signály ji drží níž, než kde je zatím křivka — křivka ji proto nechala být a nezdražila ji zpět. Převezme cenu, jakmile klesne hlouběji.",
        specialDatePriced: "Toto datum jste nacenili sami jako svátek nebo speciální den, takže tato automatická úprava ustoupila a vaše rozhodnutí nepřebila.",
        lastMinuteDiscounting: "Last-minute snižuje cenu této noci k vašemu minimu z nezlevněné sazby, a jakmile to dělá, je to jediná uplatněná sleva — další sleva pod ní by tutéž noc zlevnila dvakrát.",
        strongDemand: "Tato noc se už prodává — je obsazenější, než tato noc obvykle bývá, nebo se rezervuje rychleji než obvykle — takže slevu na osamocenou noc nepotřebuje.",
        followingMarket: "Základní cena této noci byla odvozena od místního trhu, který už víkend, svátek, sezónu i předstih rezervace naceňuje — tato úprava proto ustoupila, aby tutéž noc nezapočítala dvakrát.",
        // Jen pro řádek, který žádnou hranici neuvádí; řádek tempa nebo
        // přibývání rezervací, který ano, čte svou větu níže.
        heldByPaceBound: "Sleva při nízkém a příplatek při vysokém tempu, jak je máte nastavené ve strategii, tuto úpravu nedovolily.",
        // Viz en.ts — noc řízená poptávkou, které barva poptávky nedovolí nic.
        demandAboveLastMinuteStart: "Tento typ pokoje se řídí poptávkou a last-minute jde jen tak hluboko, jak dovolí poptávka na noc — poptávka na tuto noc je silnější než nejsilnější úroveň, kterou zlevňuje, takže cena se drží.",
        remainingDemandCoversRooms: "Tento typ pokoje se řídí poptávkou a rezervace, které obvykle ještě do uzávěrky získá, by jeho volné pokoje zaplnily — cena se proto zatím drží.",
        soldOut: "Všechny pokoje tohoto typu jsou na tuto noc prodané, takže last-minute nemá co zlevnit.",
        contradictedByBookings: "Rezervace na tuto noc odporují týdnu, se kterým počítala historie, takže týdenní signál poptávky ustoupil.",
      },
      // Viz en.ts — noci naceněné dřív, než rozhodovala barva poptávky.
      lastMinuteMarketCalm: "Poptávka odpovídá plánu (obsazenost i tempo rezervací jsou zdravé), takže sleva na poslední chvíli by se neuplatnila ani uvnitř okna.",
      lastMinuteMarketElevated: "Poptávka je slabší ({pct}% tržní tlak), takže sleva na poslední chvíli se může spustit, jakmile bude datum do {hours} hodin před příjezdem.",
      lastMinuteOverrideOutsideWindow: "Tento typ pokoje má nastaveno vždy zlevňovat, takže snížení na poslední chvíli začne, jakmile bude datum do {hours} hodin před příjezdem.",
      // Viz en.ts. {level} a {start} jsou slova z legendy kalendáře, malými
      // písmeny a vždy v 1. pádě („poptávka je zvýšená“).
      lastMinuteDemandHeldPace: "Poptávka na tuto noc je {level} — rezervace na {demandRatioPct} % obvyklého tempa —, last-minute ale zlevňuje jen noci, kde je poptávka {start} nebo slabší.",
      lastMinuteDemandHeldOccupancy: "Poptávka na tuto noc je {level} — obsazenost na {demandRatioPct} % vašeho cíle —, last-minute ale zlevňuje jen noci, kde je poptávka {start} nebo slabší.",
      lastMinuteOutsideWindowGraded: "Jakmile bude datum do {hours} hodin před příjezdem, cena se začne blížit k vašemu minimu tak daleko, jak poptávka na noc dovolí — při dnešní poptávce ({level}) o {demandDepthPct} % cesty.",
      lastMinuteOutsideWindowHeld: "Okno last-minute se otevře do {hours} hodin před příjezdem, při dnešní poptávce ({level}) by si ale noc cenu udržela — zlevňují se jen noci, kde je poptávka {start} nebo slabší.",
      // Viz en.ts — poptávka noci cenu zatím drží, ale hodiny do příjezdu
      // část toho sevření do uzávěrky uvolní.
      lastMinuteDemandReleasedPace: "Poptávka na tuto noc je {level} — rezervace na {demandRatioPct} % obvyklého tempa —, takže si cenu zatím drží; blíž k příjezdu se sleva přesto uvolní a do uzávěrky dojde {demandDepthPct} % cesty k vašemu minimu.",
      lastMinuteDemandReleasedOccupancy: "Poptávka na tuto noc je {level} — obsazenost na {demandRatioPct} % vašeho cíle —, takže si cenu zatím drží; blíž k příjezdu se sleva přesto uvolní a do uzávěrky dojde {demandDepthPct} % cesty k vašemu minimu.",
      lastMinuteOutsideWindowReleased: "Jakmile bude datum do {hours} hodin před příjezdem, cena se začne blížit k vašemu minimu: při dnešní poptávce ({level}) si ji nejprve udrží a s blížícím se příjezdem se sleva uvolní — do uzávěrky dojde {demandDepthPct} % cesty.",
      lastMinuteOutsideWindowNoReading: "Pro tuto noc zatím chybí údaj o poptávce, takže sleva na poslední chvíli by se neuplatnila ani uvnitř okna.",
      lastMinuteRemainingHeld: "Na tuto noc obvykle získá {lateBookings}{momentumClause} a {freeRooms}; jak zbývá čekat méně rezervací, sleva začne, a pokoj volný v uzávěrce dojde až na vaše minimum.",
      lastMinuteOutsideWindowRemaining: "Jakmile bude datum do {hours} hodin před příjezdem, cena se začne blížit k vašemu minimu podle toho, nakolik rezervace, které tento typ pokoje obvykle ještě získá, nestačí na jeho volné pokoje — a pokoj volný v uzávěrce dojde až na vaše minimum.",
      lastMinuteOutsideWindowSoldOut: "Všechny pokoje tohoto typu jsou na tuto noc prodané, takže by nebylo co zlevnit.",
      // Den v 1. pádě v závorce, aby se nemusel skloňovat.
      holidayOutranked: "Příplatek za den v týdnu ({day}, {dayPremium} %) je vyšší než příplatek tohoto data ({specialPremium} %), a proto platí on — nikdy se nesčítají.",
      // Totéž tam, kde byl součástí běžného kalendáře noci i měsíc nastavený
      // provozovatelem: den a měsíc spolu přebily příplatek data.
      holidayOutrankedWithMonth: "Příplatek za den v týdnu ({day}, {dayPremium} %) spolu s úpravou, kterou jste nastavili pro měsíc ({monthPremium} %), dává {ownPremium} %, víc než příplatek tohoto data ({specialPremium} %), a proto platí ony — nikdy se nesčítají.",
      // Spodní (nebo horní) hranice tempa ve strategii udržela noc, se kterou
      // by tempo pohnulo, beze změny. Říká se místo „v běžném rozmezí“, což
      // seznam psal pod hlavičkou se slabým tempem. Celou noc udrží jen
      // hranice, kterou nastavil provozovatel — spodní hranice každé předvolby
      // reaktivity leží pod 0 % a horní nad ní — proto věta jmenuje pole v jeho
      // strategii. {paceRatioPct} je blend tempa, se kterým krok počítal, jako
      // v řádku tempa.
      paceFloorHeld: "Tempo rezervací je na {paceRatioPct} % očekávání, což by znamenalo úpravu {unboundedDeltaPct} %, sleva při nízkém tempu je ve vaší strategii ale {paceBoundPct} %, takže tempo rezervací cenu snížit nemůže.",
      paceCeilingHeld: "Tempo rezervací je na {paceRatioPct} % očekávání, což by znamenalo úpravu {unboundedDeltaPct} %, příplatek při vysokém tempu je ve vaší strategii ale {paceBoundPct} %, takže tempo rezervací cenu zvýšit nemůže.",
      // Přibývání rezervací sdílí s tempem totéž rozmezí (omezuje se součin
      // obou kroků), takže ho hranice zastaví ze dvou důvodů. Leží-li na 0 %,
      // je to nastavení provozovatele, stejně jako u tempa. Neleží-li, zastavila
      // přibývání jen proto, že tempo rezervací už cenu na hranici dovedlo.
      pickupFloorHeld: "Termín se plní pomaleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, sleva při nízkém tempu je ve vaší strategii ale {paceBoundPct} % a přírůstek rezervací ji sdílí s tempem rezervací, takže cenu snížit nemůže.",
      pickupCeilingHeld: "Termín se plní rychleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, příplatek při vysokém tempu je ve vaší strategii ale {paceBoundPct} % a přírůstek rezervací ho sdílí s tempem rezervací, takže cenu zvýšit nemůže.",
      pickupFloorReachedByPace: "Termín se plní pomaleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, tempo rezervací ale už cenu snížilo o celou slevu při nízkém tempu platnou pro tuto noc ({paceBoundPct} %). Přírůstek rezervací tuto slevu sdílí, takže cenu dál snížit nemůže.",
      pickupCeilingReachedByPace: "Termín se plní rychleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, tempo rezervací ale už cenu zvýšilo o celý příplatek při vysokém tempu platný pro tuto noc ({paceBoundPct} %). Přírůstek rezervací tento příplatek sdílí, takže cenu dál zvýšit nemůže.",
    },
    decisions: {
      baseRate: {
        what: "Základní sazba",
        why: "Výchozí cena odvozená z historických dat.",
        whyPriceList: "Výchozí cena z ceníku ve vašem PMS — tento typ pokoje zatím nemá historii rezervací, ze které by šlo měřit.",
        how: "Začínáme na {basePrice} {currency} za noc.",
        whyMarket: "V nocích pokrytých konkurenční sadou je kotvou tržní medián místo uložené základní ceny.",
        howMarket: "Odvozeno od místního trhu: medián {pricedCount} naceněných konkurentů je {marketMedian} {currency} a vaše pozice je {marketPositionSigned} % → {basePrice} {currency} za noc.",
        howMarketLadder: "Odvozeno od místního trhu: medián {pricedCount} naceněných konkurentů je {marketMedian} {currency}, vaše pozice je {marketPositionSigned} % a tento typ pokoje stojí na {ladderPct} % vašeho nejlevnějšího → {basePrice} {currency} za noc.",
      },
      occupancyAdjustment: {
        what: "Úprava dle obsazenosti",
        why: "Tlak rezervací, porovnaný s obsazeností, na kterou Optivo u této noci míří, upravuje základní sazbu.",
        how: "Obsazenost {currentOccupancyPct}% → {priceAfter} {currency} (minimum {floor}).",
        // Držení základní ceny slevu z obsazenosti zadrželo: dokud se neotevře
        // okno rezervací, drží noc svou základní cenu — slovy řádku s cenovou
        // hranicí. {bookingWindow} skloňují jeho tvary `bookingWindowDays`.
        howBaseHold: "Obsazenost {currentOccupancyPct} % by cenu snížila o {heldDiscount} {currency}, noc se ale drží na základní ceně, dokud se {bookingWindow} před příjezdem neotevře okno rezervací.",
        // Totéž u víkendové nebo sváteční noci, která se drží okna, jež objekt
        // stanovil pro tyto noci, ne okna svého typu pokoje.
        howBaseHoldPeakNight: "Obsazenost {currentOccupancyPct} % by cenu snížila o {heldDiscount} {currency}, noc se ale drží na základní ceně, dokud se {bookingWindow} před příjezdem neotevře vaše okno rezervací pro víkendy a svátky.",
        // Noc převzalo last-minute a tuto slevu stáhlo spolu s ostatními. Slovy,
        // jakými ji už popisuje důvod `lastMinuteDiscounting` u stažených řádků
        // i věta simulátoru `baseLastMinuteHold`. Bez minima: minimum směsi
        // není to, kam noc míří.
        howLastMinute: "Obsazenost {currentOccupancyPct} % by cenu snížila o {withdrawnDiscount} {currency}, last-minute ale snižuje cenu této noci k vašemu minimu z nezlevněné sazby, a sleva za obsazenost proto ustoupila.",
        // Pouze historické návrhy — nic tento klíč už nezapisuje.
        howPaced: "Obsazenost {currentOccupancyPct}% oproti očekávaným {expectedOccupancyPct}% v tuto chvíli → {priceAfter} {currency} (minimum {floor}).",
      },
      dynamicDemand: {
        // Čte týdenní index poptávky z analýzy — týdny kolem data pobytu za
        // poslední rok pobytů — a nikdy rezervaci na tento termín. „Aktuální
        // signál poptávky“ vedl provozovatele hledat tady dnešní poptávku,
        // vedle barvy poptávky, která je právě tím.
        what: "Týdenní poptávka z historie",
        why: "Jak vytížené byly týdny kolem tohoto data za poslední rok oproti průměrnému týdnu — z historie, ne z dnešních rezervací.",
        howPremium: "{sourceLabel} je {demandDeltaPct} % nad běžnou poptávkou, proto se cena zvýší o {priceDeltaPct} %.",
        howDiscount: "{sourceLabel} je {demandDeltaPct} % pod běžnou poptávkou, proto se cena sníží o {priceDeltaPct} %.",
        howNeutral: "{sourceLabel} je blízko běžné poptávce, proto se cena nemění.",
        // Týdenní údaj chybí — analýza ještě neproběhla, nebo v historii tohoto
        // týdne není žádná rezervovaná noc. Řádek proto nejmenuje zdroj a věta
        // ho nemůže mít za podmět.
        howNoSignal: "Pro tuto noc není k dispozici týdenní signál poptávky, proto se cena nemění.",
        // Krok ustoupil: tento řádek je vždy v hlavní tabulce a nikdy mezi
        // faktory, které se neuplatnily, takže jen on může říct proč. Týden nad
        // nebo pod běžnou poptávkou se dál jmenuje — právě ten ústup z ceny
        // vynechal; u běžného nebo nezměřeného týdne zbývá jen důvod. Slovy
        // důvodu `notApplied.reasons.followingMarket`, s poptávkou místo
        // kalendáře, který vyjmenovává.
        howFollowingMarketPremium: "{sourceLabel} je {demandDeltaPct} % nad běžnou poptávkou, základní cena této noci ale byla odvozena od místního trhu, který už poptávku po této noci naceňuje — signál proto ustoupil, aby tutéž poptávku nezapočítal dvakrát.",
        howFollowingMarketDiscount: "{sourceLabel} je {demandDeltaPct} % pod běžnou poptávkou, základní cena této noci ale byla odvozena od místního trhu, který už poptávku po této noci naceňuje — signál proto ustoupil, aby tutéž poptávku nezapočítal dvakrát.",
        howFollowingMarket: "Základní cena této noci byla odvozena od místního trhu, který už poptávku po této noci naceňuje — týdenní signál poptávky proto ustoupil, aby tutéž poptávku nezapočítal dvakrát.",
        // Noc převzalo last-minute a tuto slevu stáhlo spolu s ostatními —
        // slovy `howLastMinute` u řádku obsazenosti i důvodu
        // `notApplied.reasons.lastMinuteDiscounting`. Stahuje se jen sleva,
        // proto ji věta jmenuje.
        howLastMinute: "{sourceLabel} je {demandDeltaPct} % pod běžnou poptávkou, last-minute ale snižuje cenu této noci k vašemu minimu z nezlevněné sazby, a sleva za poptávku proto ustoupila.",
        // Viz en.ts — typ pokoje řízený poptávkou: týden ustupuje rezervacím.
        howPremiumBookings: "{sourceLabel} je {demandDeltaPct} % nad běžnou poptávkou, rezervace na tuto noc jsou ale na {bookingsPacePct} % obvyklého tempa, takže se z toho uplatní jen {historyWeightPct} % — cena se zvýší o {priceDeltaPct} %.",
        howDiscountBookings: "{sourceLabel} je {demandDeltaPct} % pod běžnou poptávkou, rezervace na tuto noc jsou ale na {bookingsPacePct} % obvyklého tempa, takže se z toho uplatní jen {historyWeightPct} % — cena se sníží o {priceDeltaPct} %.",
        howContradictedPremium: "{sourceLabel} je {demandDeltaPct} % nad běžnou poptávkou, rezervace na tuto noc jsou ale jen na {bookingsPacePct} % obvyklého tempa — rušný týden, se kterým historie počítala, se nekoná —, a signál proto ustoupil.",
        howContradictedDiscount: "{sourceLabel} je {demandDeltaPct} % pod běžnou poptávkou, rezervace na tuto noc jsou ale na {bookingsPacePct} % obvyklého tempa — tedy napřed —, a signál proto ustoupil.",
        // Řádek, který cenu neposunul a nemá žádnou z vět výše — nic takový
        // nezapisuje —, stejně nikdy netvrdí, že ji posunul.
        howNotApplied: "Týdenní signál poptávky se na cenu této noci neuplatnil, proto se cena nemění.",
        sourceProperty: "Celý objekt",
        sourceRoomKind: "Tento typ pokoje",
        sourceFallback: "Záložní signál objektu",
        sourceBlended: "Tento typ pokoje v kombinaci s celým objektem",
        sourceNone: "Žádný signál poptávky",
      },
      marketInfluence: {
        what: "Místní trh",
        whatWeather: "Místní podmínky",
        why: "Okolní konkurence ukazuje, jak místní trh oceňuje tuto noc.",
        whyWeather: "Podmínky v okolí — školní prázdniny a předpověď počasí — ukazují, jakou poptávku tato noc nese.",
        howDemand: "Místní trh oceňuje tuto noc {marketStanceLabel} svého rozpětí, proto se cena posune o {priceDeltaPct} %.",
        howNeutral: "Místní trh je pro tuto noc blízko svému průměru, proto se cena nemění.",
        howWeather: "Podmínky vycházejí {marketStanceLabel} obvyklé pro tuto noc, proto se cena posune o {priceDeltaPct} %.",
        howExtreme: "Na tuto noc se očekává extrémní počasí, což se čte jako snížená poptávka, proto cena klesá o {priceDeltaPct} %.",
        howExtremeOutweighed: "Na tuto noc se očekává extrémní počasí, ale silnější poptávka z ostatních podmínek jej převáží, proto cena přesto roste o {priceDeltaPct} %.",
        stanceHigh: "vysoko v rámci",
        stanceLow: "nízko v rámci",
        stanceHighWeather: "silněji než",
        stanceLowWeather: "slaběji než",
        sample: "Na základě {pricedCount} konkurentů s cenou z {supplyCount} nabízených.",
        sampleWeather: "Pouze na základě svátků a počasí — sledování konkurence je pro tento objekt vypnuté.",
      },
      seasonality: {
        what: "Sezónnost",
        why: "Poptávka se mění podle sezóny.",
        // Měsíc nastavený provozovatelem cení každou svou noc, což měsíc
        // z analýzy nikdy nedělá — řádek proto říká, čí je.
        whyStated: "Úpravu pro tento měsíc jste nastavili v sekci Sezónnost; platí pro každou noc měsíce.",
        howHigh: "Hlavní sezóna: {deltaPct}%.",
        howLow: "Vedlejší sezóna: {deltaPct}%.",
        howNeutral: "Mimo sezónu — bez úpravy.",
      },
      holiday: {
        what: "Svátek / speciální datum",
        why: "Poptávka se mění o svátcích a speciálních dnech.",
        how: "„{name}\": {deltaPct}%.",
      },
      bridgeDay: {
        what: "Příplatek za spojovací den",
        why: "Osamocená volná noc mezi dvěma vytíženými nocemi — zvyšte cenu podle poptávky.",
        how: "Aplikováno {deltaPct}%.",
      },
      shoulderDay: {
        what: "Den na okraji špičky",
        why: "Běžná noc těsně vedle svátečního volna — poptávka se soustředí na samotné volno a po obou jeho stranách obvykle slábne.",
        howBefore: "Noc před svátečním volnem: {deltaPct}%.",
        howAfter: "Noc po svátečním volnu: {deltaPct}%.",
        howBeforeNamed: "Noc před svátečním volnem ({name}): {deltaPct}%.",
        howAfterNamed: "Noc po svátečním volnu ({name}): {deltaPct}%.",
        how: "Aplikováno {deltaPct}%.",
      },
      leadTime: {
        what: "Křivka lead-time",
        why: "Cena reaguje na to, jak dlouho dopředu se rezervuje.",
        howPremium: "{daysOut} dní dopředu — fáze prémie ({deltaPct}%).",
        howDiscount: "{daysOut} dní dopředu — fáze slevy ({deltaPct}%).",
        howFarOut: "{daysOut} dní dopředu — za hranicí rezervačního okna, Přirážka před booking window ({deltaPct}%).",
        howBuildup: "{daysOut} dní dopředu — poptávka roste, cena stoupá ({deltaPct}%).",
        howHold: "{daysOut} dní dopředu — vrchol poptávky, cena držena ({deltaPct}%).",
        howDecline: "{daysOut} dní dopředu — blíží se příjezd, cena klesá ({deltaPct}%).",
        howGeneric: "{daysOut} dní dopředu ({deltaPct}%).",
      },
      lastMinute: {
        what: "Cena na poslední chvíli",
        why: "Snížení ceny ve chvílích blížícího se příjezdu pro zachycení pozdní poptávky.",
        how: "{hoursRemaining} h zbývá, krok {step}/{totalSteps} ({deltaPct}%).",
        // Poptávka se zvedla a sleva zůstala, místo aby se stáhla. Číslo kroku
        // je hodiny, ne podržená hloubka, proto se neuvádí.
        howHolding: "Rezervace se rozběhly, a tak sleva zůstává tam, kam došla — {depthPct} % cesty k vašemu minimu —, místo aby se stáhla; {hoursRemaining} h zbývá ({deltaPct}%).",
        // Viz en.ts — Řídit podle poptávky jde tak hluboko, jak dovolí barva poptávky.
        howGradedPace: "Poptávka je {level} — rezervace na {demandRatioPct} % obvyklého tempa —, takže last-minute jde do uzávěrky {demandDepthPct} % cesty k vašemu minimu: zatím {depthPct} %, {hoursRemaining} h zbývá ({deltaPct}%).",
        howGradedOccupancy: "Poptávka je {level} — obsazenost na {demandRatioPct} % vašeho cíle —, takže last-minute jde do uzávěrky {demandDepthPct} % cesty k vašemu minimu: zatím {depthPct} %, {hoursRemaining} h zbývá ({deltaPct}%).",
        // Viz en.ts — sama poptávka by noc (z větší části) udržela a zbytek
        // uvolňují hodiny do příjezdu, takže řádek říká obojí.
        howGradedPaceReleased: "Poptávka je {level} — rezervace na {demandRatioPct} % obvyklého tempa —, takže sama poptávka by pustila {demandAllowedDepthPct} %; s takhle blízkým příjezdem se sleva uvolňuje a do uzávěrky jde {demandDepthPct} % cesty k vašemu minimu: zatím {depthPct} %, {hoursRemaining} h zbývá ({deltaPct}%).",
        howGradedOccupancyReleased: "Poptávka je {level} — obsazenost na {demandRatioPct} % vašeho cíle —, takže sama poptávka by pustila {demandAllowedDepthPct} %; s takhle blízkým příjezdem se sleva uvolňuje a do uzávěrky jde {demandDepthPct} % cesty k vašemu minimu: zatím {depthPct} %, {hoursRemaining} h zbývá ({deltaPct}%).",
        howRemaining: "Tento typ pokoje na tuto noc do uzávěrky obvykle získá {lateBookings}{momentumClause} a {freeRooms} — last-minute proto zatím běží na {demandAllowedDepthPct} % vaší křivky slev a do uzávěrky dojde až na vaše minimum: zatím {depthPct} % cesty, zbývá {hoursRemaining} h ({deltaPct}%).",
        remainingMomentumClause: " (při současném tempu rezervací, {momentumPct} % obvyklého, {lateBookingsAdjusted})",
        lateBookings: {
            none: "už téměř žádnou další rezervaci",
            one: "ještě {count} rezervaci",
            few: "ještě {count} rezervace",
            many: "ještě {count} rezervace",
            other: "ještě {count} rezervací",
        },
        freeRooms: {
            one: "volný zůstává {count} pokoj",
            few: "volné zůstávají {count} pokoje",
            other: "volných zůstává {count} pokojů",
        },
      },
      gapFill: {
        what: "Sleva na zaplnění mezery",
        why: "Osamocená noc mezi rezervacemi — sleva pro zaplnění.",
        how: "Aplikováno {deltaPct}%.",
      },
      pace: {
        what: "Tempo rezervací",
        // Stav, ne změna: řádek pod ním (přírůstek) počítá, kolik rezervací za
        // poslední dny přibylo, a obě věty „proč“ to říkají, aby se nedaly
        // zaměnit (Hotel Ostrov, říjen 2026: „tempo 157 %“ nad „plní se
        // pomaleji“ četl jako rozpor).
        why: "Kolik pokojů je na tuto noc už obsazeno proti tomu, kolik jich v tomto předstihu obvykle bývá – stav rezervací, ne jejich přírůstek.",
        // Čí pokoje řádek četl, kolik je obsazeno a kolik jich taková noc v
        // tomto předstihu mívá. Typ pokoje o pár pokojích čte celý objekt, a
        // bez této věty se „157 %“ nad typem bez jediné rezervace nedalo
        // pochopit. Stojí před větou o tempu, i před tou o hranici.
        howSubjectRoomKind: "Tento typ pokoje ({roomKindRooms}) má na tuto noc obsazeno {roomKindBooked}, v tomto předstihu mívá asi {roomKindExpected}.",
        howSubjectProperty: "Celý objekt ({propertyRooms}) má na tuto noc obsazeno {propertyBooked}, v tomto předstihu mívá asi {propertyExpected}.",
        howSubjectBlend: "Tento typ pokoje ({roomKindRooms}) má na tuto noc obsazeno {roomKindBooked} (mívá asi {roomKindExpected}), celý objekt ({propertyRooms}) {propertyBooked} (mívá asi {propertyExpected}); tempo počítá objekt {propertyWeightPct} % a tento typ {roomKindWeightPct} %.",
        rooms: { one: "{count} pokoj", few: "{count} pokoje", other: "{count} pokojů" },
        // Dvě různá čísla: naměřené tempo a to, kolik toto tempo pro danou noc
        // znamenalo. Do obou míst se dřív dosazoval {deltaPct}, takže úprava
        // +2 % se četla jako „Tempo na 2 % očekávání“. Uvedené tempo je blend
        // typu pokoje a celého objektu — ten, se kterým krok počítal — proto
        // se může lišit od hodnoty v hlavičce panelu.
        how: "Tempo na {paceRatioPct} % očekávání → úprava {deltaPct} %.",
        // Návrhy uložené dřív, než tempo začalo cestovat s krokem, nesou jen
        // úpravu — a raději o tempu mlčet než o něm lhát.
        howGeneric: "Úprava podle tempa rezervací {deltaPct} %.",
        // Hranice tempa pohyb zkrátila, takže úprava je hranice, ne to, oč
        // si tempo řeklo. „Platná pro tuto noc“, ne „ve vaší strategii“: u
        // cíle Doporučeno Optivem předvolba reaktivity podle předstihu dosazuje
        // hranice cíle pro tržby nebo obsazenost, takže číslo se může lišit
        // od toho, které ukazuje editor strategie.
        howPaceFloor: "Tempo na {paceRatioPct} % očekávání by znamenalo úpravu {unboundedDeltaPct} %, sleva při nízkém tempu platná pro tuto noc ji ale drží na {paceBoundPct} %.",
        howPaceCeiling: "Tempo na {paceRatioPct} % očekávání by znamenalo úpravu {unboundedDeltaPct} %, příplatek při vysokém tempu platný pro tuto noc ji ale drží na {paceBoundPct} %.",
        // Noc s málo vlastními rezervacemi začíná tím, jak se obvykle prodává
        // její typ noci – den v týdnu v sezóně, nebo noc před svátkem – takže
        // i prázdná noc může mít přirážku. Říká se jen, dokud má typ ve čtení
        // skutečnou váhu, jinak by přirážka na prázdné noci neměla vysvětlení.
        howType: "Dokud noc nemá dost vlastních rezervací, vychází tempo z toho, jak se obvykle prodávají {nightType}: {typicalPacePct} % {averageNight}. Vlastní rezervace noci zatím váží {bookingsWeightPct} %.",
        howTypeHoliday: "Dokud noc nemá dost vlastních rezervací, vychází tempo z toho, jak se loni prodávala noc před tímto svátkem: {typicalPacePct} % {averageNight}. Vlastní rezervace noci zatím váží {bookingsWeightPct} %.",
        // Podle dne v týdnu, kterým noc začíná, jak ho čte engine: neděle první.
        typeWeekdays: ["neděle", "pondělky", "úterky", "středy", "čtvrtky", "pátky", "soboty"],
        typeSeasonHigh: "{weekdays} v hlavní sezóně",
        typeSeasonLow: "{weekdays} mimo hlavní sezónu",
        averageNightSeason: "průměrné noci v sezóně",
        averageNightAll: "průměrné noci",
      },
      pickup: {
        // Dřív „Tempo přibývání rezervací“ pod „Tempo rezervací“: dvě tempa nad
        // sebou, jedno stav a druhé změna. Řádek se jmenuje po svém okně
        // (whatWindow, podle uloženého {windowDays}); návrh uložený bez okna
        // nese obecné jméno.
        what: "Přírůstek rezervací za poslední dny",
        whatWindow: { one: "Přírůstek rezervací za poslední den", few: "Přírůstek rezervací za poslední {count} dny", other: "Přírůstek rezervací za posledních {count} dní" },
        why: "Kolik rezervací za poslední dny přibylo nebo ubylo proti tomu, kolik jich v tuto dobu obvykle přibývá – změna, ne stav.",
        // Čí pokoje řádek počítal: {change} je skloňovaná věta z gained / lost /
        // unchanged, {window} z window. Stojí před větou o směru i před tou
        // o hranici.
        howSubjectRoomKind: "Tomuto typu pokoje za {window} {roomKindChange}, v tuto dobu mu běžně přibývá asi {roomKindExpectedPickup}.",
        howSubjectProperty: "Celému objektu za {window} {propertyChange}, v tuto dobu mu běžně přibývá asi {propertyExpectedPickup}.",
        howSubjectBlend: "Tomuto typu pokoje za {window} {roomKindChange} (běžně asi {roomKindExpectedPickup}), celému objektu {propertyChange} (běžně asi {propertyExpectedPickup}); přírůstek počítá objekt {propertyWeightPct} % a tento typ {roomKindWeightPct} %.",
        window: { one: "poslední den", few: "poslední {count} dny", other: "posledních {count} dní" },
        gained: { one: "přibyl {count} pokoj", few: "přibyly {count} pokoje", other: "přibylo {count} pokojů" },
        lost: { one: "ubyl {count} pokoj", few: "ubyly {count} pokoje", other: "ubylo {count} pokojů" },
        unchanged: "nepřibyl žádný pokoj",
        // Směr nese samotná věta, proto se {deltaPct} dosazuje v absolutní
        // hodnotě — „klesá o -3 %“ by se četlo jako zvýšení.
        howFaster: "Termín se plní rychleji, než se očekávalo, cena proto stoupá o {deltaPct} %.",
        howSlower: "Termín se plní pomaleji, než se očekávalo, cena proto klesá o {deltaPct} %.",
        howNeutral: "Rezervace přibývají očekávaným tempem, cena se proto nemění.",
        // Rozmezí tempa, které tempo a přibývání rezervací sdílejí, nechalo
        // přibývání jen část jeho pohybu. Na rozdíl od dvou vět výše se znaménkem:
        // věta uvádí, oč si krok řekl a co dostal, a odliší je jen znaménko.
        howPaceFloor: "Termín se plní pomaleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, tempo rezervací a přírůstek rezervací ale spolu nesmějí cenu snížit víc, než dovoluje sleva při nízkém tempu platná pro tuto noc ({paceBoundPct} %), takže se cena posune jen o {deltaPct} %.",
        howPaceCeiling: "Termín se plní rychleji, než se očekávalo, což by znamenalo úpravu {unboundedDeltaPct} %, tempo rezervací a přírůstek rezervací ale spolu nesmějí cenu zvýšit víc, než dovoluje příplatek při vysokém tempu platný pro tuto noc ({paceBoundPct} %), takže se cena posune jen o {deltaPct} %.",
      },
      propertyDemand: {
        what: "Poptávka po celém ubytování",
        why: "Obsazenost celého ubytování táhne základní cenu této kategorie s sebou, oběma směry.",
        how: "Obsazenost celého ubytování posunula základ této kategorie o {deltaPct}%.",
        howWhole: "Celá tato úprava vychází z obsazenosti celého ubytování — tato kategorie zatím nemá vlastní rezervace.",
      },
      dayAdjustment: {
        what: "Úprava dne v týdnu",
        why: "Ceny pro {day} se řídí vašimi týdenními pravidly.",
        how: "Aplikováno {deltaPct}%.",
      },
      // Bez `how`, které by hranici jmenovalo: dopad říká, o kolik se cena
      // posunula, a sloupec s cenou říká, kde skončila — věta opakující
      // hranici je totéž číslo potřetí. Řádek ale říká, O KTEROU hranici se
      // opřel — mohou pod ním stát tři různá čísla a hlavička panelu uvádí jen
      // minimální cenu. `what` / `why` čte i návrh spočítaný dřív, než se
      // hranice začaly jmenovat.
      floorClamp: {
        what: "Minimální cenová hranice",
        why: "Výsledná cena byla pod minimální hranicí.",
        // Minimum roste s víkendovým nebo svátečním příplatkem noci, proto
        // může řádek skončit nad minimem, které uvádí hlavička.
        whyPremium: "Minimální cena tohoto typu pokoje, {minPrice} {currency}, se v tuto noc zvyšuje o příplatek {premiumSigned} %.",
        // Minimum, které se řídí Sezónností, se mění s měsícem noci, samo
        // nebo spolu s příplatkem noci.
        whySeason: "Minimální cena tohoto typu pokoje, {minPrice} {currency}, se tento měsíc mění podle Sezónnosti o {seasonSigned} %.",
        whySeasonPremium: "Minimální cena tohoto typu pokoje, {minPrice} {currency}, se tento měsíc mění podle Sezónnosti o {seasonSigned} % a v tuto noc se zvyšuje o příplatek {premiumSigned} %.",
        // Držení základní ceny: dokud se neotevře okno rezervací, drží noc
        // svou základní cenu, ať říká poptávka, obsazenost nebo tempo cokoli.
        whatBaseHold: "Základní cenová hranice",
        whyBaseHold: "Dokud do příjezdu zbývá víc než {bookingWindow} (okno rezervací tohoto typu pokoje), cena nemůže klesnout pod jeho základní cenu — ani při nízké poptávce, obsazenosti nebo tempu rezervací.",
        whyBaseHoldAdjusted: "Dokud do příjezdu zbývá víc než {bookingWindow} (okno rezervací tohoto typu pokoje), cena nemůže klesnout pod jeho základní cenu upravenou pro tuto noc — ani při nízké poptávce, obsazenosti nebo tempu rezervací.",
        // Víkendová nebo sváteční noc drží okno, které objekt stanovil pro tyto
        // noci — samotné dny neřeknou, proč sobota drží 6 dní a úterý vedle 18.
        whyBaseHoldPeakNight: "Dokud do příjezdu zbývá víc než {bookingWindow} (vaše okno rezervací pro víkendy a svátky), cena nemůže klesnout pod základní cenu tohoto typu pokoje — ani při nízké poptávce, obsazenosti nebo tempu rezervací.",
        whyBaseHoldAdjustedPeakNight: "Dokud do příjezdu zbývá víc než {bookingWindow} (vaše okno rezervací pro víkendy a svátky), cena nemůže klesnout pod základní cenu tohoto typu pokoje upravenou pro tuto noc — ani při nízké poptávce, obsazenosti nebo tempu rezervací.",
        // Odvození, ne výsledek: ten je ve sloupci s cenou.
        howBaseHoldAdjusted: "Základní cena {basePrice} {currency}, úprava pro tuto noc {nightAdjustmentSigned} %.",
        bookingWindowDays: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
        whatLastMinute: "Last-minute minimum",
        whyLastMinute: "Ceny na poslední chvíli mohou tuto noc snížit nejvýš na vaše last-minute minimum.",
        // Last-minute minimum, které se řídí Sezónností: odvození, jako u
        // držení základní ceny.
        howLastMinuteSeason: "Vaše last-minute minimum {minPriceStated} {currency}, úprava podle Sezónnosti pro tento měsíc {seasonSigned} %.",
      },
      ceilingClamp: {
        what: "Maximální cenový strop",
        why: "Výsledná cena překročila nastavený strop.",
      },
      rounding: {
        what: "Zaokrouhlení",
        why: "Zaokrouhlení podle nastavené granularity ceny.",
        how: "Cena zaokrouhlena.",
        howCharm: "Zaokrouhleno na nejbližších {granularity} {currency} a sníženo o {charmEnding}, aby cena končila na {ending}.",
      },
      // Úprava bez zamčení, kterou provozovatel položil na cenu Optiva jako
      // poslední (`drafts.adjustment`). `what` a `why` se vykreslí i z
      // prázdných parametrů, což je vše, co nese řádek čtenáře s přístupem jen
      // pro čtení, a jmenují věc, nikdy čtenáře. Žádná věta neuvádí příčinu,
      // kterou řádek nenese: last-minute okno se zmiňuje jen tam, kde řádek
      // uvádí podíl zdražení nebo že právě jeho zmenšení nic nenechalo.
      operatorAdjustment: {
        what: "Úprava bez zamčení",
        why: "Nastaveno na stránce Ceny pro tuto noc a typ pokoje. Není to zámek: Optivo cenu dál přepočítává a úpravu uplatní nakonec.",
        howAmount: "{valueSigned} {currency} k ceně Optiva {priceBefore} {currency}.",
        howPercent: "{valueSigned} % z ceny Optiva {priceBefore} {currency}.",
        howShownRung: "U tohoto typu pokoje se zobrazená cena liší od ceny pro dva hosty, kterou počítají řádky výše, o {shownOffsetSigned} {currency}; úprava se počítá ze zobrazené ceny.",
        howAfterFloors: "Počítá se až po vlastních hranicích Optiva a zastaví ji jen minimální cena typu pokoje pro tuto noc.",
        heldMinimum: "Zastavila ji minimální cena typu pokoje pro tuto noc.",
        atMinimum: "Cena Optiva {priceBefore} {currency} je už na minimální ceně typu pokoje pro tuto noc nebo pod ní, takže úprava nic nesnižuje.",
        minimumIs: "Minimální cena pro tuto noc je {bound} {currency}.",
        // Kde se minimum typu pokoje řídí Sezónností měsíce noci.
        minimumIsSeason: "Minimální cena pro tuto noc je {bound} {currency}: minimum typu pokoje posunuté Sezónností tohoto měsíce o {seasonSigned} %.",
        heldCeiling: "Zastavila ji maximální cena této noci.",
        atCeiling: "Cena Optiva {priceBefore} {currency} je už na maximální ceně této noci nebo nad ní, takže úprava nic nezvyšuje.",
        ceilingIs: "Maximální cena této noci je {bound} {currency}.",
        boundAsShown: "U zde zobrazené ceny to vychází na {boundPrice} {currency}.",
        heldRounding: "Je menší než zaokrouhlení cen na {granularity} {currency}, takže cena zůstává {priceBefore} {currency}.",
        lastMinuteShare: "V last-minute okně platí {lastMinutePct} % zdražení: zmenšuje se podle toho, jak daleko last-minute křivka pro tuto noc došla, takže volný pokoj i tak klesne na své last-minute minimum.",
        lastMinuteLittle: "V last-minute okně zbývá ze zdražení tak málo, že se cena nezmění.",
        tooLow: "Nezbyla by žádná cena, proto se nepoužila.",
      },
    },
    days: {
      monday: "pondělí",
      tuesday: "úterý",
      wednesday: "středa",
      thursday: "čtvrtek",
      friday: "pátek",
      saturday: "sobota",
      sunday: "neděle",
    },
  },

  // ── Živý příklad ceny (simulátor) ───────────────────────
  //
  // Klíče `step*` odpovídají backendovému `SimulationStep.labelKey`, klíče
  // `base*` odpovídají `basePriceExplanationKey`.
  priceSimulator: {
    stepRoomKindOccupancy: "Obsazenost kategorie pokoje",
    stepPropertyDemand: "Poptávka v celém objektu",
    stepDynamicDemand: "Dynamická poptávka",
    stepDynamicDemandPremium: "Přirážka za dynamickou poptávku",
    stepDynamicDemandDiscount: "Sleva za dynamickou poptávku",
    stepMarketInfluence: "Vliv trhu",
    stepSeasonality: "Sezónnost",
    stepHoliday: "Svátek",
    stepHolidayNamed: "Svátek ({name})",
    stepHolidaySimulated: "Svátek (simulovaný)",
    stepBridgeDay: "Přirážka za přemostěný den",
    stepShoulderDay: "Den na okraji špičky",
    stepLeadTime: "Křivka předstihu ({days} d)",
    stepLeadTimeDetailed: "Křivka předstihu ({days} d, {phase}, {goal})",
    stepLastMinute: "Úprava na poslední chvíli",
    stepLastMinuteAlwaysWindow: "Poslední chvíle (vždy, začíná {hours} h před příjezdem)",
    stepLastMinuteMarket: "Poslední chvíle (poptávka: {level}, {demandDepthPct} % křivky)",
    stepLastMinuteAlwaysActive: "Poslední chvíle (vždy, zbývá {hoursLeft} h, krok {step}/{totalSteps})",
    stepLastMinuteActive: "Poslední chvíle (zbývá {hoursLeft} h, krok {step}/{totalSteps}, poptávka: {level}, {demandDepthPct} % křivky)",
    stepLastMinuteHolding: "Poslední chvíle (podrženo {depthPct} % cesty dolů, zbývá {hoursLeft} h, poptávka: {level})",
    stepLastMinuteRemaining: "Poslední chvíle (podle zbývající poptávky)",
    stepLastMinuteRemainingActive: "Poslední chvíle (zbývá {hoursLeft} h, krok {step}/{totalSteps}, očekávané rezervace pokryjí {coveragePct} % volných pokojů)",
    stepLastMinuteRemainingHolding: "Poslední chvíle (podrženo {depthPct} % cesty dolů, zbývá {hoursLeft} h)",
    lastMinuteNoDemandReading: "bez údajů",
    stepGapFill: "Sleva za vyplnění mezery",
    stepPace: "Úprava podle tempa rezervací",
    stepPickup: "Přírůstek rezervací za poslední dny",
    stepDayAdjustment: "Úprava podle dne",
    stepDayAdjustmentNamed: "Úprava – {day}",
    stepRounding: "Zaokrouhleno na {granularity}",
    // Slovník dosazovaný do popisků kroků výše.
    phaseFarOut: "daleko dopředu",
    phaseBuildup: "nárůst",
    phaseHold: "držení",
    phaseDecline: "pokles",
    goalRevenue: "výnos",
    goalOccupancy: "obsazenost",
    goalHybrid: "doporučeno Optivem",
    dayFriday: "pátek",
    daySaturday: "sobota",
    daySunday: "neděle",
    // Vysvětlení základní ceny pod řádkem se základní sazbou.
    baseRate: "Základní sazba: {basePrice}",
    baseNoHistory: "Není k dispozici historická základní cena.",
    baseOccupancyBlend:
      "Základní cena {basePrice} upravená mixem obsazenosti: {signals} → {floor} + ({basePrice} − {floor}) × {ratio} = {result}  ·  minimum: {floor}",
    // Pouze historické návrhy — nic tento klíč už nezapisuje.
    baseOccupancyBlendPaced:
      "Základní cena {basePrice} upravená mixem obsazenosti: {signals} → {floor} + ({basePrice} − {floor}) × {ratio} = {result}  ·  minimum: {floor}  ·  očekáváno {expectedOccupancyPct}% v tuto chvíli (cíl {targetOccupancyPct}%)",
    baseHold:
      "Základní cena {basePrice} podržena (výnos/hybrid před rezervačním oknem sezóny; sleva za obsazenost potlačena)",
    baseHoldPeakNight:
      "Základní cena {basePrice} podržena (výnos/hybrid před oknem rezervací pro víkendy a svátky; sleva za obsazenost potlačena)",
    baseLastMinuteHold:
      "Základní cena {basePrice} podržena (cenu této noci snižuje last-minute, sleva za obsazenost proto ustoupila)",
    // Simulátor nemá co sledovat, dosazuje proto medián rovný základní ceně
    // nejlevnějšího typu pokoje a říká to.
    baseMarketFollow:
      "Základní cena {basePrice} odvozena od trhu: medián {marketMedian} {position} %, ×{ladder} pro tento typ pokoje. Simulátor předpokládá tržní medián rovný základní ceně vašeho nejlevnějšího typu pokoje; ostrý výpočet použije zachycený.",
    baseSignalRoomKind: "kategorie pokoje {occupancyPct} % (váha {weightPct} %, poměr {ratio})",
    baseSignalProperty: "objekt {occupancyPct} % (váha {weightPct} %, poměr {ratio})",
    // Řádky s mantinely pod výslednou cenou.
    minPriceFloor: "Minimální cena",
    minPriceHighDemand: "Minimální cena (vysoká poptávka)",
    // Řádek s hranicí jmenuje mantinel, který ukazuje, stejně jako řádek
    // hranice ve vysvětlení ceny — držená základní cena není minimum.
    basePriceFloor: "Základní cenová hranice",
    lastMinuteMinimum: "Last-minute minimum",
    // Minimum, které se řídí Sezónností, posunuté simulovaným měsícem.
    minPriceFloorSeason: "Minimální cena (Sezónnost {seasonSigned} %)",
    minPriceHighDemandSeason: "Minimální cena (vysoká poptávka, Sezónnost {seasonSigned} %)",
    lastMinuteMinimumSeason: "Last-minute minimum (Sezónnost {seasonSigned} %)",
    maxPriceCeiling: "Maximální cena",
    // Ovládací prvky panelu.
    months: ["Led", "Úno", "Bře", "Dub", "Kvě", "Čvn", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"],
    demandCalm: "Klid",
    demandNormal: "Normál",
    demandHot: "Špička",
    // Předpověď hýbe cenami 10 dní dopředu, ceny se počítají rok a půl dopředu —
    // většina simulovaných dat proto nemá vlastní počasí.
    weatherLabel: "Počasí",
    weatherNone: "Dle záznamu",
    weatherPleasant: "Příjemné",
    weatherExtreme: "Extrémní",
    propertyProfile: "Profil objektu",
    calculating: "Počítání…",
    empty: "Změňte parametry strategie a zobrazí se náhled ceny.",
    decreaseDaysUntilArrival: "Snížit počet dní do příjezdu",
    increaseDaysUntilArrival: "Zvýšit počet dní do příjezdu",
  },

  // ── Settings ────────────────────────────────────────────
  settings: {
    // Karta sledování konkurence (součást předplatného, zapíná ji provozovatel)
    compsetCardTitle: "Sledování konkurence",
    marketLocationLabel: "Poloha ubytování",
    marketLocationHint:
      "Určuje časové pásmo, ve kterém se vyhodnocuje váš den, a váže signály o konkurenci a poptávce na skutečnou polohu ubytování.",
    marketLocationSaved: "Poloha ubytování uložena",
    marketLocationSaveFailed: "Polohu ubytování se nepodařilo uložit",
    compsetSaved: "Sledování konkurence bylo upraveno",
    compsetSaveFailed: "Sledování konkurence se nepodařilo upravit",
    // Odkud se berou ceny konkurence, viz komentář v `en.ts`.
    lighthouse: {
      sourceTitle: "Odkud se berou ceny konkurence",
      optivoOption: "Tržní data Optiva",
      optivoHint: "Ubytování v okolí vaší polohy, která načítá Optivo. Je součástí předplatného.",
      lighthouseOption: "Váš účet Lighthouse",
      lighthouseHint: "Sada konkurentů, kterou vedete v Lighthouse, načtená jednou denně. Potřebuje váš API token Lighthouse nebo klientské přihlašovací údaje.",
      sourceSaved: "Zdroj cen konkurence byl změněn",
      sourceSaveFailed: "Zdroj cen konkurence se nepodařilo změnit",
      linkedSet: "sada „{name}“ ({hotels})",
      linkedSetUnnamed: "sada č. {id} ({hotels})",
      setHotels: { one: "{count} hotel", few: "{count} hotely", other: "{count} hotelů" },
      linkedToken: "token …{hint}",
      linkedClient: "client ID …{hint}",
      history: {
        pending: "Ceny z minulosti se načtou během následujících dnů.",
        running: "Načítají se ceny z minulosti, po týdnech.",
        done: "Ceny za uplynulý rok jsou načtené.",
        unavailable: "Váš tarif Lighthouse neobsahuje ceny z minulosti.",
        error: "Načítání cen z minulosti se zastavilo. Propojení znovu uložte a začne znovu.",
      },
      demand: {
        none: "Poptávka na trhu zatím nenačtena.",
        read: "Poptávka na trhu načtena {date}.",
        unavailable: "Poptávka na trhu není v tomto předplatném dostupná.",
        error: "Načtení poptávky na trhu se nezdařilo; zkusí se znovu zítra.",
      },
      pricesFrom: "ceny z {when}",
      pricesNone: "ceny zatím nenačteny",
      change: "Změnit",
      unlink: "Odpojit",
      unlinkTitle: "Odpojit účet Lighthouse?",
      unlinkDescription: "Ceny konkurence z Lighthouse se z Optiva smažou. Znovu se použijí tržní data Optiva.",
      unlinkConfirm: "Odpojit",
      unlinkKeep: "Ponechat propojení",
      unlinked: "Účet Lighthouse byl odpojen",
      unlinkFailed: "Účet Lighthouse se nepodařilo odpojit",
      dialogTitle: "Propojit účet Lighthouse",
      dialogDescription: "Optivo jednou denně načte sadu konkurentů, kterou vedete v Lighthouse, a počítá s ní ceny.",
      authKindLabel: "Jak se Optivo přihlásí k vašemu účtu",
      authToken: "API token",
      authClient: "Klientské přihlašovací údaje",
      tokenLabel: "API token Lighthouse",
      tokenHint: "O API token pro váš účet požádejte podporu Lighthouse. Uloží se šifrovaně a už se nikdy nezobrazí.",
      clientIdLabel: "Client ID",
      clientSecretLabel: "Client secret",
      clientHint: "Některým účtům vydává Lighthouse místo tokenu Client ID a Client secret. Obojí se uloží šifrovaně; secret se už nikdy nezobrazí.",
      checkToken: "Ověřit token",
      checkCredentials: "Ověřit přihlašovací údaje",
      back: "Zpět",
      checking: "Ověřuji…",
      hotelLabel: "Který z vašich hotelů v Lighthouse je toto ubytování?",
      hotelNoRates: "Tento hotel nemá v Lighthouse data o cenách.",
      compsetLabel: "Sada konkurentů",
      compsetOption: "{name} ({hotels})",
      noHotels: "Tento token nečte žádný hotel.",
      linkAndSwitch: "Propojit a přepnout",
      linking: "Propojuji…",
      linked: "Účet Lighthouse je propojen. Ceny konkurence se načítají z něj.",
      errors: {
        token_rejected: "Lighthouse tyto přihlašovací údaje nepřijal. Zkontrolujte je a zkuste to znovu.",
        token_unreadable: "Uložené přihlašovací údaje nelze na tomto serveru přečíst. Zadejte je znovu pod tlačítkem Změnit.",
        rate_limited: "Lighthouse teď další požadavky odmítá. Zkuste to za chvíli.",
        no_rates_subscription: "Tento hotel nemá v Lighthouse data o cenách, nelze z něj tedy číst ceny konkurence.",
        history_unavailable: "Váš tarif Lighthouse neobsahuje ceny z minulosti.",
        demand_unavailable: "Váš tarif Lighthouse neobsahuje poptávku na trhu.",
        unavailable: "Lighthouse se nepodařilo kontaktovat. Zkuste to později.",
        not_linked: "K tomuto ubytování není propojen žádný účet Lighthouse.",
      },
    },
    title: "Nastavení Property Manageru",
    description:
      "Konfigurace integrace Property Management a nastavení aplikace",
    pmsTitle: "Integrace Property Management",
    connectionName: "Název připojení",
    connectionNamePlaceholder: "např. Můj hotel, Plážový resort",
    setupRequired: "Vyžadováno nastavení",
    pmsDescription:
      "Vyberte svůj rezervační systém (PMS) a zadejte přihlašovací údaje, které Optivo použije pro čtení hotelových dat.",
    // Místo popisu pro každého kromě vlastníka účtu, pod kterým objekt je:
    // přihlašovací údaje server ukazuje jen vlastníkům.
    pmsOwnerOnly:
      "Přihlašovací údaje, které Optivo používá pro čtení hotelových dat, může zobrazit nebo změnit jen vlastník tohoto objektu. Pokud připojení potřebuje úpravu, obraťte se na vlastníka.",
    connected: "Připojeno",
    disconnected: "Odpojeno",
    testing: "Testování...",
    notTested: "Netestováno",
    previoApiBase: "Základní URL Previo API",
    login: "Přihlášení (E-mail)",
    password: "Heslo",
    hotelId: "ID hotelu",
    languageId: "ID jazyka",
    placeholderEmail: "vas-email@example.com",
    placeholderPassword: "Zadejte heslo k Previo",
    placeholderHotelId: "např. 731",
    lastHealthCheck: "Poslední kontrola stavu:",
    testAndSave: "Uložit a otestovat",
    notesTitle: "Poznámky ke konfiguraci",
    notesDescription: "Důležité informace o integraci API",
    notesSecurity:
      "Vaše přihlašovací údaje k Property Management jsou uloženy pro každého uživatele zvlášť a nikdy nejsou sdíleny mezi účty. Hesla jsou při zobrazení maskována.",
    notesBackend:
      "Všechny požadavky na Previo API jsou z bezpečnostních důvodů směrovány přes backend službu.",
    notesHealth:
      "Připojení k Previo je ověřováno při každém přihlášení. Pokud je připojení nezdravé, budete vyzváni k opětovnému zadání přihlašovacích údajů.",
    securityLabel: "Bezpečnost:",
    backendLabel: "Backend:",
    healthLabel: "Kontroly stavu:",
    toastConnectionSuccess: "Připojení úspěšné",
    toastConnectionSuccessDesc: "Úspěšně připojeno k Previo API",
    toastConnectionFailed: "Připojení selhalo",
    toastConnectionFailedDesc:
      "Zkontrolujte prosím své přihlašovací údaje a zkuste to znovu",
    toastPmsSaved: "Přihlašovací údaje Property Management uloženy",
    toastPmsSavedDesc:
      "Vaše přihlašovací údaje k Previo API byly uloženy. Testování připojení...",
    toastPmsSaveFailed: "Uložení přihlašovacích údajů Property Management selhalo",
    toastConnectedAndSaved: "Připojeno a uloženo",
    toastConnectedAndSavedDesc: "Přihlašovací údaje byly ověřeny a uloženy.",
    toastConnectionFailedNotSaved:
      "Připojení selhalo — údaje nebyly uloženy",
    currency: "Měna nemovitosti",
    currencyHelper:
      "Ceny generované pro tuto nemovitost se ukládají a zobrazují v této měně.",
    currencyNames: {
      CZK: "Česká koruna",
      EUR: "Euro",
      USD: "Americký dolar",
    },
    exchangeRatesTitle: "Směnné kurzy",
    ratesBase: "Základ",
    ratesFetchedAt: "Aktualizováno:",
    ratesNeverFetched: "nikdy",
    ratesRefresh: "Obnovit",
    ratesSource:
      "Kurzy se obnovují jednou denně z exchangerate-api.com. Slouží k převodu výnosů a cen mezi CZK, EUR a USD.",
    ratesEditLabel: "Ručně upravit kurzy",
    ratesManualBadge: "ručně",
    ratesPerUnitLabel: "1 {code} =",
    ratesSaveManual: "Uložit kurzy",
    ratesResetAuto: "Vrátit na automatické",
    ratesManualHelper:
      "Zadejte, kolik CZK odpovídá 1 jednotce cílové měny. Ručně zadané kurzy nejsou přepisovány denním obnovením.",
    toastRatesRefreshed: "Směnné kurzy aktualizovány",
    toastRatesRefreshFailed: "Aktualizace směnných kurzů selhala",
    toastRatesSaved: "Ruční směnné kurzy uloženy",
    toastRatesSaveFailed: "Uložení kurzů selhalo",
    toastRatesReset: "Ruční úpravy zrušeny",
    toastRatesResetFailed: "Zrušení ručních úprav selhalo",
    currencySwitchWarning:
      "Změna na {to} převede cenová nastavení uložená pro tuto nemovitost — základní, minimální a maximální ceny, kroky obsazenosti a last minute limity — i vývoj cen z {from} aktuálním kurzem a zahodí cenové návrhy již vygenerované v {from}.",
    currencySwitchTitle: "Změnit měnu nemovitosti na {to}?",
    currencySwitchDescription:
      "Optivo převede cenová nastavení uložená pro tuto nemovitost z {from} na {to} aktuálním kurzem a stejným kurzem přepočítá i vývoj cen, aby se také četl v {to}. Již vygenerované cenové návrhy se místo převodu zahodí — návrh nenese vlastní měnu, a tak jej lze číst jen v té, ve které byl naceněn. Nové návrhy se v {to} vygenerují při dalším přepočtu cen. Návrat zpět později zahozené návrhy neobnoví a čísla převede podruhé.",
    currencySwitchConfirm: "Změnit měnu a zahodit návrhy",
    currencySwitchCancel: "Ponechat {from}",
    currencyChangedTitle: "Měna byla změněna",
    currencyChangedDescription:
      "Měna nemovitosti byla změněna. Uložená cenová nastavení i vývoj cen byly převedeny aktuálním kurzem a cenové návrhy vygenerované v původní měně byly zahozeny. Opětovné spuštění onboarding průvodce odvodí čísla z vaší historie rezervací v nové měně místo jejich převodu.",
    currencyChangedConfirm: "Spustit průvodce znovu",
    currencyChangedCancel: "Teď ne",
    appearance: {
      title: "Vzhled",
      description: "Vyberte, jak má Optivo vypadat. Volba \"Automaticky\" se přizpůsobí vašemu PMS, aby dashboard působil jako jeho součást.",
      auto: "Automaticky (podle mého PMS)",
      autoHint: "Aktuálně zobrazeno: {skin}",
      optivo: "Optivo",
      previo: "Previo",
      betterHotel: "Better Hotel",
      accessibilityGroup: "Přístupnost",
      highContrast: "Vysoký kontrast",
      highContrastHint: "Pro projektory a slabší displeje. Větší text, maximální kontrast.",
      saved: "Vzhled uložen.",
      saveFailed: "Vzhled se nepodařilo uložit.",
    },
  },

  // ── Strategies ──────────────────────────────────────────
  strategies: {
    title: "Cenové strategie",
    description: "Definujte a spravujte své cenové strategie",
    activeStrategy: "Aktivní strategie",
    activeLabel: "Aktivní:",
    addNew: "Přidat novou strategii",
    editStrategy: "Upravit strategii",
    createDesc: "Vytvořit novou cenovou strategii",
    updateDesc: "Aktualizovat detaily strategie",
    nameLabel: "Název strategie",
    namePlaceholder: "např. Letní hlavní sezóna",
    descriptionLabel: "Popis",
    descriptionPlaceholder: "např. Uplatnit vyšší sazby během letních měsíců",
    pacingDefaultDescription: "Strategie podle tempa rezervací: sleduje, jak obsazenost, základní cena a minimální přijatelná cena drží tempo oproti vaší vlastní historii, a podle toho, zda rezervace předbíhají, drží tempo nebo zaostávají, upravuje ceny za noc.",
    noDescription: "Zatím bez popisu.",
    updateButton: "Aktualizovat strategii",
    saveAsNew: "Uložit jako novou strategii",
    saveAndActivate: "Uložit a aktivovat",
    saveAndActivateHint: "Uloží změny jako novou verzi, aktivuje ji a předchozí verzi zachová, abyste se k ní mohli vrátit.",
    versionLabel: "Verze {version}",
    versionHistoryNote: "Každé uložení zachová verzi, kterou nahradilo. Uchováváme posledních {limit} verzí strategie — aktivací se verze vrátí k řízení vašich cen.",
    switchBackAction: "Vrátit se",
    addButton: "Přidat strategii",
    activate: "Aktivovat",
    deactivate: "Deaktivovat",
    noStrategies: "Zatím nejsou definovány žádné strategie.",
    editButton: "Upravit",
    backToStrategies: "Zpět na strategie",
    cancelEdit: "Zrušit úpravy",
    discardChangesTitle: "Zahodit neuložené změny?",
    discardChangesDesc: "Máte neuložené změny ve formuláři strategie. Opravdu chcete formulář zavřít? Všechny změny budou ztraceny.",
    discardChangesAction: "Zahodit",
    toastVersionSaved: "Uloženo a aktivováno",
    toastVersionSavedDesc: "Vaše ceny nyní určuje verze {version}. Verze {previous} zůstává zachována — pokud ceny nesedí, můžete se k ní vrátit.",
    toastVersionActivated: "Přepnuto na verzi {version}",
    toastStrategyActivated: "Strategie aktivována",
    toastStrategyActivatedDesc: "Ceny se přepočítávají podle této strategie.",
    rulesLabel: "Pravidla:",
    // Polling / Sledování tempa
    pollingStatus: "Monitor tempa rezervací",
    pollingDescription: "Systém kontroluje nové rezervace {frequency} a přepočítává ceny pro dotčená data.",
    refreshComplete: "Ceny obnoveny.",
    refreshError: "Nepodařilo se obnovit ceny.",
    refreshSlow: "Stále pracuji — váš property management systém odpovídá déle než obvykle.",
    refreshTimedOut: "Generování cen vypršelo. Váš property management systém neodpověděl včas — zkontrolujte připojení a zkuste to znovu.",
    regeneratingPrices: "Přepočítávám ceny s aktualizovanou strategií…",
    lastPoll: "Poslední aktualizace",
    nextPoll: "Příští aktualizace",
    status: "Stav",
    monitoringActive: "Sledování je aktivní",
    monitoringPaused: "Sledování je pozastavené",
    lastChangedDates: "Naposledy změněná data",
    concurrentEditors: "Tuto strategii má otevřenou i někdo další: {editors}. Kdo uloží jako poslední, přepíše změny ostatních.",
    concurrentEditorUnknown: "další uživatel",
    // ── Seznam strategií (`strategies/StrategyList.tsx`) ────
    savedAt: "Uloženo {date}",
    versionsTitle: "Verze",
    noActiveStrategy: "Vaše ceny teď neurčuje žádná strategie. Aktivujte některou níže.",
    otherStrategiesTitle: "Další strategie",
    otherStrategiesDescription: "Uložené strategie, které teď vaše ceny neurčují. Když některou aktivujete, ceny se podle ní přepočítají.",
    earlierVersionsOne: "{count} starší verze",
    earlierVersionsFew: "{count} starší verze",
    earlierVersionsOther: "{count} starších verzí",
    activateVersion: "Aktivovat verzi {version}",
    editVersion: "Upravit verzi {version}",
    deleteVersion: "Smazat verzi {version}",
    activateNamed: "Aktivovat „{name}“",
    editNamed: "Upravit „{name}“",
    deleteNamed: "Smazat „{name}“",
    deleteVersionTitle: "Smazat verzi {version}?",
    deleteVersionDescription: "Verze {version} strategie „{name}“ bude smazána a už se k ní nebudete moci vrátit.",
    deleteStrategyTitle: "Smazat tuto strategii?",
    deleteStrategyDescription: "Strategie „{name}“ bude smazána.",
    deleteStrategyWithVersionsDescription: "Strategie „{name}“ bude smazána i se všemi {count} verzemi.",
  },

  // ── Stav synchronizace s PMS ────────────────────────────
  pmsSync: {
    title: "Synchronizace cen s PMS",
    description: "Přehled odesílání návrhů cen do PMS.",
    autoSyncEnabled: "Auto-sync je zapnutý",
    autoSyncDisabled: "Auto-sync je vypnutý",
    nextSync: "Příští aktualizace",
    runsEvery: "Spouští se {cadence}",
    lastUpdate: "Poslední aktualizace",
    noUpdatesYet: "Zatím nebyly odeslány žádné aktualizace do PMS.",
    entriesSent: "{count} položek odesláno",
    status: "Stav",
    completed: "Dokončeno",
    failed: "Selhalo",
    heldBack: "Pozdrženo: {count}",
    configureAutoSync: "Nastavit Auto-Sync",
    viewUploadLogs: "Zobrazit historii nahrávání",
    enableAutoSync: "Povolte auto-sync ve Správci připojení pro automatické odesílání cen do PMS.",
  },

  // ── Parametry strategie ─────────────────────────────────
  // ── Indikátor synchronizace v postranním panelu ─────────
  syncStatus: {
    state: {
      good: "Synchronizováno",
      manual: "Ruční odesílání",
      syncing: "Synchronizuji…",
      pending: "První sync brzy",
    },
    issueShort: {
      lastRunFailed: "Poslední běh selhal",
      lastRunInterrupted: "Běh byl přerušen",
      overdue: "Zpoždění",
      pmsUnreachable: "PMS nedostupné",
      lastPushFailed: "Ceny neodešly",
      lastPushHeldBack: "Část pozdržena",
      pollerPaused: "Synchronizace stojí",
    },
    issueLong: {
      lastRunFailed: "Poslední synchronizace narazila na problém. Optivo to zkusí znovu při příštím běhu.",
      lastRunInterrupted: "Poslední synchronizace nedoběhla — nejspíš ji uprostřed běhu přerušila aktualizace Optiva. Příští běh ji dokončí.",
      overdue: "Poslední kontrola je starší, než interval synchronizace připouští. Optivo to dožene při příštím běhu; pokud se to opakuje, ozvěte se podpoře.",
      pmsUnreachable: "{provider} se s uloženými přihlašovacími údaji nepodařilo kontaktovat. Zkontrolujte připojení.",
      lastPushFailed: "Poslední aktualizace cen do {provider} nedorazila. Podrobnosti najdete v historii nahrávání.",
      lastPushHeldBack: "Část posledního odeslání Optivo samo nezapsalo a zkusí ji znovu při dalším. {provider} nic neodmítl. Důvod najdete v historii nahrávání.",
      pollerPaused: "Synchronizace na pozadí je na serveru pozastavena — rezervace se nenačítají a ceny se neodesílají.",
    },
    // Totéž tam, kde poslední nahrání vracelo ceník: obnovení nebo jeho
    // opakovaný pokus. Co pozdržená noc znamená, platí pro každé nahrání; co
    // s ní bude dál, ne: obnovení nepotvrzuje žádné návrhy, takže jeho noc
    // později nic nezkusí znovu. Říká se jen tam, kde stav uvádí, že šlo
    // o obnovení (`push.lastRestoresPriceList`).
    issueLongRestore: {
      lastPushHeldBack: "Část posledního obnovení Optivo samo nezapsalo. {provider} nic neodmítl. Optivo obnovení samo znovu nezkouší, takže si nezapsané noci ponechají minimální délku pobytu, jakou mají teď. Chcete-li vrátit tu uloženou, spusťte obnovení později znovu nebo ji nastavte přímo v systému {provider}. Důvod najdete v historii nahrávání.",
    },
    unknownIssue: {
      short: "Vyžaduje pozornost",
      long: "Optivo hlásí něco, co tato verze stránky neumí popsat. Načtěte stránku znovu a uvidíte, o co jde.",
    },
    unknownState: {
      short: "Neznámý stav",
      reload: "Načtěte stránku znovu",
      long: "Optivo hlásí stav, který tato verze stránky neumí popsat. Načtěte stránku znovu a uvidíte ho.",
    },
    summary: {
      good: "Rezervace se načítají a ceny průběžně aktualizují. Není třeba nic dělat.",
      manual: "Rezervace se načítají, ale ceny odejdou jen když je odešlete.",
      syncing: "Načítám rezervace a aktualizuji ceny.",
      pending: "Zatím nebylo nic načteno.",
    },
    flow: {
      inbound: "Rezervace sem",
      outbound: "Ceny ven",
      next: "Příští běh",
    },
    manualTag: "ručně",
    notYet: "zatím ne",
    paused: "pozastaveno",
    prices: "{count} cen",
    heldBack: "{count} pozdrženo",
    actions: {
      checkConnection: "Zkontrolovat připojení",
      uploadLog: "Historie nahrávání",
      syncSettings: "Nastavení auto-syncu",
    },
    // Dnešní noc v postranním panelu, čtená z údaje `tonight` ve stavu
    // (`components/pms/tonightReading.ts`). `pricing` je třetí řádek
    // indikátoru: co Optivo udělalo s dnešními cenami, jedno slovo pro každou
    // hodnotu, kterou server umí poslat (`services/outlookApi.ts`), takže nová
    // hodnota potřebuje své slovo v obou souborech. Každé začíná slovem
    // „Dnes“, protože samotné „Drží ceny“ pod názvem hotelu zní, jako by je
    // držel hotel, a drží se toho, co jmenuje věta na přehledu
    // (`dashboard.outlook.pricingStatus`): jejího slovesa, u `locked` zámku,
    // u `nothingToSell` vyprodání. `market` se přidává jen ve dnech,
    // kdy trh není obyčejný; pro příznivý ani klidný trh tu slovo není.
    // `stale` stojí místo řádku nad cenami staršími než den. `title` je
    // nadpis oddílu v podrobnostech, jehož věty jsou věty z přehledu, až na
    // `manual`.
    //
    // Slovo o cenách a slovo o trhu sdílejí jeden řádek panelu, 215 px při
    // kořeni 18 px, takže se delší slovo nejdřív změří. Nejširší česká
    // dvojice, „Dnes pod zámkem · Slabší trh“, má 179 px ve výchozím vzhledu
    // a 198 px v Poppins vzhledu Better Hotel: každá česká dvojice se vejde
    // v každém vzhledu.
    tonight: {
      title: "Dnes",
      pricing: {
        discounting: "Dnes zlevňuje",
        holding: "Dnes drží ceny",
        raising: "Dnes zdražuje",
        adjusting: "Dnes ladí ceny",
        locked: "Dnes pod zámkem",
        nothingToSell: "Dnes vyprodáno",
      },
      market: {
        strong: "Silný trh",
        soft: "Slabší trh",
        weak: "Slabý trh",
      },
      stale: "Ceny starší než den",
      // Věta v okně tam, kde ceny neodcházejí samy. Vlastní věta ukazatele:
      // ta z přehledu mluví o „výhledu“, který v okně není.
      manual: "Ceny se neodesílají samy: dokud je neodešlete, je to plán Optiva.",
    },
  },

  strategyParams: {
    title: "Cenové parametry",
    expertMode: "Expertní režim",
    expertModeHint: "Zobrazí i pokročilé sekce. Vše skryté už má funkční výchozí hodnoty, takže se k tomu můžete vrátit později.",
    expertSectionsHidden: "Skrytých pokročilých sekcí: {count}",
    expertSectionReveal: "Jsem expert — zobrazit pokročilá nastavení",
    expertSectionHide: "Skrýt pokročilá nastavení",
    sectionNavLabel: "Sekce strategie",
    summaryTitle: "Souhrn",
    summaryDescription: "Co tato strategie nastavuje, po sekcích. Sekci otevřete jejím řádkem.",
    sectionPosition: "{position} z {count}",
    previousSection: "Předchozí",
    nextSection: "Další",
    previousSectionLabel: "Předchozí sekce",
    nextSectionLabel: "Další sekce",
    sectionPagerLabel: "Předchozí a další sekce",
    sectionSwitcherLabel: "Vybrat sekci",
    sectionNavCollapse: "Sbalit seznam sekcí",
    sectionNavExpand: "Rozbalit seznam sekcí",
    unsavedChangesMark: "Neuložené změny",
    unsavedSectionsCountOne: "{count} sekce změněna",
    unsavedSectionsCountFew: "{count} sekce změněny",
    unsavedSectionsCountOther: "{count} sekcí změněno",
    unsavedSectionsHeading: "Zatím neuloženo. Uložením vznikne nová verze.",
    savesImmediately: "Ukládá se hned při úpravě a platí pro všechny verze strategie.",
    savesSeparately: "Ukládá se vlastním tlačítkem a platí pro všechny verze strategie.",
    setupFixBasePrices: "Doplnit základní ceny",
    setupMissingBasePrices: "Než Optivo vypočítá cenové návrhy, je nutné doplnit základní ceny.",
    setupPartialBasePrices: "U některých typů pokojů stále chybí základní cena.",
    setupSourceUser: "Upraveno",
    setupSourceAnalysis: "Analýza",
    setupSourceQuestionnaire: "Dotazník",
    setupSourceTypology: "Výchozí profil",
    setupSourceSystem: "Systémová hodnota",
    setupSourceUnanswered: "Otázka nezodpovězena",
    sectionQuestionError: "Odpověď se nepodařilo použít. Zkuste to prosím znovu.",
    // ── Popisy sekcí ───────────────────────────────────────
    marketInfluenceSectionDesc: "Určete, zda ceny konkurence a předpověď počasí ovlivňují vaše ceny a jak silně.",
    strategySectionDesc: "Zvolte cíl, ke kterému Optivo ceny vede, a jak silně reagují na obsazenost, tempo rezervací a předstih.",
    bookingWindowSectionDesc: "Jak dlouho před příjezdem drží Optivo základní cenu každého typu pokoje a odkdy smí zlevňovat, aby noc naplnilo.",
    roomKindStructureSectionDesc: "Přejmenujte, seřaďte nebo skryjte typy pokojů z PMS a nastavte, kolik má každý pokojů a lůžek.",
    roomKindOverridesSectionDesc: "Nastavte základní, minimální a maximální cenu každého typu pokoje a jednotlivému typu dejte vlastní cíl nebo cenovou křivku.",
    roomStructureSectionDesc: "Nastavte, kolik ve stejném pokoji zaplatí jeden, dva nebo více hostů a kolik k ceně noci přidá přistýlka.",
    priceGranularitySectionDesc: "Zvolte krok zaokrouhlení, na který padne každá navržená cena, aby vycházela v celých, uvěřitelných číslech.",
    distributionSectionDesc: "Nastavte, kolik stojí páteční, sobotní a nedělní noc oproti běžné noci uprostřed týdne.",
    channelMappingSectionDesc: "Posílejte cenu upravenou pro konkrétní kanál do jeho vlastního cenového plánu v Previu, podle úrovně poptávky.",
    nonRefundableSectionDesc: "Nabídněte zvýhodněnou nevratnou cenu ve vlastním cenovém plánu, nejvyšší slevu na termínech, které se prodávají nejpomaleji.",
    gapFillingSectionDesc: "Zlevněte osiřelé noci uvízlé mezi dvěma rezervacemi a nastavte, jak velká mezera se ještě počítá.",
    lastMinuteSectionDesc: "Určete, jak se ceny chovají v posledních dnech před příjezdem a kam až mohou klesnout, aby se pokoj naplnil.",
    seasonalitySectionDesc: "Označte měsíce jako hlavní, nízkou nebo běžnou sezónu a nastavte, kolik přidají nebo uberou cenám všech nocí v daném měsíci.",
    rateRestrictionsSectionDesc: "Nastavte minimální délku pobytu, pravidlo pro víkendy a svátky a kdy se před příjezdem uvolní.",
    holidaysSectionDesc: "Načtěte státní svátky pro vybrané země a nastavte přirážku nebo slevu pro každé datum, včetně vlastních.",
    bridgeDaysSectionDesc: "Naceňte běžné noci kolem svátečního volna: tu sevřenou uvnitř něj i ty těsně před ním a po něm.",
    marketInfluenceTitle: "Vliv trhu",
    marketInfluenceDesc: "Nechte místní konkurenci ovlivnit vaši noční cenu. Noci, pro které trh nebyl zachycen, se cení bez ní a simulátor to uvede.",
    marketInfluenceEnabled: "Sledovat místní trh",
    marketInfluenceStrength: "Jak těsně sledovat",
    marketInfluenceStrengthHint: "Toto nastavení řídí jen ceny konkurence — počasí má své vlastní výše. Při 0 % se konkurence ignoruje. Při 100 % posune noc, kterou trh cení nejvýš nebo nejníž, vaši cenu asi o 6 %, a asi o 9 %, když je konkurence navíc vyprodaná. Ať počasí, školní prázdniny a konkurence řeknou dohromady cokoli, cena se nikdy neposune o víc než 15 % — tato mez omezuje jak dopad špatných dat o konkurenci, tak riziko cenové signalizace na trhu, kde sami soutěžíte.",
    marketFollowEnabled: "Odvozovat základní cenu od trhu",
    marketFollowPosition: "Pozice vůči trhu",
    marketFollowHint: "V nocích, pro které má Optivo tržní údaj — medián alespoň 5 naceněných ubytování zaškrtnutých v sadě konkurentů níže — se tento medián stane základní cenou vašeho nejlevnějšího typu pokoje, posunutou o tuto hodnotu: +5 % je pět procent nad trhem, −5 % pod ním. Medián odpovídá ceně nejlevnějšího pokoje konkurence, takže ostatní typy pokojů si zachovají obvyklý odstup nad ním.",
    marketFollowStandDownHint: "Víkendové, sváteční, sezónní úpravy a úpravy podle rezervačního okna se v těchto nocích neuplatní, protože je cena trhu už obsahuje; vaše vlastní obsazenost, tempo rezervací a last-minute pravidla platí dál, v mezích vaší minimální a maximální ceny. Noci bez tržního údaje se cení jen podle vaší strategie.",
    marketFollowExample: "Při {position} % noc, kterou trh cení na {median} {currency}, začíná pro váš nejlevnější typ pokoje na {price} {currency}.",
    weatherInfluenceEnabled: "Nechat počasí ovlivnit ceny",
    weatherInfluenceStrength: "Jak moc počasí váží",
    weatherInfluenceStrengthHint: "Počasí funguje bez ohledu na to, zda sledujete konkurenci, a řídí ho jen toto nastavení. Při 0 % se předpověď v cenotvorbě ignoruje, ale na blížící se extrémní počasí vás stále upozorníme. Při 100 % dostane noc zasažená bouří slevu až kolem 7 %.",
    weatherExtremesHint: "Vlny veder, silné mrazy, přívalové deště, sněhové bouře, vichřice, bouřky a mrznoucí déšť se čtou jako snížená poptávka a právě ony noc znatelně zlevní. Běžné počasí cenu jen mírně nakloní, o procento až dvě: den teplejší, než je pro dané datum obvyklé, nahoru, chladnější nebo deštivý dolů. Teplota se čte proti tomu, co je v dané roční době obvyklé, takže běžný den nechá cenu být v lednu stejně jako v červenci a odchylka 10 °C na jednu či druhou stranu se počítá naplno. Předpověď hýbe cenami na nejbližších 10 dní, vzdálenější se jen zobrazuje. Chcete-li víkend zlevnit víc, přidejte ho v sekci Svátky a speciální dny se zápornou úpravou.",
    compsetFilterTitle: "Sada konkurentů",
    compsetFilterDesc: "Níže je vše, co vyhledávání najde v okolí vašeho ubytování. Zaškrtnutá ubytování tvoří vaši sadu konkurentů: vliv trhu čte medián jejich cen a odvozování ceny od trhu z něj nastavuje vaši základní cenu. Pravidla fungují i při změnách trhu; zaškrtnutím nebo odškrtnutím kteréhokoli ubytování je přepíšete.",
    compsetFilterUnratedNote: "Ubytování zachycená bez typu, hodnocení, počtu recenzí nebo ceny zůstávají — chybějící údaj není důvod k vyloučení. Pokud je nechcete, odškrtněte je jednotlivě.",
    compsetFilterLoadError: "Sadu konkurentů se nepodařilo načíst.",
    compsetFilterSaveError: "Sadu konkurentů se nepodařilo uložit. Poslední změna byla vrácena.",
    keyMetrics: "Klíčové metriky",
    occupancyRate: "Obsazenost",
    bookingWindowTitle: "Okno rezervací",
    bookingWindowTypicalDaysLabel: "Typické okno rezervací (dny)",
    bookingWindowTypicalDaysHint: "Až do tolika dní před příjezdem drží Optivo noc aspoň na její základní ceně, upravené o den v týdnu, sezónnost a svátky, i když se prodává pomalu. Uvnitř okna ji smí zlevnit, aby se naplnila. Delší okno znamená dřívější slevy, kratší okno drží cenu déle. Při cíli Maximalizovat obsazenost cenu nedrží a okno jen časuje pohyb ceny.",
    bookingWindowAnalysisHint: "Analýza: {days} dní",
    bookingWindowReset: "Obnovit hodnotu z analýzy",
    bookingWindowNoRoomKinds: "Zatím nejsou k dispozici žádné typy pokojů. Znovu spusťte onboarding pro načtení typů pokojů.",
    bookingWindowRoomKindColumn: "Typ pokoje",
    bookingWindowTypicalColumn: "Typické",
    bookingWindowDaysSuffix: "dní",
    bookingWindowSeasonReadout: "Rezervační okno podle sezóny",
    bookingWindowSeasonHigh: "Hlavní sezóna",
    bookingWindowSeasonLow: "Nízká sezóna",
    bookingWindowSeasonHint: "Každý termín patří do hlavní, nebo nízké sezóny. Sezóny jsou týdny, ne měsíce: hlavní sezóna jsou týdny, které historie rezervací označila za vytížené, a s měsíci v sekci Sezónnost nesouvisí. Každá sezóna se měří stejně, jen přes své vlastní noci, takže naměřené typické okno leží mezi oběma sezónami. Typ pokoje s příliš málo vlastními rezervacemi ukazuje okna celého ubytování; kde má příliš málo rezervací v jedné sezóně, stojí pomlčka a platí jeho typické okno.",
    leadTimeCurveTitle: "Pohyb ceny během rezervačního okna",
    leadTimeCurveHint: "Kde se cena nachází ve třech bodech rezervačního okna: dokud je pobyt ještě daleko, kolem vrcholu poptávky a s blížícím se příjezdem. Kladné procento cenu zvyšuje, záporné snižuje — každý bod si nastavíte oběma směry. Procenta platí globálně; časování jednotlivých fází se přizpůsobuje rezervačnímu oknu každého typu pokoje (Typické / Hlavní sezóna / Nízká sezóna).",
    leadTimeCurveStartPct: "Úprava před rezervačním oknem",
    leadTimeCurveStartPctHint: "Uplatní se, dokud je pobyt dál než typické rezervační okno — výchozí bod cenové křivky.",
    leadTimeCurvePeak: "Úprava na vrcholu poptávky",
    leadTimeCurvePeakHint: "Uplatní se kolem vrcholu rezervačního okna daného typu pokoje, kde křivka dosahuje nejvzdálenějšího bodu.",
    leadTimeCurveArrival: "Úprava před příjezdem",
    leadTimeCurveArrivalHint: "Uplatní se s blížícím se příjezdem v rámci rezervačního okna daného typu pokoje — koncový bod křivky.",
    leadTimeCurveReset: "Obnovit výchozí",
    leadTimeCurvePreviewTitle: "Náhled křivky",
    leadTimeCurvePreviewX: "Dní před příjezdem",
    leadTimeCurvePreviewY: "Úprava",
    leadTimeCurvePreviewBuildup: "Nabírání rezervací",
    leadTimeCurvePreviewArrival: "Příjezd",
    seasonality: "Sezónnost",
    seasonClassificationHint: "Označte každý měsíc jako Hlavní, Nízkou nebo Běžnou sezónu. Hlavní a nízké měsíce používají vlastní cenovou úpravu; běžné měsíce jsou neutrální.",
    seasonFromAnalysisTag: "Z analýzy",
    highSeason: "Hlavní",
    lowSeason: "Nízká",
    commonSeason: "Běžná",
    highSeasonMultiplier: "Úprava ceny v hlavní sezóně",
    lowSeasonMultiplier: "Úprava ceny v nízké sezóně",
    seasonMonthAdjustment: "Úprava za {month}",
    seasonResetToAnalysis: "Obnovit z analýzy",
    // Nakreslený rok. Stejné tři řady jako u grafu dnů v týdnu — kdo přečetl
    // jeden, umí přečíst i tenhle.
    seasonChartTitle: "Váš rok",
    seasonChartConfigured: "Vaše cena",
    seasonChartHistory: "Za kolik jste prodali",
    seasonChartPattern: "Váš sezónní průběh",
    seasonChartAxisMoney: "{currency} za noc",
    seasonChartAnchorNamed: "Počítáno ze základní ceny pokoje {roomKind}: {price} {currency}",
    seasonChartAnchor: "Počítáno z vaší základní ceny: {price} {currency}",
    seasonChartAnchorPick: "Počítáno ze základní ceny pokoje",
    seasonChartAnchorOption: "{roomKind} · {price} {currency}",
    distribution: "Denní úpravy cen",
    weekendPremium: "Denní úpravy cen",
    weekendDiscount: "Denní úpravy cen",
    mondayAdjustment: "Pondělní úprava",
    tuesdayAdjustment: "Úterní úprava",
    wednesdayAdjustment: "Středeční úprava",
    thursdayAdjustment: "Čtvrteční úprava",
    fridayAdjustment: "Páteční úprava",
    saturdayAdjustment: "Sobotní úprava",
    sundayAdjustment: "Nedělní úprava",
    dayAdjustmentsHint: "Kladná procenta použijte pro přirážky, záporná pro slevy. Ve speciálních dnech se úprava podle dne v týdnu vynechá — uplatní se pouze příplatek za speciální den.",
    dayAdjustmentsAnalysisValue: "Analýza: {value}",
    dayAdjustmentsAnalysisNights: "{nights} nocí",
    dayAdjustmentsAnalysisEvidence: "Odvozeno z {nights} pokojonocí za {months} měsíců; každý typ pokoje se porovnává sám se sebou při stejném počtu hostů.",
    dayAdjustmentsAnalysisHolidays: "Pobyty přes státní svátek nebo speciální den se nepočítají ({nights} pokojonocí): ty noci ceníte ve Svátcích a speciálních dnech.",
    dayAdjustmentsResetToAnalysis: "Vrátit na hodnoty z analýzy",
    dayAdjustmentsSourceHistoric: "Vaše vlastní historie",
    dayAdjustmentsSourceBlended: "Vaše historie zkombinovaná s výchozími hodnotami",
    dayAdjustmentsSourceDefaults: "Výchozí hodnoty Optivo",
    dayAdjustmentsStandardHint: "Nastavte, kolik má stát každá z těchto tří nocí oproti běžné noci uprostřed týdne. Ve speciálních dnech cenu nahradí příplatek za speciální den a last-minute sleva ji zruší.",
    dayAdjustmentsAnchorNamed: "Noc od pondělí do čtvrtka v pokoji {roomKind}: {price} {currency}",
    dayAdjustmentsAnchor: "Noc od pondělí do čtvrtka: {price} {currency}",
    dayAdjustmentsAnchorNote: "Od pondělí do čtvrtka zůstává tato cena. Tři noci níže se nastavují oproti ní.",
    dayAdjustmentsAnchorBaseNamed: "Základní cena pokoje {roomKind}: {price} {currency}",
    dayAdjustmentsAnchorBase: "Vaše základní cena: {price} {currency}",
    dayAdjustmentsAnchorNoteAllDays: "Každá noc níže se nastavuje oproti této ceně.",
    dayAdjustmentsAnchorExampleNamed: "Příklad: noc od pondělí do čtvrtka v typu pokoje {roomKind} stojí {price} {currency}",
    dayAdjustmentsAnchorBaseExampleNamed: "Příklad: základní cena typu pokoje {roomKind} je {price} {currency}",
    dayAdjustmentsAnchorExampleNote: "Úpravy platí pro všechny typy pokojů, každý oproti své vlastní základní ceně, která od pondělí do čtvrtka zůstává. Tři ceny níže jsou pro tento typ, ostatní se posunou o stejné procento.",
    dayAdjustmentsAnchorExampleNoteAllDays: "Úpravy platí pro všechny typy pokojů, každý oproti své vlastní základní ceně. Částky níže jsou pro tento typ, ostatní se posunou o stejné procento.",
    dayAdjustmentsMidweekHint: "Pondělí až čtvrtek začínají na 0 — vaše základní cena už je cenou všedního dne. Upravte je jen tehdy, když se ten den skutečně prodává jinak.",
    dayAdjustmentsChartTitle: "Váš týden",
    dayAdjustmentsChartConfigured: "Vaše cena",
    dayAdjustmentsChartHistory: "Za kolik jste prodali",
    dayAdjustmentsChartSuggested: "Váš týdenní vzorec",
    dayAdjustmentsChartAxisMoney: "{currency} za noc",
    dayAdjustmentsNightPrice: "Cena za noc: {day}",
    dayAdjustmentsRaise: "Zvýšit cenu: {day}",
    dayAdjustmentsLower: "Snížit cenu: {day}",
    dayAdjustmentsDelta: "{money} {currency} · {percent}",
    dayAdjustmentsPercentValue: "{value} %",
    dayAdjustmentsSameAsWeekday: "Stejně jako uprostřed týdne",
    dayAdjustmentsHistoryMoney: "Prodáno za {sold} {currency} oproti {baseline} v běžné noci ve stejných pokojích",
    dayAdjustmentsAtLimit: "Dál už úprava podle dne v týdnu nesahá. Pro větší výkyvy použijte sezónnost nebo speciální dny.",
    dayAdjustmentsSimulatorNote: "Sezónnost, poptávka a vaše cenové limity platí dál — konečnou cenu ukáže živý příklad ceny.",
    dayAdjustmentsInheritedValue: "Celé ubytování: {value}",
    channelMappingTitle: "Kanál → cenový plán",
    channelMappingBasePlanUnaffected: "Ceny základního plánu zůstávají beze změny.",
    channelMappingEnable: "Povolit mapování",
    ratePlanLabel: "Cenový plán",
    ratePlanPlaceholder: "Vyberte cenový plán…",
    channelAdjustmentByLevelLabel: "Úprava podle úrovně poptávky",
    channelAdjustmentByLevelHint: "Úroveň poptávky je tu celková poptávka po noci ve srovnání s běžnou nocí daného typu pokoje: víkend, svátek, sezóna, historie týdne, trh, tempo rezervací a obsazenost typu pokoje, jak je Optivo zvážilo při posledním výpočtu ceny noci. Barva v kalendáři cen je něco jiného — ukazuje jen tempo rezervací. Bez příplatku v běžné noci a s vyšším příplatkem v termínech, o které je největší zájem, se přímá rezervace vyplatí tam, kde na tom záleží. Úroveň bez vlastní hodnoty se řídí nejbližší nižší úrovní, která hodnotu má, takže kanál nikdy neprodává za základní cenu.",
    channelLevelDisabledHint: "Nastavit této úrovni poptávky vlastní úpravu",
    channelMappingNoChannels: "Žádné kanály nejsou nakonfigurovány. Spusťte onboarding znovu pro detekci kanálů.",
    channelMappingNoRatePlans: "Z PMS nejsou dostupné žádné cenové plány.",
    channelMappingIncomplete: "Vyberte cenový plán — tento kanál je zapnutý, ale dokud plán nemá, neposílá se do něj nic.",
    nonRefundableBasePlanTargetWarning: "Toto je základní cenový plán. Ceny z něj odvozené se odesílají až po něm, takže jejich zápis zpět by přepsal to, co Optivo právě odeslalo — vyberte jiný plán.",
    nonRefundableTitle: "Nevratné ceny",
    nonRefundableRangeHint: "Sleva běží jako rozpětí podle celkové poptávky po noci, čtené stejně jako u kanálových plánů — nejvyšší v nejklidnějších nocích a klesá v termínech, o které je největší zájem.",
    nonRefundableEnable: "Zapnout nevratné ceny",
    nonRefundableAdjustmentLabel: "Sleva nebo přirážka",
    nonRefundableMaxOccupancy: "Maximální obsazenost typu pokoje",
    nonRefundableMaxOccupancyHint: "Obsazenost je tu obsazenost daného typu pokoje, nikdy celého ubytování: podíl jeho pokojů prodaných na danou noc, opce a blokace se nepočítají. Noc s obsazeností nad touto hranicí nevratnou cenu vůbec nedostane.",
    nonRefundableMinDaysBeforeArrival: "Minimální počet dní před příjezdem",
    nonRefundableMinDaysHint: "Stejně tak ji nedostane noc bližší příjezdu, než je zadaná lhůta.",
    nonRefundableNoRatePlans: "Z PMS nejsou dostupné žádné cenové plány.",
    nonRefundableNoRoomKinds: "Zatím nejsou dostupné žádné typy pokojů. Spusťte znovu onboarding pro načtení typů pokojů.",
    nonRefundableRoomKindLabel: "Typ pokoje",
    nonRefundableCopyToAll: "Zkopírovat do všech",
    nonRefundableSharedRatePlanLabel: "Cenový plán (sdílený pro všechny typy pokojů)",
    nonRefundableSharedRatePlanHint: "Posila nevratnou cenu do tohoto jednoho cenového plánu Previo pro každý zapnutý typ pokoje.",
    nonRefundablePerRoomKindHeading: "Podle typu pokoje",
    nonRefundableIncomplete: "Vyberte cenový plán — nevratné ceny jsou zapnuté, ale dokud plán nemají, neposílá se nic.",
    nonRefundableAdjustmentUnitLabel: "Sleva zadaná jako",
    nonRefundableAdjustmentUnitPercent: "Procento",
    nonRefundableAdjustmentUnitAmount: "Částka",
    nonRefundableAdjustmentUnitHint: "Procento se řídí cenou, takže apartmá slevuje víc než jednolůžkový pokoj. Pevná částka je stejná u každého typu pokoje.",
    nonRefundableExcludedNightTypesLabel: "Nikdy nenabízet na",
    nonRefundableExcludedNightTypesHint: "Noci, které se prodají samy. Spojovací den je jediná běžná noc sevřená mezi dvěma prémiovými.",
    nonRefundableNightTypeWeekend: "Páteční a sobotní noci",
    nonRefundableNightTypeHoliday: "Svátky a významné dny",
    nonRefundableNightTypeBridgeDay: "Spojovací dny",
    nonRefundableAtMinimalDemand: "Při minimální poptávce",
    nonRefundableAtTopDemand: "Při TOP poptávce",
    nonRefundableDemandLadder: "Co dostane která úroveň poptávky",
    nonRefundableNoAmount: "Není zadaná částka — Optivo by do nevratného plánu poslalo základní cenu tohoto typu pokoje beze změny.",
    nonRefundableRoomKindColumn: "Typ pokoje",
    nonRefundableMaxOccupancyColumn: "Max. obsazenost typu",
    nonRefundableMinDaysColumn: "Min. dní",
    ratePlanNone: "Žádný cenový plán",
    ratePlanClaimedBy: "používá {holder}",
    ratePlanFreeHint: "Plán, který už používá jiné nastavení, tu nelze vybrat. Uvolníte ho tím, že u něj vyberete „Žádný cenový plán“.",
    ratePlanConflictWarning: "Do stejného cenového plánu zapisuje i {holders} — odešle se jen jeden z nich. Dejte jednomu z nich vlastní plán.",
    levelMinimal: "Minimální",
    levelLow: "Nízká",
    levelStandard: "Standardní",
    levelElevated: "Zvýšená",
    levelHigh: "Vysoká",
    levelTop: "TOP",
    strategyPosture: "Nastavení strategie",
    optimizationGoal: "Cíl optimalizace",
    revenueGoal: "Maximalizovat příjmy",
    revenueGoalHint: "Vyšší ceny při silné poptávce, mírné slevy v klidných obdobích.",
    hybridGoal: "Doporučeno Optivem",
    hybridGoalHint: "Před mediánem rezervací drží ceny jako Maximalizovat příjmy, blíž k příjezdu přepne na Maximalizovat obsazenost.",
    occupancyGoal: "Maximalizovat obsazenost",
    occupancyGoalHint: "Agresivnější slevy pro naplnění pokojů, menší přirážky při vysoké poptávce.",
    roomKindStrategyOverridesTitle: "Ceny podle typu pokoje",
    roomKindStrategyOverridesHint: "Jsou zde dva druhy nastavení a při ponechání prázdného pole se chovají jinak — pravidlo každé skupiny je uvedeno níže.",
    roomKindStrategyOverridesEssentialHint: "Nastavte základní, minimální a maximální cenu pro každý typ pokoje. Optivo z nich počítá všechny ceny daného typu pokoje.",
    overrideUseDefault: "Použít výchozí",
    overrideGoal: "Cíl optimalizace",
    overrideBasePrice: "Základní cena",
    overrideMinPrice: "Minimální cena",
    overrideMaxPrice: "Maximální cena",
    overrideFromAnalysisHint: "Z analýzy",
    overrideFromAnswersHint: "Z vašich odpovědí",
    overrideAnswersMismatchWarning: "Základní cena z analýzy se liší o více než {pct} od ceny v běžný den, kterou očekáváte ({answer}). Ověřte, která je správně.",
    overrideAnswersMismatchSummary: "Cena v běžný den, kterou očekáváte ({answer}), se liší o více než {pct} od základní ceny z analýzy u: {roomKinds}. Než začnou ceny platit, ověřte, která je správně.",
    overrideEffectiveFromSet: "Optivo použije {value} — cenu zadanou zde.",
    overrideEffectiveFromAnalysis: "Optivo použije {value} — z historie rezervací tohoto typu pokoje.",
    overrideEffectiveFromBand: "Optivo použije {value} — {pct} základní ceny.",
    overrideEffectiveFromAnswers: "Optivo použije {value} — cenu, kterou jste uvedli ve svých odpovědích.",
    overrideReferenceLabel: "Pro informaci",
    overrideBandReference: "{pct} základní ceny",
    overrideConsequenceFollowsAnalysis: "Vlastní cenu jste zde nezadali — po zadání se použije ta vaše.",
    overrideConsequenceFollowsBand: "Mění se s každou změnou základní ceny. Zadáním ceny ji zde ukotvíte.",
    overrideConsequenceFixed: "Po vymazání pole by Optivo použilo {value}.",
    overrideConsequenceFixedNothing: "Po vymazání pole by Optivo tento typ pokoje nedokázalo nacenit vůbec.",
    overridePriceBandSummary: "U typu {roomKind} počítá Optivo ceny od {base}: noční cena zůstane mezi {min} a {max}.",
    overridePriceBandCaveat: "Příplatky za další hosty mohou cenu zvednout nad toto rozmezí; minimální cena na poslední chvíli — pokud je nastavena — může klesnout pod {min}. Pod minimum platné pro danou noc neklesne cena pro žádný počet hostů.",
    overridePriceVatNote: "Uvedená cena je vč. DPH za dvoulůžkový pokoj (double use).",
    overrideMissingBasePriceWarning: "Pro tento typ pokoje není nastavena základní cena, takže pro něj Optivo nevypočítá žádné ceny. Zadejte ji zde.",
    overrideMissingBasePriceSummary: "Základní cena není nastavena pro: {roomKinds}. Dokud ji nezadáte, Optivo pro tyto typy pokojů nevypočítá žádné ceny.",
    roomKindOwnScopeTitle: "Ceny — jen tento typ pokoje",
    roomKindOwnScopeHint: "Základní cena je povinná — Optivo z ní počítá každou cenu pro tento typ pokoje. Za těmito poli nestojí žádná cena platná pro celé ubytování: pokud pole necháte prázdné, Optivo použije cenu odvozenou z historie tohoto typu pokoje, a pokud žádná není, nedokáže tento typ pokoje nacenit vůbec.",
    roomKindOwnScopeBadge: "Jen tento typ pokoje",
    roomKindBasePriceRequiredBadge: "Povinná",
    roomKindPropertyScopeTitle: "Strategie — přepisuje nastavení celého ubytování",
    roomKindPropertyScopeHint: "Prázdné pole znamená, že platí nastavení pro celé ubytování. Vyplňte je jen tehdy, když se má tento typ pokoje chovat jinak než zbytek ubytování.",
    roomKindInheritedBadge: "Celé ubytování",
    roomKindOverriddenBadge: "Přepsáno",
    roomKindOverrideReset: "Obnovit hodnotu celého ubytování",
    roomKindLeadTimeCurveTitle: "Pohyb ceny",
    roomKindLeadTimeCurveHint: "Dokud něco nezměníte, ukazují hodnoty platné pro celé ubytování. Jakákoli úprava dá tomuto typu pokoje vlastní křivku; reset vrátí nastavení celého ubytování.",
    roomKindLeadTimeCurveStartPct: "Úprava před rezervačním oknem",
    roomKindLeadTimeCurveStartPctHint: "Uplatní se, dokud je pobyt dál než typické rezervační okno tohoto typu pokoje.",
    roomKindDayAdjustmentsTitle: "Den v týdnu",
    roomKindDayAdjustmentsHint: "Každá noc se řídí týdnem celého ubytování, dokud ji nezměníte. Změněná noc patří tomuto typu pokoje, ostatní se řídí dál — i když se týden celého ubytování později změní. Reset vrátí všechny.",
    overrideTierLabel: "Kategorie",
    tierFromBasePrice: "Ze základní ceny",
    tierOverrideCount: "Přepsání pro {count} typ(ů) pokojů",
    reactivity: "Cenová reaktivita",
    reactivityHint: "Jak velkou změnu ceny smí Optivo provést, když si o ni řekne obsazenost a tempo rezervací. Všechny úrovně reagují na stejné signály — liší se velikostí kroků nahoru i dolů.",
    gentle: "Mírná",
    gentleHint: "Malé kroky. Ceny se drží blízko základní ceny — mírné přirážky v silných termínech, mírné slevy ve slabých. Hosté vidí stabilní a předvídatelné ceny. Výchozí volba, která sedí většině ubytování.",
    moderate: "Střední",
    moderateHint: "Znatelné pohyby. Ceny viditelně sledují poptávku oběma směry, ale zůstávají ukotvené k základní ceně. Pro ubytování, kde poptávka zřetelně kolísá a má se to v ceně projevit.",
    aggressive: "Agresivní",
    aggressiveHint: "Velké pohyby. Výrazné přirážky, když se termíny plní, a hluboké slevy na záchranu pomalu se prodávajících termínů. Z poptávky vytěží nejvíc, hosté ale uvidí výrazně proměnlivé ceny.",
    customLabel: "vlastní",
    occupancyScaleMax: "Max přirážka dle obsazenosti",
    occupancyScaleMaxHint: "O kolik nejvýš může obsazenost zvednout základní cenu noci. Noc dostane tím větší část přirážky, čím víc její obsazenost převyšuje tu, na kterou u ní Optivo míří; úpravy podle dne v týdnu, sezóny a tempa se přičítají navíc.",
    paceLower: "Sleva při nízkém tempu",
    paceUpper: "Příplatek při vysokém tempu",
    paceSensitivityHint: "Jak silně odchylky tempa ovlivňují cenu.",
    paceFloorHint: "Kolik nejvýš smí tempo rezervací z ceny slevit, když se termín prodává pomaleji než obvykle.",
    paceCeilingHint: "Kolik nejvýš smí tempo rezervací k ceně přidat, když se termín prodává rychleji než obvykle. Omezuje také, o kolik nad základní cenou začíná ještě před první rezervací noc, jejíž typ se obvykle prodává brzy, třeba sobota vyprodaná měsíce dopředu.",
    reactivityOccLabel: "obs.",
    reactivityPaceLabel: "tempo",
    pickupInfluenceTitle: "Přírůstek rezervací",
    pickupInfluenceHint: "Reagujte na to, jak rychle se termín plní právě teď, nejen na to, jak je plný.",
    pickupInfluenceEnabled: "Nechat nedávný pickup ovlivnit cenu",
    pickupInfluenceStrength: "Jak těsně sledovat pickup",
    pickupInfluenceStrengthHint: "Vyšší hodnota sleduje nedávnou rychlost rezervací těsněji. Dopad je oběma směry omezen na 5 % vypočtené ceny.",
    pickupWindowDays: "Měřit pickup za",
    pickupWindowDaysHint: "Jak daleko zpět se dívat při otázce, jak rychle se termín plní. Kratší reaguje dřív, delší je stabilnější.",
    pickupInfluenceGuardrail: "Pickup a tempo sdílejí jeden limit: dohromady nikdy neposunou cenu dál, než dovolují sleva při nízkém a příplatek při vysokém tempu výše. Pickup zůstává neaktivní, dokud termín nemá naměřenou historii za celé okno.",
    minRoomsForRoomKindSignal: "Min. pokojů pro vlastní signál",
    minRoomsForRoomKindSignalHint: "Kategorie s méně pokoji, než je tento práh, vždy použijí signál celého ubytování — pro obsazenost, tempo rezervací, pickup i týdenní signál poptávky — protože pár pokojů je pro výpočet ceny příliš rozkolísaný.",
    propertyPaceWeight: "Váha celkového tempa",
    propertyPaceWeightHint: "Podíl úpravy podle tempa rezervací řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že tempo ubytování přispívá 60 % a tempo kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí tempo celého ubytování.",
    propertyPaceWeightShort: "ubytování",
    roomKindPaceWeightShort: "kategorie",
    propertyDemandWeight: "Váha celkové poptávky",
    propertyDemandWeightHint: "Podíl týdenního signálu poptávky řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že týden celého ubytování přispívá 60 % a vlastní týden kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí signál poptávky celého ubytování. Ve vysvětlení ceny jde o řádek „Týdenní poptávka z historie“.",
    propertyDemandWeightShort: "ubytování",
    roomKindDemandWeightShort: "kategorie",
    propertyPickupWeight: "Váha celkového pickupu",
    propertyPickupWeightHint: "Podíl měření pickupu řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že pickup celého ubytování přispívá 60 % a vlastní pickup kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí pickup celého ubytování.",
    propertyPickupWeightShort: "ubytování",
    roomKindPickupWeightShort: "kategorie",
    postureBiasTitle: "Přechod z ochrany ceny do plnění",
    postureBiasDescription: "Doporučeno Optivem chrání vaši cenu, dokud je termín dál než vaše obvyklé rezervační okno, a pak přejde ke slevám, aby ho naplnilo. Tyto tři hodnoty určují, kde přechod nastane, jak ostře proběhne a zda ho posune tempo prodeje daného termínu.",
    postureBias: "Bod přepnutí",
    postureBiasHint: "50 přepíná ve vašem obvyklém rezervačním okně; nižší hodnota drží cenu po větší část okna, vyšší začíná plnit dřív. Při 0 se chová přesně jako Maximalizovat výnos, při 100 přesně jako Maximalizovat obsazenost.",
    postureBiasBalanced: "přepíná ve vašem obvyklém rezervačním okně",
    postureBiasRateLeaning: "kloní se k ochraně ceny",
    postureBiasFillLeaning: "kloní se k naplnění",
    postureBiasLikeRevenue: "stejné jako Maximalizovat výnos",
    postureBiasLikeOccupancy: "stejné jako Maximalizovat obsazenost",
    hybridChangeoverDays: "Přechod rozložit na",
    hybridChangeoverDaysHint: "0 přepne v jediný den. Širší okno cenu z ochrany do plnění plynule převede místo skoku, takže host, který se podívá dvakrát, neuvidí schod.",
    hybridChangeoverDaysUnit: "dní na každou stranu",
    hybridPacePull: "Nechat tempo termínu bod posunout",
    hybridPacePullHint: "0 ponechá přepnutí na kalendáři. Nad nulou si termín, který se dobře prodává, drží cenu i za bodem přepnutí a termín, který se prodává špatně, začne plnit dřív.",
    simOccupancyDemand: "Poptávka dle obsazenosti",
    simPaceAdjust: "Úprava dle tempa (vpředu)",
    simDaysUntilArrival: "Dní do příjezdu",
    simOccupancy: "Obsazenost",
    simRoomKindOccupancy: "Obsazenost kategorie pokoje",
    simRoomKindOccupancyHint: "Obsazenost vybrané kategorie pokoje — ovlivňuje úpravy před příjezdem a podle tempa rezervací pro tuto kategorii.",
    simPropertyOccupancy: "Obsazenost ubytování",
    simPropertyOccupancyHint: "Obsazenost celého ubytování — táhne s sebou cenu každého typu pokoje, nahoru i dolů.",
    simBookingSpeed: "Rychlost rezervací",
    simPaceBehind: "Pozadu",
    simPaceOnTrack: "V plánu",
    simPaceAhead: "Napřed",
    addRow: "Přidat řádek",
    lastMinuteTitle: "Last-Minute ceny",
    // Bez délky okna: ta závisí na režimu každého typu pokoje a v podtitulku
    // není místo říct, o který jde. Okno režimu podle poptávky uvádí blok
    // „Jak a kdy se použije“ níže, okno override pak legenda režimů.
    lastMinuteHint: "Při aktivaci se ceny postupně snižují s blížícím se příjezdem, aby se zaplnily zbývající pokoje.",
    lastMinuteEnabled: "Aktivovat Last-Minute ceny",
    lastMinuteMinPrice: "Cílová minimální cena",
    lastMinuteMinPriceHint: "Nejnižší cena, které systém dosáhne. Pokud necháte prázdné, použije se minimální cena z klíčových metrik.",
    lastMinuteAdjustmentStepsLabel: "Počet cenových úprav",
    // `{length}` je otevření okna objektu, je-li nastavené, jinak 36 hodin okna
    // podle poptávky; `{cadence}` doba mezi dvěma poklesy. Obojí přichází jako
    // „1 h 30 min“, tedy bez skloňování.
    lastMinuteAdjustmentStepsHint: "V okně o délce {length} klesá cena vždy po {cadence} — kadenci každého okna uvádí tabulka výše.",
    // Zobrazí se vedle jen tam, kde je některý typ pokoje v režimu „Vždy
    // zlevňovat“. Ten běží v sezónním okně, takže výše uvedená kadence je pro
    // něj několikanásobně pomalá — přesně ta věta, podle které klient
    // odpoledne napočítal 6 z 12 poklesů a ohlásil to jako chybu výpočtu.
    lastMinuteAdjustmentStepsOverrideHint: "Typy pokojů v režimu „Vždy zlevňovat“ vměstnají stejný počet poklesů do kratšího sezónního okna — {low} h ve vedlejší sezóně, {common} h v běžné a {high} h v hlavní — takže tam nastávají mnohem hustěji.",
    // Tabulka oken. Názvy obou režimů tu nejsou — řádky používají
    // `lastMinuteUseDefault` / `lastMinuteRoomKindOn`, tedy přesně ty texty,
    // které nabízí výběr u typu pokoje, aby se řádek a volba nerozešly.
    lastMinuteWindowTableHeading: "Kdy se cena mění",
    lastMinuteWindowModeColumn: "Režim",
    lastMinuteWindowSeasonColumn: "Sezóna",
    lastMinuteWindowOpensColumn: "Okno se otevírá",
    lastMinuteWindowLengthColumn: "Délka",
    lastMinuteWindowCadenceColumn: "Pokles ceny každých",
    lastMinuteWindowSeasonAny: "Kterákoli sezóna",
    lastMinuteWindowSeasonLow: "Vedlejší sezóna",
    lastMinuteWindowSeasonHigh: "Hlavní sezóna",
    // Záměrně ne „běžná sezóna“: engine řadí noc do hlavní, nebo vedlejší
    // sezóny a nic třetího nezná, takže desetihodinové okno nastane jen tam,
    // kde žádný týden hlavní sezóny určen není — než proběhne analýza, nebo
    // když žádný týden nepřekročí práh poptávky.
    lastMinuteWindowSeasonUnknown: "Hlavní sezóna zatím neurčena",
    lastMinuteWindowOpensDayBefore: "{time} předchozího dne",
    lastMinuteWindowOpensArrivalDay: "{time} v den příjezdu",
    lastMinuteWindowFootnote: "Každé okno končí v {time} v den příjezdu, kdy cena dosáhne svého last-minute minima — kratší okno tedy začíná později, nekončí dřív. Časy jsou podle hodin objektu a sezónu určuje vaše historie rezervací, týden po týdnu.",
    // Dva ze čtyř sloupců tabulky jsou nastavení, dva ne. Bez této věty
    // vypadá tabulka jako čtyři ovladače, z nichž tři nejde najít — proto
    // pojmenuje ty dva, které existují, a řekne, že okna žádný nemají. Oba
    // názvy se dosazují z popisků samotných ovladačů; kdyby se psaly sem,
    // přejmenování ovladače by ukazatel utrhlo.
    lastMinuteWindowSetWhere: "Otevření okna nastavíte níže volbou {windowLabel} (Automaticky ponechá okna z této tabulky), režim a případné vlastní otevření okna u každého typu pokoje v části {overridesTitle} a kadenci volbou {stepsLabel}. Pevně daná je jen hranice {time}.",
    // Otevření okna pro celý objekt. Možnosti jsou časy sestavené ze dvou
    // šablon `lastMinuteWindowOpens…` výše a délky okna, takže žádná nemá
    // vlastní text.
    lastMinuteWindowOpensLabel: "Kdy se okno otevírá",
    lastMinuteWindowOpensHint: "Automaticky platí okna z tabulky výše. Zvolte čas a okno se všem typům pokojů otevře právě tehdy — typ pokoje si ale může níže nastavit vlastní.",
    lastMinuteWindowOpensAutomatic: "Automaticky — podle režimu a sezóny",
    // Zobrazí se jen tam, kde je některé používané okno rozdělené jemněji,
    // než stihne nejrychlejší synchronizace.
    lastMinuteCadenceOutrunsSync: "Ceny se přepočítávají při každé synchronizaci, nejčastěji jednou za 30 minut — poklesy nastavené hustěji se proto projeví po dvou i více najednou.",
    lastMinuteHoldLabel: "Při oživení poptávky slevu podržet",
    lastMinuteHoldHint: "Platí pro typy pokojů řízené poptávkou. Když se v okně začne prodávat, sleva se přestane prohlubovat, místo aby se stáhla: rostoucí poptávka cenu zvedne jen mírně, a pokud prodej opět ustane, zlevňování pokračuje. Vypnuto se sleva stáhne, jakmile je poptávka zase v pořádku.",
    // Viz en.ts — tři tlačítka jsou slova z legendy kalendáře.
    lastMinuteDemandStartLabel: "Řídit podle poptávky: kdy začít zlevňovat",
    lastMinuteDemandStartHint: "Platí pro typy pokojů nastavené na Řídit podle poptávky. Uvnitř okna Optivo porovnává, kolik rezervací typ pokoje na danou noc obvykle ještě získá do {time} v den příjezdu — podle vlastní historie rezervací, upraveno podle toho, jak rychle rezervace v poslední době přibývají — s pokoji, které má ještě volné. Dokud by je tyto rezervace zaplnily, cena se drží; jak jich zbývá čekat méně, cena klesá, a pokoj volný v {time} dojde až na svou minimální cenu. Dříve začne zlevňovat, i když se ještě čeká víc rezervací; Později počká, až jich zbývá méně. Objekt bez dostatečné historie rezervací se místo toho řídí barvou poptávky.",
    lastMinutePacingLabel: "Tempo slevy",
    // Průběh slevy má stejný tvar bez ohledu na délku okna, takže uvádět ji
    // tady nic nepřinášelo a pro režim override to nebyla pravda.
    lastMinutePacingHint: "Určuje, jak rychle sleva roste v průběhu okna. Rychlejší = větší slevy na začátku. Pomalejší = slevy soustředěné ke konci.",
    lastMinuteSimHours: "Hodin do konce okna",
    lastMinuteActive: "Last-minute úprava aktivní",
    lastMinutePreview: "Křivka slevy — přetáhněte sloupce pro úpravu",
    lastMinuteResetCurve: "Obnovit křivku",
    lastMinutePerRoomKindTitle: "Přepsat dle typu pokoje",
    lastMinutePerRoomKindHint: "Nastavte, zda se last-minute použije, kdy se otevírá jeho okno a cílovou minimální cenu pro každý typ pokoje. Sdílená křivka slevy zůstává společná.",
    lastMinuteUseDefault: "Řídit podle poptávky",
    lastMinuteOverrideEnabled: "Přepis aktivace",
    lastMinuteOverrideMinPrice: "Přepis minimální ceny",
    lastMinuteRoomKindMinPriceLabel: "Cílová minimální cena",
    lastMinuteRoomKindMinPriceHint: "Nejnižší cena, na kterou může last-minute pro tento typ pokoje klesnout. Výchozí je odvozená minimální cena z Klíčových metrik; zadáním hodnoty ji přepíšete.",
    lastMinuteRoomKindColumn: "Typ pokoje",
    lastMinuteRoomKindModeColumn: "Last-minute",
    lastMinuteRoomKindWindowColumn: "Okno se otevírá",
    lastMinuteRoomKindWindowFollows: "Podle objektu",
    lastMinuteRoomKindMinPriceColumn: "Minimální cena",
    lastMinuteRoomKindMinPriceFollows: "Prázdné pole sleduje minimální cenu typu pokoje z Klíčových metrik — šedé číslo v něm.",
    lastMinuteRoomKindMinPriceReset: "Obnovit odvozenou",
    lastMinuteRoomKindMinPriceNoDerived: "Není odvozené minimum — zadejte hranici pro povolení last-minute slev.",
    lastMinuteHowHeading: "Jak a kdy se použije",
    // Obě poloviny — 36 hodin i podmínka poptávky — patří režimu podle
    // poptávky a override se neřídí ani jednou, takže věta říká, čí jsou,
    // místo aby je tvrdila o celé sekci.
    lastMinuteWhenHint: "Typy pokojů ponechané v režimu „Řídit podle poptávky“ drží cenu, dokud by rezervace, které obvykle ještě získají, zaplnily jejich volné pokoje, a zlevňují, jak jich zbývá čekat méně — každý volný pokoj dojde do uzávěrky na své minimum; typy pokojů v režimu „Vždy zlevňovat“ snižují cenu bez ohledu na poptávku. Kdy se u kterého cena mění, ukazuje tabulka níže.",
    lastMinuteGoalRevenue: "Při strategii Maximální výnos kroky před oknem zlevňují jen střídmě. Uvnitř okna funguje last-minute při každé strategii stejně: typy pokojů řízené poptávkou drží cenu, dokud by rezervace, které obvykle ještě získají, zaplnily jejich volné pokoje, a do uzávěrky dojdou na své minimum.",
    lastMinuteGoalOccupancy: "Při strategii Maximální obsazenost kroky před oknem zlevňují ochotně, takže last-minute často najde noc už níž. Uvnitř okna typy pokojů řízené poptávkou drží cenu, dokud by rezervace, které obvykle ještě získají, zaplnily jejich volné pokoje, a do uzávěrky dojdou na své minimum.",
    lastMinuteGoalHybrid: "Při strategii Doporučeno Optivem si noc drží základní cenu, dokud není v rezervačním okně. Uvnitř okna last-minute typy pokojů řízené poptávkou drží cenu, dokud by rezervace, které obvykle ještě získají, zaplnily jejich volné pokoje, a do uzávěrky dojdou na své minimum.",
    lastMinutePropertyDisabledNote: "Last-minute ceny jsou pro tuto strategii vypnuté. Použijí se jen u druhů pokojů výslovně zapnutých níže.",
    lastMinuteRoomKindOn: "Vždy zlevňovat (override)",
    lastMinuteRoomKindOff: "Nikdy nezlevňovat",
    lastMinuteModeExplainHeading: "Co která volba znamená",
    lastMinuteModeExplainDefault: "Řídit podle poptávky — řídí se přepínačem objektu výše: uvnitř okna drží cenu, dokud by rezervace, které tento typ pokoje obvykle ještě získá, zaplnily jeho volné pokoje, jak jich zbývá čekat méně, cenu snižuje k minimu, a pokoj volný v uzávěrce dojde až na minimum. Když je přepínač vypnutý, tento typ pokoje nezlevňuje.",
    lastMinuteModeExplainOn: "Vždy zlevňovat (override) — cena se vždy postupně snižuje k minimu s blížícím se příjezdem, bez ohledu na poptávku, a to uvnitř svého okna — pokud je automatické, v kratším okně, které určuje sezóna.",
    lastMinuteModeExplainOff: "Nikdy nezlevňovat — last-minute se pro tento typ pokoje nikdy nespustí.",
    lastMinuteNoRoomKinds: "Zatím nejsou k dispozici žádné druhy pokojů.",
    losRestrictions: "Omezení délky pobytu",
    defaultMinLos: "Výchozí min délka pobytu",
    defaultMinLosHint: "Minimální pobyt, který se použije, když jej nic jiného nezvyšuje. Vše ostatní se vrací na tuto spodní hranici.",
    losNightsUnit: "nocí",
    losAnalysisStayLength: "Analýza: medián {median} nocí · průměr {avg}",
    losAnalysisOneNightShare: "{pct} % tvoří jednonoční pobyty",
    weekendHolidayLosEnabled: "Minimální pobyt o víkendech a svátcích",
    weekendHolidayLosEnabledHint: "Nastaví minimálně 2 noci pro příjezdy v pátek a sobotu. Když státní svátky víkend prodlouží, pokryje se celý blok volna — až 4 noci, od večera příjezdu přes každý další nočleh uvnitř, aby jej nerozdělila jednonoční rezervace. Poslední den bloku je dnem odjezdu, proto zůstává bez omezení, a běžné všední dny se neomezují nikdy. V posledních dnech před příjezdem, které nastavíte níže, se minimum uvolní vždy; dřív se může uvolnit, když je poptávka slabá, pokud to níže nevypnete. Pokud pobyt narazí na noc, kdy je jeho typ pokoje už vyprodaný, minimum tohoto typu pokoje se zkrátí na pobyt, který se ještě dá rezervovat, dokud se pokoj neuvolní.",
    losReleaseWithinDays: "Vždy uvolnit do",
    losReleaseWithinDaysHint: "Pokud do příjezdu zbývá tento počet dní nebo méně, minimální pobyt se vždy vrátí na výchozí hodnotu bez ohledu na poptávku.",
    losReleaseWithinDaysOffHint: "Nastavte 0, pokud má o uvolnění minimálního pobytu rozhodovat pouze poptávka.",
    losReleaseWithinDaysUnit: "dní",
    losReleaseWithinDaysNeverHint: "Při hodnotě 0 a vypnuté volbě „Uvolnit minimum při slabé poptávce“ platí minimální pobyt až do příjezdu, pokud se nevyprodá noc, kterou pobyt potřebuje.",
    losWeakWeekRelease: "Uvolnit minimum při slabé poptávce",
    losWeakWeekReleaseHint: "Když je poptávka po víkendu nebo svátcích slabá, může se minimum vrátit na výchozí hodnotu už před lhůtou „Vždy uvolnit do“, například když byly víkendy v tom období loni obsazené méně než z 60 %. Vypněte, pokud minimální pobyty plánujete dopředu a posíláte je do PMS.",
    losWeakWeekReleaseOffHint: "Minimum platí i ve slabých týdnech; uvolní se až v posledních dnech před příjezdem, které nastavíte výše, nebo když se vyprodá noc, kterou pobyt potřebuje.",
    // Místo nápovědy výše, dokud Optivo čte vlastní rezervace víkendu.
    losWeakWeekReleaseDemandHint: "Daleko před příjezdem rozhoduje historie rezervací: minimum se uvolní v týdnu, který označila za slabý, ale ne, když se víkend už teď prodává výrazně rychleji než obvykle. Od 30 dnů před příjezdem, jakmile už obvykle bývá přijata aspoň polovina rezervací, které víkend dostává, rozhodují současné rezervace: minimum se uvolní, když podle nich víkend skončí zřetelně pod svým obvyklým obsazením, a jinak se drží. Minimum uvolněné kvůli slabému týdnu se ale vrátí, až když se víkend prodává výrazně rychleji než obvykle. Lhůta „Vždy uvolnit do“ platí vždy.",
    roomStructure: "Ceny podle počtu hostů",
    roomKindStructureTitle: "Struktura kategorií pokojů",
    roomKindStructureHint: "Přejmenujte, upravte počet nebo skryjte to, co dorazí z PMS. Změny ovlivní cenový kalendář, analytiku, návrhy i odesílání cen pro toto spojení. Každý viditelný typ je oceňován a odesílán do PMS — skryjte položky, které nejsou prodejné ubytování, například parkování, akční bloky nebo rezervy.",
    roomKindStructureColRoomKind: "Kategorie pokoje",
    roomKindStructureColDisplayName: "Zobrazený název",
    roomKindStructureColRooms: "Pokojů",
    roomKindStructureColBeds: "Lůžek",
    roomKindStructureColExtraBeds: "Přistýlek",
    roomKindStructureColMaxOcc: "Max obs.",
    roomKindStructureColVisible: "Viditelné",
    roomKindStructurePlaceholderName: "Použít název z PMS",
    roomKindStructureSave: "Uložit strukturu",
    roomKindStructureSaving: "Ukládám…",
    roomKindStructureSaved: "Uloženo",
    roomKindStructureSaveError: "Uložení selhalo",
    roomKindStructureReset: "Vrátit",
    roomKindStructureEmpty: "Z PMS zatím nedorazily žádné kategorie pokojů.",
    roomKindStructureHiddenBadge: "Skryto",
    roomKindStructureAutoHiddenBadge: "Auto-skryto",
    roomKindStructurePmsValue: "PMS",
    roomKindStructurePricedBadge: "Oceňováno a odesíláno",
    roomKindStructureNotPricedBadge: "Neoceňováno",
    roomKindStructureColOrder: "Pořadí",
    roomKindStructureMoveUp: "Posunout nahoru",
    roomKindStructureMoveDown: "Posunout dolů",
    roomKindStructureOrderHint: "Pořadí níže určuje pořadí řádků ve všech kalendářích — rezervace, návrhy cen i metrické pohledy.",
    roomCapacities: "Kapacity pokojů",
    beds: "Lůžka",
    extraBeds: "Přistýlky",
    maxOccupancy: "Max",
    roomCount: "Pokojů",
    occupancyAdjustments: "Úpravy ceny dle obsazenosti pokoje",
    occupancyLabel: "Obs. {n}",
    occupancyAdjustmentWarning: "Cena pro méně hostů nesmí překročit cenu pro více hostů. Zkontrolujte zvýrazněné úrovně obsazenosti.",
    guestStepsHint: "Dva hosté platí základní sazbu. Tyto dva kroky určují cenu pro každý jiný počet hostů — Optivo je použije na každý typ pokoje podle jeho základní ceny a změna jednoho z nich přepíše tabulku níže.",
    singleGuestStepLabel: "Jeden host místo dvou",
    singleGuestStepHint: "Kolik zaplatí jeden host oproti základní sazbě pro dva, nikdy ne pod minimum platné pro danou noc.",
    extraGuestStepLabel: "Za každého hosta nad dva",
    extraGuestStepHint: "Připočte se za každého hosta nad dva — na lůžku i na přistýlce.",
    guestStepUnitAmount: "Částka",
    guestStepUnitSelect: "Jednotka pro {field}",
    guestPricingRoomTypeColumn: "Typ pokoje",
    guestPricingGuestsHeading: "Cena za noc podle počtu hostů",
    guestPricingBaseTag: "základ",
    guestPricingBaseNote: "Dva hosté platí základní sazbu typu pokoje, kterou nastavíte v Cenách podle typu pokoje. Počty lůžek pocházejí ze Struktury typů pokojů.",
    guestPricingSingleGuestNote: "Typ pokoje jen pro jednoho hosta se prodává za svou základní sazbu — krok „{step}“ ji nemění.",
    guestPricingExtraBedNote: "Podbarvená cena patří hostovi na přistýlce.",
    guestPricingNoRoomKinds: "Zatím nejsou k dispozici žádné typy pokojů. Znovu spusťte onboarding pro načtení typů pokojů.",
    priceGranularity: "Granularita ceny",
    priceGranularityHint: "Krok zaokrouhlení generovaných cen.",
    priceCharmEnding: "Baťovská devítka",
    priceCharmEndingHint: "Odečte od zaokrouhlené ceny zvolenou částku, aby cena končila devítkou — místo 1000 vyjde 990. Nabízí se jen zakončení, která se vejdou do kroku zaokrouhlení.",
    priceCharmEndingOff: "Vypnuto",
    priceCharmEndingExample: "{before} → {after}",
    holidays: "Svátky a speciální dny",
    specialDatesHint: "Státní svátky jsou předvyplněny podle vybraných zemí. Duplicitní data jsou automaticky sloučeny. Každý záznam můžete individuálně upravit nebo přidat vlastní data. Příplatek za speciální den víkendový příplatek nahrazuje, nikdy se s ním nesčítá, a to jen tam, kde je vyšší — noc, jejíž vlastní víkendový příplatek (nebo příplatek jejího typu pokoje) je vyšší, si ponechá ten. Nový záznam začíná na tom, co u vás stojí špičková noc: {premium} %. Každý záznam pak stojí sám o sobě — změna pátku ani soboty s ním už nepohne.",
    specialDatesNightRule: "Noci, které záznam upravuje, vybíráte v jeho kalendáři: noc patří dni příjezdu, stejně jako na stránce Ceny. Státní svátek upravuje noc před sebou, protože host, který se v ten den probouzí, přijel večer předem — Nový rok tedy upravuje noc 31. 12. → 1. 1.",
    specialDatePricedNight: "noc {from} → {to}",
    // Období: {count} je tvar ze `specialDateNightCount*`, {from} první večer
    // a {to} poslední ráno.
    specialDatePricedNights: "{count}: {from} → {to}",
    specialDateNightCountOne: "{count} noc",
    specialDateNightCountFew: "{count} noci",
    specialDateNightCountOther: "{count} nocí",
    // Kalendář, ve kterém se noci vybírají.
    specialDatePickNights: "Vybrat noci",
    specialDateChangeNights: "Změnit noci",
    specialDatePickerHint: "Klikněte na noc. U období klikněte ještě na poslední noc.",
    specialDatePickerDone: "Hotovo",
    specialDateMissing: "Vyberte noci v kalendáři: do té doby záznam neupravuje cenu žádné noci.",
    specialDateUnreadable: "Tento zápis data Optivo nečte, takže záznam neupravuje cenu žádné noci. Vyberte noci v kalendáři znovu.",
    specialDateNeedsYear: "Datum bez roku platí každý rok: zapněte „Opakující se“, nebo vyberte noci v kalendáři.",
    specialDateYearOnRecurring: "Datum s rokem platí jen jednou: vypněte „Opakující se“, nebo vyberte noci v kalendáři znovu.",
    specialDateEasterPattern: "Velikonoce {offset}",
    specialDateMovesWithEaster: "Datum se každý rok posouvá s Velikonocemi, takže záznam upravuje cenu tohoto svátku v každém roce. Vypnutím „Opakující se“ zůstane jen jeho nejbližší datum.",
    specialDateOutrankedRoomTypes: "{day}: tyto typy pokojů si tu noc ponechají vlastní vyšší příplatek — {roomTypes}.",
    specialDateOutrankedRoomTypesNights: "{day}: tyto typy pokojů si ty noci ponechají vlastní vyšší příplatek — {roomTypes}.",
    specialDateOutrankedGroup: "{names} {premium} %",
    specialDateOutrankedProperty: "{day}: tu noc platí váš vlastní vyšší příplatek {premium} %.",
    specialDateOutrankedPropertyNights: "{day}: ty noci platí váš vlastní vyšší příplatek {premium} %.",
    specialDatesSaveRefused: "Nic se neuložilo: některý speciální den má datum, které Optivo nečte. Červeně označené řádky v sekci Svátky a speciální dny ukazují který.",
    selectCountry: "Vyberte zemi…",
    reloadHolidays: "Znovu načíst státní svátky",
    holidaysActive: "aktivních",
    loadingHolidays: "Načítání…",
    bulkAdjust: "Hromadné nastavení",
    bulkAdjustHint: "Nastaví stejnou úpravu u všech aktivních záznamů. Minus je udělá levnější než běžnou noc.",
    apply: "Použít",
    collapseList: "Sbalit seznam",
    expandList: "Zobrazit všechny záznamy",
    showMore: "Zobrazit více…",
    unnamed: "Nepojmenováno",
    customDateName: "Název (např. Štědrý den)",
    specialDateAdjustment: "Úprava ceny",
    premium: "Přirážka",
    discount: "Sleva",
    recurring: "Opakující se",
    noHolidays: "Žádné speciální dny. Vyberte zemi pro načtení státních svátků nebo přidejte záznamy ručně.",

    // Tipy k parametrům (vysvětlení)
    highSeasonMultiplierHint: "Posune sazbu za noc v měsících s vysokou poptávkou. Např. +15 % znamená, že základ 2 000 se stane 2 300; minus tyto měsíce naopak zlevní.",
    lowSeasonMultiplierHint: "Posune sazbu za noc v měsících s nízkou poptávkou. Obvykle minus pro přilákání hostů — plus, pokud jsou právě klidné měsíce ty, kdy máte pokojů málo.",
    weekendPremiumParamHint: "Použijte samostatné denní úpravy místo jedné víkendové přirážky.",

    // Štítky cenového simulátoru
    simBase: "Základní sazba",
    simSeason: "Sezónní úprava",
    simHighSeason: "Hlavní sezóna",
    simLowSeason: "Mimo sezónu",
    simWeekend: "Denní úprava",
    simDayType: "Den",
    simWeekday: "Pracovní den",
    simMonday: "Pondělí",
    simTuesday: "Úterý",
    simWednesday: "Středa",
    simThursday: "Čtvrtek",
    simFriday: "Pátek",
    simSaturday: "Sobota",
    simSunday: "Neděle",
    simDateType: "Typ dne",
    simDateTypeRegular: "Běžný",
    simDateTypeHoliday: "Svátek",
    simDateTypeBridge: "Přemostěný den",
    simDateTypeShoulder: "Okraj špičky",
    simDateTypeGapFill: "Vyplnění mezery",
    simLosRelaxed: "MinLOS uvolněn",
    simMonth: "Měsíc",
    simDynamicDemand: "Dynamická poptávka",
    simSpecialDayOverride: "Nahrazeno příplatkem za speciální den — příplatek za speciální den a víkendový příplatek se nikdy nesčítají.",
    simHoliday: "Svátek",
    simNights: "nocí",
    simClamped: "Omezeno limity",
    simGuardrails: "Cenové limity",
    simRounding: "Zaokrouhleno na nejbližší",
    simFinal: "Výsledná cena",
    simExampleTitle: "Živý příklad výpočtu ceny",
    simShowPreview: "Zobrazit živý příklad výpočtu ceny",
    simHidePreview: "Skrýt živý příklad výpočtu ceny",
    simRoomKind: "Typ pokoje",
    simPreviewRoomCategory: "Kategorie pokoje v náhledu",
    simPreviewRoomCategoryHint: "Pro informaci: tato kategorie je daná přiřazením typu pokoje a lze ji změnit tam.",
    simPreviewRoomFallback: "Kategorie Standard není k dispozici, náhled proto používá nejbližší dostupnou kategorii pokoje.",

    // Gap filling
    gapFillingTitle: "Vyplňování mezer",
    gapFillingHint: "Detekuje krátké mezery (osiřelé noci) mezi existujícími rezervacemi, které se zřídka prodají za stejnou cenu jako noci kolem nich, a nacení je samostatně.",
    gapFillingEnabled: "Povolit vyplňování mezer",
    gapFillingMaxGapDays: "Maximální velikost mezery",
    gapFillingMaxGapDaysHint: "Největší mezera, která se má nacenit samostatně. Jedna noc znamená osiřelou noc mezi dvěma rezervacemi.",
    gapFillingAdjustmentPct: "Úprava ceny",
    gapFillingAdjustmentPctHint: "Jak se pohne cena pro dny v detekované mezeře. Obvykle sleva — zadejte plus, pokud je jedna noc mezi dvěma pobyty spíš vzácná než nechtěná. Sleva ustoupí tam, kde ji noc nepotřebuje: u data, které jste nacenili sami, u noci, která se už prodává výrazně nad cílem, nebo u noci, jejíž cenu převzalo last-minute. Plus se uplatní vždy.",
    gapFillingNight: "noc",
    gapFillingNights: "noci",

    // Bridge-day premium and shoulder days (strategy editor labels)
    bridgeDaysTitle: "Přemosťovací dny a okraje špičky",
    bridgeDaysHint: "Pokud jediný běžný den leží mezi dvěma prémiovými dny (svátek↔víkend, víkend↔svátek nebo svátek↔svátek), nacení se také jako prémiový den.",
    bridgeDaysEnabled: "Povolit přirážku za přemosťovací den",
    bridgeDaysPremiumPct: "Úprava ceny",
    bridgeDaysPremiumPctHint: "Jak se pohne cena na detekovaném přemosťovacím dni. Obvykle přirážka — zadejte minus, pokud je právě prostřední noc ta, o kterou nikdo nestojí.",
    bridgeDaysFollowsWeekend: "Odvozeno od víkendové přirážky {weekend} %. Přemosťovací noc se oceňuje jako prémiová noc, mezi kterými leží.",
    bridgeDaysOffWeekend: "Tato přirážka stojí sama o sobě. Vaše víkendová přirážka je {weekend} %.",
  },

  // ── Pace Curve Chart ─────────────────────────────────────
  paceCurveChart: {
    title: "Tempo → Cenová křivka",
    revenueHint: "Exponenciální: malé slevy při zaostávání, rostoucí přirážky při předstihu.",
    occupancyHint: "Logaritmická: agresivní slevy při zaostávání, zpomalující přirážky při předstihu.",
    hybridHint: "Doporučeno Optivem: slevy ve stylu obsazenosti při zaostávání, přirážky ve stylu příjmů při předstihu.",
    xLabel: "Tempo rezervací",
    yLabel: "Cenová úprava",
    gentle: "Jemná",
    moderate: "Střední",
    aggressive: "Agresivní",
    behindPace: "← Zaostává",
    onTrack: "Na trase",
    aheadOfPace: "Předstih →",
    custom: "Vlastní",
  },

  // ── Auth Callback ───────────────────────────────────────
  auth: {
    error: "Chyba ověření",
    fallback: "Ověření selhalo",
    tryAgain: "Zkusit znovu",
    completing: "Dokončování přihlášení...",
  },

  // ── App / Dashboard ─────────────────────────────────────
  app: {
    checkingPms: "Kontrola připojení k Property Management...",
    pmsUnhealthy:
      "Připojení k Property Management je nezdravé. Ověřte prosím své přihlašovací údaje.",
    pmsVerifyFailed:
      "Nepodařilo se ověřit připojení k Property Management. Zkontrolujte prosím své přihlašovací údaje.",
    connectionStillUnhealthy: "Připojení je stále nezdravé.",
    pageLoadFailedTitle: "Stránku se nepodařilo načíst",
    pageLoadFailedMessage:
      "Od otevření této karty mohla vyjít nová verze. Opětovné načtení stáhne tu aktuální.",
    pageLoadFailedReload: "Načíst znovu",
    // Viz komentář v `en.ts`: pod nabídkou načtení, když nejde vykreslit celá aplikace.
    pageLoadFailedContact: "Pokud opětovné načtení nepomůže, napište na {email}.",
  },

  // ── User Management (admin-only) ────────────────────────
  userManagement: {
    searchPlaceholder: "E-mail nebo ID uživatele",
    title: "Správa uživatelů",
    description: "Zobrazení a správa všech registrovaných uživatelů",
    loading: "Načítání uživatelů...",
    retry: "Zkusit znovu",
    loadError: "Nepodařilo se načíst uživatele",
    noUsers: "Žádní uživatelé nenalezeni",
    email: "E-mail",
    role: "Role",
    status: "Stav",
    joined: "Registrace",
    actions: "Akce",
    roleAdmin: "Admin",
    roleUser: "Uživatel",
    statusActive: "Aktivní",
    statusInactive: "Neaktivní",
    statusPending: "Čeká na schválení",
    approve: "Schválit",
    makeAdmin: "Nastavit jako admina",
    removeAdmin: "Odebrat admin roli",
    activate: "Aktivovat",
    deactivate: "Deaktivovat",
    confirmDeactivate: "Deaktivovat tohoto uživatele? Bude okamžitě odhlášen a nebude se moci přihlásit.",
    confirmRemoveAdmin: "Odebrat admin roli tomuto uživateli?",
    toastRoleUpdated: "Role uživatele aktualizována",
    toastRoleUpdateFailed: "Nepodařilo se aktualizovat roli uživatele",
    toastStatusUpdated: "Stav uživatele aktualizován",
    toastStatusUpdateFailed: "Nepodařilo se aktualizovat stav uživatele",
    cannotModifySelf: "Nemůžete upravit svůj vlastní účet",
    userCount: "{count} uživatelů",
    callPhone: "Zavolat na {phone}",
    createTemporary: "Vytvořit dočasný přístup",
    temporaryTitle: "Nový dočasný přístup",
    temporaryDescription:
      "Přihlášení pro testování, které funguje okamžitě: bez ověření e-mailu, bez schválení a bez dvoufázového ověření. Po uplynutí doby přestane samo fungovat.",
    temporaryEmail: "E-mail (přihlašovací jméno)",
    temporaryEmailHint:
      "Nemusí to být skutečná schránka — nic se na ni neposílá. Slouží jen jako jméno pro přihlášení.",
    temporaryEmailInvalid: "Zadejte e-mailovou adresu, například tester@example.com.",
    temporaryPassword: "Heslo",
    temporaryPasswordHint: "Alespoň 8 znaků. Zobrazuje se nezakryté, abyste ho mohli předat.",
    temporaryPasswordTooShort: "Heslo musí mít alespoň 8 znaků.",
    generatePassword: "Vygenerovat",
    temporaryDuration: "Přístup na",
    accessDays: {
      d1: "1 den",
      d7: "7 dní",
      d14: "14 dní",
      d30: "30 dní",
      d90: "90 dní",
    },
    temporaryCreate: "Vytvořit přístup",
    temporaryCreating: "Vytváření...",
    temporaryCreatedTitle: "Dočasný přístup vytvořen",
    temporaryCreatedHint: "Údaje předejte hned — heslo se už znovu nezobrazí.",
    temporaryValidUntil: "Funguje do {date}",
    temporaryGrantHint:
      "Aby tento účet viděl ubytování, otevřete PMS připojení, vyberte ubytování a v sekci Přístup přidejte tento e-mail.",
    copyCredentials: "Zkopírovat přihlašovací údaje",
    copied: "Přihlašovací údaje zkopírovány",
    copyFailed: "Kopírování se nezdařilo — označte text a zkopírujte ho ručně",
    done: "Hotovo",
    toastTemporaryCreated: "Dočasný přístup vytvořen",
    toastTemporaryCreateFailed: "Dočasný přístup se nepodařilo vytvořit",
    statusExpired: "Vypršel",
    accessUntil: "Přístup do {date}",
    confirmActivateExpired:
      "Dočasný přístup tohoto účtu vypršel. Aktivací se časové omezení zruší — účet pak zůstane aktivní, dokud ho nedeaktivujete.",
    resetLink: "Odkaz pro obnovu hesla",
    resetLinkDescription:
      "Odkaz předejte sami — nic se neodeslalo e-mailem. Umožní jednou nastavit nové heslo a pak přestane platit.",
    resetLinkGenerating: "Generuji odkaz...",
    resetLinkFor: "Pro {email}",
    resetLinkExpiresAt: "Platí do {date}",
    resetLinkHint:
      "Odkaz se zobrazuje jen zde. Vygenerováním dalšího se nahradí a starší odkaz přestane platit.",
    resetLinkBlocked:
      "Tento účet se ani tak nepřihlásí — je potřeba ho také schválit nebo aktivovat, jinak mu nové heslo nepomůže.",
    copyLink: "Zkopírovat odkaz",
    linkCopied: "Odkaz zkopírován",
    toastResetLinkFailed: "Odkaz pro obnovu hesla se nepodařilo vygenerovat",
    deleteUser: "Smazat účet",
    confirmDeleteUser:
      "Smazat účet {email}? Přihlášení okamžitě přestane fungovat a e-mailová adresa se uvolní — lze ji znovu zaregistrovat. Záznam o tom, co účet dělal, zůstává v protokolu činnosti. Akci nelze vzít zpět.",
    toastUserDeleted: "Účet smazán",
    toastUserDeleteFailed: "Účet se nepodařilo smazat",
    allowAiAgents: "Povolit AI agenty",
    stopAiAgents: "Zakázat AI agenty",
    aiAgentsBadge: "AI agenti",
    confirmStopAiAgents:
      "Zakázat AI agenty účtu {email}? Jeho agenti okamžitě přestanou fungovat. Bezpečnostní tokeny zůstanou uložené a začnou znovu fungovat, pokud AI agenty opět povolíte.",
    toastAiAgentsAllowed: "AI agenti povoleni pro {email}",
    toastAiAgentsStopped: "AI agenti zakázáni pro {email}",
    toastAiAgentsFailed: "AI agenty se nepodařilo přepnout",
  },

  // ── Admin Price Plans (admin-only) ─────────────────────
  adminPriceDrafts: {
    searchPlaceholder: "Objekt, účet nebo vlastník",
    title: "Cenové plány",
    description: "Všechny uložené cenové plány napříč ubytováními",
    intro: "Pro každé ubytování je uložen jeden plán na následující rok. Jeho smazáním se ztratí všechny připravené ceny, které obsahuje — včetně nocí, které hotel zamkl nebo ručně upravil — a Optivo je nedokáže obnovit.",
    uploadWarning: "Plán smazaný během odesílání už nelze označit jako odeslaný. Před mazáním ověřte, že neběží žádná synchronizace.",
    loading: "Načítání cenových plánů...",
    noDrafts: "Nejsou uloženy žádné cenové plány",
    retry: "Zkusit znovu",
    property: "Ubytování",
    account: "Účet",
    range: "Období",
    prices: "Ceny",
    sent: "Odesláno",
    locked: "Zamčeno",
    updated: "Poslední změna",
    actions: "Akce",
    orphaned: "Bez ubytování",
    orphanedHint: "K tomuto plánu není připojené žádné ubytování — nikdo ho nevidí ani neobnoví",
    delete: "Smazat",
    deleting: "Mazání...",
    confirmDeleteTitle: "Smazat cenový plán tohoto ubytování?",
    confirmDelete: "Tímto se smaže všech {count} připravených cen pro {property}, včetně zamčených a ručně upravených. Ceny už odeslané do property management systému zůstanou beze změny a nový plán se spočítá při příští synchronizaci ubytování.",
    purgeOrphaned: "Smazat plány bez ubytování",
    confirmPurgeOrphanedTitle: "Smazat plány, které nemají ubytování?",
    confirmPurgeOrphaned: "Tyto plány nemají připojené žádné ubytování, přes Optivo se k nim nikdo nedostane a žádný hotel o ceny nepřijde. Běžně žádné nejsou — tlačítko zůstává vypnuté, pokud nějaké nevznikly nekonzistencí dat.",
    purgeAll: "Smazat všechny plány",
    confirmPurgeAllTitle: "Smazat všechny uložené cenové plány?",
    confirmPurgeAll: "Tímto se smaže cenový plán každého ubytování na všech účtech a nelze to vzít zpět. Ceny už odeslané do property management systémů to neovlivní; každé ubytování dostane nový plán při příští synchronizaci.",
    toastDeleted: "Cenový plán smazán",
    toastDeleteFailed: "Nepodařilo se smazat cenový plán",
    toastPurged: "Smazáno {count} cenových plánů",
    toastPurgeFailed: "Nepodařilo se smazat cenové plány",
    draftCount: "{count} plánů",
    unknownProperty: "Ubytování č. {id}",
    unknownAccount: "—",
  },

  // ── Admin: Bankovní převody ────────────────────────────
  adminWirePayments: {
    searchPlaceholder: "Variabilní symbol, účet nebo faktura",
    title: "Bankovní převody",
    description: "Převody čekající na spárování s firemním účtem",
    intro: "Platba kartou se spáruje sama. Bankovní převod přijde na firemní účet, kam Optivo nevidí — najděte variabilní symbol ve výpisu, ověřte částku a pak tady potvrďte přijetí.",
    loading: "Načítání bankovních převodů...",
    retry: "Zkusit znovu",
    noneWaiting: "Žádné převody nečekají — ve výpisu není co ověřovat",
    noneAtAll: "Zatím nebyl vyžádán žádný bankovní převod",
    scopePending: "Čekající",
    scopeAll: "Vše",
    pendingCount: "{count} čeká",
    variableSymbol: "Variabilní symbol",
    account: "Účet",
    payer: "Plátce",
    amount: "Částka",
    period: "Období",
    requested: "Vystaveno",
    status: "Stav",
    actions: "Akce",
    // Co částka pokrývá. Platbu podle ceníku z 5. 10. 2026 vysvětlují
    // objekty, které účtuje, a jejich pokoje, uvedené spolu; platbu založenou
    // před ním pokoje, ze kterých se počítala.
    properties: { one: "{count} objekt", few: "{count} objekty", other: "{count} objektů" },
    rooms: "{count} pokojů",
    // Tržní data jsou v ceně obou produktů; platba to uvádí jen tam, kde se
    // s ní účtoval příplatek dohodnutý navíc.
    marketDataIncluded: "vč. příplatku za tržní data",
    unknownAccount: "—",
    statusPending: "Čeká",
    statusPaid: "Přijato",
    statusCanceled: "Zrušeno",
    statusFailed: "Neúspěšné",
    approve: "Potvrdit přijetí",
    reject: "Nepřišlo",
    approving: "Potvrzuji...",
    confirmApproveTitle: "Potvrdit, že převod dorazil?",
    confirmApprove: "Nejdřív ověřte na firemním účtu variabilní symbol {vs}. Potvrzením označíte {amount} od {account} jako přijaté, prodloužíte zaplacené období do {periodEnd} a vystavíte fakturu.",
    confirmRejectTitle: "Označit převod jako nepřijatý?",
    confirmReject: "Zruší se pohledávka {amount} pro {account} a zákazník může založit novou platbu. Nic se neúčtuje a nevystavuje se faktura.",
    noteLabel: "Odkaz na bankovní výpis",
    notePlaceholder: "např. výpis KB 27/08, transakce 4471",
    noteHint: "Nepovinné. Uloží se k rozhodnutí, aby se dalo později dohledat ve výpisu.",
    settledBy: "Potvrdil(a) {admin}",
    // Bankovní převod potvrzuje člověk, takže vidí, s čím plátce souhlasil.
    // Prázdná hodnota znamená neznámo, nikoli nesouhlas.
    termsAccepted: "Souhlas s podmínkami {date}",
    termsNotRecorded: "Souhlas s podmínkami nezaznamenán",
    invoiceIssued: "Faktura {number}",
    toastApproved: "Převod potvrzen — předplatné je uhrazeno",
    toastApproveFailed: "Převod se nepodařilo potvrdit",
    toastRejected: "Převod označen jako nepřijatý",
    toastRejectFailed: "Převod se nepodařilo aktualizovat",
    // Peníze přijaté za období, které už zaplatila jiná platba, se
    // nezapočítají: zaznamenají se jako k vrácení a vrací se ručně. Je to
    // jediný seznam tady, ve kterém jsou i platby kartou.
    scopeRefund: "K vrácení",
    refundDueCount: "{count} k vrácení",
    noneToRefund: "Žádná platba nečeká na vrácení",
    statusRefundDue: "K vrácení",
    statusRefunded: "Vráceno",
    // Stav, pro který tato verze konzole nemá slovo.
    statusOther: "Jiný stav",
    paidByTransfer: "Bankovní převod",
    paidByCard: "Platba kartou",
    paidByCardRenewal: "Karta, strženo automaticky",
    gatewayId: "Platba v bráně {id}",
    // Převod zrušený tady — nahradila ho novější platba, nebo byl označen
    // jako nepřijatý — může na účet přesto dorazit.
    approveCanceled: "Přece jen dorazil",
    confirmApproveCanceledTitle: "Potvrdit, že zrušený převod dorazil?",
    confirmApproveCanceled: "Tento převod byl zrušen: nahradila ho novější platba, nebo byl označen jako nepřijatý. Nejdřív ověřte na firemním účtu variabilní symbol {vs}. Pokud období nezaplatila jiná platba, potvrzením označíte {amount} od {account} jako přijaté, prodloužíte zaplacené období do {periodEnd} a vystavíte fakturu. Pokud je období už zaplacené, platba se místo toho zaznamená jako k vrácení.",
    markRefunded: "Zapsat vrácení",
    markingRefunded: "Zapisuji...",
    confirmRefundTitle: "Zapsat, že platba byla vrácena?",
    confirmRefund: "Optivo samo žádné peníze neposílá. Nejdřív vraťte {amount} zákazníkovi {account} — platbu kartou v administraci platební brány, bankovní převod z firemního účtu — a pak to tady zapište. Variabilní symbol {vs}.",
    refundNoteLabel: "Odkaz na vrácení",
    refundNotePlaceholder: "např. vráceno v bráně 6. 10., nebo výpis KB 6. 10.",
    refundNoteHint: "Nepovinné. Uloží se k záznamu, aby se vrácení dalo později dohledat.",
    refundDueHint: "Přišlo za období, které je už zaplacené. Nic se nezapočetlo.",
    refundedBy: "Vrácení zapsal(a) {admin}",
    refundedOn: "Vrácení zapsáno {date}",
    toastRefundDue: "Zaznamenáno, nezapočteno: období už bylo zaplaceno, převod je k vrácení",
    toastRefunded: "Vrácení zapsáno",
    toastRefundFailed: "Vrácení se nepodařilo zapsat",
  },


  // ── Admin — PMS připojení ──────────────────────────────
  // Sdílené všemi administrátorskými seznamy: vyhledávání a stránkování.
  adminList: {
    searchLabel: "Hledat",
    clearSearch: "Zrušit hledání",
    showing: "Zobrazeno {from}–{to} z {total}",
    pageOf: "{page} / {pages}",
    previousPage: "Předchozí stránka",
    nextPage: "Další stránka",
    noMatches: "Hledání „{search}“ nic nenašlo",
    clearToSeeAll: "Zrušte hledání a uvidíte celý seznam",
  },
  adminConnections: {
    searchPlaceholder: "Objekt, účet nebo osoba",
    title: "PMS připojení",
    description: "Všechny připojené ubytovací objekty napříč účty",
    intro: "Podpůrný pohled na všechna PMS připojení v Optivu. Upravte provozní nastavení, které objekt zadal špatně, ověřte dostupnost poskytovatele a obnovte přístup, když se účet nedostane ke svému vlastníkovi. Přihlašovací údaje se zde nezobrazují ani nemění.",
    loading: "Načítám připojení...",
    retry: "Zkusit znovu",
    empty: "Zatím nebylo vytvořeno žádné PMS připojení",
    connectionCount: "{count} připojení",
    // Tabulka
    property: "Objekt",
    account: "Účet",
    provider: "Poskytovatel",
    currency: "Měna",
    health: "Stav",
    syncCadence: "Synchronizace",
    lastSync: "Poslední synchronizace",
    members: "Lidé",
    createdBy: "Vytvořil",
    actions: "Akce",
    unnamed: "Nepojmenovaný objekt",
    never: "Nikdy",
    memberCount: "{count} lidí",
    notConfigured: "Nenastaveno",
    // Kdo objekt vytvořil, se dřív nikam nezapisovalo a doplnění zpětně
    // připsalo jen účty s jediným vlastníkem — u ostatních to raději
    // přiznáme, než abychom nechali prázdnou buňku vypadající jako chyba.
    creatorUnknown: "Nezaznamenáno",
    creatorLeft: "K objektu už nemá přístup",
    createdByLine: "Vytvořil {name}",
    creatorBadge: "Zakladatel",
    healthHealthy: "V pořádku",
    healthUnhealthy: "Nedostupné",
    healthUnknown: "Neznámý",
    // Dialog nastavení
    editSettings: "Nastavení",
    settingsTitle: "Nastavení objektu {name}",
    settingsDescription: "Pouze provozní nastavení. Tato obrazovka nikdy nezobrazuje ani nemění přihlašovací údaje k PMS.",
    nameLabel: "Název objektu",
    namePlaceholder: "např. Hotel Ostrov",
    currencyLabel: "Měna",
    currencyWarning: "Změna měny přecení celý objekt: všechny uložené ceny se ve stejném kroku převedou do nové měny. Nejde o změnu popisku.",
    cadenceLabel: "Frekvence synchronizace",
    cadenceWarning: "Jak často Optivo načítá rezervace tohoto ubytování a posílá zpět ceny. Nastavit to lze pouze zde — dialog auto-syncu u ubytování frekvenci jen zobrazuje.",
    testPropertyLabel: "Testovací objekt",
    testPropertyHint: "Vlastní testovací objekt Optiva, ne zákazníkův. Jeho pokoje se nezapočítají do počtu ceněných pokojů na veřejné úvodní stránce. Nic dalšího se nemění: synchronizuje se, cení a účtuje jako dosud.",
    testBadge: "testovací objekt",
    // Předplatné účtu. Podle ceníku z 5. 10. 2026 platí každý hrazený objekt
    // cenu produktu, kterým je, a cenu za každý svůj pokoj; zákazník žádný
    // tarif nevolí. Blok proto ceník uvádí — ze stejné tabulky jako ten
    // veřejný — a drží dvě ceny, které nad jeho rámec dohodne podpora.
    subscriptionTitle: "Předplatné účtu",
    subscriptionScope: "Platí pro celý účet — pro všechny jeho objekty.",
    subscriptionListTitle: "Ceník, měsíčně",
    // Vedle vlastní ceny produktu: kolik přidá každý pokoj. {price} je cena
    // pokoje z ceníku.
    subscriptionPerRoom: "+ {price} za pokoj",
    // Pro koho je OPTIVO Rentals. {types} jsou názvy typů ubytování z průvodce;
    // hranici velikosti ceník nemá.
    subscriptionRentalsFor: "{types}, jakékoli velikosti",
    subscriptionListRule: "Každý hrazený objekt platí cenu podle toho, čím je, a za každý pokoj, pro který Optivo nastavuje ceny; zákazník žádný tarif nevolí. Objekt jiného typu nebo bez uvedeného typu platí cenu {hotels}.",
    // „Až“ je strop: frekvenci výše nastavuje podpora pro každý objekt a nic
    // ji nevynucuje, není tedy tarif, se kterým by se měla shodovat.
    subscriptionIncluded: "Obojí zahrnuje ceny až {count}× denně (frekvence synchronizace výše; nic ji nevynucuje), na {days} dní dopředu a analýzy strategie za čtvrtletí: {analyses}.",
    subscriptionNone: "Zatím bez předplatného — zákazník oddíl Platby a fakturace ještě neotevřel.",
    individualPriceLabel: "Individuální měsíční cena (CZK)",
    // Co ceník nenaceňuje, dohodne se zde: více analýz strategie, než kolik
    // čtvrtletí zahrnuje, nebo jakákoli jiná dohoda. Ceníkovou cenu má každý
    // účet, na toto pole už tedy nikdo nečeká.
    individualPriceHint: "Nahradí ceníkovou cenu všech hrazených objektů — pro více analýz strategie za čtvrtletí, než zahrnuje ceník, nebo jinou dohodu. Prázdné = ceník.",
    // Tržní data jsou v ceně obou produktů, takže není jaký trh určovat — jen
    // příplatek za rozšířená tržní data, účtovaný vlastní položkou.
    marketDataPriceLabel: "Market Data — měsíční příplatek (CZK)",
    marketDataPriceHint: "Tržní data jsou v ceně obou produktů. Příplatek dohodnutý za rozšířená tržní data se účtuje vlastní položkou navíc. Prázdné = žádný.",
    priceInvalid: "Zadejte částku v CZK alespoň 0, nejvýše na dvě desetinná místa — nebo pole nechte prázdné.",
    save: "Uložit změny",
    saving: "Ukládám...",
    cancel: "Zrušit",
    confirmCurrencyTitle: "Změnit měnu z {from} na {to}?",
    confirmCurrency: "Všechny ceny uložené pro objekt {name} se ve stejné transakci převedou na {to}. Provozovateli se ceny přepočítají. Odsud to nelze vrátit zpět.",
    confirmCurrencyAction: "Převést ceny",
    // Stav
    checkHealth: "Ověřit stav",
    checking: "Ověřuji...",
    recalculate: "Přepočítat ceny",
    recalculating: "Přepočítávám...",
    recalcConfirmTitle: "Přepočítat ceny pro {name}?",
    recalcConfirm: "Všechny ceny na rok a půl dopředu se znovu spočítají podle aktivní strategie tohoto objektu. Ceny, které provozovatel zamkl nebo zadal ručně, zůstanou. Nové ceny se do PMS dostanou při příští automatické synchronizaci — tímto se neodesílají.",
    recalcConfirmAction: "Přepočítat",
    toastRecalcStarted: "Přepočítávám ceny pro {name}",
    toastRecalcJoined: "Pro {name} už přepočet běžel",
    toastRecalcDone: "Ceny pro {name} přepočítány",
    toastRecalcFailed: "Přepočet se nezdařil",
    toastRecalcNoStrategy: "{name} nemá aktivní strategii, není tedy podle čeho počítat",
    // Znovuspuštění analýzy objektu
    refreshAnalysis: "Znovu spustit analýzu",
    refreshingAnalysis: "Analyzuji...",
    analysisConfirmTitle: "Znovu spustit analýzu pro {name}?",
    analysisConfirm: "Optivo znovu načte rok rezervací tohoto objektu z jeho PMS a znovu je změří — obvyklé předstihové okno, analyzovanou cenu podle typu pokoje, rozložení dnů v týdnu. Trvá to několik minut a znamená to dlouhou sérii dotazů na poskytovatele. Hodnoty, které zadal provozovatel, zůstanou; ostatní se přepíšou a ceny objektu se podle nich při dalším výpočtu posunou. Optivo to samo dělá jednou denně.",
    analysisConfirmAction: "Spustit analýzu",
    toastAnalysisStarted: "Načítám rok rezervací pro {name} — potrvá to několik minut",
    toastAnalysisJoined: "Pro {name} už analýza běžela",
    toastAnalysisDone: "Analýza pro {name} obnovena",
    toastAnalysisDoneCount: "Načteno {count} rezervací",
    toastAnalysisFailed: "Analýza se nezdařila",
    // Dialog přístupů
    manageAccess: "Přístup",
    accessTitle: "Kdo má přístup k objektu {name}",
    accessDescription: "Lidé, kteří mají přístup k objektu {name}: jeho vlastníci, členové, jejichž plný přístup se ho týká, a každý, kdo k němu má přístup jen pro čtení.",
    accessScopeWarning: "Počet objektů tohoto účtu: {count}. Zvolte přístup a potom, kterých objektů se týká.",
    accessScopeMemberThis: "Plný přístup se týká pouze objektu {name}: jeho cen, strategie a nastavení. Ostatní objekty účtu zahrnuty nejsou.",
    accessScopeMemberAll: "Plný přístup se týká všech {count} objektů tohoto účtu i těch, které účet přidá později.",
    accessScopeMemberSome: "Plný přístup se týká {count} zvolených objektů: jejich cen, strategií a nastavení.",
    accessScopeViewerSome: "Přístup jen pro čtení se týká {count} zvolených objektů: jejich cen a analýz, nikdy strategie ani nastavení.",
    accessScopeFrontDeskSome: "Přístup recepce se týká {count} zvolených objektů: jejich cen, rezervací a toho, proč je cena taková — nikdy analýz, tržeb, strategie ani nastavení.",
    accessScopeSingle: "Tento účet vlastní pouze tento objekt. Plný přístup se bude týkat i objektů, které účet přidá později; přístup jen pro čtení jen tohoto objektu.",
    accessScopeSingleFull: "Tento účet vlastní pouze tento objekt. Plný přístup se týká jeho i objektů, které účet přidá později.",
    roleOwner: "Vlastník",
    roleMember: "Člen",
    makeOwner: "Povýšit na vlastníka",
    makeMember: "Změnit na člena",
    removeMember: "Odebrat",
    addPlaceholder: "E-mail existujícího uživatele",
    add: "Udělit přístup",
    adding: "Uděluji...",
    noMembers: "K tomuto objektu nemá přístup nikdo.",
    userNotFound: "Uživatel s tímto e-mailem v Optivu neexistuje. Musí se nejprve jednou přihlásit.",
    confirmRemoveTitle: "Odebrat {email} z účtu {account}?",
    confirmRemove: "Ztratí plný přístup ke všem objektům tohoto účtu. Jeho vlastní data zůstanou nedotčena a lze ho kdykoli přidat zpět.",
    confirmRemovePropertyTitle: "Odebrat {email} z objektu {name}?",
    confirmRemoveOnlyThis: "Ztratí přístup k objektu {name}, jedinému objektu tohoto účtu, ke kterému přístup má. Jeho vlastní data zůstanou nedotčena a přístup mu lze později udělit znovu.",
    confirmStepDownTitle: "Změnit {email} na člena?",
    confirmStepDown: "Přístup si ponechá, ale nebude už moci zvát lidi, měnit fakturaci ani mazat objekty.",
    // Toasty
    toastSaved: "Nastavení uloženo",
    toastSaveFailed: "Nastavení se nepodařilo uložit",
    toastHealthy: "Připojení je v pořádku",
    toastUnhealthy: "Připojení je nedostupné",
    toastHealthFailed: "Ověření stavu selhalo",
    toastMemberAdded: "Přístup udělen",
    toastMemberAddFailed: "Přístup se nepodařilo udělit",
    toastRoleUpdated: "Role změněna",
    toastRoleUpdateFailed: "Roli se nepodařilo změnit",
    toastMemberRemoved: "Přístup odebrán",
    toastMemberRemoveFailed: "Přístup se nepodařilo odebrat",
    loadError: "Připojení se nepodařilo načíst",
    membersLoadError: "Lidi s přístupem k tomuto objektu se nepodařilo načíst",
    roleViewer: "Jen pro čtení",
    addRoleMember: "Plný přístup",
    addRoleViewer: "Jen pro čtení (ceny a analýzy)",
    accessScopeViewer: "Přístup jen pro čtení se týká pouze objektu {name}: jeho cen a analýz, nikdy strategie ani nastavení.",
    makeViewer: "Jen pro čtení",
    makeFullAccess: "Dát plný přístup",
    confirmMakeViewerTitle: "Nastavit {email} přístup jen pro čtení k objektu {name}?",
    confirmMakeViewer: "Ponechá si přístup jen pro čtení k tomuto objektu — ceny a analýzy, žádná strategie.",
    confirmMakeViewerAction: "Nastavit jen pro čtení",
    confirmFullAccessTitle: "Dát {email} plný přístup?",
    confirmFullAccess: "Bude moci měnit ceny, strategie a nastavení všech objektů, které tento účet vlastní, i těch, které přidá později.",
    confirmFullAccessThis: "Bude moci měnit ceny, strategii a nastavení objektu {name} i objektů, které účet přidá později.",
    confirmFullAccessAction: "Dát plný přístup",
    confirmRemoveViewer: "Ztratí přístup jen pro čtení k objektu {name}. Nic jiného se nemění a přístup mu lze později udělit znovu.",
    roleFrontDesk: "Recepce",
    addRoleFrontDesk: "Recepce (ceny a rezervace, bez analýz)",
    chooseAccess: "Zvolte, jaký přístup udělit",
    accessScopeFrontDesk: "Přístup recepce se týká pouze objektu {name}: jeho cen, rezervací a toho, proč je cena taková — nikdy analýz, tržeb, strategie ani nastavení.",
    makeFrontDesk: "Nastavit přístup recepce",
    makeAnalyticsViewer: "Povolit analýzy",
    confirmMakeFrontDeskTitle: "Nastavit {email} přístup recepce k objektu {name}?",
    confirmMakeFrontDesk: "Ponechá si přístup recepce k tomuto objektu — ceny a rezervace, žádné analýzy ani tržby.",
    confirmNarrowToFrontDesk: "Ponechá si rezervace tohoto objektu s jejich cenami a přestane vidět jeho analýzy (obsazenost, tržby a poslední rezervace), ostatní pohledy kalendáře cen, vývoj cen a historii nahrávání.",
    confirmMakeFrontDeskAction: "Nastavit přístup recepce",
    confirmWidenToAnalyticsTitle: "Umožnit {email} vidět analýzy objektu {name}?",
    confirmWidenToAnalytics: "Uvidí i analýzy tohoto objektu (obsazenost, tržby a poslední rezervace) a celou stránku Ceny: všechny pohledy kalendáře, vývoj cen a historii nahrávání.",
    confirmWidenToAnalyticsAction: "Povolit analýzy",
    toastAlreadyReadOnly: "Tento člověk už má k tomuto objektu přístup: {kind}. Změňte ho na jeho řádku.",
    toastFrontDeskNotGranted: "Přístup recepce se nepodařilo udělit: uživatel má teď přístup jen pro čtení včetně analýz. Pokud to tak být nemá, přístup odeberte.",
  },

  // ── Funkce (jen pro správce) ───────────────────────────
  // Co se zavádí po jednotlivých osobách a přepínač pro každou z nich.
  // Seznam funkcí je serveru: funkce, pro kterou tu stránka nemá záznam
  // pod `features`, se vypíše svým klíčem.
  adminFeatures: {
    searchPlaceholder: "E-mail nebo objekt",
    title: "Funkce",
    description: "Co se zavádí po jednotlivých osobách",
    intro: "Novou funkci nedostanou všichni zákazníci v den, kdy vyjde. Zapněte ji tady lidem, kteří ji mají mít, nejdřív sobě. Přepínač patří osobě: funkci má u každého objektu, který spravuje, a její kolegové u téhož objektu ji nemají, dokud ji nezapnete i jim. Komu ji nikdo nezapnul, ten ji má vypnutou.",
    loading: "Načítám osoby...",
    retry: "Zkusit znovu",
    empty: "Účet zatím nemá nikdo",
    loadError: "Funkce se nepodařilo načíst",
    personCount: {
      one: "{count} osoba",
      few: "{count} osoby",
      other: "{count} osob",
    },
    // Tabulka
    person: "Osoba",
    properties: "Spravované objekty",
    // Pod adresou osoby: kým je pro Optivo, a účet, který se nemůže přihlásit,
    // takže pro něj přepínač zatím nic nemění.
    administrator: "Správce Optiva",
    accountPending: "Účet čeká na schválení",
    accountDeactivated: "Účet je deaktivovaný",
    accountExpired: "Dočasný přístup skončil",
    // Stav účtu, pro který tato verze stránky nemá slova.
    accountUnknown: "Stav účtu: {status}",
    // Kam přepínač osoby dosáhne: objekty, které spravuje.
    noProperties: "Nespravuje žádný objekt",
    unnamedProperty: "Nepojmenovaný objekt",
    // Které osoby seznam ukazuje.
    scopeAll: "Všichni",
    scopeEnabled: "Se zapnutou funkcí",
    noneEnabled: "Nikdo zatím nemá zapnutou žádnou funkci",
    // Stav funkce u jedné osoby, vedle přepínače. Datum říká, kdy ji někdo
    // naposledy přepnul; osoba, o které nikdo nerozhodl, žádné nemá.
    on: "Zapnuto",
    off: "Vypnuto",
    onSince: "Zapnuto od {date}",
    offSince: "Vypnuto od {date}",
    // Název samotného přepínače: funkce a osoba, které se týká.
    switchLabel: "{feature} pro {name}",
    // Jak daleko zavádění došlo, přes všechny osoby a bez ohledu na hledání.
    enabledNowhere: "Není zapnuto nikomu",
    enabledCount: {
      one: "Zapnuto {count} osobě",
      few: "Zapnuto {count} osobám",
      other: "Zapnuto {count} osobám",
    },
    // Na zapnutí se stránka nejdřív zeptá: mění, co lidé zákazníka dostanou,
    // a to u každého objektu, který osoba spravuje.
    confirmTitle: "Zapnout {feature} pro {name}?",
    confirmReach: "Spravuje tyto objekty a funkci dostane u každého z nich:",
    confirmNoReach: "Zatím nespravuje žádný objekt, takže se pro ni nic nezmění, dokud nějaký spravovat nezačne.",
    confirmAction: "Zapnout",
    cancel: "Zrušit",
    toastOn: "{feature}: zapnuto pro {name}",
    toastOff: "{feature}: vypnuto pro {name}",
    toastFailed: "Funkci se nepodařilo přepnout",
    // Funkce, kterou server zavádí a kterou tato verze stránky neumí popsat.
    unknownFeature: "Tato verze stránky nemá popis této funkce. Načtěte stránku znovu a zobrazí se.",
    // Něco na serveru funkci zatím nedává nikomu a tato verze stránky pro to
    // nemá slova.
    holdUnknown: "Tento server ji teď nenabízí nikomu, takže ji zatím nedostane ani ten, komu ji zapnete.",
    features: {
      assistant: {
        name: "Chat s asistentem Optiva (AI)",
        // Záhlaví sloupce: krátké, řádek je úzký.
        short: "Asistent (AI)",
        description: "Nabídne osobě v okně „Zeptat se Optiva“ chat s asistentem Optiva (AI), a to u každého objektu, který spravuje. Když se zeptá, posílají se nastavení, ceny a počty rezervací objektu, který má právě otevřený, ke zpracování společnosti Anthropic. Kdo ho má vypnutý, neposílá nic.",
        holds: {
          not_configured: "Tento server nemá klíč k Anthropic API, takže asistent zatím není nabízen nikomu. Komu ho zapnete, ten ho dostane, až bude klíč nastaven.",
          switched_off: "Asistent je na tomto serveru vypnutý pro všechny (ASSISTANT=off). Komu ho zapnete, ten ho dostane, až bude vypnutí zrušeno.",
        },
      },
      mcp: {
        name: "AI agenti a bezpečnostní tokeny",
        // Záhlaví sloupce: krátké, řádek je úzký.
        short: "AI agenti",
        description: "Zobrazí osobě v nastavení účtu bezpečnostní tokeny a sekci AI agentů, takže se AI agent může k Optivu připojit tokenem, který si vytvoří. Token dosáhne na objekty, které osoba spravuje, a nikdy nesmí víc než ona. Vypnutím o tokeny nepřijde: po opětovném zapnutí fungují dál.",
        holds: {
          switched_off: "MCP server je na tomto serveru vypnutý pro všechny (MCP_SERVER=off). Komu funkci zapnete, může si vytvářet tokeny, a jeho agenti se připojí, až bude vypnutí zrušeno.",
        },
      },
      lighthouse: {
        name: "Ceny konkurence z vlastního účtu Lighthouse",
        // Záhlaví sloupce: krátké, řádek je úzký.
        short: "Lighthouse",
        description: "Nabídne osobě na kartě sledování konkurence u každého objektu, který spravuje, možnost číst ceny konkurence z vlastního účtu Lighthouse objektu místo z tržních dat Optiva. Vloží svůj API token Lighthouse a vybere hotel a sadu konkurentů. Vypnutím o propojení nepřijde: po opětovném zapnutí funguje dál.",
        holds: {
          switched_off: "Lighthouse je na tomto serveru vypnutý pro všechny (LIGHTHOUSE=off). Komu funkci zapnete, uvidí volbu, až bude vypnutí zrušeno.",
        },
      },
    },
  },

  // ── Záznam aktivit (jen pro správce) ───────────────────
  adminActivity: {
    searchPlaceholder: "Osoba, akce nebo objekt",
    title: "Záznam aktivit",
    description: "Co který uživatel udělal, napříč všemi účty",
    intro: "Každá změna, kterou přihlášený uživatel provede, se sem zapisuje v okamžiku, kdy k ní dojde — uložení nastavení, odeslání cen, udělení přístupu a přihlášení kolem nich — spolu s odpovědí, kterou server vrátil. Čtení se nezaznamenává a nezaznamenává se ani nic, co požadavek nesl: žádná hesla, žádné údaje o kartách.",
    loading: "Načítám aktivity...",
    retry: "Zkusit znovu",
    empty: "Zatím nebylo nic zaznamenáno",
    entryCount: "{count} záznamů",
    retentionNote: "Záznamy se po {days} dnech automaticky mažou.",
    retentionNoteOne: "Záznamy se po 1 dni automaticky mažou.",
    loadError: "Nepodařilo se načíst záznam aktivit",
    // Tabulka
    when: "Kdy",
    who: "Kdo",
    what: "Co",
    outcome: "Výsledek",
    filterByUser: "Zobrazit jen {name}",
    propertyById: "Objekt #{id}",
    openedByStaff: "Otevřeno jen pro čtení, jako správce",
    viaMcpToken: "AI agentem, s tokenem „{name}“",
    changesNone: "Beze změn",
    changesOne: "1 změna",
    changesFew: "{count} změny",
    changesMany: "{count} změn",
    changeCreated: "vytvořena",
    changeDeleted: "smazána",
    changeVersioned: "vytvořena jako verze {version}, oproti verzi {from}",
    changesTruncated: "+{count} dalších, nezaznamenáno",
    outcomeOk: "OK",
    outcomeRefused: "Odmítnuto",
    outcomeFailed: "Selhalo",
    actions: {
      signedIn: "Přihlášení",
      signedOut: "Odhlášení",
      wireTransferSettledAdmin: "Potvrzení bankovního převodu (správce)",
      wireTransferRejectedAdmin: "Zamítnutí bankovního převodu (správce)",
      paymentRefundRecordedAdmin: "Zápis vrácení platby (správce)",
      connectionSettingsChangedAdmin: "Změna názvu nebo měny objektu (správce)",
      syncCadenceChangedAdmin: "Změna četnosti synchronizace (správce)",
      userFeatureSwitchedAdmin: "Funkce zapnuta nebo vypnuta pro osobu (správce)",
      individualPricingChangedAdmin: "Změna individuálních cen (správce)",
      recalculationForcedAdmin: "Vynucený přepočet cen (správce)",
      analysisStartedAdmin: "Znovuspuštění analýzy objektu (správce)",
      accessGrantedAdmin: "Udělení přístupu (správce)",
      memberRoleChangedAdmin: "Změna role člena (správce)",
      accessRemovedAdmin: "Odebrání přístupu (správce)",
      pricePlanDeletedAdmin: "Smazání cenového plánu (správce)",
      pricePlansPurgedAdmin: "Hromadné smazání cenových plánů (správce)",
      userRoleChangedAdmin: "Změna role uživatele (správce)",
      userFeatureChangedAdmin: "Přepnutí funkce uživatele (správce)",
      userActiveChangedAdmin: "Aktivace nebo deaktivace uživatele (správce)",
      temporaryAccessCreatedAdmin: "Vytvoření dočasného přístupu (správce)",
      userDeletedAdmin: "Smazání účtu (správce)",
      passwordResetLinkIssuedAdmin: "Vygenerování odkazu pro obnovu hesla (správce)",
      strategiesSaved: "Uložení strategií",
      autoSyncChanged: "Změna nastavení automatické synchronizace",
      promoCodeRedeemed: "Uplatnění promo kódu",
      subscriptionChanged: "Změna nastavení předplatného",
      paymentOpened: "Založení platby",
      paymentRefreshed: "Opětovné ověření stavu platby",
      billingDetailsChanged: "Změna fakturačních údajů",
      recurringCancelled: "Zrušení automatického obnovení",
      connectionSettingsSaved: "Uložení nastavení objektu",
      distributionSettingsSaved: "Uložení nastavení distribuce",
      ratesWrittenToPms: "Zápis cen do PMS",
      exchangeRatesRefreshed: "Obnovení směnných kurzů",
      exchangeRatesOverridden: "Ruční nastavení směnných kurzů",
      exchangeRateOverridesCleared: "Zrušení ručně nastavených kurzů",
      marketLocationSaved: "Uložení polohy trhu",
      propertyFactsSaved: "Uložení typu objektu nebo údajů o cenách",
      compsetFilterSaved: "Uložení filtru konkurence",
      compsetRefreshed: "Obnovení cen konkurence",
      marketSignalsRefreshed: "Obnovení tržních signálů",
      competitorPinned: "Připnutí konkurenta",
      pinnedCompetitorChanged: "Změna připnutého konkurenta",
      competitorUnpinned: "Odepnutí konkurenta",
      competitorSearchRun: "Vyhledání konkurence",
      lighthouseLinked: "Propojení účtu Lighthouse",
      lighthouseUnlinked: "Odpojení účtu Lighthouse",
      compsetSourceChanged: "Změna zdroje cen konkurence",
      lighthouseRefreshed: "Načtení cen konkurence z Lighthouse",
      allNotificationsRead: "Označení všech oznámení jako přečtených",
      notificationRead: "Označení oznámení jako přečteného",
      aiSettingsSavedAdmin: "Uložení nastavení AI (správce)",
      aiModelPullStartedAdmin: "Spuštění stahování modelu AI (správce)",
      wizardStarted: "Spuštění průvodce nastavením",
      wizardReset: "Vynulování průvodce nastavením",
      wizardStepSaved: "Uložení kroku průvodce nastavením",
      analysisLinked: "Propojení analýzy s nastavením",
      wizardCompleted: "Dokončení průvodce nastavením",
      connectionCreated: "Vytvoření připojení objektu",
      connectionChanged: "Změna připojení objektu",
      connectionDeleted: "Smazání připojení objektu",
      activePropertySwitched: "Přepnutí aktivního objektu",
      pmsCredentialsTested: "Ověření přihlašovacích údajů PMS",
      accessGranted: "Udělení přístupu",
      memberRoleChanged: "Změna role člena",
      accessRemoved: "Odebrání přístupu",
      pmsCredentialsSaved: "Uložení přihlašovacích údajů PMS",
      priceListBackedUp: "Zálohování ceníku",
      priceListRestoredFromFile: "Obnovení ceníku ze souboru",
      priceListBackupRestored: "Obnovení ceníku ze zálohy",
      pricePlanEntriesEdited: "Úprava položek cenového plánu",
      pricesBulkEdited: "Hromadná úprava cen",
      priceAdjustmentSet: "Úprava cen bez zamčení nastavena",
      priceAdjustmentRemoved: "Úprava cen bez zamčení odebrána",
      apiTokenCreated: "Vytvoření bezpečnostního tokenu",
      apiTokenRevoked: "Odvolání bezpečnostního tokenu",
      mcpSettingsChanged: "Změna přístupu AI agentů",
      termsAccepted: "Přijetí obchodních podmínek",
      mcpPriceEditApplied: "Úprava cen AI agentem",
      mcpPriceAdjustmentSet: "Úprava cen bez zamčení nastavena AI agentem",
      mcpPriceAdjustmentRemoved: "Úprava cen bez zamčení odebrána AI agentem",
      priceGenerationStarted: "Spuštění výpočtu cen",
      priceGenerationStatusCleared: "Vymazání stavu výpočtu cen",
      bookingSyncTriggered: "Spuštění synchronizace rezervací",
      priceUploadStarted: "Spuštění nahrání cen do PMS",
      priceUploadResumed: "Pokračování v nahrávání cen",
      priceUploadStatusCleared: "Vymazání stavu nahrávání cen",
      analysisStarted: "Spuštění analýzy objektu",
      analysisCleared: "Vymazání analýzy objektu",
      bookingsClassified: "Spuštění klasifikace rezervací",
      roomKindStructureSaved: "Uložení struktury typů pokojů",
      roomKindOverrideRemoved: "Odebrání úpravy typu pokoje",
      segmentChannelOverridesSaved: "Uložení úprav kanálů a segmentů",
      strategyHelpRequested: "Žádost o pomoc se strategií",
      assistantHandedOver: "Konverzace s asistentem předána podpoře",
      helpRequestCancelled: "Stažení žádosti o pomoc",
      strategyProposalActivated: "Aktivace návrhu strategie",
      strategyProposalDeclined: "Odmítnutí návrhu strategie",
      strategyProposalSentAdmin: "Odeslání návrhu strategie (správce)",
      strategyProposalResentAdmin: "Opětovné odeslání návrhu strategie e-mailem (správce)",
      helpRequestOpenedAdmin: "Založení případu za zákazníka (správce)",
      helpRequestClosedAdmin: "Uzavření žádosti o pomoc (správce)",
      helpRequestRepliedAdmin: "Odeslání odpovědi na žádost o pomoc (správce)",
      supportReminderCreatedAdmin: "Nastavení připomínky podpory (správce)",
      supportReminderChangedAdmin: "Úprava připomínky podpory (správce)",
      supportReminderDoneAdmin: "Vyřízení připomínky podpory (správce)",
      supportReminderReopenedAdmin: "Znovuotevření připomínky podpory (správce)",
      supportReminderDeletedAdmin: "Smazání připomínky podpory (správce)",
      supportReminderCaseOpenedAdmin: "Založení případu k připomínce podpory (správce)",
    },
  },

  // ── Správa: pomoc se strategií ─────────────────────────
  adminStrategyHelp: {
    title: "Pomoc se strategií",
    description: "Dotazy a žádosti o analýzu strategie — odeslané z aplikace nebo založené podporou za zákazníka — odpovědi a navržená nastavení a připomínky toho, co podpora slíbila na později.",
    intro: "Na případ odpovězte dopisem z Optiva — samotným, nebo jako návrh: nastavení, se kterým počítal replay analýzy, shrnutí pro stránku s návrhem a PDF a obrázky analýzy. Návrh provozovatel aktivuje jedním klepnutím; do té doby se nic nezmění. Případ, na který jste odpověděli jinak, uzavřete.",
    loading: "Načítám žádosti…",
    retry: "Zkusit znovu",
    searchPlaceholder: "Hledat případ, ubytování, e-mail nebo zprávu…",
    scopeOpen: "Čekající",
    scopeAll: "Vše",
    openCount: "Čeká: {count}",
    noneWaiting: "Na odpověď nic nečeká.",
    noneAtAll: "Zatím žádné žádosti.",
    caseNumber: "Případ",
    requested: "Vyžádáno",
    property: "Ubytování",
    from: "Od",
    request: "Žádost",
    status: "Stav",
    actions: "Akce",
    originSupport: "Založila podpora",
    originFollowUp: "Navazující analýza",
    originRevenueReview: "Měsíční kontrola tržeb",
    followUpConfirmedFrom: "Potvrzena při aktivaci návrhu z {case}, {date}.",
    followUpConfirmedNoCase: "Potvrzena při aktivaci návrhu, {date}.",
    kindQuestion: "Dotaz",
    kindStudy: "Analýza strategie",
    kindSetup: "Nastavení od Optiva",
    noMessage: "Bez zprávy",
    languageCs: "česky",
    languageEn: "anglicky",
    answerIn: "Odpovědět {language}",
    requestCopies: "Kopie: {emails}",
    statusOpen: "Čeká",
    statusClosed: "Uzavřeno",
    statusAnswered: "Zodpovězeno",
    statusProposalPending: "Návrh odeslán",
    statusProposalUnsent: "Návrh uložen, neodeslán",
    statusProposalAccepted: "Aktivováno",
    statusProposalDeclined: "Odmítnuto",
    statusProposalSuperseded: "Nahrazeno",
    pendingSince: "Čeká na aktivaci od {date}",
    followUpDue: "Navazující analýza připadá na {date}",
    followUpOpenedCase: "Navazující analýza otevřena: {case}",
    followUpOpened: "Navazující analýza otevřena {date}",
    followUpCancelledAt: "Navazující analýza zrušena {date}",
    followUpCancelledByNewerAt: "Navazující analýzu zrušila novější aktivace, {date}",
    followUpNotWanted: "Navazující analýzu nechtěli",
    statusReplySent: "Odpověď odeslána",
    statusReplyUnsent: "Odpověď uložena, neodeslána",
    answer: "Odpovědět",
    sendProposal: "Poslat návrh",
    closeRequest: "Uzavřít",
    closeTitle: "Uzavřít tuto žádost?",
    closeBody: "Uzavřete ji, když jste odpověděli jinak — e-mailem nebo po telefonu. Pro provozovatele se nic nezmění.",
    closed: "Žádost uzavřena",
    closeFailed: "Žádost se nepodařilo uzavřít",
    copyLink: "Zkopírovat odkaz na návrh",
    linkCopied: "Odkaz zkopírován",
    resend: "Poslat návrh e-mailem znovu",
    resent: "Návrh odeslán e-mailem",
    resendFailed: "Návrh se nepodařilo odeslat e-mailem",
    resendReply: "Poslat odpověď e-mailem znovu",
    replyResent: "Odpověď odeslána e-mailem",
    replyResendFailed: "Odpověď se nepodařilo odeslat e-mailem",
    resendWithoutAccessTitle: "Poslat e-mailem znovu i na adresu bez přístupu?",
    resendAnyway: "Přesto poslat e-mailem",
    dialogTitle: "Navrhnout nastavení",
    dialogDescription: "Pro {property}. Provozovatel uvidí každé nastavení dřív, než ho aktivuje.",
    answerTitle: "Odpovědět na případ",
    answerDescription: "Pro {property}. Dopis odejde z Optiva. S nastavením je to návrh, který provozovatel prohlédne a aktivuje jedním klepnutím; bez něj jen odpověď.",
    questionTitle: "Dotaz, na který odpovídáte",
    questionAskedBy: "{email}, {date}",
    questionQuoted: "E-mail ho pod podpisem zopakuje: kdo se ptal, kdy a jakými slovy. V dopise ho tedy opakovat nemusíte.",
    questionHandedOver: "Konverzace s asistentem Optiva (AI), předaná bez poznámky. Přečtete si ji v e-mailu, který o případu dostala podpora.",
    questionCreditedHandedOver: "E-mail pod podpisem uvede jen to, kdo a kdy konverzaci předal, a nic z ní necituje. V dopise proto napište, na co odpovídáte.",
    questionFollowUp: "Navazující analýza, potvrzená při aktivaci návrhu z {case}.",
    questionFollowUpNoCase: "Navazující analýza, potvrzená při aktivaci návrhu.",
    questionCreditedFollowUp: "E-mail pod podpisem jednou větou uvede, kdo ji potvrdil a kdy.",
    importTitle: "Načíst z analýzy",
    importHint: "Přetáhněte sem složku případu nebo její složku deliver, nebo vyberte její soubory — u odpovědi reply.txt a obrázky, které ukazuje. Vyplní nastavení, shrnutí, dopis s příjemcem a předmětem, PDF i obrázky a návrh rovnou zkontroluje.",
    importChoose: "Vybrat soubory",
    importReading: "Načítám…",
    importLoaded: "Načteno z analýzy",
    importPatch: "Nastavení",
    importSteps: "Kroky",
    importSummary: "Shrnutí",
    importLetter: "Dopis",
    importStudy: "Analýza",
    importFigures: "Obrázky",
    importNothing: "Žádný z těchto souborů do návrhu nepatří.",
    importFailed: "Soubory se nepodařilo načíst.",
    importSeveral: "{files}: víc souborů pro jedno pole, proto se nenačetl žádný.",
    importFigureNotShown: "{file} vynechán: dopis ho nikde neukazuje.",
    importAttachmentMismatch: "Dopis jmenuje přílohu {named}, ale PDF je {file}.",
    importUnused: "Nepoužito: {files}.",
    importStepsInvalid: "{file}: kroky nejde poslat tak, jak jsou — viz pod Kroky.",
    importFollowUpInvalid: "{file} uvádí termín navazující analýzy {named}, který nejde nabídnout (termín je {min} až {max} dní): žádný termín nebyl zvolen.",
    importCaseMismatch: "Tyto soubory odpovídají na případ {fileCase}, ale tento návrh na {case}. Nic se nenačetlo: pošlete je z případu {fileCase} v seznamu.",
    importCaseElsewhere: "Tyto soubory odpovídají na případ {fileCase}. Nic se nenačetlo: pošlete je z případu {fileCase} v Pomoci se strategií, ať odpověď zůstane u svého případu.",
    connectionLabel: "Ubytování (id připojení)",
    recipient: "Příjemce",
    copies: "Kopie",
    copiesHint: "Kolegové, kterým má provozovatel odpověď poslat v kopii, oddělení čárkou. Řádek Kopie: v dopise je nahradí.",
    recipientsWithoutAccess: "Bez plného přístupu k tomuto ubytování: {emails}. V Optivu takový účet odpověď neotevře, dopis mu ale e-mailem přijde i s přílohami. Pokračujte jen tehdy, je-li to záměr.",
    language: "Jazyk",
    patchLabel: "Nastavení — JSON merge patch nad parametry strategie",
    patchHint: "Soubor PATCH z replaye analýzy, jak je: zlomky, ne procenta; null klíč odebere.",
    patchOptionalHint: "Nepovinné — bez nastavení odejde jen dopis. Soubor PATCH z replaye analýzy, jak je: zlomky, ne procenta; null klíč odebere.",
    loadJson: "Načíst soubor JSON",
    patchInvalidJson: "Toto není platný JSON.",
    stepsLabel: "Kroky — proč se která změna dělá a co sleduje",
    stepsHint: "Seznam kroků z kroky.json analýzy: každý s nadpisem, proč, co sleduje a nastavením, které mění — nebo kde ho provozovatel udělá sám. Provozovatel je čte na stránce s návrhem a znovu při aktivaci.",
    stepsInvalidJson: "Tyto kroky nejsou platný JSON.",
    loadSteps: "Načíst kroky.json",
    stepsPreviewHint: "Jak je ukáže stránka s návrhem:",
    followUpLabel: "Navazující analýza",
    followUpNone: "Žádná",
    followUpDays: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
    followUpHint: "Nabídne se provozovateli při aktivaci: potvrdí ji a její případ se otevře sám, až přijde termín.",
    followUpFileDiffers: "Soubor kroků analýzy uvádí {days}: dopis i PDF mluví o tomto termínu.",
    followUpFileNone: "Soubor kroků analýzy navazující analýzu nenabízí: dopis ani PDF žádný termín neuvádějí.",
    stepsUnchecked: "Před odesláním návrh zkontrolujte: kroky a termín dostane jen server, který je čte.",
    stepsNotRead: "Tento server kroky ani termín navazující analýzy zatím nečte: odeslaný návrh by k provozovateli dorazil bez nich. Pro odeslání obojí vymažte, nebo návrh uložte bez e-mailu.",
    uncoveredKeys: "Tato nastavení nejmenuje žádný krok: {keys}.",
    stepsNotAList: "Kroky nejsou seznam.",
    stepsTooMany: "Nejvýše {max} kroků.",
    stepNotAnObject: "Krok {step} není objekt.",
    stepUnknownField: "Krok {step}: {field} není pole kroku (title, why, aim, settings, where).",
    stepTextInvalid: "Krok {step}: {field} chybí, nebo to není text.",
    stepTextTooLong: "Krok {step}: {field} je delší než {max} znaků.",
    stepSettingsInvalid: "Krok {step}: settings je seznam nejvýše {max} cest, každá nejvýše o {chars} znacích.",
    stepSettingNotInPatch: "Krok {step} jmenuje nastavení, která návrh nemění, nebo položku seznamu podle pořadí či podle pole uvnitř ní: {paths}.",
    stepWithoutSettingsOrWhere: "Krok {step} nejmenuje žádné nastavení ani místo (where), kde ho udělat ručně.",
    followUpDaysInvalid: "Termín navazující analýzy je {min} až {max} dní.",
    stepProblemOther: "Kroky nejde poslat tak, jak jsou.",
    summaryLabel: "Shrnutí na stránce s návrhem",
    summaryHint: "Dvě až tři věty v jazyce provozovatele: co se mění a proč.",
    sendFromOptivo: "Poslat e-mailem z Optiva",
    sendFromOptivoHint: "Vypnuto: návrh se uloží a dostanete odkaz, který pošlete sami.",
    subject: "Předmět",
    message: "Zpráva",
    messageHint: "Vložte email.txt z analýzy nebo reply.txt z odpovědi — řádky Komu a Předmět vyplní pole nahoře a číslo případu na konci předmětu musí být toto. Řádek [obrázek 1: soubor.png] ukáže obrázek; s nastavením určí [tlačítko] místo tlačítka, jinak zprávu uzavře.",
    study: "Analýza (PDF)",
    figures: "Obrázky (PNG nebo JPEG)",
    removeFile: "Odebrat {name}",
    check: "Zkontrolovat",
    checking: "Kontroluji…",
    send: "Poslat návrh",
    store: "Uložit návrh",
    sendReply: "Poslat odpověď",
    sending: "Odesílám…",
    changesAgainst: "Změny oproti {strategy} (verze {version})",
    noChanges: "Strategie už všechna tato nastavení má.",
    noActiveStrategy: "Ubytování nemá aktivní strategii.",
    replyCheck: "Bez nastavení: dopis odejde jako odpověď, bez tlačítka.",
    unknownKeys: "Tato nastavení Optivo nezná: {keys}. Zkontrolujte pravopis.",
    unreadableDates: "Tato zvláštní data engine nepřečte: {patterns}.",
    problemEmpty: "Zpráva je prázdná.",
    problemHeaders: "Zpráva pořád začíná řádky Komu / Předmět.",
    problemPlaceholder: "Ve zprávě zbývá vyplnit [DOPLNIT …].",
    problemFigureMissing: "Zpráva ukazuje {file}, ale takový obrázek není přiložený.",
    problemFigureUnplaced: "{file} je přiložený, ale zpráva ho nikde neukazuje.",
    problemButtonWithoutSettings: "Zpráva určuje místo tlačítka [tlačítko], ale bez nastavení nemá tlačítko co otevřít.",
    resultSent: "Návrh odeslán e-mailem na {email}.",
    resultSentCopies: "Návrh odeslán e-mailem na {email}, v kopii {copies}.",
    resultStored: "Návrh uložen. Tento odkaz pošlete sami:",
    resultStepsDropped: "Tento server návrh uložil bez kroků a termínu navazující analýzy: provozovatel neuvidí ani jedno.",
    resultEmailFailed: "Návrh je uložený, ale e-mail selhal ({error}). Pošlete ho znovu ze seznamu, nebo tento odkaz pošlete sami:",
    resultSkipped: "Návrh je uložený, ale tento server e-maily neposílá. Tento odkaz pošlete sami:",
    replyResultSent: "Odpověď odeslána e-mailem na {email}.",
    replyResultSentCopies: "Odpověď odeslána e-mailem na {email}, v kopii {copies}.",
    replyResultEmailFailed: "Odpověď je uložená, ale e-mail selhal ({error}). Pošlete ji znovu ze seznamu.",
    replyResultSkipped: "Odpověď je uložená, ale tento server e-maily neposílá.",
    copy: "Kopírovat",
    done: "Hotovo",
    failed: "Návrh se neuložil",
    replyFailed: "Odpověď se neodeslala",
    remind: "Nastavit připomínku",
    remindersDue: "Připomínky na řadě: {count}",
    scopeDue: "Připomínka na řadě",
    noneDue: "Žádná připomínka není na řadě.",
    caselessTitle: "Připomínky bez případu ({count})",
    caselessIntro: "Každá byla nastavena jen na ubytování. Založte k ní případ, aby odpověď — dopis nebo doporučené nastavení — šla přes Optivo; připomínka se na případ přesune.",
    newCase: "Nový případ",
    newCaseTitle: "Založit případ za zákazníka",
    newCaseDescription: "Dotaz nebo analýza strategie, o kterou zákazník požádal telefonem nebo e-mailem. Odpovídá se na něj tady jako na každý jiný případ; zákazník z něj nic neuvidí, dokud mu nepřijde odpověď.",
    newCasePropertyHint: "Ubytování, kterého se případ týká.",
    newCaseKindLabel: "Oč zákazník požádal",
    newCaseMessageLabel: "Na co se zákazník ptal",
    newCaseMessageHintQuestion: "Vlastními slovy, pro frontu. Odpověď ho necituje.",
    newCaseMessageHintStudy: "U analýzy nepovinné: na co se má zákazník podívat. Odpověď ho necituje.",
    newCaseRecipientLabel: "Odpověď půjde na",
    newCaseRecipientHint: "Obvykle ten, kdo se ptal. Při psaní se nabízejí lidé, kteří ubytování spravují.",
    newCaseCopiesLabel: "Kopii odpovědi poslat na",
    newCaseCopiesHint: "Nejvýš 5 adres oddělených čárkou. Nepovinné.",
    newCaseLanguageLabel: "Odpovědět",
    newCaseFollowUp: "Vrátit se k němu později",
    newCaseFollowUpHint: "Nastaví na případu připomínku: ten den dostane support@ jeden e-mail s tím, co bylo slíbeno.",
    newCaseSave: "Založit případ",
    newCaseSaving: "Zakládám…",
    newCaseOpened: "Případ {case} založen",
    newCaseOpenedWithFollowUp: "Případ {case} založen, připomínka na {date}",
    newCaseFailed: "Případ se nepodařilo založit.",
    newCaseForReminderTitle: "Založit případ k připomínce",
    newCaseForReminderDescription: "Aby odpověď — dopis nebo doporučené nastavení — šla přes Optivo z případu. Připomínka se na něj přesune, její den i slib zůstanou.",
    newCaseForReminderFor: "{property}, připomínka na {date}:",
    newCaseOpenedForReminder: "Případ {case} založen, připomínka je na něm",
    newCaseErrorProperty: "Vyberte ubytování.",
    newCaseErrorMessage: "Napište, na co se zákazník ptal.",
    newCaseErrorMessageTooLong: "Zpráva může mít nejvýš 4 000 znaků.",
    newCaseErrorRecipient: "Zadejte e-mail, na který půjde odpověď.",
    newCaseErrorCopies: "Každá kopie musí být e-mailová adresa, nejvýš 5.",
    propertyById: "Ubytování č. {id}",
  },

  // ── Správa: připomínky podpory ─────────────────────────
  adminSupportReminders: {
    propertyById: "Ubytování č. {id}",
    mailThread: "Vlákno {ref}",
    recordedBy: "{email}, {date}",
    stateOverdue: "Po termínu",
    stateToday: "Dnes",
    stateUpcoming: "Čeká",
    listLabel: "Připomínky",
    followUpOn: "Připomínka",
    mailSent: "Odesláno {date}",
    mailSkipped: "Neodesláno: {reason}",
    mailSending: "Odesílá se…",
    mailUnconfirmed: "Nepotvrzeno, odejde znovu při dalším běhu",
    mailUnconfirmedLast: "Poslední pokus se nepotvrdil; znovu neodejde",
    mailDoneUnconfirmed: "Nepotvrzeno; znovu neodejde, vyřízeno",
    mailRetrying: "Selhalo ({reason}), zkouší se každou hodinu — pokus {attempts} z {max}",
    mailGaveUp: "Vzdáno po {attempts} pokusech: {reason}",
    mailDue: "Na řadě, zatím neodesláno",
    mailUpcoming: "Odejde v den termínu",
    mailDoneUnsent: "Neodesláno: vyřízeno dřív, než odešlo",
    mailDoneFailed: "Selhalo ({reason}); znovu se nezkouší, vyřízeno",
    mailUnknown: "Stav, který tato verze stránky nezná; načtěte ji znovu",
    mailReasonNoTransport: "na tomto serveru není nastavené odesílání e-mailů",
    mailReasonNotDelivered: "e-mailová služba ho neodeslala",
    mailReasonLeaseLapsed: "poslední pokus se nepotvrdil",
    noteMore: "Zobrazit celou poznámku",
    noteLess: "Zobrazit méně",
    markDone: "Vyřízeno",
    edit: "Upravit",
    delete: "Smazat",
    deleteTitle: "Smazat tuto připomínku?",
    deleteBody: "Smaže se natrvalo a v její den nepřijde žádný e-mail.",
    markedDone: "Připomínka je vyřízená",
    deleted: "Připomínka je smazaná",
    markDoneFailed: "Připomínku se nepodařilo označit jako vyřízenou",
    deleteFailed: "Připomínku se nepodařilo smazat",
    createTitle: "Nová připomínka",
    createDescription: "Co podpora slíbila a na který den. V ten den přijde na support@ jeden e-mail s touto připomínkou.",
    createForCase: "K případu {case} · {property}.",
    editTitle: "Upravit připomínku",
    editDescription: "Při změně dne přijde e-mail znovu v nový den.",
    editFor: "Týká se: {subject}.",
    propertyLabel: "Ubytování",
    propertyPlaceholder: "Vyberte ubytování",
    propertyLoading: "Načítám ubytování…",
    propertyLoadFailed: "Ubytování se nepodařilo načíst.",
    propertyNotLoaded: "Nenačteno",
    propertyRetry: "Načíst znovu",
    propertySearchLabel: "Najít ubytování",
    propertySearchPlaceholder: "Část jeho názvu nebo názvu účtu",
    propertiesCapped: "Zobrazeno prvních {shown} z {total} ubytování. Jiné najdete napsáním části názvu.",
    propertyNoMatch: "Žádné ubytování neodpovídá.",
    dueLabel: "Termín",
    dueHint: "Den podpory podle pražského kalendáře.",
    noteLabel: "Co bylo slíbeno",
    noteHint: "Anglicky, jak to v ten den bude číst support@.",
    noteCount: "{count} / {max} znaků",
    mailRefLabel: "E-mailové vlákno",
    mailRefPlaceholder: "support:114",
    mailRefHint: "Nepovinné: schránka a zpráva, ve které odpovědět.",
    save: "Uložit",
    saving: "Ukládám…",
    created: "Připomínka nastavena na {date}",
    saved: "Připomínka uložena",
    failed: "Připomínku se nepodařilo uložit",
    errorDueInvalid: "Vyberte den, kdy je na řadě.",
    errorDuePast: "Ten den už uplynul. Vyberte dnešek nebo pozdější den.",
    errorNoteRequired: "Napište, co bylo slíbeno.",
    errorNoteTooLong: "Vejděte se do 1000 znaků.",
    errorMailRefInvalid: "Vlákno je schránka a číslo, například support:114.",
    errorCaseNotFound: "Případ s tímto číslem neexistuje.",
    errorPropertyNotFound: "Toto ubytování už neexistuje.",
    errorCaseMismatch: "Tento případ patří k jinému ubytování.",
    errorNotFound: "Tato připomínka už neexistuje.",
    errorDone: "Tato připomínka je vyřízená. Chcete-li ji změnit, otevřete ji znovu.",
    errorAlreadyDone: "Tato připomínka už byla vyřízená.",
    errorNotDone: "Tato připomínka je stále otevřená.",
    errorHasCase: "Tato připomínka už na případu je.",
    openCase: "Založit případ",
  },

  // ── Account Deactivated ────────────────────────────────
  accountDeactivated: {
    title: "Účet deaktivován",
    message: "Váš účet byl deaktivován administrátorem. Pro pomoc kontaktujte svého administrátora.",
    signOut: "Odhlásit se",
    signInWithDifferentAccount: "Přihlásit se pod jiným účtem",
  },

  // ── Temporary Access Ended ─────────────────────────────
  accountExpired: {
    title: "Dočasný přístup skončil",
    message: "Dočasný přístup k tomuto účtu skončil. Pokud ho potřebujete prodloužit, obraťte se na administrátora.",
    signInWithDifferentAccount: "Přihlásit se pod jiným účtem",
  },

  // ── Account Pending Admin Approval ─────────────────────
  termsConsent: {
    title: "Obchodní podmínky Optiva",
    intro: "Abyste mohli Optivo používat, potvrďte firmu, za kterou jednáte, a přijměte všeobecné obchodní podmínky. Uživatele, který se zaregistroval dříve, vážou ode dne, kdy je zde přijme.",
    pendingActivation: "Po přijetí podmínek váš účet ještě zkontrolujeme a aktivujeme. Do té doby Optivo používat nemůžete. Jakmile bude účet aktivní, dáme vám vědět e-mailem.",
    signedInAs: "Přihlášeno jako {email}",
    companyIdLabel: "IČO firmy, za kterou jednáte",
    companyIdHint: "Osm číslic, jak je uvádí obchodní nebo živnostenský rejstřík.",
    companyIdInvalid: "Toto IČO neodpovídá své kontrolní číslici. Zkontrolujte ho prosím.",
    companyIdPrefilled: "Vyplnili jsme IČO, které jste uvedli minule.",
    business: "Prohlašuji, že smlouvu uzavírám v rámci své podnikatelské činnosti.",
    terms: "Potvrzuji, že znám všeobecné obchodní podmínky a jejich přílohy, a přijímám je.",
    documentsLabel: "Přečtěte si je zde (každý se otevře na nové kartě):",
    documentTerms: "Všeobecné obchodní podmínky",
    documentSpecification: "Specifikace služby (příloha č. 1)",
    documentDpa: "Smlouva o zpracování osobních údajů (příloha č. 2)",
    documentPriceList: "Ceník (příloha č. 3)",
    expressHeading: "Výslovně přijímám tato ujednání obchodních podmínek:",
    serviceNature: "o povaze služby: Optivo je nástroj, nikoli poradce, a o cenách rozhoduji já",
    pricePush: "o propisu cen do rezervačního systému",
    automatedTools: "o využití automatizovaných nástrojů a umělé inteligence",
    anonymisedData: "o užití anonymizovaných dat",
    knowHowPenalty: "o ochraně služby a know-how včetně smluvní pokuty",
    liability: "o náhradě újmy a jejím omezení",
    articleLink: "čl. {article}",
    allRequired: "Pro pokračování potvrďte všechny body.",
    submit: "Přijmout a pokračovat",
    submitting: "Ukládám…",
    signOut: "Odhlásit se",
    versionChanged: "Obchodní podmínky se mezitím změnily. Načtěte stránku znovu a přečtěte si aktuální znění.",
    reload: "Načíst znovu",
    failed: "Přijetí se nepodařilo uložit. Zkuste to prosím znovu.",
  },
  accountPendingApproval: {
    title: "Čeká se na schválení administrátorem",
    message: "Děkujeme za registraci! Váš účet čeká na schválení administrátorem. Jakmile bude váš účet aktivován, budete se moci přihlásit. Dáme vám vědět e-mailem.",
    signOut: "Odhlásit se",
    signInWithDifferentAccount: "Přihlásit se pod jiným účtem",
  },

  // ── Otevřený je jiný objekt ────────────────────────────
  // Otevřený objekt sdílejí všechny karty, okna i zařízení uživatele a stránka
  // si ho načte jednou. `title` a `description` zazní, když server odmítne
  // uložení z karty nastavení nebo strategie, protože se stránka načetla pro
  // jiný objekt, než je otevřený teď; stránka už nemusí existovat, když odpověď
  // dorazí. `pageDescription` a `sendTitle` říkají totéž o změnách a odeslání
  // ze stránky Ceny. Zbytek je upozornění, které aplikace ukáže místo každé
  // stránky, jakmile zjistí, že je otevřený jiný objekt
  // (`components/pms/PropertyOpenedElsewhereNotice.tsx`).
  activeConnectionChanged: {
    title: "Nic nebylo uloženo",
    description:
      "Otevřený objekt se od načtení této stránky změnil, v této nebo jiné kartě. Načtěte stránku znovu a uvidíte nastavení objektu, který je otevřený teď.",
    pageDescription:
      "Od načtení této stránky se otevřel jiný objekt, v této nebo jiné kartě. Načtěte stránku znovu a uvidíte objekt, který je otevřený teď.",
    sendTitle: "Nic nebylo odesláno",
    recalculateTitle: "Ceny nebyly přepočítány",
    reload: "Načíst stránku znovu",
    openedTitle: "Teď je otevřený jiný objekt",
    openedDescription:
      "Otevřel se až po načtení této karty, v jiné kartě, okně nebo zařízení. Stránka je proto skrytá, aby se na ní nic nenačetlo z nesprávného objektu ani do něj neuložilo.",
    closedTitle: "Teď není otevřený žádný objekt",
    closedDescription:
      "Objekt, který tato karta ukazuje, už otevřený není, a žádný jiný se neotevřel. Stránka je proto skrytá, aby se z ní nic neuložilo.",
    thisTab: "Tato karta ukazuje",
    openNow: "Otevřený teď",
    noneOpen: "Žádný objekt",
    unsavedKept: "Neuložené změny tu zůstávají. Uložíte je, když znovu otevřete {name} — načtení stránky je zahodí.",
    unsavedKeptUnnamed:
      "Neuložené změny tu zůstávají. Uložíte je, když znovu otevřete objekt této karty — načtení stránky je zahodí.",
    switchBack: "Znovu otevřít {name}",
    switchBackUnnamed: "Znovu otevřít objekt této karty",
    switchBackFailed:
      "Objekt se nepodařilo znovu otevřít. Načtěte stránku znovu a pokračujte s objektem, který je otevřený.",
  },

  // ── Drafts ──────────────────────────────────────────────
  drafts: {
    title: "Cenový plán",
    description: "Prohlédněte si ceny, které Optivo připravilo, odešlete je do Property Management nebo je smažte",
    loading: "Načítání cen…",
    loadError: "Nepodařilo se načíst cenový plán",
    refreshData: "Obnovit data",
    refreshingData: "Načítám data…",
    lastCalculated: "Ceny naposledy spočítány {time}",
    lastCalculatedUnknown: "Čas posledního výpočtu není zaznamenán",
    empty: "Pro tato data není cenový plán",
    emptyHint: "Optivo připravuje ceny při každé synchronizaci a při každé změně strategie. Zkuste to po další synchronizaci nebo zvolte jiné období.",
    dateRange: "{from} — {to}",
    entries: "{count} cen",
    createdAt: "Vytvořeno: {date}",
    updatedAt: "Aktualizováno: {date}",
    sendToPms: "Odeslat do Property Management",
    sending: "Odesílání...",
    sendSuccess: "{count} cen úspěšně odesláno do Property Management",
    nothingToSend: "Žádné ceny k odeslání — možná už jsou synchronizované nebo odfiltrované",
    sendConfirm: "Odeslat všech {count} cen do Property Management? Tím se okamžitě aktualizují vaše sazby.",
    ratePlan: "Cenový plán",
    ratePlanDefault: "Základní cenový plán (výchozí)",
    ratePlanLoading: "Načítání cenových plánů...",
    ratePlanError: "Nepodařilo se načíst cenové plány",
    sendRangeLabel: "Rozsah dat k odeslání",
    sendRange1: "Pouze dnes",
    sendRange3: "Příštích 3 dny",
    sendRange7: "Příštích 7 dní",
    sendRange14: "Příštích 14 dní",
    sendRange30: "Příštích 30 dní",
    sendRange60: "Příštích 60 dní",
    sendRange90: "Příštích 90 dní",
    // Pojmenované podle toho, co odešle: dialog dostává data na obrazovce,
    // nikdy celý rok plánu.
    sendRangeAll: "Všechna data na obrazovce",
    // ── Co odeslání zapíše ──────────────────────────────────────────────────
    // Pod počtem: první a poslední noc, kterou odeslání zapíše, a počet nocí.
    // `{nights}` je tvar z `sendNightCount`. Odesílá se jen to, co odesláno
    // není, takže odeslání může mezi první a poslední nocí některé vynechat —
    // `sendNightsSparse` to řekne, místo aby jmenovalo souvislý úsek.
    sendNightCount: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
    sendNightsSingle: "{date}, {nights}",
    sendNightsSpan: "{from} – {to}, {nights}",
    sendNightsRange: "{from} – {to}",
    sendNightsSparse: "{nights} mezi {from} a {to}",
    // Cena, o jejímž odeslání nemá Optivo záznam, přepíše to, co pro tu noc
    // drží PMS — zpravidla vlastní ceník hotelu. „Podle záznamů“: záznamem je
    // plán a historie nahrávání, která se uchovává několik týdnů.
    sendFirstTime: {
      one: "Podle záznamů Optiva jde {count} cena do Property Management poprvé a přepíše cenu, která tam je teď.",
      few: "Podle záznamů Optiva jdou {count} ceny do Property Management poprvé a přepíšou ceny, které tam jsou teď.",
      other: "Podle záznamů Optiva jde {count} cen do Property Management poprvé a přepíše ceny, které tam jsou teď.",
    },
    // Říká se, když některá z nich leží za poslední nocí, kterou posílá
    // auto-sync. `{days}` je tvar z `sendAutoSyncDays`, `{date}` ta poslední noc.
    sendAutoSyncDays: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
    sendAutoSyncReach: "Auto-sync posílá ceny jen na {days} dopředu, do {date}.",
    // Data na obrazovce nabídnutá místo výchozího rozsahu, ve kterém nebylo co
    // odeslat: rozsah, který nikdo nevybral. `{window}` je název toho rozsahu,
    // `{range}` první a poslední noc odeslání.
    sendFallbackTitle: "Tento rozsah jste nevybrali",
    sendFallbackWhy: "V rozsahu „{window}“ není co odeslat, proto se nabízejí všechna data na obrazovce.",
    sendFallbackAck: "Chci odeslat ceny na {range}.",
    // U tlačítka Odeslat, které čeká na zaškrtnutí: varování, ke kterému
    // patří, je nahoře v dialogu, který se posouvá, a Odeslat je dole.
    sendHeldForTick: "Pro odeslání zaškrtněte potvrzení výše.",
    sendLongRangeWarningTitle: "Tento přenos zahrnuje více než 90 dní",
    sendLongRangeWarning: "Chystáte se odeslat {count} cen pokrývajících {days} dní. Váš property management je přijímá po dávkách, takže to potrvá přibližně {minutes} minut a nelze to pozastavit. Auto-sync umí podle vlastního rozvrhu udržovat aktuální až rok a půl dopředu — tolik nocí ručně odesílejte jen když je to opravdu nutné.",
    sendLongRangeAck: "Rozumím, že odeslání potrvá přibližně {minutes} minut.",
    // ── Měna ceníku ─────────────────────────────────────────────────────────
    currencyBlockedTitle: "Odeslání je zablokované: měny se neshodují",
    currencyNothingSentTitle: "Nic nebylo odesláno: měny se neshodují",
    currencyMismatchWhole: "Optivo počítá ceny této nemovitosti v {pricedIn}, ale ceník v systému {pms} je vedený v {listCurrency}. Systém {pms} by každou cenu přečetl jako {listCurrency}.",
    currencyMismatchPartial: "Optivo počítá ceny této nemovitosti v {pricedIn}, ale ceník typů pokojů {roomTypes} je v systému {pms} vedený v {listCurrency}. Systém {pms} by jejich ceny přečetl jako {listCurrency}.",
    currencyFixWhole: "Změňte měnu nemovitosti v Optivu ({settingsPath}) na {listCurrency}, nebo měnu ceníku v systému {pms} na {pricedIn}, a ceny odešlete znovu.",
    currencyFixPartial: "Změňte měnu ceníku těchto typů pokojů v systému {pms} na {pricedIn} a ceny odešlete znovu.",
    currencyFixMixed: "Sjednoťte měnu nemovitosti v Optivu ({settingsPath}) s měnou ceníku v systému {pms} a ceny odešlete znovu.",
    currencyUnchecked: "Optivo nemohlo ověřit, v jaké měně je vedený ceník v systému {pms}, takže by se ceny odeslaly bez této kontroly.",
    sendRoomKindsLabel: "Typy pokojů k odeslání",
    sendRoomKindsAll: "Všechny typy pokojů",
    sendRoomKindsSearch: "Hledat typy pokojů…",
    expand: "Zobrazit položky",
    collapse: "Skrýt položky",
    room: "Pokoj",
    date: "Datum",
    source: "Zdroj",
    sourceManual: "Ruční",
    sourceRegenerated: "Přepočítáno",
    occupancy: "Obsazenost",
    prices: "Ceny",
    los: "LOS",
    goToCalendar: "Přejít do kalendáře",
    saving: "Ukládání...",
    saveError: "Nepodařilo se uložit změny",
    generateDrafts: "Generovat koncepty",
    generating: "Generování…",
    generateSuccess: "Vygenerováno {count} cenových položek",
    generateError: "Nepodařilo se generovat koncepty",
    viewCalendar: "Kalendář",
    viewReservations: "Rezervace",
    viewMinStay: "Min. pobyt",
    searchRoomTypes: "Hledat…",
    allRoomTypes: "Všechny typy pokojů",
    allSources: "Všechny stavy",
    filterSource: "Stav",
    clearFilters: "Vyčistit filtry",
    noFilterResults: "Žádné ceny neodpovídají vybraným filtrům.",
    roomKinds: "typů pokojů",
    days: "dní",
    uploadProgress: "Nahrávání cen… {completed} z {total}",
    uploadComplete: "{count} cen úspěšně nahráno",
    settlingForPms: "Čekáme, až Property Management dokončí přepočet navázaných cenových plánů…",
    sendingDerivedPlans: "Odesíláme kanálové a nevratné cenové plány…",
    derivedPlansPending: "Zbývá odeslat {count} navázaných cenových plánů",
    uploadCompleteOf: "Odesláno {sent} z {requested} cen do Property Management",
    uploadSkippedSoldOut: "{count} přeskočeno — tyto noci jsou vyprodané, nová cena by nic nezměnila.",
    uploadFinishedHintPartial: "Přeskočené ceny zůstávají v návrhu a odešlou se při dalším odeslání, jakmile se pokoj uvolní. Historie nahrávání obsahuje rozpis po jednotlivých cenách.",
    uploadShowSkipped: "Zobrazit přeskočené",
    uploadHideSkipped: "Skrýt přeskočené",
    roomKindRenamed: "Přejmenováno v PMS — Optivo nyní zobrazuje aktuální název.",
    manageRoomKinds: "Spravovat typy pokojů",
    uploadPartial: "Do Property Management doručeno {delivered} z {total} cen",
    uploadRefused: "Odmítnuto Property Managementem: {count}. Opakované odeslání nic nezmění, dokud tam nebude odstraněn důvod uvedený níže; do té doby zůstávají v návrhu neodeslané.",
    uploadHeld: "Pozdrženo Optivem: {count}. Optivo je z důvodu uvedeného níže samo nezapsalo, takže Property Management nic neodmítl a není tam co opravovat. V návrhu zůstávají neodeslané a další odeslání je zkusí znovu.",
    // Totéž o obnovení a o jeho opakovaném pokusu, kde poslední věta odeslání
    // neplatí: obnovení nepotvrzuje žádné návrhy, takže v žádném nic nezůstává
    // a noc později nic neodešle. Obnovení pozdrží jen minimální délku pobytu
    // vracenou bez ceny; odmítnuta může být cena i minimální délka pobytu.
    uploadRefusedRestore: "Odmítnuto Property Managementem: {count}. Opakované obnovení nic nezmění, dokud tam nebude odstraněn důvod uvedený níže. Optivo obnovení samo znovu nezkouší, takže si tyto noci ponechají to, co mají teď. Chcete-li vrátit uložené hodnoty, spusťte obnovení znovu, až bude důvod odstraněn, nebo je nastavte přímo v Property Management.",
    uploadHeldRestore: "Pozdrženo Optivem: {count}. Optivo je z důvodu uvedeného níže samo nezapsalo, takže Property Management nic neodmítl a není tam co opravovat. Optivo obnovení samo znovu nezkouší, takže si tyto noci ponechají minimální délku pobytu, jakou mají teď. Chcete-li vrátit tu uloženou, spusťte obnovení později znovu nebo ji nastavte přímo v Property Management.",
    uploadHeldHint: "Historie nahrávání uvádí každou noc a důvod, proč byla pozdržena.",
    uploadUndelivered: "Nedoručeno kvůli chybě spojení: {count}. Nový pokus je ještě může doručit.",
    uploadRetry: "Zkusit znovu ({count})",
    uploadMoreErrors: "dalších {count} chyb",
    uploadMoreReasons: "dalších {count}",
    uploadFinishedHint: "U těchto cen již není co odesílat. Historie nahrávání obsahuje rozpis po jednotlivých cenách včetně původních hodnot.",
    uploadDryRunHint: "Žádné ceny nebyly odeslány. Historie nahrávání zaznamenává tento zkušební běh, abyste si mohli prohlédnout, co by se změnilo.",
    uploadFailedHint: "Historie nahrávání obsahuje úplné podrobnosti o chybách tohoto pokusu.",
    viewUploadLog: "Otevřít historii nahrávání",
    queued: "Ve frontě",
    queuedCount: "+{count} ve frontě",
    uploadQueuedToast: "Nahrávání zařazeno do fronty — spustí se po dokončení aktuálního.",
    uploadBusyToast: "Právě probíhá plánovaná synchronizace cen — zkuste to prosím za minutu znovu.",
    uploadStartFailedToast: "Nahrávání se nespustilo: {error}",
    // ── Rozsah odesílání ────────────────────────────────────────────────────
    sendPricesLabel: "Odeslat ceny",
    sendPricesHint: "Zapíše noční ceny z tohoto návrhu do Property Management.",
    sendMinLosLabel: "Odeslat minimální délku pobytu",
    // Co přepínač v PMS udělá, ať už se dá říct cokoli dalšího, nebo ne:
    // nahradí minimální pobyty, které tam jsou, a proto je vypnutý.
    sendMinLosHint: "Zapíše pro každou noc minimální délku pobytu z Optiva místo té, kterou máte v Property Management. Ve výchozím stavu vypnuto, aby ta vaše zůstala beze změny.",
    sendScopeNothingSelected: "Vyberte alespoň ceny nebo minimální délku pobytu.",
    sendMinLosUnsupported: "Toto připojení k Property Management neumí přijmout omezení minimální délky pobytu.",
    // Nabídne i to, co už Optivo odeslalo — po změně provedené přímo v PMS,
    // kterou Optivo nevidí.
    resendLabel: "Odeslat znovu i už odeslané",
    resendHint: "Odešle i noci, které už Optivo poslalo, pro případ, že se mezitím změnily přímo ve vašem Property Management. Vypnuté odešle jen to, co se od posledního odeslání změnilo.",
    // Odeslání nikdy nezapíše typ pokoje, který v danou noc nemá volný pokoj.
    soldOutLeftOut: {
      one: "Vynechána {count} vyprodaná noc typu pokoje: odeslání přeskočí typ pokoje, který v danou noc nemá volný pokoj.",
      few: "Vynechány {count} vyprodané noci typů pokojů: odeslání přeskočí typ pokoje, který v danou noc nemá volný pokoj.",
      other: "Vynecháno {count} vyprodaných nocí typů pokojů: odeslání přeskočí typ pokoje, který v danou noc nemá volný pokoj.",
    },
    dryRunLabel: "Zkušební běh",
    dryRunHint: "Ověří položky a vytvoří záznam v historii nahrávání bez odeslání cen do Property Management.",
    dryRunBadge: "Zkušební běh",
    dryRunInProgress: "Zkušební běh — ověřuji {completed} z {total} (ceny se neodesílají)",
    dryRunComplete: "Zkušební běh dokončen — ověřeno {count} položek, ceny nebyly odeslány do Property Management",
    bookingHighlight: {
      title: "Rezervace · {roomKind}",
      nightsAffected: "dotčených nocí",
      nightsHighlighted: "zvýrazněných nocí",
      price: "Cena",
      occupancy: "Obsazenost",
      explanationLoading: "Zjišťujeme, co tato rezervace změnila…",
      demandShift: "Poptávka se změnila z {demandBefore} na {demandAfter}.",
      demandSteady: "Poptávka zůstala {demandAfter}.",
      explanationHighlight: "Níže je zvýrazněno {nights} nocí tohoto pobytu — u každé buňky je aktuální návrh ceny pro danou noc.",
      explanationUnchanged: "Obsazenost těchto nocí zůstala na {occ}%, takže se návrh ceny nemění.",
      explanationUp: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}% a cenový model zvýšil návrh noční sazby.",
      explanationDown: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}% a cenový model snížil návrh noční sazby.",
      explanationFlat: "Obsazenost se po této rezervaci změnila z {occBefore}% na {occAfter}%, ale návrh noční sazby vyšel stejně.",
      cellMark: "Dopad rezervace",
    },
    lockPrice: "Zamknout cenu",
    unlockPrice: "Odemknout cenu",
    priceLockedToast: "Cena zamčena",
    priceUnlockedToast: "Cena odemčena",
    unlockRecalculatingToast: "Odemykám cenu a přepočítávám…",
    priceUnlockedRecalculatedToast: "Cena odemčena a přepočítána",
    pricesSavedToast: "Ceny uloženy a zamčeny",
    lockConfirmTitle: "Zamknout tuto cenu?",
    // Zámek ceny je jen cenový: minimální pobyt má zámek vlastní a obě
    // potvrzení to říkají dřív, než se cokoli změní.
    lockConfirmDesc: "Zamčené ceny nebudou přepsány automatickým generováním cen a do PMS odchází zamčená cena. Optivo pro noc dál počítá svou cenu a ukazuje ji vedle vaší. Všechny varianty obsazenosti pro tento den a pokoj budou zamčeny společně. Zamyká se jen cena: minimální pobyt má vlastní zámek v zobrazení Min. pobyt.",
    unlockConfirmTitle: "Odemknout tuto cenu?",
    unlockConfirmDesc: "Odemknutí vrací tuto cenu Optivu: noc se ihned přepočítá, takže hned uvidíte, jak ji ocení vaše strategie. Nová cena se do vašeho Property Management systému odešle při příští synchronizaci — automatické i ruční — i když se hodnota nezměnila. Odemyká se jen cena: zamčený minimální pobyt zůstane zamčený, dokud ho neodemknete zvlášť.",
    lockedBadge: "Zamčeno",
    uploadedBadge: "Nahráno",
    // Více nocí × typů pokojů jednou úpravou, z hlavičky stránky Ceny
    // (`drafts/BulkPriceEditDialog.tsx`, `services/core/bulkPriceEdit.ts`).
    bulkEdit: {
      openButton: "Upravit více nocí",
      title: "Úprava více nocí najednou",
      description: "Nastavte a zamkněte jednu cenu, ceny zdražte nebo zlevněte, zamkněte je tak, jak jsou, nebo je odemkněte — pro několik nocí za sebou a jeden či více typů pokojů.",
      titleSelection: "Úprava cen",
      descriptionSelection: "Nastavte nebo změňte ceny označených nocí, případně je zamkněte či odemkněte.",
      selectionLabel: "Označené noci",
      nightsLabel: "Noci",
      nightsHint: "Klikněte na první noc a pak na poslední.",
      nightCount: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
      roomTypeCount: { one: "{count} typ pokoje", few: "{count} typy pokojů", other: "{count} typů pokojů" },
      scope: "{nights} pro {roomTypes}",
      tooManyNights: "Najednou lze upravit nejvýše {max} nocí.",
      roomTypesLabel: "Typy pokojů",
      actionLabel: "Co udělat",
      actionSetAndLock: "Nastavit cenu a zamknout",
      actionSetAndLockHint: "Optivo tyto ceny přestane přepočítávat a odešle místo nich vaši cenu.",
      actionChange: "Zdražit nebo zlevnit a zamknout",
      actionChangeHint: "Každá cena se posune od své současné výše o stejnou částku nebo procento a Optivo ji přestane přepočítávat.",
      directionLabel: "Směr",
      raise: "Zdražit",
      lower: "Zlevnit",
      changeLabel: "O kolik",
      unitLabel: "Jednotka",
      changeAmountHint: "Cena pro každý počet hostů se posune o stejnou částku.",
      changePercentHint: "Cena pro každý počet hostů se posune o stejné procento a zaokrouhlí se tak, jak ceny zaokrouhluje vaše strategie.",
      percentTooDeep: "Snížení musí zůstat pod 100 %.",
      actionLock: "Zamknout současné ceny",
      actionLockHint: "Ceny zůstanou tak, jak jsou teď; minimální pobyt dál nastavuje Optivo.",
      // Zámek ceny je jen cenový, proto je zamknutí obojího samostatná volba.
      actionLockPriceAndMinStay: "Zamknout současné ceny i minimální pobyty",
      actionLockPriceAndMinStayHint: "Ceny i minimální pobyty zůstanou tak, jak jsou teď.",
      actionUnlock: "Odemknout",
      actionUnlockHint: "Optivo tyto noci hned znovu ocení podle vaší strategie. Zamčený minimální pobyt zůstane zamčený.",
      priceLabel: "Cena za noc ({currency})",
      priceHint: "Pro obsazenost, kterou ukazuje kalendář, obvykle 2 osoby. Ceny pro jiný počet hostů se posunou o stejnou částku.",
      priceUnreadable: "Zadejte cenu jen jako číslo, například 1500: bez mezer, znaménka a měny.",
      submitSetAndLock: "Nastavit a zamknout",
      submitChange: "Změnit a zamknout",
      submitLock: "Zamknout",
      submitLockPriceAndMinStay: "Zamknout obojí",
      submitUnlock: "Odemknout",
      submitting: "Ukládám…",
      doneSetAndLock: { one: "{count} cena nastavena a zamčena", few: "{count} ceny nastaveny a zamčeny", other: "{count} cen nastaveno a zamčeno" },
      doneChange: { one: "{count} cena změněna a zamčena", few: "{count} ceny změněny a zamčeny", other: "{count} cen změněno a zamčeno" },
      doneLock: { one: "{count} cena zamčena", few: "{count} ceny zamčeny", other: "{count} cen zamčeno" },
      doneLockPriceAndMinStay: { one: "{count} cena i minimální pobyt zamčeny", few: "{count} ceny i minimální pobyty zamčeny", other: "{count} cen i minimálních pobytů zamčeno" },
      doneUnlock: { one: "{count} cena odemčena", few: "{count} ceny odemčeny", other: "{count} cen odemčeno" },
      recalculatedNote: "Optivo je hned znovu ocenilo podle vaší strategie.",
      notRecalculatedNote: "Optivo je znovu ocení při příští synchronizaci.",
      skippedNote: "Přeskočeno: {count} — Optivo pro ně zatím nemá cenu.",
      tooLowNote: "Beze změny: {count} — změna by cenu stáhla na nulu nebo pod ni.",
      previewPrices: "Ceny: {before} → {after} {currency}",
      previewTooLow: "Beze změny zůstane: {count} — změna by cenu stáhla na nulu nebo pod ni.",
      previewMissing: "Bez ceny ke změně: {count}.",
      alreadyLockedNote: "Už zamčené: {count}.",
      notLockedNote: "Nezamčené: {count}.",
      nothingChanged: "Na těchto nocích nebylo co změnit.",
      errors: {
        INVALID_RANGE: "Vyberte znovu první a poslední noc.",
        PAST_RANGE: "Tyto noci už proběhly.",
        RANGE_TOO_LONG: "Tolik nocí jedna úprava nepokryje.",
        NO_ROOM_KINDS: "Vyberte alespoň jeden typ pokoje.",
        INVALID_ACTION: "Vyberte, co s cenami udělat.",
        INVALID_PRICE: "Zadejte cenu vyšší než nula.",
        INVALID_AMOUNT: "Zadejte částku větší než nula.",
        INVALID_PERCENT: "Zadejte procento větší než nula; snížení musí zůstat pod 100 % a zvýšení nejvýš 300 %.",
        INVALID_MIN_LOS: "Zadejte celý počet nocí od 1 do 14.",
        generic: "Ceny se nepodařilo upravit. Zkuste to prosím znovu.",
      },
    },
    selection: {
      region: "Označené ceny",
      summary: "{roomTypes} · {nights}",
      dateRange: "{from} – {to} ({nights})",
      menuButton: "Upravit ceny",
      detail: "Detail ceny",
      setPrice: "Zadat novou cenu…",
      shiftPrice: "Změnit o částku…",
      scalePrice: "Změnit o procento…",
      // Co čtou obě položky výběru „změnit o“: obě zapsanou cenu zamknou, a
      // vedle „Upravit bez zamčení…“ by popisek bez zámku byl první, na který
      // narazí ten, kdo chce „zlevnit o 300“.
      shiftPriceAndLock: "Změnit o částku a zamknout…",
      scalePriceAndLock: "Změnit o procento a zamknout…",
      lock: "Zamknout ceny",
      lockPriceAndMinStay: "Zamknout ceny i minimální pobyty",
      unlock: "Odemknout ceny…",
      adjust: "Upravit bez zamčení…",
      removeAdjustment: "Odebrat úpravu…",
      chooseNights: "Vybrat noci v kalendáři…",
      clear: "Zrušit výběr",
      extendHint: "Shift+klik nebo tažení označí víc",
      extendOnTap: "Označit rozsah",
      extendOnTapHint: "Klepněte na noc, kde rozsah končí.",
    },
    // ── Úprava bez zamčení ────────────────────────────────────────────────
    // Částka nebo procento se znaménkem, které provozovatel položí na cenu
    // Optiva pro noc a typ pokoje a které platí, zatímco Optivo noc dál
    // přepočítává (`drafts/priceAdjustment.ts`; čte se přes
    // `priceAdjustmentCopy`). Nikdy se jí neříká „ruční“: tak se říká ceně
    // zadané ručně. Žádná věta neříká, kdy se čekající úprava uplatní: vyřeší
    // ji přepočet, který její noc nacení jako další, a stránka neví, kdy to
    // bude.
    adjustment: {
      title: "Upravit bez zamčení",
      // Táž úprava jako volba v „Upravit více nocí“, hned pod zdražením nebo
      // zlevněním, které zamyká: liší se jen zámkem, a nápověda říká, co to
      // znamená, dřív než si provozovatel vybere.
      bulkAction: "Zdražit nebo zlevnit bez zamčení",
      bulkActionHint: "Nic se nezamyká: Optivo tyto noci dál přepočítává a vaši úpravu uplatní u každé nové ceny, dokud ji neodeberete nebo noc neuplyne.",
      raise: "Zdražit",
      lower: "Zlevnit",
      size: "O kolik",
      // Pod polem velikosti, dokud jeho text není velikost. Pole čte
      // desetinnou čárku i tečku a nebere znaménko: směr určuje přepínač
      // vedle něj.
      sizeUnreadable: "Zadejte číslo bez znaménka, například 150 nebo 7,5.",
      stands: "Nic se nezamyká. Optivo tyto noci dál přepočítává a vaši úpravu uplatní u každé nové ceny, v mezích minimální a maximální ceny typu pokoje pro danou noc. Platí, dokud ji neodeberete nebo noc neuplyne.",
      amountHint: "Částka posune cenu pro každý počet hostů o stejnou sumu.",
      percentHint: "Procenta z ceny, kterou Optivo pro danou noc vypočítá, ne ze základní ceny.",
      // Zdražení se zmenšuje podle toho, jak daleko last-minute křivka pro noc
      // došla, což Optivo ukládá od otevření okna, ať už křivka jeho vlastní
      // cenou pohnula, nebo ne. Zlevnění se nezmenšuje.
      raiseLastMinute: "V last-minute okně se zdražení zmenšuje podle toho, jak daleko last-minute křivka pro danou noc došla, i když ještě nepohnula cenou Optiva, takže volný pokoj i tak klesne na své last-minute minimum.",
      lowerLastMinute: "Zlevnění platí až do konce a cenu nikdy nesníží pod minimální cenu typu pokoje pro danou noc.",
      scopeChange: "Změnit noci a typy pokojů…",
      scopeNights: "Noci",
      scopeRoomTypes: "Typy pokojů",
      previewLoading: "Počítám ceny…",
      previewPrices: "Ceny nyní {before} → s úpravou {after} {currency}",
      // Pod řádkem cen, kde platí jen část zdražení. `{share}`: „16 %“,
      // „16–40 %“; `{inForce}`: „+140 Kč“ proti ceně Optiva. Počítaný tvar,
      // kde okno dosahuje jen na část cen.
      previewLastMinuteShare: "V last-minute okně z ní dnes platí {share} ({inForce}).",
      previewLastMinuteShareSome: {
        one: "U {count} ceny v last-minute okně z ní dnes platí {share} ({inForce}).",
        few: "U {count} cen v last-minute okně z ní dnes platí {share} ({inForce}).",
        other: "U {count} cen v last-minute okně z ní dnes platí {share} ({inForce}).",
      },
      previewNone: "Žádná cena se teď nezmění.",
      previewUnavailable: "Ceny se teď nepodařilo spočítat. Úpravu přesto můžete uložit.",
      heldRecalculating: "Ceny se právě přepočítávají. Uložit půjde, až přepočet skončí.",
      save: "Uložit úpravu",
      savedNoneMoved: "Žádná cena se teď nezměnila.",
      nothingToRemove: "U těchto cen není nastavena žádná úprava.",
      removedNoneRestored: "Žádná cena se teď nezměnila.",
      // Odebrání se nejdřív ptá a říká, co zmizí. `{nights}` je rozsah dat,
      // jak ho píše lišta výběru (`selection.dateRange`, u jedné noci jedno
      // datum), nikdy počet, který by čeština musela skloňovat.
      removeTitle: "Odebrat úpravu?",
      removeOne: "{delta}, {nights}, {roomTypes}.",
      removeSeveral: "Úpravy: {nights}, {roomTypes}.",
      removeEffect: "Tyto ceny se vrátí na to, co počítá Optivo. Zamčená cena si úpravu ponechá, dokud ji neodemknete.",
      removeConfirm: "Odebrat",
      // Jak se jmenuje značka v buňce, podle toho, co tam úprava dělá.
      // `{delta}` je, co bylo nastaveno („+200 CZK“, „−15 %“), `{inForce}`
      // posun zobrazené ceny, který platí teď, jako částka se znaménkem.
      markApplied: "Úprava bez zamčení: {delta}",
      markAppliedInForce: "Úprava bez zamčení {delta}: teď platí {inForce}",
      markIdle: "Úprava {delta} je nastavena a tuto cenu teď nemění",
      markIdleMinimum: "Úprava {delta} teď nic nesnižuje: cena je na minimální ceně typu pokoje pro tuto noc",
      markIdleCeiling: "Úprava {delta} teď nic nezvyšuje: cena je na maximální ceně této noci",
      markIdleRounding: "Úprava {delta} tuto cenu nemění: je menší než zaokrouhlení cen",
      markIdleLastMinute: "Úprava {delta} teď nic nezvyšuje: v last-minute okně z ní zbývá příliš málo",
      markIdleTooLow: "Úprava {delta} se nepoužije: nezbyla by žádná cena",
      markLockedWith: "Zamčeno včetně úpravy {delta}",
      markWaitingLocked: "Úprava {delta} čeká: cena je zamčená",
      markWaitingTyped: "Úprava {delta} čeká: cena byla zadána ručně a ještě nebyla přepočítána",
      markWaitingRecalculation: "Úprava {delta} čeká, až Optivo tuto noc znovu přepočítá",
      // Stav, pro který tato verze stránky nemá slovo.
      markUnknown: "Úprava {delta}: co právě dělá, ukáže nové načtení stránky",
      legend: "Upraveno bez zamčení",
      legendIdle: "Úprava nastavena, cenu teď nemění",
      legendWaiting: "Úprava čeká",
      noticeShow: "Zobrazit",
      listTitle: "Úpravy bez zamčení",
      listDescription: "Každá platí, dokud ji neodeberete nebo její noc neuplyne.",
      listAdd: "Přidat úpravu…",
      listRemove: "Odebrat…",
      listRemoveLabel: "Odebrat úpravu {delta} ({nights})",
      listSetBy: "{who} · {when}",
      listSetByUnknown: "Nezaznamenáno",
      listLeftOut: "U typu pokoje, který teď Optivo nenaceňuje",
      listLeftOutUnnamed: "Typ pokoje, který se už nepoužívá",
      // Věty s počtem, každá se čte přes `pluralForm`. Sdílí je náhled
      // v dialogu, oznámení a důvody v seznamu.
      replaced: {
        one: "Nahradí {count} dříve nastavenou úpravu.",
        few: "Nahradí {count} dříve nastavené úpravy.",
        other: "Nahradí {count} dříve nastavených úprav.",
      },
      // Proč může „Žádná cena se teď nezmění.“ stát vedle úpravy, která
      // platí: tyto ceny měly právě tuto úpravu už před uložením.
      unchanged: {
        one: "{count} cena už tuto úpravu má.",
        few: "{count} ceny už tuto úpravu mají.",
        other: "{count} cen už tuto úpravu má.",
      },
      heldMinimum: {
        one: "{count} cenu zastaví minimální cena typu pokoje pro danou noc.",
        few: "{count} ceny zastaví minimální cena typu pokoje pro danou noc.",
        other: "{count} cen zastaví minimální cena typu pokoje pro danou noc.",
      },
      heldCeiling: {
        one: "{count} cenu zastaví maximální cena noci.",
        few: "{count} ceny zastaví maximální cena noci.",
        other: "{count} cen zastaví maximální cena noci.",
      },
      heldRounding: {
        one: "{count} cena se nezmění: úprava je menší než zaokrouhlení cen.",
        few: "{count} ceny se nezmění: úprava je menší než zaokrouhlení cen.",
        other: "{count} cen se nezmění: úprava je menší než zaokrouhlení cen.",
      },
      heldLastMinute: {
        one: "U {count} ceny zbývá v last-minute okně ze zdražení tak málo, že se nezmění.",
        few: "U {count} cen zbývá v last-minute okně ze zdražení tak málo, že se nezmění.",
        other: "U {count} cen zbývá v last-minute okně ze zdražení tak málo, že se nezmění.",
      },
      tooLow: {
        one: "{count} cena by klesla na nulu, proto zůstane beze změny.",
        few: "{count} ceny by klesly na nulu, proto zůstanou beze změny.",
        other: "{count} cen by kleslo na nulu, proto zůstane beze změny.",
      },
      waitingLocked: {
        one: "{count} cena je zamčená: úprava počká na odemčení.",
        few: "{count} ceny jsou zamčené: úprava počká na odemčení.",
        other: "{count} cen je zamčených: úprava počká na odemčení.",
      },
      waitingTyped: {
        one: "{count} cena byla zadána ručně: úprava počká, až ji Optivo přepočítá.",
        few: "{count} ceny byly zadány ručně: úprava počká, až je Optivo přepočítá.",
        other: "{count} cen bylo zadáno ručně: úprava počká, až je Optivo přepočítá.",
      },
      waitingRecalculation: {
        one: "{count} cena čeká, až Optivo noc znovu přepočítá; úprava se uplatní potom.",
        few: "{count} ceny čekají, až Optivo noci znovu přepočítá; úprava se uplatní potom.",
        other: "{count} cen čeká, až Optivo noci znovu přepočítá; úprava se uplatní potom.",
      },
      unpriced: {
        one: "{count} cena zatím chybí: úprava se uplatní, až ji Optivo nacení.",
        few: "{count} ceny zatím chybí: úprava se uplatní, až je Optivo nacení.",
        other: "{count} cen zatím chybí: úprava se uplatní, až je Optivo nacení.",
      },
      saved: {
        one: "Úprava uložena pro {count} cenu",
        few: "Úprava uložena pro {count} ceny",
        other: "Úprava uložena pro {count} cen",
      },
      savedMoved: {
        one: "Teď se změnila {count} cena.",
        few: "Teď se změnily {count} ceny.",
        other: "Teď se změnilo {count} cen.",
      },
      removed: {
        one: "Úprava odebrána u {count} ceny",
        few: "Úprava odebrána u {count} cen",
        other: "Úprava odebrána u {count} cen",
      },
      removedRestored: {
        one: "{count} cena je zpět na výpočtu Optiva.",
        few: "{count} ceny jsou zpět na výpočtu Optiva.",
        other: "{count} cen je zpět na výpočtu Optiva.",
      },
      removedKeptLocked: {
        one: "{count} cena je zamčená a úpravu si ponechá, dokud ji neodemknete.",
        few: "{count} ceny jsou zamčené a úpravu si ponechají, dokud je neodemknete.",
        other: "{count} cen je zamčených a úpravu si ponechá, dokud je neodemknete.",
      },
      removeCount: {
        one: "{count} cena",
        few: "{count} ceny",
        other: "{count} cen",
      },
      notice: {
        one: "{count} noc má úpravu bez zamčení",
        few: "{count} noci mají úpravu bez zamčení",
        other: "{count} nocí má úpravu bez zamčení",
      },
      listIdle: {
        one: "{count} cenu teď nemění.",
        few: "{count} ceny teď nemění.",
        other: "{count} cen teď nemění.",
      },
      listLockedWith: {
        one: "{count} zamčená cena ji obsahuje.",
        few: "{count} zamčené ceny ji obsahují.",
        other: "{count} zamčených cen ji obsahuje.",
      },
      // Podle kódu, kterým server úpravu odmítl; `unavailable` je vlastní kód
      // stránky pro uložení, které narazilo na API bez této funkce. Jen
      // řetězce: odmítnutí se hledá podle kódu a věta s počtem by jím nebyla.
      errors: {
        INVALID_RANGE: "Vyberte noci, které chcete upravit.",
        PAST_RANGE: "Tyto noci už uplynuly.",
        RANGE_TOO_LONG: "Vyberte nejvýše 62 nocí.",
        RANGE_TOO_FAR: "Optivo naceňuje rok a půl dopředu. Vyberte noci v tomto období.",
        NO_ROOM_KINDS: "Vyberte aspoň jeden typ pokoje.",
        ROOM_KINDS_NOT_PRICED: "Vybrané typy pokojů už Optivo nenaceňuje. Načtěte stránku znovu.",
        INVALID_UNIT: "Zvolte částku, nebo procento.",
        INVALID_AMOUNT: "Zadejte částku větší než nula.",
        INVALID_PERCENT: "Zadejte procento větší než nula; zlevnění musí zůstat pod 100 % a zdražení nejvýše 300 %.",
        TOO_MANY_ADJUSTMENTS: "Platí příliš mnoho úprav. Některé nejdřív odeberte.",
        unavailable: "Úpravy teď nejsou k dispozici. Za chvíli načtěte stránku znovu.",
        generic: "Úpravu se nepodařilo uložit. Zkuste to prosím znovu.",
        removeFailed: "Úpravu se nepodařilo odebrat. Zkuste to prosím znovu.",
      },
    },
    // ── Minimální pobyt ───────────────────────────────────────────────────
    // Zobrazení minimálního pobytu, značka v rohu každé noci, která ho má, a
    // úpravy, které k němu nabízí výběr (`drafts/minStay.ts`). Minimální pobyt
    // má vlastní zámek, oddělený od zámku ceny: nastavení zde drží minimální
    // pobyt a cenu nechává Optivu.
    minStay: {
      // Značka v rohu je symbol a číslo, proto se jmenuje slovy.
      // `{nights}` je tvar z `bulkEdit.nightCount`.
      markLabel: "Minimální pobyt {nights}",
      markRelaxed: "Minimální pobyt {nights}, zkrácený z {from}, protože týden je slabý",
      // `{night}` je vyprodaná noc, jako „so 10. 10.“.
      markRelaxedSoldOut: "Minimální pobyt {nights}, zkrácený z {from}, protože noc {night} je vyprodaná",
      markRelaxedSoldOutBefore: "Minimální pobyt {nights}, zkrácený z {from}, protože předchozí noc {night} je vyprodaná",
      markRelaxedBehind: "Minimální pobyt {nights}, zkrácený z {from}, protože se víkend prodává pomaleji než obvykle",
      // Důvod, pro který tato stránka nemá slova: nikdy slabý týden.
      markRelaxedGeneric: "Minimální pobyt {nights}, zkrácený z {from}",
      // Minimální pobyt nastavený bez zamčení, zkrácený volbou „Vždy uvolnit do“.
      markRelaxedRelease: "Minimální pobyt {nights}, zkrácený z {from} v rámci „Vždy uvolnit do“",
      // `{label}` je dosavadní název značky.
      markStated: "{label}, nastaveno bez zamčení",
      // Buňka zobrazení minimálního pobytu.
      relaxedShort: "z {from}",
      freeRoomsLabel: "Volné {free} z {total} pokojů",
      soldOut: "Vyprodáno: žádný pokoj tohoto typu už není volný",
      // Řádek pokojů noci, kde žádný nezbývá, místo „0/4“.
      roomsFull: "plno",
      roomsClosed: "zavřeno",
      closedHere: "Zavřeno: volné pokoje tohoto typu jsou v PMS uzavřené",
      // Karta vyprodané noci vedle toho, zda byl odeslán: její minimální pobyt
      // dnes nikoho neodmítne a čeká na pokoj.
      soldOutRestrictsNobody: "Teď nikoho neomezuje: žádný pokoj tohoto typu není volný. Platí pro další pokoj, který se uvolní.",
      noArrival: "Nelze přijet",
      noArrivalLabel: "Nelze přijet: minimální pobyt zasahuje do noci {night}, kdy je tento typ pokoje vyprodaný",
      earlierArrival: "Prodat ji jde hostovi s příjezdem o noc dřív, {night}.",
      // Řádky zobrazení na kartě buňky.
      lockedOwn: "Zamčeno: Optivo ponechá minimální pobyt, který jste nastavili.",
      lockedWithPrice: "Zamčeno spolu s cenou: po odemčení minimálního pobytu ho Optivo zase nastaví samo.",
      followsStrategy: "Nastavuje ho Optivo podle vaší strategie.",
      // Třetí stav noci, mezi strategií a zámkem. `{nights}` je tvar z `bulkEdit.nightCount`.
      statedOwn: "Nastaveno na {nights} bez zamčení: Optivo toto číslo drží, před vyprodanou nocí a v rámci „Vždy uvolnit do“ ho zkrátí a po uvolnění pokoje se k němu vrátí.",
      sent: "Tento minimální pobyt už je v systému správy ubytování.",
      notSent: "Do systému správy ubytování zatím neodeslán.",
      relaxed: "Zkráceno z {from}: týden je podle historie rezervací slabý.",
      relaxedWeekendFill: "Zkráceno z {from}: o víkendech v tomto období bylo loni obsazeno jen {fill} % pokojů.",
      relaxedSoldOut: "Zkráceno z {from}: tento typ pokoje je v noci {night} vyprodaný, takže delší pobyt dnes začít nemůže. Jakmile se v tu noc uvolní pokoj, minimum se vrátí.",
      relaxedSoldOutBefore: "Zkráceno z {from}: tento typ pokoje je vyprodaný v předchozí noci {night}, takže dřívější příjezd do dnešní noci nedosáhne a minimum žádá jen zbytek víkendu či svátků. Jakmile se v tu noc uvolní pokoj, minimum se vrátí.",
      // Co řekly rezervace víkendu, kde je Optivo četlo: pokoje celého objektu
      // na noc (jedno desetinné místo), každý počet až za podstatným jménem.
      // `{nights}` je tvar z `bulkEdit.nightCount`.
      relaxedWeakWeekBookings: "Zkráceno z {from}: týden je podle historie rezervací slabý a současné rezervace to nevyvracejí.",
      relaxedBehind: "Zkráceno z {from}: víkend se prodává pomaleji než obvykle. Prodané pokoje na noc v celém objektu: teď {booked} z {rooms}, obvykle touto dobou {usual}; odhad do příjezdu kolem {projected}.",
      relaxedGeneric: "Zkráceno z {from}.",
      relaxedRelease: "Zkráceno z {from}: noc spadá do „Vždy uvolnit do“.",
      heldAhead: "Drží se {nights}: víkend se prodává rychleji než obvykle, proto se neuvolní ani ve slabém týdnu. Prodané pokoje na noc v celém objektu: teď {booked}, obvykle touto dobou {usual}.",
      heldAsUsual: "Drží se {nights}: týden je podle historie slabý, ale rezervace neukazují, že by víkend skončil zřetelně pod svým obvyklým obsazením. Odhad prodaných pokojů na noc v celém objektu do příjezdu: kolem {projected} z {rooms}.",
      // Pod zobrazením: zda se to, co ukazuje, vůbec dostane do PMS.
      dayCount: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
      sendingOn: "Automatická synchronizace odesílá tyto minimální pobyty do systému {pms} na {days} dopředu.",
      sendingPricesOnly: "Automatická synchronizace posílá jen ceny, takže se tyto minimální pobyty do systému {pms} nedostanou. Zapněte „Odeslat minimální délku pobytu“ v nastavení synchronizace, nebo je odešlete ručně.",
      sendingOff: "Automatická synchronizace je vypnutá: tyto minimální pobyty se do systému {pms} dostanou, jen když je odešlete ručně se zaškrtnutou volbou „Odeslat minimální délku pobytu“.",
      sendingUnsupported: "Systém {pms} minimální pobyty z Optiva nepřijímá, takže zůstávají jen v Optivu.",
      syncSettings: "Nastavení synchronizace",
      // Legenda pod zobrazením.
      legendLabel: "Minimální pobyt",
      legendNoArrival: "Nelze přijet",
      legendLocked: "Zamčeno",
      legendStated: "Nastaveno bez zamčení",
      legendRelaxed: "„z 3“: zkráceno při slabé poptávce",
      legendRelaxedStated: "„z 3“: zkráceno slabou poptávkou nebo „Vždy uvolnit do“",
      legendRelaxedSoldOut: "„z 2“: zkráceno kvůli vyprodané noci",
      legendRooms: "„2/4“: volné 2 ze 4 pokojů · „plno“: vyprodáno · „zavřeno“: uzavřeno v PMS",
      legendMark: "Minimální pobyt v nocích",
      // Nabídka výběru v tomto zobrazení.
      menuButton: "Upravit min. pobyt",
      setStated: "Nastavit bez zamčení…",
      set: "Nastavit a zamknout…",
      lock: "Zamknout minimální pobyt",
      unlock: "Odemknout minimální pobyt…",
      // Totéž, je-li mezi označenými nocemi nastavený pobyt: nic tam zamčené není.
      handBack: "Vrátit minimální pobyt strategii…",
      // Úprava z výběru nebo z hlavičky stránky v tomto zobrazení.
      title: "Minimální pobyt pro více nocí",
      description: "Nastavte minimální pobyt se zámkem nebo bez něj, zamkněte ho tak, jak je, nebo ho vraťte strategii — pro několik nocí za sebou a jeden či více typů pokojů. Ceny se nemění.",
      titleSelection: "Úprava minimálního pobytu",
      descriptionSelection: "Nastavte minimální pobyt označených nocí, případně ho zamkněte či odemkněte. Ceny se nemění.",
      actionStated: "Nastavit bez zamčení",
      actionStatedHint: "Optivo toto číslo drží, před vyprodanou nocí a v rámci „Vždy uvolnit do“ ho zkrátí a po uvolnění pokoje se k němu vrátí.",
      actionSet: "Nastavit minimální pobyt a zamknout",
      actionSetHint: "Optivo na těchto nocích tento minimální pobyt ponechá a ceny dál počítá.",
      actionLock: "Zamknout současný minimální pobyt",
      actionLockHint: "Minimální pobyt zůstane takový, jaký je teď.",
      actionUnlock: "Odemknout",
      actionUnlockHint: "Optivo minimální pobyt těchto nocí hned znovu nastaví podle vaší strategie. Vrátí tím i pobyt nastavený bez zamčení.",
      actionHandBack: "Vrátit minimální pobyt strategii",
      actionHandBackHint: "Optivo vaše číslo zahodí a minimální pobyt těchto nocí hned znovu nastaví podle vaší strategie.",
      nightsLabel: "Minimální pobyt (noci)",
      nightsHint: "1 noc znamená bez minimálního pobytu.",
      statedNightsHint: "1 až {max} noci. 1 noc znamená bez minimálního pobytu.",
      statedTooLong: "Bez zamčení lze nastavit nejvýše {max} noci. Delší pobyt nastavte a zamkněte.",
      nightsUnreadable: "Zadejte celý počet nocí, například 2.",
      submitStated: "Nastavit",
      submitSet: "Nastavit a zamknout",
      submitLock: "Zamknout",
      submitUnlock: "Odemknout",
      submitHandBack: "Vrátit",
      preview: "Minimální pobyt (noci): {before} → {after}",
      // Služba starší než tato volba odpoví, že ji nezná.
      statedUnavailable: "Nastavení bez zamčení zatím není k dispozici. Použijte „Nastavit a zamknout“, nebo stránku znovu načtěte a zkuste to ještě jednou.",
      // Počítá se bez rezervací, proto to, co se žádá, ne to, co bude platit.
      previewStated: "Minimální pobyt (noci): {before} → {after}, kratší tam, kde to vyžaduje vyprodaná noc nebo „Vždy uvolnit do“",
      doneStated: { one: "{count} minimální pobyt nastaven bez zamčení", few: "{count} minimální pobyty nastaveny bez zamčení", other: "{count} minimálních pobytů nastaveno bez zamčení" },
      doneSet: { one: "{count} minimální pobyt nastaven a zamčen", few: "{count} minimální pobyty nastaveny a zamčeny", other: "{count} minimálních pobytů nastaveno a zamčeno" },
      doneLock: { one: "{count} minimální pobyt zamčen", few: "{count} minimální pobyty zamčeny", other: "{count} minimálních pobytů zamčeno" },
      doneUnlock: { one: "{count} minimální pobyt vrácen strategii", few: "{count} minimální pobyty vráceny strategii", other: "{count} minimálních pobytů vráceno strategii" },
      // `{nights}` je tvar z `bulkEdit.nightCount`: co Optivo nastavilo na
      // vrácených nocích, pokud na všech stejně.
      setToNote: "Optivo je nastavilo na {nights}.",
      setToNoteOne: "Optivo ho nastavilo na {nights}.",
      recalculatedNote: "Optivo je hned znovu nastavilo podle vaší strategie.",
      notRecalculatedNote: "Optivo je znovu nastaví při příští synchronizaci.",
      skippedNote: "Přeskočeno: {count} — Optivo pro ně zatím nemá plán.",
      // Co platí teď, je-li to méně než právě nastavené číslo.
      statedLoweredNote: "Zatím platí {nights}: zkracuje ho vyprodaná noc nebo „Vždy uvolnit do“. Chcete-li číslo udržet, použijte „Nastavit a zamknout“.",
      statedNotRecalculatedNote: "Co bude platit, Optivo určí při příští synchronizaci.",
      alreadyStatedNote: "Už nastaveno na toto číslo: {count}.",
      alreadyFollowsNote: "Už je nastavuje strategie: {count}.",
      // Sekce délky pobytu v detailu ceny.
      detailLocked: "Zamčeno",
      detailLockedWithPrice: "Zamčeno s cenou",
      detailUnlock: "Odemknout",
      detailUnlockLabel: "Odemknout minimální pobyt",
      detailStated: "Nastaveno bez zamčení",
      // `{nights}` je tvar z `bulkEdit.nightCount`.
      detailStatedNights: "Nastaveno bez zamčení: {nights}",
      detailStatedSaveLocks: "Číslo změněné tady se zamkne. Bez zamčení ho změníte volbou „Nastavit bez zamčení“ v zobrazení Min. pobyt.",
      detailHandBack: "Vrátit minimální pobyt",
      detailHandBackLabel: "Vrátit minimální pobyt strategii",
      // Slovo v buňce zobrazení minimálního pobytu, vedle „Zamčeno“ a „Nahráno“:
      // „Nastaveno“ se do nejužší noci nevejde.
      statedBadge: "Zadáno",
      unlockingToast: "Odemykám minimální pobyt…",
      unlockedToast: "Minimální pobyt odemčen",
      unlockedRecalculatedToast: "Minimální pobyt odemčen a znovu nastaven",
      handingBackToast: "Vracím minimální pobyt strategii…",
      handedBackToast: "Minimální pobyt vrácen strategii",
      handedBackRecalculatedToast: "Minimální pobyt vrácen a nastaven podle strategie",
      savedToast: "Minimální pobyt uložen a zamčen",
      savedBothToast: "Ceny a minimální pobyt uloženy a zamčeny",
      saveMinStay: "Uložit a zamknout min. pobyt",
      saveBoth: "Uložit a zamknout",
    },
    deltaVsUploaded: "{delta} / {previous}",
    deltaNeverUploaded: "Zatím nenahráno do systému správy ubytování",
    deltaVsLastUpload: "Porovnáno s poslední cenou nahranou do systému správy ubytování",
    deltaVsLastCalculation: "Porovnáno s předchozí vypočítanou cenou",
    viewLabel: "Zobrazení kalendáře",
    calendarControls: "Ovládání kalendáře",
    densityCompact: "Kompaktní zobrazení",
    densityCompactHint: "Užší noci, takže se na obrazovku vejde víc z vybraného období.",
    phoneLayout: {
      heading: "Ceny",
      options: "Zobrazení a filtry",
      optionsShort: "Možnosti",
      sectionView: "Zobrazit",
      sectionRoomTypes: "Typy pokojů",
      sectionPrices: "Ceny",
      sectionNotices: "Upozornění",
      sectionLegend: "Legenda",
      sectionMore: "Další",
      calculatedAt: "Spočítáno {time}",
      send: "Odeslat",
      needsAttention: "vyžaduje pozornost",
      selectNight: "Označit všechny typy pokojů na {night}",
    },
    baselineLabel: "Porovnat s",
    baselineLastCalculation: "Poslední přepočet",
    baselineYesterday: "Včera",
    baselineWeek: "Minulý týden",
    baselineMonth: "Minulý měsíc",
    baselineYesterdayTitle: "Porovnáno s cenou před 24 hodinami",
    baselineWeekTitle: "Porovnáno s cenou před 7 dny",
    baselineMonthTitle: "Porovnáno s cenou před 30 dny",
    baselineRecordedOn: "zaznamenáno {date}",
    baselineNoHistory: "Zatím nejsou zaznamenány ceny tak daleko zpět — toto porovnání se doplní, jak se ceny budou postupně přepočítávat.",
    metricScaleLabel: "Škála údaje v buňce",
    metricDelta: "Změna ceny",
    metricOccupancy: "Obsazenost",
    metricDemand: "Poptávka",
    metricMarket: "Tlak trhu",
    metricOccupancyShort: "obs.",
    metricMarketShort: "trh",
    metricOccupancyTitle: "Při výpočtu této ceny byl tento typ pokoje obsazen z {current} % · celý objekt {property} % oproti cíli {target} %",
    metricOccupancyPropertyTitle: "Při výpočtu této ceny byla obsazenost celého objektu {current} % oproti cíli {target} % — obsazenost tohoto typu pokoje se objeví po dalším přepočtu ceny",
    metricMarketTitle: "Při výpočtu této ceny byl tlak trhu {pct} %. Vyšší hodnota znamená slabší poptávku — obsazenost pod cílem a rezervace za tempem — a tam, kde last-minute ceny sledují poptávku, silnější tlak na slevu těsně před příjezdem. 0 % znamená, že termín byl na cíli nebo nad ním.",
    metricUnavailable: "K této ceně nejsou uloženy podrobnosti výpočtu — doplní se při dalším přepočtu ceny",
    soldOut: "Vyprodáno",
    soldOutTitle: "Při výpočtu ceny už byly všechny pokoje tohoto typu na tuto noc obsazené",
    paceScopeProperty: "Tento typ pokoje má příliš málo pokojů, než aby jeho vlastní čísla něco znamenala, takže hodnocení sleduje celé ubytování.",
    paceLegendLabel: "Tempo",
    paceLegendAtTarget: "Na cíli",
    paceLegendOnPace: "V tempu nebo příliš brzy",
    paceLegendWatch: "Brzy zaostane",
    paceLegendBehind: "Zaostává",
    paceBehindIn: "zaostane za {days} d",
    paceBehindToday: "zaostane dnes",
    paceScopePropertyAtTarget: "{pct} % na cíli",
    paceScopePropertyBehind: "{pct} % zaostává",
    paceScopePropertyIn: "{pct} % za {days} d",
    paceScopePropertyToday: "{pct} % dnes",
    paceBehindLabel: "zaostává",
    paceBehindSince: "Zaostává už {days} dní.",
    paceUnavailable: "Tempo rezervací se nepodařilo načíst — pohled obsazenosti zobrazuje čísla bez hodnocení tempa.",
    paceAtTargetTitle: "Obsazeno {current} % — na cíli {target} % nebo nad ním. Není co dohánět.",
    paceOutlookTitle: "Rezervace sem obvykle přicházejí do ~{window} dní před příjezdem, takže dnes by bylo běžné ~{expected} % obsazeno oproti cíli {target} %. Při {current} % je tento termín zatím v pořádku — pokud ale nic nepřibude, za {days} dní začne zaostávat.",
    paceWatchTitle: "K dnešnímu dni by bylo běžné ~{expected} % obsazeno (~{window}denní okno rezervací), tento termín je na {current} % oproti cíli {target} %. Zatím ne kriticky, ale ujíždí — {days} dní do zaostání.",
    paceCloseTitle: "Obsazeno {current} % oproti cíli {target} % — dost blízko na to, aby tento termín nemohl výrazně zaostat.",
    paceBehindTitle: "Takto daleko předem by bylo běžné ~{expected} % obsazeno (~{window}denní okno rezervací), tento termín je ale na {current} % — zaostává za cílem {target} %. Je čas jednat.",
    metricLegendDemandLow: "Za tempem",
    metricLegendDemandHigh: "Před tempem",
    metricLegendMarketLow: "V pořádku",
    metricLegendMarketHigh: "Pod tlakem",
    metricCompset: "Ceny trhu",
    metricCompsetShort: "{pct} %",
    metricCompsetTitle: "Místní konkurence ocenila tuto noc na {pct}. percentilu svého zachyceného rozpětí — 100 % je nejdražší zachycená noc, 0 % nejlevnější. Medián {median} napříč {count} konkurenty s cenou. Prázdné noci nemají zachycená data.",
    metricLegendCompsetLow: "Trh levný",
    metricLegendCompsetHigh: "Trh drahý",
  },

  // ── Probíhající přepočet cen ────────────────────────────
  // Co stránka Ceny ukazuje místo kalendáře, dokud se ceny objektu
  // přepočítávají, a po přepočtu, který se nedokončil.
  priceRecalculation: {
    running: "Přepočítávám ceny…",
    runningHint: "Kalendář se zobrazí, jakmile budou nové ceny hotové, obvykle do několika sekund. Odeslání a úpravy cen do té doby počkají.",
    slow: "Stále pracuji — váš property management systém odpovídá déle než obvykle.",
    progressLabel: "Průběh přepočtu",
    failed: "Přepočet cen se nedokončil.",
    failedHint: "Část cen v kalendáři může být ještě z doby před změnou.",
    dismiss: "Zavřít",
  },

  // ── Vývoj cen ───────────────────────────────────────────
  // Cena jedné noci v čase, pro každý typ pokoje. Každý přepočet, který cenu
  // změnil, je schod; čára drží cenu až do další změny.
  priceHistory: {
    tabTitle: "Vývoj cen",
    intro: "Jak se cena jedné noci měnila v čase, řádek pro každý typ pokoje — stejná cena, jakou ukazuje kalendář. Každý schod je přepočet, který ji změnil, nebo cena zadaná ručně; čára drží cenu až do další změny. Rezervovaný pokoj je na čáře vyznačen v okamžiku rezervace a schod, na kterém se prodal, je zvýrazněn druhou barvou. Zamčenou cenu žádný přepočet nemění a doba, kdy byla zamčená, je podbarvená.",
    legendLabel: "Legenda",
    legendLocked: "Zamčená cena",
    legendBookedStep: "Cena, za kterou se pokoj prodal, po celou dobu, kdy platila",
    lockedSince: "Zamčeno od {date}",
    lockedSinceAtLatest: "Zamčeno nejpozději od {date}",
    lockSet: "Zamčeno",
    lockLifted: "Odemčeno",
    lockSeeded: "Zamčeno už dříve",
    lockedPoint: "Cena byla zamčená",
    locksUnavailable: "Zámky pro tuto noc se nepodařilo načíst, proto nejsou vyznačené.",
    nightLabel: "Noc",
    previousNight: "Předchozí noc",
    nextNight: "Další noc",
    tonight: "Dnešní noc",
    periodLabel: "Období",
    periodDay: "24 h",
    periodWeek: "7 dní",
    periodMonth: "30 dní",
    periodAll: "Vše",
    loading: "Načítání vývoje cen…",
    loadError: "Vývoj cen pro tuto noc se nepodařilo načíst.",
    empty: "Pro tuto noc zatím není zaznamenaná žádná cena",
    emptyHint: "Optivo zaznamená cenu pokaždé, když ji přepočet změní, a historii noci uchovává ještě 30 dní poté, co noc uplyne.",
    followsOccupancy: "Obsazenost {occupancy}",
    changeCount: "Počet změn: {count}",
    changeSince: "od {date}",
    range: "Rozpětí {low} – {high}",
    noChange: "V tomto období beze změny",
    moveCalculated: "Přepočet",
    moveManual: "Zadáno ručně",
    moveFirst: "První záznam",
    carriedIn: "Platila už na začátku období",
    standingNow: "Platí dosud",
    standingPast: "Poslední cena této noci",
    fromPrice: "z {price}",
    showChanges: "Zobrazit změny",
    hideChanges: "Skrýt změny",
    colWhen: "Kdy",
    colPrice: "Cena",
    colChange: "Změna",
    colSource: "Zdroj",
    chartLabel: "{roomKind} — vývoj ceny noci {night}",
    booked: "Rezervace",
    bookedSummary: "Rezervováno: {rooms}",
    bookedEarlier: "+{count} dříve",
    bookedPrice: "Cena v době rezervace",
    bookedDayPrices: "Ceny platné v ten den",
    bookedDayOnly: "PMS uvádí jen den této rezervace, ne čas",
    bookingsUnavailable: "Rezervace pro tuto noc se nepodařilo načíst, proto nejsou vyznačené.",
    statusOption: "Opce",
    statusBlocked: "Blokace",
    stayTotal: "{total} za {nights}",
    soldPerNight: "pokoj {price} za noc",
    soldWholePerNight: "{price} za noc",
    roomsTimes: "{count} ×",
    moreRooms: "+{count} další",
    rooms: { one: "{count} pokoj", few: "{count} pokoje", other: "{count} pokojů" },
    guests: { one: "{count} host", few: "{count} hosté", other: "{count} hostů" },
    nights: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
  },

  // ── Upload Log ──────────────────────────────────────────
  uploadLog: {
    // ── Rozsah obnovení ─────────────────────────────────────────────────────
    restorePricesLabel: "Obnovit ceny",
    restorePricesHint: "Vrátí uložené noční ceny zpět do Property Management.",
    restoreMinLosLabel: "Obnovit minimální délku pobytu",
    restoreMinLosHint: "Vrátí uloženou minimální délku pobytu, aniž by změnila ceny.",
    restoreMinLosMissing: "Tento ceník nemá uloženou minimální délku pobytu, nelze ji obnovit.",
    restoreMinLosUnsupported: "Toto připojení k Property Management neumí přijmout omezení minimální délky pobytu.",
    restoreScopeNothingSelected: "Vyberte alespoň ceny nebo minimální délku pobytu.",
    // Obnovení vrací vrstvy, které nahrání zapsalo, pokud je záloha obsahuje.
    // Jedna vrstva se řekne, nepřepíná se, a za ní důvod, proč se druhá
    // nevrací; nadpis a tlačítko jmenují, co se odešle.
    restoreOnlyPrices: "Vrátí se jen ceny.",
    restoreOnlyMinLos: "Vrátí se jen minimální délka pobytu.",
    restoreNothing: "Není co vrátit.",
    restorePricesNotWritten: "Toto nahrání neodeslalo ceny, takže ty zůstanou, jak jsou teď.",
    restoreMinLosNotWritten: "Toto nahrání neodeslalo minimální délku pobytu, takže ta zůstane, jak je teď.",
    restorePricesNotHeld: "Ceník uložený před tímto nahráním neobsahuje žádné ceny.",
    restoreMinLosNotHeld: "Ceník uložený před tímto nahráním neobsahuje minimální délku pobytu.",
    restoreNothingHeld: "Ceník uložený před tímto nahráním neobsahuje ceny ani minimální délku pobytu.",
    restoreConfirmTitleMinLos: "Obnovit minimální délku pobytu v Property Management?",
    restoreConfirmTitleBoth: "Obnovit ceny i minimální délku pobytu v Property Management?",
    restoreConfirmActionMinLos: "Obnovit minimální délku pobytu",
    restoreConfirmActionBoth: "Obnovit ceny i minimální délku pobytu",
    // ── Navázané cenové plány ───────────────────────────────────────────────
    derivedPlansTitle: "Navázané cenové plány",
    derivedPlansHint: "Ceny vypočítané ze základního plánu a odeslané po něm.",
    derivedPlanChannel: "Kanálový cenový plán {id}",
    derivedPlanNonRefundable: "Nevratný cenový plán {id}",
    derivedPlanChannelNamed: "Kanálový cenový plán {name}",
    derivedPlanNonRefundableNamed: "Nevratný cenový plán {name}",
    derivedPlanNights: "{count} nocí",
    derivedStatusSent: "Odesláno",
    derivedStatusFailed: "Chyba",
    derivedStatusSkipped: "Neodesláno",
    tabTitle: "Historie nahrávání",
    title: "Historie nahrávání cen",
    loading: "Načítání historie nahrávání...",
    empty: "Zatím nebyly nahrány žádné ceny do Property Management.",
    date: "Datum",
    status: "Stav",
    entriesUploaded: "Položky",
    errors: "Chyby",
    warnings: "Varování",
    statusCompleted: "Dokončeno",
    statusFailed: "Selhalo",
    statusHeld: "Pozdrženo: {count}",
    detailTitle: "Detail nahrávání — {date}",
    expandDetails: "Rozbalit na celou obrazovku",
    collapseDetails: "Sbalit panel",
    errorsList: "Chyby:",
    heldReasonsList: "Proč je Optivo pozdrželo:",
    moreErrors: "+{count} dalších chyb",
    warningsList: "Varování:",
    moreWarnings: "+{count} dalších varování",
    notSentSoldOut: "Vyprodáno — cena neodeslána",
    notDeliveredRefused: "Odmítnuto Property Managementem — nedoručeno",
    notDeliveredHeld: "Pozdrženo Optivem — nezapsáno",
    notDeliveredHeldHint: "Noc, kterou Optivo pozdrželo, Property Management neodmítl: Optivo ji samo nezapsalo a není tam co opravovat. V návrhu zůstává neodeslaná a další odeslání ji zkusí znovu.",
    // Totéž pod řádkem obnovení, kde poslední věta odeslání neplatí: obnovení
    // nepotvrzuje žádné návrhy, takže noc později nic neodešle. Obnovení
    // pozdrží jen minimální délku pobytu vracenou bez ceny.
    notDeliveredHeldHintRestore: "Noc, kterou Optivo pozdrželo, Property Management neodmítl: Optivo ji samo nezapsalo a není tam co opravovat. Optivo obnovení samo znovu nezkouší, takže si noc ponechá minimální délku pobytu, jakou má teď. Chcete-li vrátit tu uloženou, spusťte obnovení později znovu nebo ji nastavte přímo v Property Management.",
    notDeliveredUndelivered: "Nedoručeno — selhalo spojení",
    notSentHint: "Noc, na kterou už není co prodat, se do Property Management neodesílá — nová cena by neměla co ovlivnit. Jakmile se pokoj uvolní, cena odejde při dalším odeslání.",
    uploadedRooms: "Nahrané typy pokojů:",
    roomEntries: "{count} položek",
    dateRange: "{from} — {to}",
    startedAt: "Zahájeno: {date}",
    pageInfo: "Stránka {page} z {totalPages} ({total} nahrávání)",
    occupancy: "Obs. {n}",
    // Minimální délka pobytu, kterou nahrání pro noc zapsalo, kde ji odesílalo.
    minStayColumn: "Min. pobyt",
    minStayNights: { one: "{count} noc", few: "{count} noci", other: "{count} nocí" },
    changeDelta: "{delta} oproti {previous}",
    changeNew: "Nové",
    changeUnchanged: "Beze změny",
    summaryLabel: "Změny cen:",
    summaryIncreased: "{count} zvýšeno",
    summaryDecreased: "{count} sníženo",
    summaryUnchanged: "{count} beze změny",
    summaryNew: "{count} nových",
    filterDateFrom: "Od data",
    filterDateTo: "Do data",
    filterRoomKind: "Typ pokoje",
    filterAllRooms: "Všechny typy pokojů",
    filterClear: "Smazat filtry",
    retentionNote: "Historie se po {days} dnech automaticky maže.",
    retentionNoteOne: "Historie se po 1 dni automaticky maže.",
    dryRunBadge: "Zkušební běh",
    restore: "Obnovit",
    restoreTitle: "Obnovit celý ceník, který byl v Property Management před tímto nahráním",
    restoreTitleCells: "Vrátit to, co toto nahrání v Property Management změnilo",
    restorePointExpired: "Záloha ceníku vypršela",
    restorePointExpiredHint: "Ceník zálohovaný před tímto nahráním už není k dispozici, takže ho odtud nelze stáhnout ani obnovit.",
    restoredBadge: "Obnovení",
    downloadPriceList: "Stáhnout ceník",
    downloadRangeHint: "Stáhne aktuální ceník z Property Management na následujících 90 dní",
    downloadSuccess: "Ceník byl stažen",
    downloadError: "Stažení ceníku se nezdařilo",
    downloadBackup: "Stáhnout zálohu",
    downloadBackupError: "Stažení zálohy se nezdařilo",
    restoreConfirmTitle: "Obnovit ceny v Property Management?",
    restoreConfirmBody: "Znovu odešle celý ceník uložený před nahráním dne {date}: všechny typy pokojů ve všech nocích jeho období, nejen to, co toto nahrání změnilo. Co bylo na toto období odesláno později, bude přepsáno.",
    restoreConfirmBodyCells: "Vrátí to, co bylo v Property Management těsně před nahráním dne {date}, jen u nocí a typů pokojů, které toto nahrání zapsalo. Co na ně bylo odesláno později, bude přepsáno.",
    // Každá z vět jmenuje jen to, co obnovení vrací: ceny, minimální délku
    // pobytu, nebo obojí. Minimální délka pobytu se do zálohy čte ze stejné
    // kopie jako ceny. Kde obnovení nevrací nic, neříká se nic.
    restoreConfirmCachedRead: "Property Management odpovídá Optivu z kopie ceníku, která může být hodinu i déle stará. Cena změněná krátce před tímto nahráním proto může v záloze chybět a obnovení pak vrátí tu předchozí.",
    restoreConfirmCachedReadMinLos: "Property Management odpovídá Optivu z kopie ceníku, která může být hodinu i déle stará. Minimální délka pobytu změněná krátce před tímto nahráním proto může v záloze chybět a obnovení pak vrátí tu předchozí.",
    restoreConfirmCachedReadBoth: "Property Management odpovídá Optivu z kopie ceníku, která může být hodinu i déle stará. Cena nebo minimální délka pobytu změněná krátce před tímto nahráním proto může v záloze chybět a obnovení pak vrátí tu předchozí.",
    restoreConfirmAction: "Obnovit ceny",
    restoreStarted: "Obnovení zahájeno — znovu se odesílá do Property Management",
    restoreSuccess: "Ceník byl obnoven v Property Management",
    restoreError: "Obnovení ceníku se nezdařilo",
    restoreHeld: "Z odesílaného Optivo samo pozdrželo {count} a Property Management nic neodmítl. Historie nahrávání uvádí každou noc a důvod.",
    uploadPriceList: "Nahrát ceník",
    uploadRangeHint: "Odešle dříve stažený ceník zpět do Property Management",
    uploadInvalidFile: "Tento soubor není platný ceník",
    uploadEmptyFile: "Tento ceník neobsahuje žádné ceny k nahrání",
    uploadTooLarge: "Tento soubor je příliš velký k nahrání",
    uploadConfirmTitle: "Nahrát ceník do Property Management?",
    // Věta i tlačítko jmenují, co přepínače odešlou: ceny, minimální délku
    // pobytu, nebo obojí. `{prices}` a `{minStays}` jsou počítané tvary níže,
    // ve 4. pádě po „Odešle“. Buňky souboru neobsahují vždy obojí, proto má
    // každá vrstva vlastní počet.
    uploadConfirmBody: "Odešle {prices} ze souboru do Property Management a přepíše aktuální ceny pro tato data. Nejprve se vytvoří záloha, abyste mohli akci vrátit zpět.",
    uploadConfirmBodyMinLos: "Odešle {minStays} ze souboru do Property Management a přepíše aktuální minimální délku pobytu pro tato data. Nejprve se vytvoří záloha, abyste mohli akci vrátit zpět.",
    uploadConfirmBodyBoth: "Odešle {prices} a {minStays} ze souboru do Property Management a přepíše aktuální ceny i minimální délku pobytu pro tato data. Nejprve se vytvoří záloha, abyste mohli akci vrátit zpět.",
    uploadConfirmBodyNothing: "Takto se ze souboru nic neodešle.",
    uploadConfirmPrices: { one: "{count} cenu", few: "{count} ceny", other: "{count} cen" },
    uploadConfirmMinStays: { one: "{count} minimální délku pobytu", few: "{count} minimální délky pobytu", other: "{count} minimálních délek pobytu" },
    uploadConfirmAction: "Nahrát ceny",
    uploadConfirmActionMinLos: "Nahrát minimální délku pobytu",
    uploadConfirmActionBoth: "Nahrát ceny i minimální délku pobytu",
    // Pod přepínačem cen u souboru, který žádné ceny neobsahuje (přepínač je
    // pak vypnutý a zamčený); a místo „vyberte alespoň…“ tam, kde PMS neumí
    // přijmout nic z toho, co soubor obsahuje.
    uploadPricesMissing: "Tento ceník nemá uložené žádné ceny, nelze je obnovit.",
    uploadNothingToSend: "Z tohoto souboru není co odeslat.",
    uploadStarted: "Nahrávání zahájeno",
    uploadSuccess: "Ceník byl nahrán do Property Management",
    uploadError: "Nahrání ceníku se nezdařilo",
    // ── Co nahrání spustilo ─────────────────────────────────────────────────
    startedBy: "Spustilo",
    nights: "Noci",
    triggerManual: "Ruční odeslání",
    triggerAutoSync: "Auto-sync",
    triggerScheduleSave: "Uložení auto-syncu",
    triggerRetry: "Opakování",
    triggerRestore: "Obnovení",
    triggerNotRecorded: "Nezaznamenáno",
    triggerWithEmail: "{trigger} — {email}",
    triggerNotRecordedDetail: "Nebylo zaznamenáno, co toto nahrání spustilo",
    detailNights: "Noci: {nights}",
  },

  // ── Analysis ─────────────────────────────────────────────
  analysis: {
    title: "Analýza ubytování",
    description: "Poznatky z historických dat o rezervacích za poslední rok",
    metaSummary: "Na základě {count} rezervací od {from} do {to}",
    emptyTitle: "Zatím žádná data analýzy",
    emptyDescription: "Spusťte úvodní analýzu z Nastavení Property Manageru po připojení k PMS.",

    // Settings card
    settingsTitle: "Analýza ubytování",
    settingsDescription: "Analyzujte historii rezervací pro detekci sezónnosti, vzorců poptávky a výkonnosti kanálů",
    completed: "Dokončeno",
    failed: "Selhalo",
    fetchingProgress: "Stahování rezervací… týden {current} z {total}",
    computing: "Výpočet metrik…",
    completedSummary: "Analýza dokončena — {count} analyzovaných rezervací",
    completedNoHistory: "Zatím bez historie rezervací",
    completedNoHistorySummary:
      "Ve vašem PMS zatím nejsou žádné pobyty, ze kterých by Optivo mohlo měřit — u provozu, který ještě neotevřel, je to běžné. Optivo vyjde z cen v ceníku vašeho PMS, pokud tam nějaké jsou, a z nastavení, která zvolíte v dalších krocích, a jakmile začnou přicházet rezervace, bude se učit z nich.",
    lastRun: "Poslední spuštění:",
    unknownError: "Došlo k neznámé chybě",
    noAnalysisYet: "Dosud nebyla spuštěna žádná analýza. Klikněte níže pro analýzu historie rezervací.",
    runAnalysis: "Spustit analýzu",
    reRun: "Spustit znovu",
    viewResults: "Zobrazit výsledky",
    toastStarted: "Analýza zahájena",
    toastStartedDesc: "Stahování dat o rezervacích za poslední rok — bude to trvat několik minut.",
    resultsLoadFailed: "V\u00fdsledky anal\u00fdzy se nepoda\u0159ilo na\u010d\u00edst. Zkuste anal\u00fdzu spustit znovu.",
    toastFailed: "Nepodařilo se spustit analýzu",

    // Seasonality section
    seasonalityTitle: "Sezónnost",
    seasonalityDesc: "Měsíční průměrné noční sazby a objemy pokojů-nocí za poslední rok",
    seasonalityEmpty: "Zatím není dostatek historie rezervací pro určení sezónnosti.",
    peakMonth: "Vrcholný měsíc",
    offPeakMonth: "Mimo sezónu",
    month: "Měsíc",
    avgPrice: "Prům. noční sazba",
    reservations: "Pokoj-noci",
    totalRevenue: "Celkový příjem",

    // Weekday distribution section
    weekdayTitle: "Rozložení dnů check-inu",
    weekdayDesc: "Které dny v týdnu mají nejvíce příjezdů hostů",
    busiestDay: "Nejrušnější den",
    quietestDay: "Nejklidnější den",

    // Booking window section
    bookingWindowTitle: "Rezervační okno",
    bookingWindowDesc: "Doba mezi vytvořením rezervace a příjezdem hosta",
    avgLeadTime: "Průměrná doba předstihu",
    medianLeadTime: "Mediánová doba předstihu",
    days: "dní",

    // Length of stay section
    lengthOfStayTitle: "Délka pobytu",
    lengthOfStayDesc: "Rozložení délek pobytů napříč všemi rezervacemi",
    avgNights: "Průměrný pobyt",
    medianNights: "Mediánový pobyt",
    nights: "nocí",

    // Channel mix section
    channelMixTitle: "Rezervační kanály",
    channelMixDesc: "Přehled zdrojů rezervací a jejich průměrných cen",
    topChannel: "Hlavní kanál",
    channel: "Kanál",
    bookings: "Rezervace",
    share: "Podíl",

    // Key metrics section
    keyMetricsTitle: "Klíčové výkonnostní metriky",
    occupancyRate: "Obsazenost",
    basePrice: "Základní cena",
    minimalAcceptablePrice: "Minimální cena",
    roomNightsSold: "Prodané pokojnoci",
    uniqueRooms: "Unikátní pokoje",

    // Occupancy spread section
    occupancySpreadTitle: "Cenové rozpětí dle obsazenosti",
    occupancySpreadDesc: "Cenové multiplikátory pro každý typ pokoje odvozené z dat sazeb PMS za poslední měsíc — ukazuje typický cenový rozdíl mezi úrovněmi obsazenosti",
    occupancySpreadRoomKind: "Typ pokoje",
    occupancySpreadBasePrice: "Základní cena (obs. 2)",
    occupancySpreadLevel: "Obsazenost",
    occupancySpreadMultiplier: "Multiplikátor",
    occupancySpreadDiff: "Prům. Δ cena",
    occupancySpreadStep: "obs. {from}→{to}",
    occupancySpreadNoData: "Nejsou k dispozici žádná data sazeb — sazby budou načteny při příštím spuštění analýzy.",

    // Create strategy from analysis
    createStrategyTitle: "Vytvořit strategii z analýzy",
    createStrategyDesc: "Generujte novou cenovou strategii s parametry automaticky odvozenými z této analýzy. Nová strategie bude nastavena jako aktivní; stávající strategie budou zachovány pro referenci.",
    createStrategyButton: "Vytvořit strategii",
    createStrategySuccess: "Strategie vytvořena a aktivována",
    createStrategySuccessDesc: "Nová strategie \"{name}\" je nyní aktivní. Přejděte do Strategie pro kontrolu a úpravy.",
    createStrategyError: "Nepodařilo se vytvořit strategii",

    // Room kind price tiers section
    roomKindTiersTitle: "Cenové úrovně typů pokojů",
    roomKindTiersDesc: "Typy pokojů klasifikované do cenových úrovní na základě průměrných nočních sazeb z individuálních (neskupinových) rezervací",
    roomKindTiersNoData: "Žádná data o cenových úrovních typů pokojů — nejprve spusťte analýzu.",
    roomKindTiersName: "Typ pokoje",
    roomKindTiersAvgPrice: "Prům. noční cena",
    roomKindTiersCount: "Rezervace",
    roomKindTiersTier: "Úroveň",
    roomKindTiersExcluded: "{count} skupinových rezervací vyloučeno z výpočtu úrovní.",
    tierBudget: "Budget",
    tierEconomy: "Economy",
    tierStandard: "Standard",
    tierHigh: "High",
    tierLuxury: "Luxury",

    // AI property classification
    aiInsightsTitle: "AI analýza ubytování",
    aiInsightsNote: "Vygenerováno umělou inteligencí na základě vaší historie rezervací",
    propertyTypes: {
      hotel: "Hotel",
      boutique_hotel: "Boutique hotel",
      pension_bb: "Penzion / B&B",
      apartment: "Apartmán / Apart-hotel",
      resort: "Resort",
      hostel: "Hostel",
      villa_vacation_rental: "Vila / Rekreační pronájem",
      campsite: "Kemp",
    },
    propertyTypeDescriptions: {
      hotel: "Ubytování s privátními pokoji a širokým mixem hostů; obvykle vyvážená poptávka mezi týdnem a víkendem.",
      boutique_hotel: "Menší designově zaměřené ubytování s prémiovějším positioningem a vyšší citlivostí na průměrnou cenu.",
      pension_bb: "Menší penzion nebo B&B s jednodušším servisem a výraznější sezónností volnočasových pobytů.",
      apartment: "Apartmány nebo apart-hotel se zpravidla delší délkou pobytu a poptávkou od rodin a delších pobytů.",
      resort: "Destinačně orientované ubytování s výraznými sezónními výkyvy, víkendovými/svátečními špičkami a vyšší citlivostí na události.",
      hostel: "Sdílené nebo budget ubytování s vyšší cenovou citlivostí a častějšími rezervacemi na kratší předstih.",
      villa_vacation_rental: "Samostatné vily/pronájmy s kolísavou poptávkou, delšími pobyty a silnou koncentrací během svátků.",
      campsite: "Kemp nebo glamping s chatkami, bungalovy či místy pro stany a karavany; výrazně sezónní, s poptávkou soustředěnou do léta a kolem svátků.",
    },
  },

  // ── PMS Connections ────────────────────────────────────
  connections: {
    title: "Připojení k Property Manageru",
    addConnection: "Přidat připojení",
    newConnection: "Nové připojení k Property Manageru",
    connectionName: "Název připojení (volitelné)",
    hotelId: "ID hotelu",
    login: "Přihlášení",
    password: "Heslo",
    noActiveConnection: "Žádné aktivní připojení",
    manage: "Spravovat připojení",
    // Viz komentář v `en.ts`: odkaz, který v menu stojí místo přepínače objektů.
    switcherUnavailable:
      "Objekty se tu nepodařilo vypsat. Přepnete mezi nimi na stránce {page}, nebo načtěte stránku znovu.",
    empty: "Zatím žádná připojení k Property Manageru. Přidejte jedno pro začátek.",
    confirmDelete: "Opravdu chcete smazat toto připojení? Všechna související data budou ztracena a lidé, jejichž přístup se týká jen tohoto objektu, ztratí přístup k účtu.",
    clickToRename: "Klikněte pro přejmenování",
    members: "Členové",
    manageMembers: "Spravovat členy",
    invite: "Pozvat",
    inviteEmailPlaceholder: "Zadejte e-mail pro pozvání",
    inviteIdentifierPlaceholder: "E-mail nebo ID uživatele v Logto",
    removeMember: "Odebrat člena",
    makeOwner: "Nastavit uživatele {user} jako vlastníka",
    makeMember: "Změnit uživatele {user} na člena",
    confirmStepDown: "Opravdu se vzdát vlastnických práv? Vrátit vám je může jen jiný vlastník.",
    roleUpdated: "Role člena byla změněna",
    roleUpdateFailed: "Roli člena se nepodařilo změnit",
    userNotFound: "Nebyl nalezen uživatel s tímto e-mailem ani ID",
    inviteSuccess: "Uživatel byl úspěšně přidán",
    delete: "Smazat připojení",
    testHealth: "Otestovat připojení",
    modify: "Upravit nastavení připojení",
    activate: "Nastavit jako aktivní",
    activeLabel: "Aktivní",
    redoWizard: "Znovu spustit průvodce",
    healthStatusHealthy: "Zdravé",
    healthStatusUnhealthy: "Nezdravé",
    previoApiBase: "Previo API Base",
    languageId: "ID jazyka",
    testAndConnect: "Otestovat a připojit",
    cancel: "Zrušit",
    connectionCreated: "Připojení bylo úspěšně vytvořeno",
    connectionTestFailed: "Test připojení selhal",
    testing: "Testování…",
    backToConnections: "Zpět na připojení",
    roleOwner: "Vlastník",
    roleMember: "Plný přístup",
    roleViewer: "Jen pro čtení",
    roleStaff: "Náhled správce, jen pro čtení",
    inviteRole: "Přístup",
    inviteRoleMember: "Plný přístup — ceny, strategie a nastavení",
    inviteRoleViewer: "Jen pro čtení — ceny a analýzy",
    makeViewer: "Nastavit uživateli {user} přístup jen pro čtení k tomuto objektu",
    makeFullAccess: "Dát uživateli {user} plný přístup",
    confirmMakeViewer: "Uživatel {user} si ponechá přístup jen pro čtení k tomuto objektu.",
    confirmMakeFullAccess: "Uživatel {user} bude moci měnit ceny a nastavení všech objektů v tomto účtu i těch, které účet přidá později. Pokračovat?",
    confirmMakeFullAccessThis: "Uživatel {user} bude moci měnit ceny a nastavení tohoto objektu i objektů, které účet přidá později. Pokračovat?",
    confirmContinue: "Pokračovat?",
    viewerInviteSuccess: "Přístup jen pro čtení byl udělen",
    roleFrontDesk: "Recepce",
    inviteRoleFrontDesk: "Recepce — ceny a rezervace, bez analýz a tržeb",
    chooseAccess: "Zvolte, co smí u tohoto objektu dělat",
    makeFrontDesk: "Nastavit uživateli {user} přístup recepce k tomuto objektu",
    makeAnalyticsViewer: "Umožnit uživateli {user} vidět i analýzy tohoto objektu",
    confirmMakeFrontDesk: "Uživatel {user} si ponechá přístup recepce k tomuto objektu — ceny a rezervace, žádné analýzy ani tržby.",
    confirmWidenToAnalytics: "Uživatel {user} uvidí i analýzy tohoto objektu (obsazenost, tržby a poslední rezervace) a celou stránku Ceny: všechny pohledy kalendáře, vývoj cen a historii nahrávání. Pokračovat?",
    frontDeskInviteSuccess: "Přístup recepce byl udělen",
    alreadyReadOnly: "Tento uživatel už má k tomuto objektu přístup: {kind}. Změňte ho na jeho řádku.",
    frontDeskNotGranted: "Přístup recepce se nepodařilo udělit: uživatel má teď přístup jen pro čtení včetně analýz. Pokud to tak být nemá, přístup odeberte.",
  },

  restartWizard: {
    title: "Znovu spustit průvodce nastavením pro {name}?",
    intro: "Průvodce začne znovu od prvního kroku. Pokud už objekt naceňuje nějaká strategie, bude ho naceňovat dál, dokud průvodce nedokončíte, a stránky objektu zůstanou přístupné. Je-li zapnutý auto-sync, bude tyto ceny dál odesílat.",
    keptHeading: "Zůstane beze změny",
    keptDistribution: "Napojení kanálů na cenové plány a nevratná cena, nastavené v editoru strategie. Průvodce se na ně neptá.",
    keptLocation: "Lokalita ubytování a zdrojové trhy hostů",
    keptOpeningDate: "Datum otevření, pokud bylo zadáno",
    keptCompset: "Zda je zapnuté sledování konkurence",
    keptPropertyFacts: "Typ objektu a to, co obsahují vaše ceny",
    keptShown: "Lokalitu, datum otevření, typ objektu, odpovědi o vašich cenách a sledování konkurence vám průvodce ukáže tak, jak jsou, abyste je potvrdili nebo změnili.",
    askedHeading: "Zeptá se znovu",
    askedPricing: "Ceny podle typu pokoje, Denní úpravy cen, Svátky a speciální dny a všechna ostatní cenová nastavení. Průvodce je navrhne znovu z analýzy vašich rezervací a výchozích hodnot Optiva. Změny provedené v současné strategii se nepřenesou.",
    finish: "Dokončením průvodce se uloží nová strategie a začne se používat. Strategie, kterou nahradí, zůstane v sekci Strategie a můžete se k ní vrátit.",
    confirm: "Spustit průvodce znovu",
  },

  // ── Objekt a jeho ceny ───────────────────────────────────
  // Viz en.ts.
  propertyFacts: {
    cardTitle: "Objekt a jeho ceny",
    cardDescription: "Jaký objekt to je a co obsahuje cena ve vašem ceníku. Na totéž se ptá průvodce nastavením.",
    propertyTypeLabel: "Typ objektu",
    propertyTypeHint: "Penzion, apartmán, rekreační pronájem nebo kemp se účtuje jako OPTIVO Rentals bez ohledu na počet pokojů, každý jiný typ jako OPTIVO Hotels.",
    saved: "Uloženo",
    saveFailed: "Odpověď se nepodařilo uložit. Nic se nezměnilo.",
    loadFailed: "Uložené odpovědi se nepodařilo načíst. Zkuste stránku načíst znovu.",
    notStated: "Neuvedeno",
    statedByOperator: "Uvádí je provozovatel v průvodci nastavením nebo v Nastavení a jen tam se mění.",
    groupTitle: "Co obsahují vaše ceny",
    groupDescription: "Tyto odpovědi ceny Optiva nemění. Podle nich v analýzách správně čteme vaše čísla a Optivo vám níže řekne, kde základní cenu počítá jinak než vy.",
    choose: "Vyberte…",
    vatLabel: "Ceny ve vašem ceníku jsou",
    vat: {
      gross: "S DPH",
      net: "Bez DPH",
      not_vat_payer: "Nejsme plátci DPH",
    },
    breakfastLabel: "Snídaně",
    breakfast: {
      included: "Je v ceně",
      separate: "Platí se zvlášť",
      not_offered: "Snídani nenabízíme",
    },
    baseLabel: "Základní cena ve vašem ceníku je",
    base: {
      double_room: "Pokoj pro dva hosty",
      single_use: "Pokoj pro jednoho (single use)",
      per_person: "Cena za osobu",
    },
    notices: {
      matches: "Optivo ji počítá stejně: jeho základní cena je cena pokoje pro dva hosty.",
      singleUse: "Základní cena Optiva je cena pokoje pro dva hosty a cenu pro jednoho hosta z ní odvozuje v Cenách podle počtu hostů. Základní ceny v Cenách podle typu pokoje proto zadávejte jako cenu pro dva. Výjimkou je pokoj jen pro jednoho hosta: jeho základní cena je jeho jediná cena.",
      singleUseOnePrice: "Základní cena Optiva je cena pokoje pro dva hosty a {pms} od Optiva přijímá jednu cenu za pokoj a noc: cenu pro dva. Základní ceny v Cenách podle typu pokoje proto zadávejte jako cenu pro dva.",
      perPerson: "Optivo nastavuje cenu za celý pokoj, pro každý počet hostů zvlášť, nikdy za osobu. Pokud máte v ceníku cenu za osobu, napište nám přes Zeptat se Optiva dřív, než Optivo začne ceny posílat, ať ověříme, že je váš PMS převezme správně.",
      perPersonPerGuest: "Optivo posílá do {pms} cenu za celý pokoj, pro každý počet hostů zvlášť, nikdy cenu za osobu. Napište nám přes Zeptat se Optiva dřív, než Optivo začne ceny posílat, ať ověříme, že je {pms} převezme správně.",
      perPersonOnePrice: "{pms} od Optiva přijímá jednu cenu za pokoj a noc: cenu pro dva hosty, nikdy cenu za osobu. Než do něj Optivo začne ceny posílat, změňte ceník v {pms} na jednu cenu za celý pokoj.",
    },
  },

  readOnlyAccess: {
    badge: "Jen pro čtení",
    badgeHint: "Vidíte ceny a analýzy tohoto objektu. Jeho ceny spravuje revenue manager.",
    staffBadge: "Náhled správce, jen pro čtení",
    staffBadgeHint: "Vidíte v tomto objektu vše včetně strategie a nic tu nelze změnit. Majitelé vás vidí v jeho seznamu přístupů.",
    frontDeskBadge: "Recepce",
    frontDeskBadgeHint: "Vidíte ceny a rezervace tohoto objektu a proč je každá cena taková, jaká je. Jeho ceny spravuje revenue manager.",
    priceDetailNote: "Přístup jen pro čtení — ceny nastavuje revenue manager objektu.",
    explanationTitle: "Co ovlivnilo tuto cenu",
    movedUp: "Zvýšilo",
    movedDown: "Snížilo",
    noMoves: "Žádný faktor tuto cenu neposunul od výchozí hodnoty.",
    explanationNote: "V přístupu jen pro čtení vidíte, které faktory cenu posunuly. Nastavení za nimi zůstává u revenue managera objektu.",
    marketSettingsNote: "Které konkurenty porovnáváme a kde měříme trh, nastavuje revenue manager objektu.",
    marketNotConfigured: "Sledování trhu zatím pro tento objekt není nastaveno.",
  },

  adminReadOnly: {
    openProperty: "Otevřít jen pro čtení",
    openPropertyTitle: "Otevřít {name} jen pro čtení?",
    openPropertyDescription:
      "Přidáte se do jeho seznamu přístupů jako „jen pro čtení“ a majitelé vás tam uvidí. Jako správce v něm uvidíte vše včetně strategie a nic nepůjde změnit. Až budete hotovi, odeberte se v Přístupu.",
    openPropertyConfirm: "Přidat mě a otevřít",
    openPropertyFailed: "Objekt se nepodařilo otevřít",
    openPropertyMember:
      "K tomuto objektu už máte plný přístup, takže se otevře s plným přístupem, ne jen pro čtení. Otevřete ho v přepínači objektů.",
    strategyBadge: "Jen pro čtení",
    strategyNote: "Tuto strategii si prohlížíte jen pro čtení. Nic zde nelze změnit ani uložit.",
    viewStrategy: "Zobrazit strategii",
    viewButton: "Zobrazit",
    viewVersion: "Zobrazit verzi {version}",
    viewNamed: "Zobrazit {name}",
  },

  // ── Automatická synchronizace ─────────────────────────────
  autoSync: {
    title: "Auto-sync do PMS",
    description: "Automaticky odesílat návrhy cen do PMS podle synchronizačního plánu ubytování.",
    enabled: "Povolit auto-sync",
    disabled: "Auto-sync je vypnutý",
    configure: "Nastavit Auto-Sync",
    frequencyLabel: "Frekvence aktualizací",
    frequencyHelp: "Jak často kontrolujeme nové rezervace ve vašem PMS a posíláme zpět aktualizované ceny. Nastavuje Optivo pro celé připojení — pro změnu nás kontaktujte.",
    daysAheadLabel: "Počet dní dopředu k odeslání",
    daysAhead1: "Pouze dnes",
    daysAhead3: "Příštích 3 dny",
    daysAhead7: "Příštích 7 dní",
    daysAhead14: "Příštích 14 dní",
    daysAhead30: "Příštích 30 dní",
    daysAhead60: "Příštích 60 dní",
    daysAhead90: "Příštích 90 dní",
    daysAhead180: "Příštích 180 dní",
    daysAhead365: "Příštích 365 dní",
    daysAhead548: "Příštích 548 dní (1,5 roku)",
    farHorizonHint: "Noci vzdálené více než {days} dní se přepočítávají jednou denně, ne při každé synchronizaci. Každá synchronizace odešle nejvýše {days} nocí, nejbližší jako první, takže delší období se do PMS dostane během několika synchronizací.",
    roomKindsLabel: "Typy pokojů k synchronizaci",
    roomKindsAll: "Všechny typy pokojů",
    roomKindsSelected: "{count} / {total} vybráno",
    saveSuccess: "Nastavení auto-sync uloženo",
    saveError: "Nepodařilo se uložit nastavení auto-sync",
    loadError: "Nepodařilo se načíst nastavení auto-sync",
    sendsOnSave: "Po uložení Optivo odešle do PMS, co je nové nebo se od posledního odeslání změnilo: hned a potom {cadence}.",
    noPricesYet: "Pro toto ubytování Optivo zatím nespočítalo žádné ceny, takže není co odesílat. Po uložení je auto-sync začne odesílat sám, jakmile budou spočítané.",
    stillOnUntilSaved: "Auto-sync je zatím zapnutý. Odesílat přestane, až nastavení uložíte.",
    unsavedTitle: "Zavřít bez uložení?",
    unsavedTurnedOn: "Auto-sync je tady zapnutý, ale není to uložené. Když okno teď zavřete, zůstane vypnutý a Optivo do PMS samo nic odesílat nebude.",
    unsavedTurnedOff: "Auto-sync je tady vypnutý, ale není to uložené. Když okno teď zavřete, zůstane zapnutý a Optivo bude do PMS odesílat dál.",
    unsavedChanged: "Co jste tady změnili, není uložené. Když okno teď zavřete, auto-sync poběží dál podle dříve uloženého nastavení.",
    unsavedKeepEditing: "Zpět k nastavení",
    unsavedDiscard: "Zavřít bez uložení",
    continuesBelow: "Pokračování níže",
    minLosChangeInOptivo: "Dokud minimální pobyty odesílá Optivo, měňte je v Optivu. Změnu provedenou přímo v systému {pms} může pozdější odeslání přepsat.",
    statusEnabled: "Auto-sync povolený",
    statusDisabled: "Auto-sync vypnutý",
  },

  // ── Minimální pobyt držený v PMS na noc ─────────────────
  // Hotel v Previu volí, zda drží minimální pobyt pro každý typ pokoje, nebo
  // jeden na noc pro všechny typy. Optivo ho plánuje po typech pokojů, takže
  // tam, kde PMS drží jeden na noc, odeslání každou noc srovná.
  //
  // Hláška říká, co Optivo udělá, a po provozovateli nic nechce. Její poslední
  // věta začínala „Chcete-li mít minimální pobyt pro každý typ pokoje zvlášť,
  // nastavte v systému {pms}…“ a hotel ji četl jako výzvu a nastavení neuložil
  // (OPT-55, říjen 2026). Proto `body` začíná tím, že není co měnit, a `option`
  // říká, co PMS umí: žádná věta tu nikoho nevyzývá, aby něco nastavil.
  minStayScope: {
    title: "Systém {pms} drží jeden minimální pobyt na noc",
    body: "V systému {pms} nemusíte nic měnit. Tento objekt v něm má jeden minimální pobyt na noc pro všechny typy pokojů, a Optivo proto každé noci pošle nejvyšší minimální pobyt z jejích typů pokojů.",
    nights: {
      one: "V tomto odeslání se to týká {count} noci, kde mají typy pokojů různý minimální pobyt.",
      few: "V tomto odeslání se to týká {count} nocí, kde mají typy pokojů různý minimální pobyt.",
      other: "V tomto odeslání se to týká {count} nocí, kde mají typy pokojů různý minimální pobyt.",
    },
    lost: "Ztratí se tím kratší minimální pobyt jen pro jeden typ pokoje, například ten, který Optivo nastaví před vyprodanou nocí.",
    // Jen tam, kde je jeden minimální pobyt na noc vlastním nastavením hotelu v PMS.
    option: "Systém {pms} umí držet minimální pobyt i pro každý typ pokoje zvlášť. Je to nastavení na jeho straně a Optivo pak každému typu pokoje pošle jeho vlastní.",
    // Pod vlastními minimálními pobyty typů pokojů v Omezení délky pobytu, kde
    // žádné odeslání není na obrazovce: čím se tam minimum typu pokoje v PMS stane.
    roomKindsBody: "Když Optivo odesílá minimální pobyty, pošle každé noci všem typům pokojů nejvyšší z jejich minimálních pobytů. Minimální pobyt nastavený tady jednomu typu pokoje proto v systému {pms} platí pro celou noc, je-li nejvyšší, a nižší se tam neprojeví.",
  },

  minStayAlone: {
    title: "Systém {pms} zapíše minimální pobyt jen spolu s cenou",
    body: "Systém {pms} drží minimální pobyt přímo u ceny. Při vypnutých cenách proto Optivo s minimálním pobytem každé noci znovu pošle i její aktuální cenu, kterou nejdřív ověří proti ceně, kterou samo naposledy odeslalo.",
    held: "Noc, jejíž cena byla v systému {pms} změněna přibližně v době, kdy ji Optivo naposledy odeslalo, zůstane na další odeslání, protože nelze říct, která z obou cen je novější. Stejně tak noc bez ceny. Historie nahrávání je uvádí jako pozdržené.",
    together: "Při odeslání spolu s cenami se zapíší všechny noci najednou.",
    // Totéž o minimálních pobytech vracených bez cen (potvrzení obnovení a
    // nahrání ceníku ze souboru v Historii nahrávání).
    restoreBody: "Systém {pms} drží minimální pobyt přímo u ceny. Optivo proto spolu s každým minimálním pobytem, který vrací, znovu pošle i cenu, kterou noc má teď, a nejdřív ji ověří proti ceně, kterou samo naposledy odeslalo.",
    restoreHeld: "Noc, jejíž cena byla v systému {pms} změněna přibližně v době, kdy ji Optivo naposledy odeslalo, si ponechá minimální pobyt, jaký má teď, protože nelze říct, která z obou cen je novější. Stejně tak noc bez ceny. Historie nahrávání je uvádí jako pozdržené.",
    restoreTogether: "Noc vrácená spolu se svou uloženou cenou se zapíše, ať je její cena teď jakákoli.",
  },

  // ── Průvodce nastavením ──────────────────────────────────
  notifications: {
    title: "Oznámení",
    empty: "Máte vše přečteno.",
    unread: "nepřečtené",
    markRead: "Označit jako přečtené",
    markAllRead: "Označit vše",
    preferencesTitle: "Oznámení",
    preferencesDesc: "Vyberte, na co vás má Optivo upozorňovat.",
    // Jeden přepínač na každé oznámení, které Optivo opravdu posílá. Upozornění
    // na poptávku ve vzdálenějších termínech je jediné; čtyři před ním se
    // nikdy neposílala.
    prefFarOutDemand: "Neobvyklá poptávka ve vzdálenějších termínech",
    prefFarOutDemandHint:
      "Když noc vzdálená víc než 30 dní získá během týdne aspoň 3 obsazené pokoje, nebo pětinu všech pokojů, je-li to víc. Upozornění se objeví ve zvonečku na přehledu a přijde e-mailem vlastníkům a členům tohoto objektu.",
    saved: "Předvolby oznámení uloženy.",
    saveError: "Předvolby oznámení se nepodařilo uložit.",
    types: {
      onboarding_complete: {
        title: "Vaše cenová strategie je aktivní",
        body: "Optivo teď navrhuje noční ceny na nadcházející týdny.",
      },
      far_out_demand: {
        title: "Neobvyklá poptávka ve vzdálenějších termínech",
        // {minDaysOut} a {windowDays} přicházejí s upozorněním.
        body: "Tyto noci vzdálené víc než {minDaysOut} dní se za posledních {windowDays} dní nezvykle rychle plní:",
      },
    },
    // Pod každou nocí nebo souvislými nocemi skupiny: „+14 pokojů, obsazeno 15 z 15“.
    farOutDemandFigures: "{gained}, obsazeno {taken} z {total}",
    farOutDemandRoomsGained: { one: "+{count} pokoj", few: "+{count} pokoje", other: "+{count} pokojů" },
  },
  wizard: {
    title: "Průvodce nastavením ubytování",
    subtitle: "Pojďme nastavit vaše ubytování pro optimální cenotvorbu.",
    valueSources: {
      pms: "Importováno z PMS",
      location: "Podle polohy ubytování",
      analysis: "Vypočteno z vaší historie rezervací",
      priceList: "Z ceníku ve vašem PMS",
      questionnaire: "Odvozeno z odpovědí dotazníku",
      typology: "Výchozí profil",
      default: "Výchozí doporučení",
    },
    continue: "Pokračovat",
    back: "Zpět",
    enabled: "Zapnuto",
    disabled: "Vypnuto",
    exitWizard: "Zpět na přehled",
    expertMode: "Expertní režim",
    expertModeHint: "Projde i pokročilé kroky. Vše přeskočené už má funkční výchozí hodnoty a doladit se dá později v editoru strategie.",
    expertStepsHidden: "Přeskočených pokročilých kroků: {count}",
    expertSectionReveal: "Jsem expert — zobrazit pokročilá nastavení",
    expertSectionHide: "Skrýt pokročilá nastavení",

    // ── Vzdělávací onboarding (dotazník typologie) ──
    education: {
      // Úvod / uvítací krok
      introTitle: "Vítejte v Optivu",
      introSubtitle: "Chytrá cenotvorba, která se přizpůsobí vašemu stylu práce.",
      introWhatTitle: "Co je revenue management?",
      introWhatBody:
        "Revenue management znamená prodat správný pokoj za správnou cenu na správnou noc. Místo jedné pevné ceny se vaše ceny pružně mění podle poptávky — vyšší, když je pokojů málo, nižší, když potřebujete zaplnit.",
      introHowTitle: "Jak Optivo funguje",
      introHowBody:
        "Optivo sleduje vaše rezervace, obsazenost a tempo rezervací a pak navrhne cenu na každou noc. Vy rozhodujete, kolik toho dělá automaticky — od jemných doporučení až po plný autopilot.",
      introExpectTitle: "Co vás čeká",
      introExpectBody:
        "Nejprve připojíme vaše ubytování a načteme historii rezervací. Pak si vyberete: Optivo zapnete s doporučeným nastavením, necháte si ho nastavit od nás, nebo projdete krátkým průvodcem. Jakékoli nastavení můžete později změnit.",
      introGlossaryHint: "Neznáte termín? Najeďte na podtržené slovo a uvidíte srozumitelné vysvětlení.",
      glossaryTermAvgPrice: "průměrná cena",
      introStart: "Začít",
      introTime: "Zabere asi 5 minut",

      // Chrome dotazníku
      quizProgress: "Otázka {current} z {total}",
      quizMultiHint: "Vyberte jednu nebo více",
      quizSingleHint: "Vyberte jednu možnost",
      quizNext: "Další",
      quizPrev: "Zpět",
      quizSelectPrompt: "Pro pokračování vyberte alespoň jednu odpověď.",
      quizSelectOnePrompt: "Pro pokračování vyberte odpověď.",
      quizAffectsSettings: "Ovlivní: {settings}",
      quizRefinesTypology: "Ovlivňuje profil, který vám doporučíme na další obrazovce; přímo nemění žádnou číselnou hodnotu.",
      quizCurrentProfile: "Průběžná shoda: {profile}",
      quizPricesHint: "Zadejte všechny tři ceny",
      quizPricesPrompt: "Pokračovat můžete po zadání všech tří cen.",
      quizPricesOrderWarning: "Zkontrolujte pořadí — minimální cena má být pod cenou v běžný den a cena při špičkové poptávce nad ní.",

      // Otázkové kroky — ptáme se těsně před nastavením, které ovlivní
      gateSubtitle: "Vaše odpovědi nastaví výchozí hodnoty na další obrazovce. Každé číslo tam můžete změnit.",
      gateConfigures: "Následuje: {section}",
      gateSkip: "Přeskočit — použít výchozí hodnoty typologie",
      gateSkipToRecommendation: "P\u0159esko\u010dit \u2014 doporu\u010dte mi profil",
      gateFinish: "Pokračovat na {section}",

      // Rekapitulace na kroku nastavení, který odpovědi ovlivnily
      recapTitle: "Nastaveno podle vašich odpovědí",
      recapYouAnswered: "Odpověděli jste",
      recapChangeAnswer: "Změnit odpověď",
      recapValuesSet: "Nastavené hodnoty: {fields}",
      // Srozumiteln\u00e9 n\u00e1zvy nastaven\u00ed, kter\u00e1 odpov\u011b\u010f zm\u011bnila.
      recapFields: {
        occupancyScaleMax: "Jak moc pln\u00fd hotel zvedne cenu",
        paceSensitivity: "Jak moc tempo rezervac\u00ed h\u00fdbe cenou",
        occupancyDamping: "Jak klidn\u011b cena reaguje",
        marketInfluenceEnabled: "Ceny konkurence",
        marketInfluenceStrength: "Jak moc konkurence h\u00fdbe va\u0161\u00ed cenou",
        minPricePct: "Nejni\u017e\u0161\u00ed povolen\u00e1 cena",
        maxPricePct: "Nejvy\u0161\u0161\u00ed povolen\u00e1 cena",
        priceGranularity: "Zaokrouhlov\u00e1n\u00ed cen",
        weekendHolidayLosEnabled: "Minim\u00e1ln\u00ed pobyt o v\u00edkendech a sv\u00e1tc\u00edch",
        defaultMinLos: "Minim\u00e1ln\u00ed po\u010det noc\u00ed",
        losReleaseWithinDays: "Kdy se minim\u00e1ln\u00ed pobyt uvoln\u00ed",
        lastMinutePacing: "Jak rychle se m\u011bn\u00ed ceny na posledn\u00ed chv\u00edli",
        lastMinuteAdjustmentSteps: "Po\u010det krok\u016f last-minute ceny",
        gapFillingEnabled: "Zapl\u0148ov\u00e1n\u00ed kr\u00e1tk\u00fdch mezer mezi pobyty",
        gapFillingMaxGapDays: "Nejdel\u0161\u00ed mezera, kterou m\u00e1 cenu zapl\u0148ovat",
        gapFillingAdjustmentPct: "\u00daprava ceny p\u0159i zapl\u0148ov\u00e1n\u00ed mezery",
        bridgeDayPremiumMultiplier: "\u00daprava ceny za spojovac\u00ed dny",
        fridayAdjustmentPct: "P\u00e1tek",
        saturdayAdjustmentPct: "Sobota",
        sundayAdjustmentPct: "Ned\u011ble",
        premiumRoomControlIntent: "P\u0159\u00edsn\u011bj\u0161\u00ed kontrola prémiov\u00fdch pokoj\u016f",
      },

      // Editor strategie — zodpovězení otázky sekce bez průvodce
      sectionQuestionUnanswered: "Zodpovězte jednu otázku a dolaďíte tuto sekci.",
      sectionQuestionAnswer: "Zodpovědět otázku",
      sectionQuestionApply: "Použít odpověď",
      sectionQuestionCancel: "Zrušit",
      // Povinná otázka nemá výchozí hodnotu, kterou by šlo použít — uvádí údaj
      // o ubytování, který nic jiného nedodá.

      // Otázky
      quiz: {
        q33: {
          question: "Za jaké ceny očekáváte, že budete noc prodávat?",
          hint: "Nepovinné. Optivo oceňuje každý typ pokoje z jeho vlastní rezervační historie — nebo, dokud žádnou nemá, z ceníku ve vašem PMS — a ceny konkrétního typu pokoje si můžete nastavit níže. Tyto ceny nastoupí jen u typu pokoje, na který nestačí nic z toho. Pokud je uvedete, určují zároveň, jak daleko se cena smí pohnout od své základní ceny. Uveďte je pro standardní dvoulůžkový pokoj vč. DPH.",
          minLabel: "Minimální cena",
          minHint: "Nejnižší cena, za kterou byste noc ještě prodali.",
          regularLabel: "Cena v běžný den",
          regularHint: "Vaše obvyklá cena za běžnou noc.",
          topLabel: "Cena při špičkové poptávce",
          topHint: "Cena, za kterou očekáváte prodej, když je poptávka nejvyšší.",
        },
      },

      // Archetypy
      archetypes: {
        volume_chaser: {
          name: "Hlavně ať je plno",
          tagline: "Radši plno za slušnou cenu než prázdno.",
        },
        revpar_hunter: {
          name: "Hlavně dobrá cena",
          tagline: "Míříme vysoko, každou noc, automaticky.",
        },
        set_and_forget: {
          name: "Zapnout a mít klid",
          tagline: "Zapnete a dál se o nic nestaráte.",
        },
        lost: {
          name: "Začínáme",
          tagline: "Provedeme vás krok za krokem.",
        },
      },

      // Glosář (srozumitelné vysvětlivky)
      glossary: {
        adr: "Průměrná cena — průměrná noční cena pokojů, které jste skutečně prodali.",
        occupancy: "Obsazenost — podíl vašich pokojů, které jsou na danou noc rezervované.",
        pace: "Tempo — jak daleko jsou rezervace termínu oproti tomu, kde v tuto dobu obvykle bývají.",
        pickup: "Pickup — čistá změna rezervací termínu za dané období (nové rezervace minus storna).",
        los: "LOS — délka pobytu. Kolik nocí rezervace zahrnuje. Minimální LOS blokuje krátké pobyty ve vytížených termínech.",
        rgi: "RGI — index tvorby tržeb. Jak si vedete v tržbách oproti okolní konkurenci (100 = na stejné úrovni).",
      },
    },

    // ── Způsob nastavení (krok `typology`) ──
    setupChoice: {
      title: "Jak chcete ceny nastavit?",
      subtitle: "Ubytování je připojené a historie rezervací načtená. Doporučujeme nechat první nastavení na nás — cokoli pak můžete změnit v editoru strategie.",
      switchOn: "Zapnout a jet",
      switchOnMeta: "Hotovo za minutu",
      switchOnDescription: "Optivo nastaví strategii podle historie vašich rezervací a doporučených hodnot a vezme vás rovnou na přehled. Ceny posílá do PMS, jen když je zapnuté odesílání — a to je zpočátku vypnuté.",
      switchOnAction: "Zapnout a dokončit",
      switchOnNeedsPrices: "Optivo zatím nemá z čeho vaše typy pokojů nacenit — nejsou žádné rezervace k měření a ceník ve vašem PMS neuvádí žádné ceny. Pokračujte a zadejte základní cenu pro každý typ pokoje; odtud už můžete nastavení dokončit.",
      optivo: "Nastaví to za mě Optivo",
      optivoMeta: "Ozveme se do {hours} hodin",
      optivoDescription: "Náš tým s pomocí AI projde historii rezervací vašeho ubytování a strategii nastaví za vás. Do té doby se nic nemění.",
      optivoRecommended: "Doporučujeme",
      optivoIncludes: "Co od nás dostanete",
      optivoIncludesAnalysis: "Úvodní analýzu vašich rezervací a cen, kterou vám pošleme e-mailem",
      optivoIncludesStrategy: "Cenovou strategii nastavenou na míru vašemu ubytování",
      optivoIncludesNothingToFill: "Nic nevyplňujete — stačí odeslat žádost",
      optivoMessageLabel: "Chcete nám něco vzkázat? (nepovinné)",
      optivoMessagePlaceholder: "Třeba telefon a kdy se vám hodí zavolat",
      optivoAction: "Požádat Optivo o nastavení",
      requestedTitle: "Žádost o nastavení jsme přijali",
      requestedBody: "Požádali jste {date}. Ozveme se vám na {email} do {hours} hodin a do té doby nemusíte nic dělat.",
      requestedBodyAccount: "Požádali jste {date}. Ozveme se vám e-mailem do {hours} hodin a do té doby nemusíte nic dělat.",
      requestedCancel: "Zrušit žádost",
      sentToast: "Žádost odeslána do Optiva",
      sentToastDescription: "Ozveme se vám do {hours} hodin.",
      cancelledToast: "Žádost zrušena",
      closedToast: "Žádost už byla uzavřena",
      errorCancel: "Žádost se nepodařilo zrušit. Zkuste to za chvíli znovu.",
      ownWays: "Raději si ceny nastavíte sami?",
      ownWaysWaiting: "Nechcete čekat? Nastavte si ceny sami",
      guide: "Zjednodušený průvodce",
      guideSteps: { one: "{count} krátký krok", few: "{count} krátké kroky", other: "{count} krátkých kroků" },
      guideDescription: "Projdete jen to podstatné — ceny, víkendy a svátky — a zbytek necháte na funkčních výchozích hodnotách, které doladíte později v editoru strategie.",
      guideGoal: "Na co má Optivo cílit?",
      guideGoalRecommended: "Doporučeno Optivem",
      guideGoalRecommendedHint: "Drží cenu, dokud je příjezd daleko, a plní pokoje, jak se blíží.",
      guideAction: "Pokračovat v nastavení",
    },

    // Názvy kroků
    introStep: "Vítejte",
    pmsConnection: "Připojení PMS",
    setupChoiceStep: "Způsob nastavení",
    questionnaireStep: "Váš styl",
    doneForNow: "Prozatím mám hotovo",
    doneForNowTitle: "Pokračovat s výchozím nastavením profilu?",
    doneForNowDescription: "Optivo zachová vaše uložené volby a vytvoří aktivní strategii z výchozího nastavení profilu. Do PMS se žádné ceny neodešlou. Zbývající nastavení můžete doplnit v editoru strategie.",
    doneForNowConfirm: "Použít profil a pokračovat",
    doneForNowSuccess: "Vaše výchozí strategie je připravena k doladění.",
    doneForNowError: "Výchozí strategii se nepodařilo vytvořit. Zkuste to znovu.",
    stepSaveError: "Tento krok se nepoda\u0159ilo ulo\u017eit. Va\u0161e odpov\u011bdi z\u016fst\u00e1vaj\u00ed na obrazovce \u2014 zkuste za chv\u00edli pokra\u010dovat znovu.",
    analysis: "Analýza",
    propertyIdentity: "Profil ubytování",
    keyMetrics: "Klíčové metriky",
    propertyStructure: "Struktura ubytování",
    lastMinutePricing: "Last-Minute ceny",
    gapFilling: "Vyplnění mezer",
    bridgeDays: "Přemostění a okraje špičky",
    dayAdjustments: "Denní úpravy cen",
    seasonClassification: "Sezónnost",
    holidays: "Svátky",
    rateRestrictions: "Omezení délky pobytu",
    distribution: "Distribuce",
    strategyPosture: "Nastavení strategie",
    marketInfluenceStep: "Vliv trhu",
    roomKindOverrides: "Přepisy podle typu pokoje",
    bookingWindowStep: "Okno rezervací",
    lastMinuteStep: "Last-minute ceny",
    priceGranularity: "Granularita cen",
    restrictions: "Omezení",
    reviewActivate: "Kontrola a aktivace",

    // Průvodce v3 — otázkové kroky a sloučené kroky nastavení
    keyMetricsStep: "Klíčové metriky",
    specialDatesStep: "Svátky a speciální dny",
    keyMetricsQuestions: "K vašim cenám",
    marketInfluenceQuestions: "Ke konkurenci",
    priceGranularityQuestions: "K přesnosti cen",
    rateRestrictionsQuestions: "K pravidlům pobytu",
    lastMinuteQuestions: "K last-minute prodeji",
    dayAdjustmentsQuestions: "K vašemu týdnu",

    roomKindOverridesTitle: "Ceny podle typu pokoje",
    roomKindOverridesDescription: "Dva druhy nastavení: ceny patří každému typu pokoje zvlášť, zatímco cíl strategie a pohyb ceny během rezervačního okna jsou nastavení celého ubytování, která může typ pokoje převzít.",
    roomKindOverridesEmpty: "Žádné typy pokojů zatím nejsou detekovány — dokončete nejprve předchozí kroky.",
    occupancyRecommendation: "Doporučení z analýzy:",

    // Nové překlady kroků
    priceGranularityStepDescription: "Zvolte krok zaokrouhlování generovaných cen. Menší kroky umožňují jemnější kontrolu.",
    priceGranularityHint: "Krok zaokrouhlení generovaných cen.",
    priceCharmEnding: "Baťovská devítka",
    priceCharmEndingHint: "Odečte od zaokrouhlené ceny zvolenou částku, aby cena končila devítkou — místo 1000 vyjde 990. Nabízí se jen zakončení, která se vejdou do kroku zaokrouhlení.",
    priceCharmEndingOff: "Vypnuto",
    priceCharmEndingExample: "{before} → {after}",
    granularityExample: "Příklad",
    restrictionsTitle: "Omezení",
    restrictionsDescription: "Nastavte mantinely pro generované ceny a limity délky pobytu.",
    losRestrictionsHint: "Nastavte minimální a maximální počet nocí, které host může zarezervovat.",
    losRestrictionsTitle: "Omezení délky pobytu",
    losRestrictionsDescription: "Nastavte minimální a maximální počet nocí, které host může zarezervovat.",
    noDistributionData: "Z analýzy nejsou k dispozici žádná distribuční data. Můžete pokračovat.",
    reviewAnalysisSummary: "Na základě historické analýzy vašich rezervačních dat.",
    reviewNoHistorySummary: "Zatím bez historie rezervací — ceny vycházejí z ceníku ve vašem PMS a z nastavení výše a jakmile začnou přicházet rezervace, budou se učit z nich.",
    reviewEdit: "Upravit",
    reviewRoomPricesTitle: "Ceny podle typu pokoje",
    reviewRoomTypeColumn: "Typ pokoje",
    reviewMissingBasePriceTitle: "N\u011bkter\u00e9 typy pokoj\u016f zat\u00edm nemaj\u00ed cenu",
    reviewMissingBasePriceBody: "Optivo nedok\u00e1\u017ee spo\u010d\u00edtat ceny pro: {roomKinds}. Zadejte z\u00e1kladn\u00ed cenu te\u010f, nebo aktivujte a dopl\u0148te ji pozd\u011bji \u2014 ostatn\u00ed typy pokoj\u016f se nacen\u00ed tak \u010di tak.",
    reviewSpecialDatesActive: "{count} aktivn\u00edch",
    reviewCapacityValue: "{beds} l\u016f\u017eka \u00b7 pro {max} host\u016f",
    reviewNoCapacities: "Z va\u0161eho PMS se nena\u010detly \u017e\u00e1dn\u00e9 kapacity pokoj\u016f.",
    reviewPremiumTiers: "přirážkových úrovní",
    reviewDiscountTiers: "slevových úrovní",

    // Krok 1 - PMS
    pmsTitle: "Připojte svůj PMS",
    pmsDescription: "Připojte se ke svému PMS pro import pokojů a rezervací.",
    pmsConnected: "PMS úspěšně připojeno.",
    pmsAlreadyConfigured: "Vaše PMS připojení je aktivní.",

    // Krok 2 - Analýza
    analysisTitle: "Analyzujte své ubytování",
    analysisDescription: "Prověříme vaše historické rezervace a odvodíme vzory poptávky, obsazenosti a příjmů.",
    startAnalysis: "Spustit analýzu",
    analysisRunning: "Analyzuji rezervace…",
    analysisComplete: "Analýza dokončena.",
    analysisPrompt: "Klikněte níže pro zahájení analýzy historie rezervací. Může to trvat několik minut.",

    // Krok 2.5 - Klasifikace ubytování
    aiTitle: "Profil ubytování",
    aiDescription: "Potvrďte, čím vaše ubytování je, kde se nachází a jak jsou strukturované pokoje. Výchozí typ je Hotel — volitelně můžete spustit AI klasifikaci a výsledek ručně potvrdit nebo upravit.",
    aiAssistLabel: "Volitelná AI pomoc",
    aiChecking: "Kontroluji dostupnost AI…",
    aiClassifying: "Klasifikuji vaše ubytování…",
    aiClassifyingHint: "AI analyzuje historii vašich rezervací a určuje typ ubytování. Může to chvíli trvat.",
    aiCancel: "Zrušit",
    aiClassified: "Klasifikace dokončena",
    aiOverride: "Potvrďte nebo změňte typ ubytování",
    aiRetry: "Zkusit znovu",
    aiStartClassify: "Klasifikovat pomocí AI",
    aiCancelled: "Klasifikace zrušena",
    aiFailed: "Klasifikace selhala",
    aiCancelledDescription: "Zrušili jste AI klasifikaci. Vyberte typ ubytování ručně níže.",
    aiFailedDescription: "AI klasifikace nemohla být dokončena. Vyberte typ ubytování ručně níže.",
    aiAvailable: "AI dostupné",
    aiUnavailable: "AI nedostupné",
    aiAvailableTitle: "AI engine připojen",
    aiAvailableDescription: "Váš AI engine běží a automaticky klasifikuje typ ubytování během kroku analýzy. Není potřeba další nastavení.",
    aiUnavailableDescription: "AI engine (Ollama) není dostupný. Vyberte typ ubytování ručně níže.",
    aiSelectPropertyType: "Vyberte typ ubytování",
    aiSelectPlaceholder: "Zvolte typ ubytování…",

    // Lokalita trhu — zdroj externích signálů poptávky (prázdniny, počasí)
    marketLocationTitle: "Lokalita ubytování",
    marketLocationHint: "Určuje vaše časové pásmo — podle hodin ubytování se pozná, který den je dnes a kdy začíná last minute slevování — a načítá školní prázdniny, státní svátky a počasí pro váš trh.",
    marketLocationRequired: "Povinné — odsud se bere časové pásmo ubytování a podle jeho hodin se určuje, který den je dnes a kdy začíná last minute slevování.",
    openingDateLabel: "Datum otevření",
    openingDateHint:
      "Pro provoz, který ještě neotevřel nebo otevřel během posledního roku: první noc, kterou prodává. Optivo bude jeho rezervace měřit od tohoto dne — měsíce před otevřením se nebudou počítat jako měsíce, kdy nikdo nerezervoval — a první rok s ním bude zacházet jako s novým provozem.",
    marketLocationSearchPlaceholder: "Vyhledejte obec nebo město…",
    marketLocationNoResults: "Hledání nenašlo žádné místo.",
    marketLocationSharedName: "Stejný název má víc míst. Vyberte to své podle okresu a kraje.",
    marketLocationClear: "Vymazat",
    marketLocationChange: "Změnit",
    marketLocationFeederTitle: "Zdrojové trhy hostů",
    marketLocationFeederHint: "Země, ze kterých k vám hosté jezdí. Jejich školní prázdniny často ovlivní obsazenost víc než tuzemské — nastavte podíl každé z nich.",
    marketLocationFeederWeight: "Jak moc tento trh váží",
    marketLocationFeederCountry: "Zdrojový trh {n}",
    marketLocationFeederAdd: "Přidat",
    marketLocationFeederRemove: "Odebrat: {country}",

    // Sledování konkurence — od ceníku z 5. 10. 2026 součást předplatného,
    // dál jen po výslovném souhlasu. Viz komentář v `en.ts`.
    compsetTitle: "Sledování cen konkurence",
    compsetDescription: "Načítá, kolik si účtují okolní ubytování, a zahrne to do podmínek na trhu — ceny pak reagují na trh, ne jen na vaši vlastní obsazenost.",
    compsetIncludedNote: "Sledování konkurence je součástí předplatného a zůstává vypnuté, dokud ho nezapnete. Počet načtení cen z okolí je pro každý objekt měsíčně omezený; větší rozsah sjednáváme individuálně. Svátky, školní prázdniny a počasí načítáme tak jako tak.",
    compsetLocationRequired: "Nejdřív výše přiřaďte lokalitu ubytování — sledování konkurence vyhledává podle místa, bez něj nemůže běžet.",
    compsetEnableLabel: "Zapnout sledování konkurence",
    compsetOptOutHint: "Kdykoli to můžete vypnout v Nastavení → Sledování konkurence.",
    aiTypeDescriptionFallback: "Tento profil ubytování ovlivňuje doporučené výchozí chování cenotvorby.",
    aiLocationLabel: "Poloha ubytování",
    aiOptional: "volitelné",
    aiLocationPlaceholder: "např. Praha, Česká republika",
    aiLocationHint: "Zadání polohy pomůže AI provést přesnější klasifikaci.",

    // Krok 3 - Struktura
    propertyTitle: "Struktura ubytování",
    propertyDescription: "Ověřte typy pokojů, úrovně a kapacity. Upravte cokoliv, co nevypadá správně.",
    roomKind: "Typ pokoje",
    beds: "Lůžka",
    extraBeds: "Přistýlky",
    maxOccupancy: "Max. obsaz.",
    roomCount: "Pokojů",
    avgPrice: "Prům. cena",
    reservations: "rezervací",
    noRoomKinds: "Nebyly zjištěny žádné typy pokojů. Nejprve spusťte analýzu.",
    roomCapacities: "Kapacity pokojů",
    occupancyAdjustments: "Ceny dle počtu hostů",
    occupancyLabel: "Obs. {n}",
    occupancyAdjustmentWarning: "Cena pro méně hostů nesmí překročit cenu pro více hostů.",
    guestStepsHint: "Dva hosté platí základní sazbu. Tyto dva kroky určují cenu pro každý jiný počet hostů — Optivo je použije na každý typ pokoje podle jeho základní ceny a změna jednoho z nich přepíše tabulku níže.",
    singleGuestStepLabel: "Jeden host místo dvou",
    singleGuestStepHint: "Kolik zaplatí jeden host oproti základní sazbě pro dva, nikdy ne pod minimum platné pro danou noc.",
    extraGuestStepLabel: "Za každého hosta nad dva",
    extraGuestStepHint: "Připočte se za každého hosta nad dva — na lůžku i na přistýlce.",
    guestStepUnitAmount: "Částka",
    guestStepUnitSelect: "Jednotka pro {field}",
    guestPricingRoomTypeColumn: "Typ pokoje",
    guestPricingGuestsHeading: "Cena za noc podle počtu hostů",
    guestPricingBaseTag: "základ",
    guestPricingBaseNote: "Dva hosté platí základní sazbu typu pokoje. Počty lůžek pocházejí ze Struktury typů pokojů.",
    guestPricingSingleGuestNote: "Typ pokoje jen pro jednoho hosta se prodává za svou základní sazbu — krok „{step}“ ji nemění.",
    guestPricingExtraBedNote: "Podbarvená cena patří hostovi na přistýlce.",

    // Krok 4 - Poptávka
    demandTitle: "Přehled poptávkových vzorů",
    demandDescription: "Zkontrolujte sezónnost, chování při rezervacích a historické metriky. Upravte hodnoty, které nesedí.",
    occupancyRate: "Obsazenost",
    minimalAcceptablePrice: "Minimální cena",
    analysisInsights: "Poznatky z analýzy",
    avgLeadTime: "Průměrná doba předstihu",
    avgStay: "Průměrná délka pobytu",
    busiestDay: "Nejrušnější den příjezdu",
    quietest: "Nejklidnější",
    days: "dní",
    nights: "nocí",
    median: "Medián",
    seasonality: "Klasifikace sezón",
    seasonalityStepDescription: "Rozdělte měsíce na hlavní a vedlejší sezónu a nastavte odpovídající cenové úpravy.",
    seasonClassificationHint: "Označte každý měsíc jako Hlavní, Nízkou nebo Běžnou sezónu. Hlavní a nízké měsíce používají vlastní cenovou úpravu; běžné měsíce jsou neutrální.",
    seasonFromAnalysisTag: "Z analýzy",
    highSeason: "Hlavní",
    lowSeason: "Nízká",
    commonSeason: "Běžná",
    highSeasonMultiplier: "Úprava ceny v hlavní sezóně",
    lowSeasonMultiplier: "Úprava ceny mimo sezónu",
    seasonMonthAdjustment: "Úprava za {month}",
    seasonResetToAnalysis: "Obnovit z analýzy",
    // Nakreslený rok. Stejné tři řady jako u grafu dnů v týdnu — kdo přečetl
    // jeden, umí přečíst i tenhle.
    seasonChartTitle: "Váš rok",
    seasonChartConfigured: "Vaše cena",
    seasonChartHistory: "Za kolik jste prodali",
    seasonChartPattern: "Váš sezónní průběh",
    seasonChartAxisMoney: "{currency} za noc",
    seasonChartAnchorNamed: "Počítáno ze základní ceny pokoje {roomKind}: {price} {currency}",
    seasonChartAnchor: "Počítáno z vaší základní ceny: {price} {currency}",
    seasonChartAnchorPick: "Počítáno ze základní ceny pokoje",
    seasonChartAnchorOption: "{roomKind} · {price} {currency}",
    bookingWindow: "Nastavení rezervačního okna",
    shortWindowDays: "Krátké okno (dny)",
    lastMinuteDiscount: "Sleva na poslední chvíli",
    occupancyThreshold: "Práh obsazenosti",
    // Krok 4.5 - Svátky
    holidaysTitle: "Cenotvorba svátků a speciálních dnů",
    holidaysDescription: "Nastavte cenové úpravy pro státní svátky a vlastní speciální dny.",
    holidayCountries: "Země hostů",
    holidayCountriesHint: "Vyberte jednu nebo více zemí pro sloučení jejich státních svátků.",
    holidayCountriesPlaceholder: "Vyberte země…",
    countrySelected: "země vybrána",
    countriesSelected: "zemí vybráno",
    selectCountry: "Vyberte zemi…",
    detectingCountry: "Automatická detekce z PMS…",
    countryNotReported: "Vaše PMS neuvedla zemi tohoto objektu. Vyberte ji níže a načtou se jeho státní svátky.",
    reloadHolidays: "Znovu načíst státní svátky",
    weekendHolidayLosEnabled: "Minimální pobyt o víkendech a svátcích",
    weekendHolidayLosEnabledHint: "Nastaví minimálně 2 noci pro příjezdy v pátek a sobotu. Když státní svátky víkend prodlouží, pokryje se celý blok volna — až 4 noci, od večera příjezdu přes každý další nočleh uvnitř, aby jej nerozdělila jednonoční rezervace. Poslední den bloku je dnem odjezdu, proto zůstává bez omezení, a běžné všední dny se neomezují nikdy. V posledních dnech před příjezdem, které nastavíte níže, se minimum uvolní vždy; dřív se může uvolnit, když je poptávka slabá, pokud to níže nevypnete. Pokud pobyt narazí na noc, kdy je jeho typ pokoje už vyprodaný, minimum tohoto typu pokoje se zkrátí na pobyt, který se ještě dá rezervovat, dokud se pokoj neuvolní.",
    losReleaseWithinDays: "Vždy uvolnit do",
    losReleaseWithinDaysHint: "Pokud do příjezdu zbývá tento počet dní nebo méně, minimální pobyt se vždy vrátí na výchozí hodnotu bez ohledu na poptávku.",
    losReleaseWithinDaysOffHint: "Nastavte 0, pokud má o uvolnění minimálního pobytu rozhodovat pouze poptávka.",
    losReleaseWithinDaysUnit: "dní",
    losReleaseWithinDaysNeverHint: "Při hodnotě 0 a vypnuté volbě „Uvolnit minimum při slabé poptávce“ platí minimální pobyt až do příjezdu, pokud se nevyprodá noc, kterou pobyt potřebuje.",
    losWeakWeekRelease: "Uvolnit minimum při slabé poptávce",
    losWeakWeekReleaseHint: "Když je poptávka po víkendu nebo svátcích slabá, může se minimum vrátit na výchozí hodnotu už před lhůtou „Vždy uvolnit do“, například když byly víkendy v tom období loni obsazené méně než z 60 %. Vypněte, pokud minimální pobyty plánujete dopředu a posíláte je do PMS.",
    losWeakWeekReleaseOffHint: "Minimum platí i ve slabých týdnech; uvolní se až v posledních dnech před příjezdem, které nastavíte výše, nebo když se vyprodá noc, kterou pobyt potřebuje.",
    // Místo nápovědy výše, dokud Optivo čte vlastní rezervace víkendu.
    losWeakWeekReleaseDemandHint: "Daleko před příjezdem rozhoduje historie rezervací: minimum se uvolní v týdnu, který označila za slabý, ale ne, když se víkend už teď prodává výrazně rychleji než obvykle. Od 30 dnů před příjezdem, jakmile už obvykle bývá přijata aspoň polovina rezervací, které víkend dostává, rozhodují současné rezervace: minimum se uvolní, když podle nich víkend skončí zřetelně pod svým obvyklým obsazením, a jinak se drží. Minimum uvolněné kvůli slabému týdnu se ale vrátí, až když se víkend prodává výrazně rychleji než obvykle. Lhůta „Vždy uvolnit do“ platí vždy.",
    specialDates: "Speciální dny",
    specialDatesHint: "Státní svátky jsou předvyplněny podle vybraných zemí. Duplicitní data jsou automaticky sloučeny. Každý záznam můžete individuálně upravit nebo přidat vlastní data. Příplatek za speciální den víkendový příplatek nahrazuje, nikdy se s ním nesčítá, a to jen tam, kde je vyšší — noc, jejíž vlastní víkendový příplatek (nebo příplatek jejího typu pokoje) je vyšší, si ponechá ten. Každý záznam začíná na tom, co u vás stojí špičková noc: {premium} %. Jakmile záznam upravíte, přestane ji sledovat.",

    // Krok 5 - Okno rezervací
    bookingWindowStepTitle: "Okno rezervací",
    bookingWindowStepDescription: "Jak dlouho před příjezdem drží Optivo základní cenu každého typu pokoje a odkdy smí zlevňovat, aby noc naplnilo.",
    bookingWindowTypicalDaysLabel: "Typické okno rezervací (dny)",
    bookingWindowTypicalDaysHint: "Až do tolika dní před příjezdem drží Optivo noc aspoň na její základní ceně, upravené o den v týdnu, sezónnost a svátky, i když se prodává pomalu. Uvnitř okna ji smí zlevnit, aby se naplnila. Delší okno znamená dřívější slevy, kratší okno drží cenu déle. Při cíli Maximalizovat obsazenost cenu nedrží a okno jen časuje pohyb ceny.",
    bookingWindowAnalysisHint: "Analýza: {days} dní",
    bookingWindowReset: "Obnovit hodnotu z analýzy",
    bookingWindowNoRoomKinds: "Zatím nejsou k dispozici žádné typy pokojů. Znovu spusťte onboarding pro načtení typů pokojů.",
    bookingWindowRoomKindColumn: "Typ pokoje",
    bookingWindowTypicalColumn: "Typické",
    bookingWindowDaysSuffix: "dní",
    bookingWindowSeasonReadout: "Rezervační okno podle sezóny",
    bookingWindowSeasonHigh: "Hlavní sezóna",
    bookingWindowSeasonLow: "Nízká sezóna",
    bookingWindowSeasonHint: "Každý termín patří do hlavní, nebo nízké sezóny. Sezóny jsou týdny, ne měsíce: hlavní sezóna jsou týdny, které historie rezervací označila za vytížené, a s měsíci v sekci Sezónnost nesouvisí. Každá sezóna se měří stejně, jen přes své vlastní noci, takže naměřené typické okno leží mezi oběma sezónami. Typ pokoje s příliš málo vlastními rezervacemi ukazuje okna celého ubytování; kde má příliš málo rezervací v jedné sezóně, stojí pomlčka a platí jeho typické okno.",
    leadTimeCurveTitle: "Pohyb ceny během rezervačního okna",
    leadTimeCurveHint: "Kde se cena nachází ve třech bodech rezervačního okna: dokud je pobyt ještě daleko, kolem vrcholu poptávky a s blížícím se příjezdem. Kladné procento cenu zvyšuje, záporné snižuje — každý bod si nastavíte oběma směry. Procenta platí globálně; časování jednotlivých fází se přizpůsobuje rezervačnímu oknu každého typu pokoje (Typické / Hlavní sezóna / Nízká sezóna).",
    leadTimeCurveStartPct: "Úprava před rezervačním oknem",
    leadTimeCurveStartPctHint: "Uplatní se, dokud je pobyt dál než typické rezervační okno — výchozí bod cenové křivky.",
    leadTimeCurvePeak: "Úprava na vrcholu poptávky",
    leadTimeCurvePeakHint: "Uplatní se kolem vrcholu rezervačního okna daného typu pokoje, kde křivka dosahuje nejvzdálenějšího bodu.",
    leadTimeCurveArrival: "Úprava před příjezdem",
    leadTimeCurveArrivalHint: "Uplatní se s blížícím se příjezdem v rámci rezervačního okna daného typu pokoje — koncový bod křivky.",
    leadTimeCurveReset: "Obnovit výchozí",
    leadTimeCurvePreviewTitle: "Náhled křivky",
    leadTimeCurvePreviewX: "Dní před příjezdem",
    leadTimeCurvePreviewY: "Úprava",
    leadTimeCurvePreviewBuildup: "Nabírání rezervací",
    leadTimeCurvePreviewArrival: "Příjezd",
    weekendPremium: "Denní úpravy cen",
    weekendPremiumHint: "Nastavte samostatné úpravy pro pátek, sobotu a neděli.",
    weekendDiscount: "Denní úpravy cen",
    weekendDiscountHint: "Kladná procenta použijte pro přirážky, záporná pro slevy.",
    dayAdjustmentsTitle: "Denní úpravy cen",
    dayAdjustmentsHint: "Kladná procenta použijte pro přirážky, záporná pro slevy. Ve speciálních dnech se úprava podle dne v týdnu vynechá — uplatní se pouze příplatek za speciální den.",
    dayAdjustmentsAnalysisValue: "Analýza: {value}",
    dayAdjustmentsAnalysisNights: "{nights} nocí",
    dayAdjustmentsAnalysisEvidence: "Odvozeno z {nights} pokojonocí za {months} měsíců; každý typ pokoje se porovnává sám se sebou při stejném počtu hostů.",
    dayAdjustmentsAnalysisHolidays: "Pobyty přes státní svátek nebo speciální den se nepočítají ({nights} pokojonocí): ty noci ceníte ve Svátcích a speciálních dnech.",
    dayAdjustmentsResetToAnalysis: "Vrátit na hodnoty z analýzy",
    dayAdjustmentsSourceHistoric: "Vaše vlastní historie",
    dayAdjustmentsSourceBlended: "Vaše historie zkombinovaná s výchozími hodnotami",
    dayAdjustmentsSourceDefaults: "Výchozí hodnoty Optivo",
    dayAdjustmentsStandardHint: "Nastavte, kolik má stát každá z těchto tří nocí oproti běžné noci uprostřed týdne. Ve speciálních dnech cenu nahradí příplatek za speciální den a last-minute sleva ji zruší.",
    dayAdjustmentsAnchorNamed: "Noc od pondělí do čtvrtka v pokoji {roomKind}: {price} {currency}",
    dayAdjustmentsAnchor: "Noc od pondělí do čtvrtka: {price} {currency}",
    dayAdjustmentsAnchorNote: "Od pondělí do čtvrtka zůstává tato cena. Tři noci níže se nastavují oproti ní.",
    dayAdjustmentsAnchorBaseNamed: "Základní cena pokoje {roomKind}: {price} {currency}",
    dayAdjustmentsAnchorBase: "Vaše základní cena: {price} {currency}",
    dayAdjustmentsAnchorNoteAllDays: "Každá noc níže se nastavuje oproti této ceně.",
    dayAdjustmentsAnchorExampleNamed: "Příklad: noc od pondělí do čtvrtka v typu pokoje {roomKind} stojí {price} {currency}",
    dayAdjustmentsAnchorBaseExampleNamed: "Příklad: základní cena typu pokoje {roomKind} je {price} {currency}",
    dayAdjustmentsAnchorExampleNote: "Úpravy platí pro všechny typy pokojů, každý oproti své vlastní základní ceně, která od pondělí do čtvrtka zůstává. Tři ceny níže jsou pro tento typ, ostatní se posunou o stejné procento.",
    dayAdjustmentsAnchorExampleNoteAllDays: "Úpravy platí pro všechny typy pokojů, každý oproti své vlastní základní ceně. Částky níže jsou pro tento typ, ostatní se posunou o stejné procento.",
    dayAdjustmentsMidweekHint: "Pondělí až čtvrtek začínají na 0 — vaše základní cena už je cenou všedního dne. Upravte je jen tehdy, když se ten den skutečně prodává jinak.",
    dayAdjustmentsChartTitle: "Váš týden",
    dayAdjustmentsChartConfigured: "Vaše cena",
    dayAdjustmentsChartHistory: "Za kolik jste prodali",
    dayAdjustmentsChartSuggested: "Váš týdenní vzorec",
    dayAdjustmentsChartAxisMoney: "{currency} za noc",
    dayAdjustmentsNightPrice: "Cena za noc: {day}",
    dayAdjustmentsRaise: "Zvýšit cenu: {day}",
    dayAdjustmentsLower: "Snížit cenu: {day}",
    dayAdjustmentsDelta: "{money} {currency} · {percent}",
    dayAdjustmentsPercentValue: "{value} %",
    dayAdjustmentsSameAsWeekday: "Stejně jako uprostřed týdne",
    dayAdjustmentsHistoryMoney: "Prodáno za {sold} {currency} oproti {baseline} v běžné noci ve stejných pokojích",
    dayAdjustmentsAtLimit: "Dál už úprava podle dne v týdnu nesahá. Pro větší výkyvy použijte sezónnost nebo speciální dny.",
    dayAdjustmentsSimulatorNote: "Sezónnost, poptávka a vaše cenové limity platí dál — konečnou cenu ukáže živý příklad ceny.",
    dayAdjustmentsInheritedValue: "Celé ubytování: {value}",
    mondayAdjustment: "Pondělní úprava",
    tuesdayAdjustment: "Úterní úprava",
    wednesdayAdjustment: "Středeční úprava",
    thursdayAdjustment: "Čtvrteční úprava",
    fridayAdjustment: "Páteční úprava",
    saturdayAdjustment: "Sobotní úprava",
    sundayAdjustment: "Nedělní úprava",

    // Krok 5.6 - Last-Minute ceny (nyní vlastní krok průvodce)
    lastMinuteStepTitle: "Last-Minute ceny",
    lastMinuteStepDescription: "Nastavte automatické snižování cen s blížícím se příjezdem, aby se zaplnily neprodané pokoje.",
    lastMinuteTitle: "Last-Minute ceny",
    lastMinuteEnabled: "Aktivovat Last-Minute ceny",
    lastMinuteMinPrice: "Cílová minimální cena",
    lastMinuteMinPriceHint: "Nejnižší cena, které last-minute algoritmus dosáhne. Nechte prázdné pro použití minimální ceny z klíčových metrik.",
    lastMinuteAdjustmentStepsLabel: "Počet cenových úprav",
    lastMinuteStepsUnit: "kroků",
    lastMinutePacingLabel: "Tempo slevy",
    lastMinutePreview: "Křivka slevy — přetáhněte sloupce pro úpravu",
    lastMinuteResetCurve: "Obnovit křivku",
    lastMinutePerRoomKindTitle: "Přepsat dle typu pokoje",
    lastMinutePerRoomKindHint: "Přepisuje aktivaci pro jednotlivé typy pokojů. Minimální cena se kopíruje z kroku Přepisy dle typu pokoje a sdílená křivka slevy zůstává společná.",
    lastMinuteUseDefault: "Řídit podle poptávky",
    lastMinuteOverrideEnabled: "Přepis aktivace",
    lastMinuteOverrideMinPrice: "Přepis minimální ceny",
    lastMinuteRoomKindMinPriceLabel: "Minimální cena",
    lastMinuteRoomKindMinPriceHint: "Zkopírováno z kroku Přepisy dle typu pokoje. Upravte hodnotu tam.",
    gapFillingTitle: "Vyplňování mezer",
    gapFillingHint: "Detekuje krátké osiřelé mezery mezi rezervacemi a zlevní je, aby se lépe doprodaly.",
    gapFillingEnabled: "Povolit vyplňování mezer",
    gapFillingMaxGapDays: "Maximální velikost mezery",
    gapFillingMaxGapDaysHint: "Největší mezera, která se má ještě nacenit samostatně.",
    gapFillingAdjustmentPct: "Úprava ceny",
    gapFillingAdjustmentPctHint: "Jak se pohne cena pro dny, které spadají do detekované rezervační mezery. Obvykle sleva — zadejte plus, pokud je jedna noc mezi dvěma pobyty spíš vzácná než nechtěná. Sleva ustoupí tam, kde ji noc nepotřebuje: u data, které jste nacenili sami, u noci, která se už prodává výrazně nad cílem, nebo u noci, jejíž cenu převzalo last-minute. Plus se uplatní vždy.",
    gapFillingNight: "noc",
    gapFillingNights: "noci",
    gapFillingStepTitle: "Vyplňování mezer",
    gapFillingStepDescription: "Detekuje krátké osiřelé mezery mezi rezervacemi a nacení je samostatně, aby se lépe doprodaly.",

    // Step 5.6b - Bridge-day premium (wizard step strings)
    bridgeDaysStepTitle: "Přemosťovací dny a okraje špičky",
    bridgeDaysStepDescription: "Naceňte běžné noci kolem svátečního volna: tu sevřenou uvnitř něj i ty těsně před ním a po něm.",
    bridgeDaysTitle: "Přemosťovací dny",
    bridgeDaysHint: "Pokud jediný běžný den leží mezi dvěma prémiovými dny (svátek↔víkend, víkend↔svátek nebo svátek↔svátek), nacení se také jako prémiový den.",
    bridgeDaysEnabled: "Povolit přirážku za přemosťovací den",
    bridgeDaysPremiumPct: "Úprava ceny",
    bridgeDaysPremiumPctHint: "Jak se pohne cena na detekovaném přemosťovacím dni. Obvykle přirážka — zadejte minus, pokud je právě prostřední noc ta, o kterou nikdo nestojí.",
    bridgeDaysFollowsWeekend: "Odvozeno od víkendové přirážky {weekend} %. Přemosťovací noc se oceňuje jako prémiová noc, mezi kterými leží.",
    bridgeDaysOffWeekend: "Nastaveno ručně. Vaše víkendová přirážka je {weekend} %.",

    // Krok 5.7 - Denní úpravy cen
    dayAdjustmentsStepTitle: "Denní úpravy cen",
    dayAdjustmentsStepDescription: "Nastavte, kolik má která noc v týdnu stát oproti vaší základní ceně. Pátek, sobotu a neděli mění většina ubytování; expertní režim otevře zbytek týdne.",

    // Krok 6 - Strategie (Pacing)
    strategyTitle: "Nastavení strategie",
    strategyDescription: "Vyberte cíl optimalizace a nastavte cenové limity.",
    pacingStrategyTitle: "Strategie tempa rezervací",
    pacingStrategyDescription: "Vaše strategie sleduje tempo rezervací vůči historickým benchmarkům a automaticky upravuje ceny.",
    howPacingWorks: "Jak funguje sledování tempa",
    aheadOfPace: "Před plánem",
    aheadOfPaceHint: "Rezervace překonávají očekávání — zvyšte ceny pro vyšší příjem.",
    onTrack: "Podle plánu",
    onTrackHint: "Rezervace odpovídají očekávání — udržujte aktuální ceny.",
    behindPace: "Za plánem",
    behindPaceHint: "Rezervace zaostávají za očekáváním — snižte ceny pro stimulaci poptávky.",
    kpiBaselines: "KPI Referenční hodnoty (z analýzy)",
    optimizationGoal: "Cíl optimalizace",
    revenueGoal: "Maximalizovat příjmy",
    revenueGoalHint: "Vyšší ceny při silné poptávce, mírné slevy v pomalých obdobích.",
    hybridGoal: "Doporučeno Optivem",
    hybridGoalHint: "Před mediánem rezervací drží ceny jako Maximalizovat příjmy, blíž k příjezdu přepne na Maximalizovat obsazenost.",
    occupancyGoal: "Maximalizovat obsazenost",
    occupancyGoalHint: "Agresivnější slevy pro naplnění pokojů, menší přirážky při vysoké poptávce.",
    roomKindStrategyOverridesTitle: "Ceny podle typu pokoje",
    roomKindStrategyOverridesHint: "Jsou zde dva druhy nastavení a při ponechání prázdného pole se chovají jinak — pravidlo každé skupiny je uvedeno níže.",
    roomKindStrategyOverridesEssentialHint: "Nastavte základní, minimální a maximální cenu pro každý typ pokoje. Optivo z nich počítá všechny ceny daného typu pokoje.",
    overrideUseDefault: "Použít výchozí",
    overrideGoal: "Cíl optimalizace",
    overrideBasePrice: "Základní cena",
    overrideMinPrice: "Minimální cena",
    overrideMaxPrice: "Maximální cena",
    overrideFromAnalysisHint: "Z analýzy",
    overrideMinPriceFromBase: "Návrh podle vašeho cenového profilu: {pct} základní ceny. Zadejte vlastní hodnotu pro přepsání.",
    overrideMaxPriceFromBase: "Návrh podle vašeho cenového profilu: {pct} základní ceny. Zadejte vlastní hodnotu pro přepsání.",
    overridePriceVatNote: "Uvedená cena je vč. DPH za dvoulůžkový pokoj (double use).",
    overrideMissingBasePriceWarning: "Pro tento typ pokoje není nastavena základní cena, takže pro něj Optivo nevypočítá žádné ceny. Zadejte ji zde.",
    overrideMissingBasePriceSummary: "Základní cena není nastavena pro: {roomKinds}. Dokud ji nezadáte, Optivo pro tyto typy pokojů nevypočítá žádné ceny.",
    roomKindOwnScopeTitle: "Ceny — jen tento typ pokoje",
    roomKindOwnScopeHint: "Základní cena je povinná — Optivo z ní počítá každou cenu pro tento typ pokoje. Za těmito poli nestojí žádná cena platná pro celé ubytování: pokud pole necháte prázdné, Optivo použije cenu odvozenou z historie tohoto typu pokoje, a pokud žádná není, nedokáže tento typ pokoje nacenit vůbec.",
    roomKindOwnScopeBadge: "Jen tento typ pokoje",
    roomKindBasePriceRequiredBadge: "Povinná",
    roomKindPropertyScopeTitle: "Strategie — přepisuje nastavení celého ubytování",
    roomKindPropertyScopeHint: "Prázdné pole znamená, že platí nastavení pro celé ubytování. Vyplňte je jen tehdy, když se má tento typ pokoje chovat jinak než zbytek ubytování.",
    roomKindInheritedBadge: "Celé ubytování",
    roomKindOverriddenBadge: "Přepsáno",
    roomKindOverrideReset: "Obnovit hodnotu celého ubytování",
    overrideBasePriceNoPropertyFallback: "Prázdné — Optivo použije cenu odvozenou z historie tohoto typu pokoje. Žádná základní cena pro celé ubytování neexistuje.",
    roomKindLeadTimeCurveTitle: "Pohyb ceny",
    roomKindLeadTimeCurveHint: "Dokud něco nezměníte, ukazují hodnoty platné pro celé ubytování. Jakákoli úprava dá tomuto typu pokoje vlastní křivku; reset vrátí nastavení celého ubytování.",
    roomKindLeadTimeCurveStartPct: "Úprava před rezervačním oknem",
    roomKindLeadTimeCurveStartPctHint: "Uplatní se, dokud je pobyt dál než typické rezervační okno tohoto typu pokoje.",
    roomKindDayAdjustmentsTitle: "Den v týdnu",
    roomKindDayAdjustmentsHint: "Každá noc se řídí týdnem celého ubytování, dokud ji nezměníte. Změněná noc patří tomuto typu pokoje, ostatní se řídí dál — i když se týden celého ubytování později změní. Reset vrátí všechny.",
    overrideTierLabel: "Kategorie",
    tierFromBasePrice: "Ze základní ceny",
    tierOverrideCount: "Přepsání pro {count} typ(ů) pokojů",
    reactivity: "Cenová reaktivita",
    reactivityHint: "Jak velkou změnu ceny smí Optivo provést, když si o ni řekne obsazenost a tempo rezervací. Všechny úrovně reagují na stejné signály — liší se velikostí kroků nahoru i dolů.",
    gentle: "Mírná",
    gentleHint: "Malé kroky. Ceny se drží blízko základní ceny — mírné přirážky v silných termínech, mírné slevy ve slabých. Hosté vidí stabilní a předvídatelné ceny. Výchozí volba, která sedí většině ubytování.",
    moderate: "Střední",
    moderateHint: "Znatelné pohyby. Ceny viditelně sledují poptávku oběma směry, ale zůstávají ukotvené k základní ceně. Pro ubytování, kde poptávka zřetelně kolísá a má se to v ceně projevit.",
    aggressive: "Agresivní",
    aggressiveHint: "Velké pohyby. Výrazné přirážky, když se termíny plní, a hluboké slevy na záchranu pomalu se prodávajících termínů. Z poptávky vytěží nejvíc, hosté ale uvidí výrazně proměnlivé ceny.",
    reactivityUpTo: "až",
    reactivityOccLabel: "obs.",
    reactivityPaceLabel: "tempo",
    pickupInfluenceTitle: "Přírůstek rezervací",
    pickupInfluenceHint: "Reagujte na to, jak rychle se termín plní právě teď, nejen na to, jak je plný.",
    pickupInfluenceEnabled: "Nechat nedávný pickup ovlivnit cenu",
    pickupInfluenceStrength: "Jak těsně sledovat pickup",
    pickupInfluenceStrengthHint: "Vyšší hodnota sleduje nedávnou rychlost rezervací těsněji. Dopad je oběma směry omezen na 5 % vypočtené ceny.",
    pickupWindowDays: "Měřit pickup za",
    pickupWindowDaysHint: "Jak daleko zpět se dívat při otázce, jak rychle se termín plní. Kratší reaguje dřív, delší je stabilnější.",
    pickupInfluenceGuardrail: "Pickup a tempo sdílejí jeden limit: dohromady nikdy neposunou cenu dál, než dovolují sleva při nízkém a příplatek při vysokém tempu výše. Pickup zůstává neaktivní, dokud termín nemá naměřenou historii za celé okno.",
    minRoomsForRoomKindSignal: "Min. pokojů pro vlastní signál",
    minRoomsForRoomKindSignalHint: "Kategorie s méně pokoji, než je tento práh, vždy použijí signál celého ubytování — pro obsazenost, tempo rezervací, pickup i týdenní signál poptávky — protože pár pokojů je pro výpočet ceny příliš rozkolísaný.",
    propertyPaceWeight: "Váha celkového tempa",
    propertyPaceWeightHint: "Podíl úpravy podle tempa rezervací řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že tempo ubytování přispívá 60 % a tempo kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí tempo celého ubytování.",
    propertyPaceWeightShort: "ubytování",
    roomKindPaceWeightShort: "kategorie",
    propertyDemandWeight: "Váha celkové poptávky",
    propertyDemandWeightHint: "Podíl týdenního signálu poptávky řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že týden celého ubytování přispívá 60 % a vlastní týden kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí signál poptávky celého ubytování. Ve vysvětlení ceny jde o řádek „Týdenní poptávka z historie“.",
    propertyDemandWeightShort: "ubytování",
    roomKindDemandWeightShort: "kategorie",
    propertyPickupWeight: "Váha celkového pickupu",
    propertyPickupWeightHint: "Podíl měření pickupu řízený celým ubytováním (oproti vybrané kategorii pokoje). 60 % znamená, že pickup celého ubytování přispívá 60 % a vlastní pickup kategorie 40 %. Kategorie pod nastaveným prahem vždy použijí pickup celého ubytování.",
    propertyPickupWeightShort: "ubytování",
    roomKindPickupWeightShort: "kategorie",
    postureBiasTitle: "Přechod z ochrany ceny do plnění",
    postureBiasDescription: "Doporučeno Optivem chrání vaši cenu, dokud je termín dál než vaše obvyklé rezervační okno, a pak přejde ke slevám, aby ho naplnilo. Tyto tři hodnoty určují, kde přechod nastane, jak ostře proběhne a zda ho posune tempo prodeje daného termínu.",
    postureBias: "Bod přepnutí",
    postureBiasHint: "50 přepíná ve vašem obvyklém rezervačním okně; nižší hodnota drží cenu po větší část okna, vyšší začíná plnit dřív. Při 0 se chová přesně jako Maximalizovat výnos, při 100 přesně jako Maximalizovat obsazenost.",
    postureBiasBalanced: "přepíná ve vašem obvyklém rezervačním okně",
    postureBiasRateLeaning: "kloní se k ochraně ceny",
    postureBiasFillLeaning: "kloní se k naplnění",
    postureBiasLikeRevenue: "stejné jako Maximalizovat výnos",
    postureBiasLikeOccupancy: "stejné jako Maximalizovat obsazenost",
    hybridChangeoverDays: "Přechod rozložit na",
    hybridChangeoverDaysHint: "0 přepne v jediný den. Širší okno cenu z ochrany do plnění plynule převede místo skoku, takže host, který se podívá dvakrát, neuvidí schod.",
    hybridChangeoverDaysUnit: "dní na každou stranu",
    hybridPacePull: "Nechat tempo termínu bod posunout",
    hybridPacePullHint: "0 ponechá přepnutí na kalendáři. Nad nulou si termín, který se dobře prodává, drží cenu i za bodem přepnutí a termín, který se prodává špatně, začne plnit dřív.",
    simOccupancyDemand: "Poptávka dle obsazenosti",
    simPaceAdjust: "Úprava dle tempa (vpředu)",
    simDaysUntilArrival: "Dní do příjezdu",
    simOccupancy: "Obsazenost",
    simRoomKindOccupancy: "Obsazenost kategorie pokoje",
    simRoomKindOccupancyHint: "Obsazenost vybrané kategorie pokoje — ovlivňuje úpravy před příjezdem a podle tempa rezervací pro tuto kategorii.",
    simPropertyOccupancy: "Obsazenost ubytování",
    simPropertyOccupancyHint: "Obsazenost celého ubytování — táhne s sebou cenu každého typu pokoje, nahoru i dolů.",
    simBookingSpeed: "Rychlost rezervací",
    simPaceBehind: "Pozadu",
    simPaceOnTrack: "V plánu",
    simPaceAhead: "Napřed",
    customizeValues: "Upravit hodnoty",
    customLabel: "vlastní",
    paceLower: "Sleva při nízkém tempu",
    paceUpper: "Příplatek při vysokém tempu",
    paceFloorHint: "Kolik nejvýš smí tempo rezervací z ceny slevit, když se termín prodává pomaleji než obvykle.",
    paceCeilingHint: "Kolik nejvýš smí tempo rezervací k ceně přidat, když se termín prodává rychleji než obvykle. Omezuje také, o kolik nad základní cenou začíná ještě před první rezervací noc, jejíž typ se obvykle prodává brzy, třeba sobota vyprodaná měsíce dopředu.",
    losRestrictions: "Omezení délky pobytu",
    defaultMinLos: "Výchozí min. LOS",
    defaultMinLosHint: "Minimální pobyt, který se použije, když jej nic jiného nezvyšuje. Vše ostatní se vrací na tuto spodní hranici.",
    losNightsUnit: "nocí",
    losAnalysisStayLength: "Analýza: medián {median} nocí · průměr {avg}",
    losAnalysisOneNightShare: "{pct} % tvoří jednonoční pobyty",

    // Tipy k parametrům (průvodce)
    highSeasonMultiplierHint: "Posune sazbu v hlavní sezóně. Např. +15 % znamená, že základ 2 000 se stane 2 300; minus tyto měsíce naopak zlevní.",
    lowSeasonMultiplierHint: "Posune sazbu v klidných měsících. Obvykle minus pro zvýšení obsazenosti — plus, pokud jsou právě klidné měsíce ty, kdy máte pokojů málo.",
    weekendPremiumParamHint: "Denní úpravy umožňují nastavit jinou cenu pro pátek, sobotu i neděli.",
    simDayType: "Den",
    simWeekday: "Pracovní den",
    simMonday: "Pondělí",
    simTuesday: "Úterý",
    simWednesday: "Středa",
    simThursday: "Čtvrtek",
    simFriday: "Pátek",
    simSaturday: "Sobota",
    simSunday: "Neděle",
    simDateType: "Typ dne",
    simDateTypeRegular: "Běžný",
    simDateTypeHoliday: "Svátek",
    simDateTypeBridge: "Přemostěný den",
    simDateTypeShoulder: "Okraj špičky",
    simDateTypeGapFill: "Vyplnění mezery",
    simLosRelaxed: "MinLOS uvolněn",
    simMonth: "Měsíc",
    simDynamicDemand: "Dynamická poptávka",
    simSpecialDayOverride: "Nahrazeno příplatkem za speciální den — příplatek za speciální den a víkendový příplatek se nikdy nesčítají.",

    // Štítky cenového simulátoru
    simBase: "Základní sazba",
    simSeason: "Sezónní úprava",
    simHighSeason: "Hlavní sezóna",
    simLowSeason: "Mimo sezónu",
    simRoomKind: "Typ pokoje",
    simShowPreview: "Zobrazit živý příklad výpočtu ceny",
    simHidePreview: "Skrýt živý příklad výpočtu ceny",
    // Řádky rozpadu ceny — bez nich panel spadne zpět na napevno psanou angličtinu.
    simExampleTitle: "Živý příklad výpočtu ceny",
    simFinal: "Výsledná cena",
    simClamped: "Omezeno limity",
    simNights: "nocí",
    lastMinuteSimHours: "Hodin do konce okna",
    lastMinuteActive: "Last-minute úprava aktivní",

    // Krok 7 - Kontrola
    reviewTitle: "Kontrola a aktivace",
    reviewDescription: "Ověřte svá nastavení a aktivujte cenovou strategii.",
    bookingBehavior: "Chování při rezervacích",
    strategyPostureLabel: "Nastavení strategie",
    analysisSummary: "Shrnutí analýzy",
    analysisSummaryDetail: "Na základě analyzovaných rezervací bude vytvořena strategie s těmito parametry.",
    finalized: "Nastavení dokončeno! Vaše cenová strategie byla vytvořena.",
    finalizeError: "Nepodařilo se dokončit nastavení. Zkuste to prosím znovu.",
    finalizing: "Vytvářím strategii…",
    activateStrategy: "Aktivovat strategii",
    reviewReassuranceTitle: "Do vašeho PMS se zatím nic neodesílá",
    reviewReassuranceNoAutoPush: "Žádné ceny se do PMS neodesílají automaticky — aktivací se cenový plán vytvoří pouze uvnitř Optiva.",
    reviewReassuranceReviewFirst: "Každé doporučení si nejprve projdete v cenovém kalendáři, noc po noci.",
    reviewReassuranceExplicitConfirm: "Ceny se do PMS dostanou až poté, co je výslovně potvrdíte a odešlete.",
    reviewReassurancePreviewMode: "Optivo zůstává v režimu náhledu, dokud odesílání nezapnete ve Správci připojení.",
  },

  // ── Recent Bookings ─────────────────────────────────────
  recentBookings: {
    title: "Poslední rezervace",
    subtitle: "Rezervace podle data vytvoření",
    emptyTitle: "Žádné rezervace",
    emptyDescription: "V tomto období nebyly vytvořeny žádné rezervace. Zkuste vybrat jiný rozsah dat.",
    channel: "Kanál",
    roomType: "Typ pokoje",
    stayDates: "Termín pobytu",
    bookedOn: "Datum rezervace",
    pricePaid: "Zaplacená cena",
    roomPerNight: "Pokoj / noc",
    priceImpact: "Navrhovaná cena",
    priceImpactTooltip: "Navrhovaná noční sazba po této rezervaci. Delta zahrnuje vliv obsazenosti typu pokoje, vliv obsazenosti celého ubytování i úpravu dle tempa rezervací – stejný výpočet jako u návrhů cen.",
    occupancyChange: "Obsazenost",
    occupancyTooltip: "Obsazenost typu pokoje a celého hotelu v první noci pobytu – před a po této rezervaci. Rozbalte řádek pro zobrazení všech nocí.",
    hotelWideDemand: "Hotelová poptávka",
    hotelWideOccupancy: "celk. obs.",
    propertyOccupancyUplift: "Hotelová poptávková přirážka",
    propertyOccupancy: "Obsazenost hotelu",
    roomKindOccupancy: "Obsazenost typu",
    paceEffect: "Vliv tempa",
    paceRatioLabel: "tempo",
    paceAheadOfExpected: "rychleji než očekáváno",
    paceBehindExpected: "pomaleji než očekáváno",
    paceOnExpected: "v souladu s očekáváním",
    perNightBreakdown: "Rozpis po nocích",
    expandNights: "Zobrazit všechny noci",
    collapseNights: "Skrýt noci",
    nightColumn: "Noc",
    occupancyEffectLabel: "Vliv obsazenosti",
    night: "noc",
    nights: "nocí",
    searchChannelPlaceholder: "Hledat kanál…",
    searchRoomTypePlaceholder: "Filtrovat typy pokojů…",
    noRoomTypes: "Žádné typy pokojů",
    priceFrom: "Od",
    priceTo: "Do",
    clearFilter: "Smazat",
    clearAllFilters: "Smazat vše",
    rowsPerPage: "Řádků",
    showing: "Zobrazeno",
    of: "z",
    period: "Období",
    refresh: "Obnovit",
    viewInCalendar: "Zobrazit v kalendáři",
    viewInDrafts: "Zobrazit v návrzích",
    quickRanges: "Rychlý výběr",
    today: "Dnes",
    last3Days: "Poslední 3 dny",
    lastWeek: "Poslední týden",
    last2Weeks: "Poslední 2 týdny",
    last3Weeks: "Poslední 3 týdny",
    canceled: "Stornováno",
    canceledTooltip: "Tato rezervace byla stornována. Dopad na cenu ukazuje, kolik kapacity bylo uvolněno a jak se ceny snížily.",
    notReportedWithCommission: "Neuvedeno · s provizí",
  },

  // ── Forecast / Prognóza ────────────────────────────────
  forecast: {
    title: "Prognóza tržeb",
    totalRevenue: "Celkové tržby",
    totalRoomNights: "Prodané pokojové noci",
    totalReservations: "Rezervace",
    revenueByRoomKind: "Tržby podle typu pokoje",
    roomType: "Typ pokoje",
    revenue: "Tržby",
    lastYearRevenue: "Loňský rok",
    change: "Změna",
    comparison: "Porovnání",
    roomNights: "Pokojové noci",
    avgRate: "Prům. sazba",
    total: "Celkem",
    refresh: "Obnovit",
    upToToday: "Do dneška",
    fullMonth: "Celý měsíc",
    days: "dny",
    comparing: "Porovnání",
    vs: "vs",
    fullMonthWarning: "",
    incompleteLastYear: "",
    emptyTitle: "Žádná data o tržbách",
    emptyDescription: "Data o tržbách se zde zobrazí po zaznamenání rezervací v systému.",
    months: ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"] as const,
  },

  // ── Prognóza poptávky a obsazenosti ──────────────────
  demandForecast: {
    title: "Prognóza poptávky a obsazenosti",
    subtitle: "Výhledová prognóza poptávky a obsazenosti na 365 dní.",
    emptyState: "Žádný inventar pokojů — připojte PMS pro načtení prognózy.",
    noBaselineWarning: "Zatím málo historických dat — prognóza vychází pouze ze stávajících rezervací. Přesnost se zlepší s přibývající historii.",
    horizon: {
      "30": "30 dní",
      "90": "90 dní",
      "180": "180 dní",
      "365": "365 dní",
    },
    summary: {
      next7d: "Příštích 7 dní",
      next30d: "Příštích 30 dní",
      next90d: "Příštích 90 dní",
      next365d: "Příštích 365 dní",
      avgOccupancy: "Prům. predikovaná obsazenost",
      rooms: "pokojů",
    },
    charts: {
      forecastTitle: "Predikovaná obsazenost",
      forecastHint: "Predikce kombinuje aktuální rezervace s historickou křivkou pickupu. Neomezená poptávka odstraňuje strop kapacity.",
      forecastSeries: "Predikce %",
      unconstrainedSeries: "Neomezená poptávka %",
      historicalAvgSeries: "Historický průměr %",
      capacity: "100% kapacita",
      bookingCurveTitle: "Historické tempo rezervací",
      bookingCurveHint: "Typický podíl finálních rezervací již zaknihovaných v daném předstihu. Strmější křivka = více last-minute rezervací.",
      bookingCurveSeries: "Zaknihováno",
      daysBeforeStay: "dní před pobytem",
      otbTitle: "Aktuálně zaknihováno",
      otbHint: "Pokojové noci aktuálně rezervované pro každý budoucí datum pobytu.",
      otbSeries: "Pokojové noci",
    },
    demandIndex: {
      title: "Detekovaný index poptávky",
      hint: "Týdenní tlak trhu kombinující historickou průměrnou cenu s tím, jak je každý týden ve skutečnosti obsazený, takže sezóny s vysokou poptávkou rozpozná i tehdy, když byly v minulosti podhodnocené.",
      propertySeries: "Celý objekt",
      roomKindSeries: "Typ pokoje",
      typicalBand: "Běžné",
      premiumBand: "Premium",
      discountBand: "Sleva",
      selectRoomKind: "Vyberte typ pokoje",
      noRoomKind: "Pouze celý objekt",
      fallback: "Pro {roomKind} používáme index poptávky celého objektu, dokud není dostatek dat pro typ pokoje.",
      empty: "Spusťte analýzu objektu pro detekci týdenních vzorců poptávky.",
      week: "Týden",
      adr: "Prům. cena",
      reservations: "Rezervace",
      action: "Akce",
      premium: "Premium",
      discount: "Sleva",
      noAction: "Bez akce",
      source: "Zdroj",
      occupancyIndexSeries: "Poptávka dle obsazenosti",
      occupancyRate: "Obsazenost",
    },
    insights: {
      topDemand: "Data s nejvyšší poptávkou",
      topDemandHint: "Kandidáti na navýšení cen.",
      promotionCandidates: "Kandidáti na promo",
      promotionCandidatesHint: "Data s nejnižší poptávkou v příštích 90 dnech — zvažte slevy nebo marketing.",
      empty: "Nejsou žádná data.",
    },
    table: {
      dailyTitle: "Denní prognóza (příštích 90 dní)",
      dailyHint: "Denní rozpis on-the-books, očekávaného pickupu a predikované obsazenosti.",
      date: "Datum",
      daysOut: "Dní",
      otb: "OTB",
      expectedPickup: "Pickup",
      forecast: "Predikce",
      unconstrained: "Neomezená",
      historicalAvg: "Hist. prům.",
      confidence: "Spolehlivost",
    },
    confidence: {
      low: "Nízká",
      medium: "Střední",
      high: "Vysoká",
    },
  },

  // ── Performance / Výkonnost ─────────────────────────────────
  performance: {
    title: "Výkonnost",
    subtitle: "Rezervace, ceny a obsazenost v čase.",
    emptyState: "V tomto období zatím nejsou žádné rezervace.",
    rangePresets: {
      "30_30": "\u00b130 dní",
      "30_90": "-30 / +90",
      "90_90": "\u00b190 dní",
      "180_180": "\u00b1180 dní",
    },
    summary: {
      totalBookings: "Vytvořené rezervace",
      totalBookingsHint: "Ve vybraném období",
      totalNights: "Pokojové noci",
      totalNightsHint: "Prodané v období",
      avgOccupancy: "Prům. obsazenost",
      rooms: "pokojů",
      avgPrice: "Prům. cena za noc",
      avgPriceHint: "Přes prodané noci",
      totalRevenue: "Celkové tržby",
      totalRevenueHint: "Tržby dle data pobytu",
    },
    charts: {
      occupancyPriceTitle: "Obsazenost a průměrná cena",
      occupancyPriceHint: "Podle data pobytu. Levá osa = obsazenost %, pravá = prům. cena za noc ({currency}).",
      occupancySeries: "Obsazenost %",
      avgPriceSeries: "Prům. cena",
      bookingsTitle: "Vytvořené rezervace za den",
      bookingsHint: "Počet nových rezervací zavedených do PMS každý den.",
      bookingsSeries: "Rezervace",
      today: "Dnes",
    },
    // The month-by-month card under the daily charts (`performance/MonthlyComparisonCard`).
    monthly: {
      title: "Měsíc po měsíci oproti loňsku",
      hint: "Ukončené měsíce proti stejným měsícům o rok dříve. Oba roky se počítají s dnešními pokoji.",
      periodLabel: "Období",
      monthsOptions: { "3": "3 měsíce", "6": "6 měsíců", "12": "12 měsíců" },
      metricLabel: "Zobrazit",
      metrics: { revenue: "Tržby", revpar: "RevPAR", occupancy: "Obsazenost", adr: "ADR" },
      metricHints: {
        revenue: "Tržby měsíce podle data pobytu",
        revpar: "Tržba na dostupnou pokojovou noc",
        occupancy: "Prodané pokojové noci z dostupných",
        adr: "Průměrná cena prodané pokojové noci",
      },
      thisYear: "Letos",
      lastYear: "Loni",
      lastYearValue: "Loni {value}",
      summaryHint: "Za měsíce, které mají oba roky",
      month: "Měsíc",
      pointsUnit: "p. b.",
      refresh: "Načíst měsíce znovu",
      noLastYear: "Loňský měsíc v uložené historii není, proto se neporovnává.",
      empty: "Žádný ukončený měsíc zatím nemá rezervaci.",
    },
  },

  // ── Segment & Channel Yield Report ────────────────────
  segmentChannel: {
    title: "Kanály a segmenty",
    subtitle: "Čisté tržby a délka pobytu podle zdroje rezervace.",
    emptyState: "Ve vybraném období zatím nejsou žádné rezervace.",
    rangePresets: {
      "30": "Posledních 30 dní",
      "90": "Posledních 90 dní",
      "180": "Posledních 180 dní",
      "365": "Posledních 365 dní",
    },
    summary: {
      netRevenue: "Čisté tržby",
      commissionDeducted: "odečteno na provizích",
      directShare: "Podíl direct rezervací",
      directShareHint: "Z čistých tržeb (bez provize)",
      avgLos: "Prům. délka pobytu",
      avgLosHint: "Noci na rezervaci",
    },
    actionHint: {
      title: "Direct poptávka je silná",
      description:
        "Direct rezervace už generují {direct} čistých tržeb. Zvažte uzavření nebo omezení kanálů s vysokou provizí ({channels}) na špičkové noci a chraňte si marži.",
    },
    table: {
      title: "Rozpad podle kanálu",
      hint: "Seřazeno podle čistých tržeb. Provize jsou odhady podle běžných sazeb v oboru.",
      hintWithReported:
        "Seřazeno podle čistých tržeb. Provize pochází z PMS, kde ji uvádí, jinak z běžných sazeb v oboru.",
      channel: "Kanál / segment",
      bookings: "Rezervace",
      roomNights: "Pokojové noci",
      avgLos: "LOS",
      revenue: "Hrubé tržby",
      commission: "Odhad provize",
      commissionWithReported: "Provize",
      netRevenue: "Čisté tržby",
      share: "Podíl čistých tržeb",
      directLabel: "Direct (bez provize)",
      estCommission: "Odhad provize",
      reportedCommission: "Provize podle PMS",
      channelNotReported: "Kanál neuveden",
      totalsRow: "Celkem",
    },
    disclaimer:
      "Sazby provizí jsou odhady: Booking.com 15 %, Expedia 18 %, Airbnb 14 %, Agoda 17 %, ostatní OTA 12 %, direct 0 %. Nahraďte vlastními sjednanými sazbami, jakmile je budete mít.",
    editor: {
      openButton: "Upravit provize",
      title: "Vlastní sazby provizí kanálů",
      description:
        "Nahraďte výchozí hodnoty svými sjednanými sazbami. Prázdné pole zachová výchozí sazbu. Uloženo pro aktuální PMS připojení.",
      defaultLabel: "Výchozí",
      overrideBadge: "vlastní sazba",
      resetRow: "Vrátit výchozí",
      resetAll: "Resetovat vše",
      save: "Uložit",
      saving: "Ukladám…",
      savedToast: "Sazby provizí uloženy.",
      invalidValue: "{label}: zadejte procento mezi 0 a 100.",
    },
  },

  // ── Situace na trhu ───────────────────────────────────
  marketConditions: {
    title: "Situace na trhu",
    subtitle: "Co žene poptávku ve vašem okolí a za kolik nabízejí okolní ubytování.",
    rangePresets: {
      "30": "Následujících 30 dní",
      "90": "Následujících 90 dní",
      "180": "Následujících 180 dní",
    },
    context: {
      market: "Trh",
      captured: "Načteno {when}",
      neverCaptured: "Zatím nenačteno",
      stale: "Neaktuální",
      mixedCurrency:
        "Tento trh má záznamy i v měně {currencies}. Ceny se nepřepočítávají, zobrazuje se proto jen {currency}.",
      noCurrencyMatch:
        "Tento trh byl načten pouze v měně {currencies}, ale vy vykazujete v {currency}. Spusťte načtení znovu, ať vidíte ceny ve své měně.",
    },
    actions: {
      refreshSignals: "Načíst signály",
      refreshSignalsHint: "Svátky a počasí. Žádné vyhledávání nespotřebuje.",
      refreshCompset: "Načíst konkurenci",
      refreshCompsetHint: "Za každou načtenou noc spotřebuje jedno vyhledávání z měsíčního limitu.",
      refreshing: "Načítám…",
      fetchLighthouse: "Načíst z Lighthouse",
      fetchLighthouseHint: "Načte vaši sadu konkurentů z Lighthouse hned, nejvýše jednou za hodinu.",
    },
    summary: {
      demandIndex: "Prům. poptávka",
      demandIndexHint: "0,50 je běžná noc",
      supply: "Konkurence",
      supplyHint: "Medián nabídek na noc",
      medianRate: "Mediánová cena",
      medianRateHint: "Napříč načtenými nocemi",
      spread: "Rozptyl",
      spreadHint: "p75 − p25 vůči mediánu",
      captures: "Načtení",
      capturesHint: "Běhy pokrývající toto období",
    },
    weatherExtremes: {
      label: "Extrémní počasí",
      lowersDemand: "Čte se jako snížená poptávka",
      watch: "Sledovat",
      severe: "Závažné",
      kinds: {
        heat: "Vlna veder",
        cold: "Silný mráz",
        downpour: "Přívalový déšť",
        snowstorm: "Sněhová bouře",
        gale: "Vichřice",
        thunderstorm: "Bouřka",
        freezingRain: "Mrznoucí déšť",
      },
      readings: {
        heat: "Pocitově {value} °C",
        cold: "Pocitově {value} °C",
        downpour: "{value} mm srážek",
        snowstorm: "{value} cm sněhu",
        gale: "Nárazy až {value} km/h",
        thunderstorm: "Předpověď bouřky",
        freezingRain: "Předpověď mrznoucího deště",
      },
    },
    demandChart: {
      title: "Výhled poptávky",
      hint: "Jedno skóre na noc ze svátků, počasí, vašich speciálních dat a cen konkurence.",
      series: "Index poptávky",
      highBand: "Silnější než obvykle",
      typicalBand: "Běžná",
      lowBand: "Slabší než obvykle",
      drivers: "Způsobeno",
      noDrivers: "Pro tuto noc nic výjimečného.",
      empty: "Zatím nejsou načteny žádné signály. Načtěte svátky a počasí.",
      metrics: {
        "holiday.school": "Školní prázdniny",
        "special_date.multiplier": "Vaše speciální data",
        "weather.temp_max_c": "Teplota",
        "weather.temp_min_c": "Noční minimum",
        "weather.apparent_max_c": "Pocitově (den)",
        "weather.apparent_min_c": "Pocitově (noc)",
        "weather.precipitation_mm": "Déšť",
        "weather.snowfall_cm": "Sníh",
        "weather.wind_gust_kmh": "Nárazy větru",
        "weather.extreme_severity": "Extrémní počasí",
        "compset.rate_percentile": "Ceny konkurence",
        "compset.supply_count": "Nabídka konkurence",
        "compset.median_rate": "Mediánová cena konkurence",
        "compset.market_demand": "Poptávka na trhu",
      },
    },
    marketDemand: {
      title: "Poptávka na trhu po nocích",
      night: "Noc",
      level: "Poptávka na trhu",
      note: "Načteno s vaší konkurenční sadou. Zatím se jen zobrazuje, ceny neovlivňuje.",
    },
    bandChart: {
      title: "Ceny konkurence po nocích",
      hint: "Medián se zvýrazněnou prostřední polovinou trhu. Nejlevnější dostupný pokoj, dvě osoby.",
      medianSeries: "Medián",
      bandSeries: "Prostřední polovina",
      minSeries: "Nejlevnější",
      maxSeries: "Nejdražší",
      supply: "Nabídky",
      empty: "Pro toto období zatím nejsou načteny ceny konkurence.",
    },
    heatStrip: {
      title: "Noci na první pohled",
      hint: "Odstín podle poptávky. Vyberte načtenou noc a uvidíte, kdo je na trhu.",
      legendLow: "Klid",
      legendHigh: "Rušno",
      noData: "Pro toto období zatím není co zobrazit.",
      supply: "nabídek",
      today: "Dnes",
    },
    table: {
      title: "Kdo je na trhu",
      hint: "Nejnižší nabízená cena za noc pro dva dospělé, {date}.",
    },
    states: {
      noLocationTitle: "Není určena poloha ubytování",
      noLocationBody:
        "Tržní signály se vážou na skutečnou polohu ubytování. Nastavte ji v kroku Profil ubytování a svátky, počasí i konkurence se doplní.",
      noLocationAction: "Přejít na nastavení",
      noCaptureTitle: "Zatím nenačteny žádné ceny konkurence",
      noCaptureBody:
        "Ceny konkurence se načítají v omezeném počtu za měsíc: každá načtená noc spotřebuje jedno vyhledávání. Noční úloha data postupně doplní, nebo je načtěte hned.",
      noCaptureAction: "Načíst hned",
      noCaptureUnavailable:
        "Server nemá nastavený zdroj dat o konkurenci, načtení proto není dostupné.",
    },
    toast: {
      signalsRefreshed: "Tržní signály načteny.",
      compsetRefreshed: "Načteno {dates} nocí za {searches} vyhledávání.",
      nothingDue: "Všechny noci v období jsou aktuální — žádné vyhledávání se nespotřebovalo.",
      budgetExhausted: "Limit vyhledávání konkurence je vyčerpán. Načítání bude pokračovat po jeho obnovení; větší limit sjednáváme individuálně.",
      captureFailed: "Načtení konkurence selhalo. Zkuste to prosím později.",
      notConfigured: "Na serveru není nastavený zdroj dat o konkurenci.",
      failed: "Tržní data se nepodařilo načíst.",
      lighthouseFetched: "Ceny konkurence načteny z Lighthouse.",
      lighthouseBudget: "Lighthouse už byl dnes načten tolikrát, kolikrát lze. Znovu se čte zítra.",
      lighthouseRunning: "Lighthouse se právě načítá.",
      lighthouseRecent: "Lighthouse byl načten před méně než hodinou. Zkuste to později.",
      lighthouseNotLinked: "K tomuto ubytování není propojen žádný účet Lighthouse.",
      lighthouseOff: "Lighthouse je na tomto serveru vypnutý.",
    },
  },

  pms: {
    // Rezervační systém, pro který připojení je. Ptáme se na něj před všemi
    // poli formuláře, protože každé pole patří jednomu PMS
    // (`components/pms/PmsProviderPicker.tsx`). Uložené připojení svůj PMS
    // jen uvádí, protože žádné uložení ho nemění.
    providerChoice: {
      question: "Který rezervační systém (PMS) používáte?",
      description: "Podle vaší volby se níže zobrazí jen údaje, které Optivo k připojení potřebuje.",
      options: {
        previo: "API klíč (nebo přihlašovací e-mail a heslo) a ID hotelu z Previa",
        better_hotel: "API klíč z Better Hotelu",
      },
      label: "Rezervační systém",
      fixedHint: "Připojení zůstává u systému, pro který vzniklo. Objekt v jiném systému připojíte jako nové připojení.",
    },
    providers: {
      previo: {
        displayName: "Previo",
        fields: {
          authMode: {
            label: "Způsob ověření",
            placeholder: "Vyberte způsob",
            help: "Doporučujeme API klíč. Přihlášení jménem a heslem zvolte jen u starších účtů Previo, které ještě nemají API klíč.",
            options: {
              apiKey: "API klíč",
              password: "Jméno a heslo",
            },
          },
          apiKey: {
            label: "API klíč",
            placeholder: "např. 3hNNDdGVFNaRDdmSDNvd",
            help: "Vložte API klíč vydaný Previem. Odesílá se v autorizační hlavičce a ukládá se jako tajemství.",
          },
          login: {
            label: "Přihlašovací e-mail",
            placeholder: "vy@hotel.cz",
            help: "E-mail, kterým se přihlašujete do Previa.",
          },
          password: {
            label: "Heslo",
            placeholder: "Zadejte své heslo do Previa",
            help: "V Optivu uloženo šifrovaně a používá se pouze pro volání Previo API vaším jménem.",
          },
          hotId: {
            label: "ID hotelu",
            placeholder: "např. 12345",
            help: "Číselný identifikátor, který vašemu ubytování přidělilo Previo.",
          },
          langId: {
            label: "ID jazyka",
            placeholder: "1",
            help: "Kód jazyka pro odpovědi Previo API. Ponechte výchozí (1), pokud nepotřebujete jiné prostředí.",
          },
        },
      },
      better_hotel: {
        displayName: "Better Hotel",
        fields: {
          apiKey: {
            label: "API klíč",
            placeholder: "Vložte svůj Better Hotel API klíč",
            help: "Získáte ho sami v Better Hotelu: v „Připojené aplikace“ přidejte aplikaci OptivoRMS (je mezi „Revenue managery“) a zkopírujte její klíč. Zacházejte s ním jako s tajemstvím.",
          },
        },
      },
    },
    nonRefundableHintTemplate:
      "Pošle nevratnou cenu do tohoto jednoho cenového plánu {providerName} pro každý povolený typ pokoje.",
    noRatePlansHintTemplate:
      "Z PMS nejsou k dispozici žádné cenové plány. Ověřte, že vaše připojení k {providerName} je v pořádku.",
  },

  // ── Ollama AI Settings ───────────────────────────────────
  ollamaSettings: {
    title: "Nastavení Ollama AI",
    description: "Konfigurujte připojení k serveru Ollama AI a spravujte lokální modely.",
    configTitle: "Připojení",
    configDescription: "Zadejte URL vašeho serveru Ollama a ověřte konektivitu.",
    ollamaApiBase: "Základní URL Ollama API",
    helper: "Nechte prázdné pro výchozí hodnotu (http://localhost:11434).",
    healthChecking: "Probíhá kontrola...",
    healthCheckButton: "Uložit a otestovat připojení",
    saveButton: "Uložit",
    healthStatusOk: "Připojeno.",
    healthStatusError: "Nedostupné.",
    toastSaved: "Nastavení uloženo",
    toastSavedDesc: "Nastavení připojení Ollama bylo uloženo.",
    toastSaveFailed: "Uložení selhalo",
    healthSuccess: "Připojení úspěšné",
    healthSuccessDesc: "Ollama je dostupná a odpovídá.",
    healthFailed: "Připojení selhalo",
    modelPullStarted: "Stahování modelu zahájeno",
    modelPullComplete: "Stahování modelu dokončeno",
    modelPullFailed: "Stahování modelu selhalo",
    modelTitle: "Model",
    modelDescription: "Vyberte AI model, který bude Optivo používat pro cenové návrhy.",
    modelLabel: "Aktivní model",
    modelPlaceholder: "Hledat modely…",
    localModelsGroup: "Lokální modely",
    cloudModelsGroup: "Cloudové / API modely",
    badgeCloud: "Cloud",
    badgeAvailable: "Staženo",
    badgeNotDownloaded: "Nestaženo",
    badgeSelected: "Aktivní",
    pullButton: "Stáhnout",
    pullError: "Chyba stahování",
    pullProgress: "Stahování…",
    downloadedTitle: "Stažené modely",
    downloadedDescription: "Všechny modely aktuálně dostupné na vašem serveru Ollama.",
    loadingModels: "Načítání modelů…",
    noModelsDownloaded: "Zatím nejsou staženy žádné modely.",
    refreshModels: "Obnovit",
    notesTitle: "Poznámky",
    notesDescription: "Důležité informace o konfiguraci Ollama.",
    accessLabel: "Přístup:",
    notesAccess: "Server Ollama musí být dostupný ze služby Optivo, nejen z vašeho prohlížeče.",
    serverLabel: "Server:",
    notesServer: "Optivo se připojuje k Ollama na straně serveru. Ujistěte se, že základní URL API je dostupná ze serverového prostředí.",
  },

  // Nastavení účtu — stránka za dveřmi účtu v liště, jeden oddíl na položku
  // `components/account/account-sections.ts`.
  account: {
    title: "Nastavení účtu",
    description: "Platby, bezpečnostní tokeny a AI agenti vašeho účtu.",
    doorLabel: "Nastavení účtu",
    navLabel: "Oddíly nastavení účtu",
    sections: {
      billing: {
        title: "Platby a fakturace",
        description: "Vaše předplatné Optivo, měsíční cena a platby.",
      },
      tokens: {
        title: "Bezpečnostní tokeny",
        description: "Tokeny, se kterými vaši AI agenti a skripty pracují s vašimi objekty.",
      },
      mcp: {
        title: "AI agenti (MCP)",
        description: "Připojte AI agenta, například Claude, k serveru MCP Optiva.",
      },
    },
    // Místo oddílu tokenů nebo AI agentů, když je server odmítne kvůli
    // přepínači, který administrátor vypnul, zatímco byla stránka otevřená
    // (`FEATURE_NOT_ENABLED`). "{email}" je adresa podpory, kreslená jako odkaz.
    featureNotEnabled: {
      title: "AI agenti nejsou pro váš účet zapnuti",
      body: "Optivo je zapíná každému zvlášť. Napište na {email} a zapneme vám je. Vaše bezpečnostní tokeny zůstávají uložené a po zapnutí budou znovu fungovat.",
    },
    // Tělo oddílu bezpečnostních tokenů; nadpis a popis kreslí stránka
    // ze `sections.tokens`.
    tokens: {
      intro: "Pro každého agenta nebo skript vytvořte vlastní token, ať můžete každý odvolat zvlášť. Token dosáhne jen na objekty, které spravujete, a nic z toho, co udělá, se samo neodešle do vašeho PMS.",
      listTitle: "Vaše tokeny",
      count: "{count} z {limit}",
      create: "Vytvořit token",
      limitReached: {
        one: "Máte {count} token, nejvíc, kolik smí jeden účet mít. Chcete-li vytvořit další, odvolejte ho.",
        few: "Máte {count} tokeny, nejvíc, kolik smí jeden účet mít. Chcete-li vytvořit další, jeden odvolejte.",
        other: "Máte {count} tokenů, nejvíc, kolik smí jeden účet mít. Chcete-li vytvořit další, jeden odvolejte.",
      },
      empty: "Zatím nemáte žádný bezpečnostní token. Vytvořte jeden pro každého AI agenta nebo skript, který připojíte.",
      loadFailed: "Bezpečnostní tokeny se nepodařilo načíst.",
      retry: "Zkusit znovu",
      columns: {
        name: "Název",
        access: "Přístup",
        properties: "Objekty",
        lastUsed: "Naposledy použit",
        expires: "Platnost",
      },
      access: {
        read: "Jen čtení",
        write: "Čtení a úprava cen",
      },
      allProperties: "Všechny objekty, které spravujete",
      propertyById: "Objekt č. {id}",
      propertiesUnavailable: {
        one: "{count} objekt už není dostupný",
        few: "{count} objekty už nejsou dostupné",
        other: "{count} objektů už není dostupných",
      },
      neverUsed: "Zatím nepoužit",
      expired: "Vypršel",
      expiredHint: "Smaže se {date}, {days} dní po vypršení.",
      expiresSoon: "Vyprší {relative}",
      revokeLabel: "Odvolat {name}",
      revoke: "Odvolat",
      revokeTitle: "Odvolat token „{name}“?",
      revokeBody: "Každý agent nebo skript, který ho používá, okamžitě přestane fungovat. Tento krok nelze vrátit.",
      revokeConfirm: "Odvolat token",
      revoked: "Token „{name}“ byl odvolán.",
      revokeFailed: "Token se nepodařilo odvolat. Zkuste to znovu.",

      createTitle: "Vytvořit bezpečnostní token",
      createDescription: "Token uvidíte jen jednou, hned po vytvoření.",
      nameLabel: "Název",
      nameHint: "Napište, co ho bude používat, třeba „Claude na mém notebooku“, ať víte, který odvolat.",
      nameRequired: "Pojmenujte token.",
      nameTooLong: "Název může mít nejvýš {max} znaků.",
      accessLabel: "Co smí dělat",
      accessReadHint: "Čte ceny, rezervace, analýzy, strategii a stav synchronizace. Nic nemění.",
      accessWriteHint: "Navíc smí nastavovat, posouvat a zamykat ceny a minimální pobyty. Nikdy nic neodesílá do vašeho PMS — to udělá automatická synchronizace objektu.",
      accessInvalid: "Vyberte, co token smí dělat.",
      propertiesLabel: "Objekty",
      propertiesAll: "Všechny objekty, které spravujete, i ty, které přidáte později",
      propertiesSome: "Jen tyto objekty",
      propertiesNone: "Zatím nespravujete žádný objekt. Token na něj dosáhne, jakmile nějaký připojíte nebo se k němu přidáte.",
      propertiesRequired: "Vyberte alespoň jeden objekt.",
      propertiesUnknown: "Jeden z vybraných objektů už nespravujete. Vyberte znovu.",
      expiryLabel: "Platnost",
      expiryDays: {
        d7: "7 dní",
        d30: "30 dní",
        d90: "90 dní",
        d180: "180 dní",
        d365: "1 rok",
      },
      expiryDaysOther: { one: "{count} den", few: "{count} dny", other: "{count} dní" },
      expiryOn: "Přestane fungovat {date}.",
      expiryInvalid: "Vyberte, jak dlouho bude token platit.",
      submit: "Vytvořit token",
      creating: "Vytváření…",
      createFailed: "Token se nepodařilo vytvořit. Zkuste to znovu.",

      createdTitle: "Token „{name}“ je vytvořen",
      createdDescription: "Zkopírujte si ho hned — Optivo ho ukáže jen tentokrát. Uložte ho jako heslo.",
      secretLabel: "Váš nový bezpečnostní token",
      copy: "Zkopírovat token",
      copied: "Token zkopírován.",
      copyFailed: "Token se nepodařilo zkopírovat. Označte ho a zkopírujte ručně.",
      connectAgent: "Připojit AI agenta",
      connectAgentHint: "Nastavení AI agentů se otevře na nové kartě, takže token tu zůstane, dokud si ho nezkopírujete.",
      done: "Hotovo",
    },
    // Tělo oddílu AI agentů: vypínač, adresa serveru a co na něm agenti smí.
    mcp: {
      loadFailed: "Nastavení AI agentů se nepodařilo načíst.",
      retry: "Zkusit znovu",
      unavailableTitle: "Server MCP je vypnutý",
      unavailableBody: "Optivo svůj server MCP zatím vypnulo, takže se žádný agent nepřipojí. Vaše nastavení i tokeny zůstávají, až bude server zase v provozu.",
      accessTitle: "Přístup",
      enabledLabel: "Povolit připojení AI agentů",
      enabledHint: "Vypnutím odmítnete každého agenta, který používá vaše tokeny, aniž byste je odvolali.",
      enabledOn: "AI agenti se mohou připojit s vašimi tokeny.",
      enabledOff: "AI agenti se už s vašimi tokeny nepřipojí.",
      saveFailed: "Nastavení se nepodařilo uložit. Zkuste to znovu.",
      endpointLabel: "Adresa serveru",
      copyEndpoint: "Zkopírovat adresu",
      endpointCopied: "Adresa zkopírována.",
      copyFailed: "Zkopírování se nepodařilo. Označte text a zkopírujte ho ručně.",
      transport: "Streamable HTTP · protokol {version}",
      authHint: "Agent se přihlašuje bezpečnostním tokenem, který posílá s každým požadavkem jako",
      createToken: "Vytvořit bezpečnostní token",
      connectTitle: "Připojte svého agenta",
      connectDescription: "Místo {placeholder} vložte jeden ze svých bezpečnostních tokenů a soubor uložte tam, kde ho čtete jen vy.",
      clients: {
        claudeCode: "Claude Code",
        claudeDesktop: "Claude Desktop",
        vscode: "VS Code",
        other: "Cursor / jiný",
      },
      clientHints: {
        claudeCode: "Spusťte v terminálu.",
        claudeDesktop: "Vložte do souboru claude_desktop_config.json a Claude Desktop restartujte. Kvůli npx potřebuje Node.js.",
        vscode: "Vložte do souboru .vscode/mcp.json (nebo do uživatelského mcp.json) — VS Code čte servery pod klíčem \"servers\".",
        other: "Pro Cursor nebo jiného klienta, který se připojuje přes HTTP a čte soubor mcp.json s hlavičkami pod klíčem \"mcpServers\".",
      },
      copySnippet: "Zkopírovat",
      copySnippetLabel: "Zkopírovat nastavení pro {client}",
      snippetCopied: "Zkopírováno.",
      toolsTitle: "Co agenti umí",
      toolsDescription: "Nástroje, které server MCP Optiva nabízí, popsané tak, jak je čte agent — proto anglicky. Token jen pro čtení uvidí jen nástroje, které čtou.",
      toolRead: "Čte",
      toolWrite: "Upravuje ceny",
      limitsTitle: "Co agenti nikdy nedělají",
      limitScope: "Agent dosáhne jen na objekty, které jeho token pokrývá, a jen dokud je spravujete.",
      limitNever: "Nikdy neodesílá ceny do vašeho PMS, nemění strategii, nevidí přihlašovací údaje k PMS a nesahá na fakturaci ani na členy.",
      limitLocked: "Cenu, kterou agent nastaví, Optivo zamkne a odešle ji příští automatická synchronizace objektu.",
      limitLog: "Optivo zaznamená každou úpravu, kterou agent udělá, i token, se kterým ji udělal.",
    },
  },

  // Billing — předplatné, zkušební období, promo kód, platby QR + GoPay
  billing: {
    loadFailed: "Předplatné se nepodařilo načíst.",

    // Karta stavu předplatného
    statusTitle: "Předplatné",
    statusTrialing: "Zkušební období zdarma",
    statusActive: "Aktivní",
    statusUnpaid: "Vyžaduje platbu",
    trialEndsOn: "Zkušební období končí {date}",
    trialDaysLeft: "Zbývá dní: {count}",
    paidUntil: "Zaplaceno do {date}",
    unpaidHint: "Zkušební období skončilo. Zaplaťte níže první měsíc, aby cenotvorba běžela dál.",
    currencyLabel: "Měna fakturace",
    currencyFixedHint: "Optivo fakturuje v českých korunách.",

    // Promo kód
    promoTitle: "Promo kód",
    promoHint: "Máte promo kód? Prodlouží zkušební období o jeden měsíc.",
    promoPlaceholder: "Zadejte kód",
    promoApply: "Použít",
    promoAppliedToast: "Promo kód použit — zkušební období prodlouženo o měsíc.",
    promoAppliedBadge: "Promo kód uplatněn",
    promoFailed: "Promo kód se nepodařilo použít.",

    // Fakturační období
    intervalMonthly: "Měsíčně",
    intervalYearly: "Ročně",
    intervalSaved: "Fakturační období změněno",
    intervalSaveFailed: "Fakturační období se nepodařilo změnit",
    priceTotalYear: "Celkem za rok",
    // Pod roční částkou. Rok je dvanáct měsíců za měsíční cenu, takže řádek
    // říká, že měsíc stojí stejně jako při měsíční platbě.
    priceMonthlyEquivalent: "{amount} měsíčně, stejně jako při měsíční platbě",

    // Hrazené objekty — která napojení na PMS předplatné hradí. Každý hrazený
    // objekt platí měsíční cenu svého produktu a cenu za každý svůj pokoj
    // (5. 10. 2026), řádek proto uvádí produkt, pokoje a měsíční částku.
    propertiesTitle: "Hrazené objekty",
    // Věty …Price následují za svou nápovědou jen tam, kde cenu určuje ceník:
    // ne u individuální ceny ani nad API z doby před ceníkem.
    propertiesHint: "Vyberte, které z vašich objektů toto předplatné hradí.",
    propertiesHintPrice: "Každý hrazený objekt platí měsíční cenu svého produktu a za každý svůj pokoj.",
    propertiesAllHint: "Hrazeny jsou všechny vaše objekty včetně těch, které přidáte později.",
    propertiesAllHintPrice: "Každý další přidá měsíční cenu svého produktu a svých pokojů.",
    propertiesSomeHint: "Předplatné hradí {count} z vašich {total} objektů. Objekt přidaný později hrazen nebude, dokud ho zde nezaškrtnete.",
    // Objekty jsou připojené a žádný není zaškrtnutý: ty, které předplatné
    // hradilo, byly smazány a uložený výběr nejmenuje nic, co ještě existuje.
    // Dál vede zaškrtnutí, a proto se seznam ukáže i pro jediný objekt.
    propertiesNoneHint: "Předplatné nehradí žádný z vašich objektů, takže se nic neúčtuje. Zaškrtněte ty, které má hradit.",
    // Pokoje, pro které Optivo v objektu nastavuje ceny: účtuje se každý.
    // Jednotka pronájmu se počítá jako pokoj, jak říká ceník.
    propertiesRooms: { one: "{count} pokoj", few: "{count} pokoje", other: "{count} pokojů" },
    propertiesMonthlyPrice: "{price} / měsíc",
    propertiesUnnamed: "Objekt {id}",
    propertiesExcluded: "Neúčtuje se",
    // Hrazený objekt bez naceněného pokoje se neúčtuje také.
    propertiesNoRooms: "Bez pokojů zařazených do cenotvorby",
    propertiesAtLeastOne: "Ponechte hrazený alespoň jeden objekt.",
    propertiesSaved: "Hrazené objekty byly upraveny",
    propertiesSaveFailed: "Hrazené objekty se nepodařilo upravit",

    // Rozpis ceny — jeden řádek na každý účtovaný objekt: ceníková cena jeho
    // produktu a jeho pokojů, potom to, co dohodla podpora, a celkem. Žádná
    // sleva.
    priceTitle: "Měsíční cena",
    // Jak produkty jmenuje ceník. Klíče jsou z cenového jádra: `pro` je klíč,
    // pod kterým se OPTIVO Hotels ukládalo, dokud šlo o tarif, který si
    // předplatné volilo. Od 5. 10. 2026 se produkt řídí objektem.
    productNames: {
        pro: "OPTIVO Hotels",
        rentals: "OPTIVO Rentals",
    },
    // Jediný řádek nad API starším než tento ceník: jeho cena se počítá
    // jinak, stránka proto uvede jeho částku a žádnou ceníkovou cenu.
    priceSubscription: "Předplatné Optivo",
    // Jedno předplatné, jedna platba, všechny objekty — uvedeno tam, kde se
    // platí. Říká se jen o dvou a více objektech.
    priceCoverageTitle: "Zahrnuto v této platbě",
    priceCoverageMulti: {
        one: "Jedna platba pokrývá váš objekt.",
        few: "Jedna platba pokrývá všechny {count} objekty.",
        other: "Jedna platba pokrývá všech {count} objektů.",
    },
    priceCoverageSome: "Tato platba pokrývá {count} z vašich {total} objektů.",
    // Jediný objekt: odpověď na „mohu platit za více objektů najednou?“ dřív,
    // než druhý přibude. Za ceníkovou cenu přidá další objekt cenu svého
    // produktu a svých pokojů; u individuální ceny platí, co bylo dohodnuto.
    priceCoverageSingle:
        "Jedno předplatné pokrývá všechny objekty na vašem účtu — jakmile připojíte další, přidá se do této platby.",
    priceCoverageSingleList:
        "Jedno předplatné pokrývá všechny objekty na vašem účtu — jakmile připojíte další, přidá se do této platby za cenu svého produktu a svých pokojů.",
    // Hrazený objekt, který v ceně není, a proč.
    priceNotBilled: "Neúčtuje se: {properties} (bez pokojů zařazených do cenotvorby).",
    // Nic se neúčtuje: karta ukáže ceník jako nabídku místo nulového součtu.
    priceNoneCovered: "Předplatné zatím nehradí žádný objekt, takže se nic neúčtuje. Každý připojený objekt platí měsíční cenu svého produktu a za každý svůj pokoj.",
    // Objekty jsou připojené a žádný není zaškrtnutý: řečeno jako zaškrtnutí,
    // které je třeba udělat, nikdy jako objekt, který je třeba připojit.
    priceNoneTicked: "Předplatné nehradí žádný z vašich objektů, takže se nic neúčtuje. Zaškrtněte objekt v části „Hrazené objekty“ a bude platit měsíční cenu svého produktu a za každý svůj pokoj.",
    priceNoRoomsPriced: "Žádný hrazený objekt zatím nemá pokoje zařazené do cenotvorby, takže se nic neúčtuje.",
    // Tržní data jsou v ceně obou produktů, takže řádek mají jen tam, kde
    // podpora dohodla příplatek navíc.
    priceMarketData: "OPTIVO Market Data",
    priceMarketDataAgreed: "Příplatek dohodnutý s Optivo",
    priceTotal: "Celkem za měsíc",
    // Hrazený objekt, jehož PMS se nepodařilo načíst, nemá žádný pokoj, a
    // objekt bez naceněného pokoje se neúčtuje: v ceně chybí celý.
    roomsIncomplete: "Jeden z vašich objektů se nepodařilo načíst, takže v této ceně chybí. Zaplatit půjde, jakmile se ho podaří načíst.",
    // Cena od podpory, která nahrazuje ceníkovou cenu všech hrazených objektů.
    priceIndividual: "Individuální cena dohodnutá s Optivo",

    // Ceník vlastními slovy stránky, pod rozpisem ceny. Každé číslo je z API
    // (`billing.products`); stránka žádné nepíše sama, takže nemůže uvést
    // cenu, kterou pokladna neúčtuje.
    priceListTitle: "Ceník",
    priceListUnit: "měsíčně: za objekt a za každý pokoj",
    // Pod vlastní cenou produktu: kolik přidá každý jeho pokoj. {price} je
    // cena pokoje z API.
    priceListPerRoom: "+ {price} za pokoj",
    // Pro koho každý produkt je, podle jeho rodiny. {types} jsou názvy typů
    // ubytování tak, jak je nabízí průvodce nastavením, a {rentals} produkt,
    // od kterého se ten hotelový odlišuje: stojí v seznamu první, takže by
    // „každý jiný objekt“ zaznělo dřív, než byl nějaký jmenován. Ceník žádnou
    // hranici velikosti nemá, takže ji neuvádí ani jedna věta.
    priceListAudience: {
        hotels: "Každý objekt jiného typu, než pro jaké je {rentals}, i ten, u kterého typ zatím není uvedený.",
        rentals: "Objekt typu {types}, bez ohledu na počet pokojů.",
    },
    priceListIncludedLabel: "V ceně každého z nich:",
    // Řádek o tržních datech se uvádí, jen dokud API říká, že je zahrnuje
    // každý produkt. Odkud data jsou, se neuvádí nikdy.
    priceListIncluded: {
        cadence: "ceny aktualizované až {count}× denně, na {days} dní dopředu",
        marketData: "tržní data (ceny konkurence) a počasí",
        analytics: "prognózy a analytika",
        ratePlans: "navázané cenové plány tam, kde je váš hotelový systém má",
        users: "neomezený počet uživatelů",
    },
    // Analýzy strategie zahrnuté ve čtvrtletí: ta, o kterou se žádá přes
    // „Zeptat se Optiva“. Tvar se volí podle počtu z API.
    priceListAnalyses: {
        one: "jedna analýza strategie s doporučeným nastavením za čtvrtletí",
        few: "{count} analýzy strategie s doporučeným nastavením za čtvrtletí",
        other: "{count} analýz strategie s doporučeným nastavením za čtvrtletí",
    },
    priceListByAgreement: {
        one: "Více než jedna analýza strategie za čtvrtletí nebo rozšířená tržní data se sjednávají individuálně — napište na {email}.",
        few: "Více než {count} analýzy strategie za čtvrtletí nebo rozšířená tržní data se sjednávají individuálně — napište na {email}.",
        other: "Více než {count} analýz strategie za čtvrtletí nebo rozšířená tržní data se sjednávají individuálně — napište na {email}.",
    },
    priceListTypeHint: "Produkt objektu se řídí jen jeho typem a účtuje se každý pokoj, pro který v něm Optivo nastavuje ceny. Typ se volí v průvodci nastavením, v kroku Profil ubytování.",
    priceListLink: "Celý ceník",

    // Platba
    payTitle: "Platba",
    // Období určuje, co platba kupuje a jak často se karta strhává, takže
    // věty, které ho jmenují, mají měsíční a roční podobu.
    payHint: "Zaplaťte další měsíc předplatného.",
    payHintYear: "Zaplaťte další rok předplatného.",
    payGopay: "Zaplatit přes GoPay",
    payGopayUnavailable: "GoPay zatím není nastaveno — použijte bankovní převod.",
    legalTitle: "Podmínky služby",
    legalHint: "Dokumenty, kterými se vaše předplatné řídí. Každý se otevře v nové záložce.",
    // Předplatné nehradí žádný objekt, platba by nic nekoupila a server ji
    // odmítne. Řečeno větou, ne vypnutým tlačítkem.
    payNothingToBill: "Před předplatným připojte objekt — cena je za objekt a jeho pokoje, takže zatím není za co platit. Zkušební období mezitím běží dál.",
    // Objekty jsou připojené a žádný není zaškrtnutý: takovou platbu server
    // odmítne stejně jako platbu za žádný objekt.
    payNoneTicked: "Předplatné nehradí žádný z vašich objektů, takže zatím není za co platit. Zaškrtněte objekt výše v části „Hrazené objekty“.",
    // Objekty hrazené jsou, ale žádný nemá naceněný pokoj: cena je nulová
    // a server takovou platbu odmítne také.
    payNoRoomsPriced: "Zatím není co platit — žádný hrazený objekt nemá pokoje zařazené do cenotvorby.",
    // Hrazený objekt se nepodařilo načíst: v ceně chybí nebo je oceněn jako
    // jiný produkt a server ceníkovou platbu neotevře, dokud neodpoví. Zkusit
    // to znovu znamená znovu načíst, ne zkoušet platit.
    payRoomsUnread:
        "Váš objekt se nepodařilo načíst z hotelového systému, takže cenu zatím nelze potvrdit. Platbu půjde zahájit, jakmile systém odpoví.",
    payRoomsUnreadSeveral:
        "Jeden z hrazených objektů se nepodařilo načíst z hotelového systému, takže cenu zatím nelze potvrdit. Platbu půjde zahájit, jakmile systém odpoví — nebo tento objekt, který je výše uveden bez ceny, z předplatného vynechte a zaplaťte za ostatní.",
    payCheckAgain: "Zkusit znovu",
    // Podpora sjednala nulovou cenu: nic se nedluží a platbu na nulu server
    // odmítne.
    payNothingOwed: "Podle ceny sjednané s Optivem není co platit, takže se žádná platba nevytváří.",
    payAcceptedCards: "Přijímáme karty Visa a Mastercard zabezpečené 3-D Secure. Údaje o kartě zadáváte v bráně GoPay a k Optivu se nedostanou.",

    // Opakované platby — souhlas musí být popsán dřív, než je udělen
    recurringOptIn: "Obnovovat automaticky každý měsíc",
    recurringOptInYear: "Obnovovat automaticky každý rok",
    recurringDisclosureMonthly: "Zaplacením zároveň souhlasíte s tím, že z této karty budeme jednou měsíčně strhávat platbu, vždy v den, kdy končí zaplacené období.",
    recurringDisclosureYearly: "Zaplacením zároveň souhlasíte s tím, že z této karty budeme jednou ročně strhávat platbu, vždy v den, kdy končí zaplacené období.",
    // Co částkou hýbe podle ceníku z 5. 10. 2026: hrazené objekty, počet
    // jejich pokojů, individuálně sjednaná služba a ceník sám. Jde o platbu s
    // proměnnou částkou, slovy dokumentu /recurring-terms.
    recurringDisclosureVariable: "Částka se řídí objekty, které předplatné pokrývá, a počtem jejich pokojů: změní se jen tehdy, když se změní ony, individuálně sjednaná služba nebo ceník. Aktuální částku vždy uvidíte zde ještě před stržením.",
    recurringDisclosureCancel: "Automatické obnovování můžete kdykoli zrušit na této stránce. Zrušení je okamžité a zdarma.",
    recurringTermsLink: "Podmínky opakovaných plateb",
    recurringActive: "Automatické obnovování je zapnuté.",
    recurringActiveFrequency:
        "Z této karty strháváme platbu automaticky vždy v den, kdy končí zaplacené období, a to za období následující.",
    recurringSince: "Karta ověřena {date}",
    recurringLastCharge: "Naposledy strženo {date}",
    recurringCancel: "Zrušit automatické obnovování",
    recurringCanceled: "Automatické obnovování zrušeno",
    recurringCancelFailed: "Automatické obnovování se nepodařilo zrušit",
    // Stojí před poznámkou, kterou zanechal průchod obnovení. Poznámka není
    // vždy selhání — když brána na stržení neodpověděla, není známo, jestli
    // platba prošla — takže tato slova o výsledku nic netvrdí.
    recurringFailedNotice: "K poslední automatické platbě:",
    // Obnovení se strhává jednou (/recurring-terms, článek 5): když platba
    // neprojde, stránka řekne kdy, co se s ní stalo a že kartu znovu
    // nezkoušíme. Jedna věta pro každý druh, který API pojmenuje; „Other“
    // je pro druh, který pojmenuje až pozdější API, s tím, co zaznamenalo.
    recurringFailedDeclined: "Automatická platba {date} neproběhla: karta byla zamítnuta.",
    recurringFailedTimedOut:
        "Automatická platba {date} neproběhla: u platební brány vypršela dřív, než byla dokončena.",
    recurringFailedRefused: "Automatická platba {date} neproběhla: platební brána ji odmítla.",
    recurringFailedOther: "Automatická platba {date} neproběhla.",
    recurringNoRetry: "Znovu se o stržení platby z karty sami nepokusíme.",
    recurringPayAnotherWay:
        "Toto období zaplaťte níže kartou nebo bankovním převodem. Automatické obnovování pak pokračuje od období následujícího.",
    payTermsNotice: "Zaplacením souhlasíte s",
    termsLink: "obchodními podmínkami",
    payWire: "Zaplatit bankovním převodem",
    // Bankovní převod nemá bránu, která by souhlas převzala — bereme ho zde.
    wireTermsAccept: "Přečetl/a jsem si obchodní podmínky a souhlasím s nimi",
    wireTermsHint:
        "Příkaz k bankovnímu převodu vystavíme až po vašem souhlasu. U platby kartou přebírá souhlas platební brána.",
    wireCovers: "Zahrnuje",
    paymentCreateFailed: "Platbu se nepodařilo vytvořit.",
    // Dřívější platba předplatného se ukázala jako zaplacená ve chvíli, kdy
    // se žádalo o tuto, takže se žádná nezaložila.
    paymentAlreadySettled: "Platební brána právě potvrdila dřívější platbu tohoto předplatného, nová platba se proto nezaložila.",
    // Hrazený objekt, který nejde načíst, v ceně chybí nebo je oceněn jako
    // jiný produkt, proto server platbu za ceníkovou cenu neotevře, dokud se
    // nenačte.
    paymentRoomsIncomplete:
        "Jeden z hrazených objektů se nepodařilo načíst, takže cenu předplatného zatím neznáme. Zkuste to znovu, až se ho podaří načíst.",
    gopayRedirecting: "Přesměrování na GoPay…",

    // Údaje pro bankovní převod
    wireTitle: "Bankovní převod",
    wireHint: "Naskenujte QR kód v bankovní aplikaci, nebo použijte údaje níže.",
    wireRecipient: "Příjemce",
    wireAccount: "Účet",
    wireIban: "IBAN",
    wireBic: "BIC/SWIFT",
    wireVariableSymbol: "Variabilní symbol",
    wireAmount: "Částka",
    wirePending: "Předplatné se aktivuje po připsání platby — obvykle následující pracovní den.",

    // DPH
    vatRow: "DPH ({rate} %)",
    vatReverseCharge: "Přenesená daňová povinnost — daň odvede zákazník.",
    vatOutsideScope: "Mimo předmět české DPH.",

    // Fakturační údaje tištěné na fakturách
    detailsTitle: "Fakturační údaje",
    detailsHint: "Údaje společnosti tištěné na vašich fakturách.",
    custName: "Firma / jméno",
    custStreet: "Ulice",
    custCity: "Město",
    custZip: "PSČ",
    custCountry: "Kód země",
    custIco: "IČO",
    custDic: "DIČ",
    detailsSave: "Uložit údaje",
    detailsSaved: "Fakturační údaje uloženy",
    detailsSaveFailed: "Fakturační údaje se nepodařilo uložit",

    // Faktury
    invoicesTitle: "Faktury",
    invoicesEmpty: "Zatím žádné faktury — vystavují se za každou uhrazenou platbu.",
    invoiceNumberCol: "Číslo",
    invoiceDateCol: "Vystaveno",
    invoiceTotalCol: "Celkem",
    invoiceView: "Zobrazit",
    invoicesExport: "Export CSV",

    // Historie plateb
    historyTitle: "Platby",
    historyEmpty: "Zatím žádné platby.",
    historyDate: "Vytvořeno",
    historyPeriod: "Období",
    historyMethod: "Metoda",
    historyAmount: "Částka",
    historyStatus: "Stav",
    methodWire: "Bankovní převod",
    methodGopay: "GoPay",
    paymentStatusPending: "Čeká na zaplacení",
    paymentStatusPaid: "Zaplaceno",
    paymentStatusCanceled: "Zrušeno",
    paymentStatusFailed: "Selhalo",
    // Přišlo za období, které už zaplatila jiná platba: nic se nezapočetlo
    // a peníze se vracejí.
    paymentStatusRefundDue: "K vrácení",
    paymentStatusRefunded: "Vráceno",
    // Stav, pro který tato verze stránky nemá slovo.
    paymentStatusOther: "Jiný stav",
  },


  // ── Cookie / storage disclosure ─────────────────────────
  cookieNotice: {
    regionLabel: "Informace o úložišti prohlížeče",
    title: "Optivo ukládá jen to, co potřebuje k provozu",
    body:
      "Relační cookie vás udržuje přihlášené a v tomto prohlížeči se ukládá vaše nastavení vzhledu, jazyka a zobrazení. Nepoužíváme reklamní ani sledovací nástroje třetích stran, není zde tedy co vypínat.",
    privacyLink: "Přečíst zásady ochrany osobních údajů",
    acknowledge: "Rozumím",
  },
  legalOpenPoints: {
    note: "Některé hodnoty v tomto dokumentu ještě doplníme ({count}); v textu jsou zvýrazněny.",
  },

  // ── Holidays: source markets the holiday list leaves out ─
  holidaySourceMarkets: {
    missingOne: "V seznamu chybí svátky zdrojového trhu: {countries}.",
    missingMany: "V seznamu chybí svátky těchto zdrojových trhů: {countries}.",
    add: "Přidat svátky",
  },

};
